/**
 * API client for CommunicoOS backend.
 * Centralised fetch wrapper with auth token management.
 *
 * Tokens are persisted in sessionStorage so a refresh inside the tab keeps
 * the user signed in. They never reach localStorage. Stage 4+ swaps in a
 * refresh-token flow.
 */

import type { McpLibraryToolList } from '../types/mcpLibraryTools.js';
import type { DriveFileEntry, DrivePermission } from '../types/drive.js';
import type { BillingDelivery } from '../types/billing-delivery.js';
import type { DeskCandidate, DeskCheckoutRequest, DeskLogCategory, DeskLogPatronType, DeskLogTag, DeskLogTapboardButton, DeskPlaceHoldGroupRequest, DeskPlaceHoldRequest } from '../types/desk.js';
import type {
	DeskOfflineDeletedRegistrations,
	DeskOfflineDevice,
	DeskOfflineEligibility,
	DeskOfflineExportRecord,
	DeskOfflineFollowup,
	DeskOfflineFollowupSummary,
	DeskOfflinePing,
	DeskOfflineResult,
	DeskOfflineSnapshotPage,
	DeskOfflineUnlockHolder,
	DeskOfflineWireEntry,
	DeskPatronAccount,
	DeskPaymentResult,
	DeskPaymentType,
} from './desk-offline/types.js';
import type { DeskCapabilities } from '../desk/ils-capabilities.js';
import type {
	ExploreSearchRequest,
	ExploreSearchResult,
	ExploreSimilarRequest,
	ExploreWork,
	ExploreWorksListRequest,
} from '../types/explore.js';
import type { RosterPeopleImportMapping, RosterScheduleImportMapping } from '../types/roster.js';
import type { AttendEmailResult, AttendSeriesEditInput } from '../types/attend.js';
import type { SeriesEditScope } from '../utils/seriesEditScope.js';
import { isKnownCommunicoStaff } from '../utils/sessionUser.js';
import type { RankBody, ReleaseOption, TicketChildren } from '../utils/ticketPlanning.js';
import type { TicketRequestBody, TicketRequestAnswer, PartnerStatusOption } from '../utils/ticketRequest.js';
import {
	TICKET_LINK_SUMMARY_LIMIT,
	type TicketKind, type TicketLink, type TicketLinkCandidate, type TicketLinkKind, type TicketLinkSummary,
	type CreateEngineeringTicketBody, type CreateEngineeringTicketResult,
} from '../utils/ticketLinks.js';
import type { TicketAgentLogEntry, TicketAgentLogInput, TicketFlowVocabulary } from '../utils/ticketFlow.js';
import type { TicketPullRequest, PullRequestSummary } from '../utils/ticketPullRequests.js';
import type { WorkSection } from '../utils/workSections.js';
import type { ReleasedTicket } from '../utils/releases.js';
import type { TicketDefect, TicketDefectComment, DefectAssetInput } from '../utils/ticketDefects.js';
import type { TicketChecklistItem } from '../utils/ticketChecklist.js';
import type {
	NurtureCommsInboxAdminRow,
	NurtureCommsInboxLinkableUser,
	NurtureCommsInboxMember,
	NurtureCorrespondencePurgeResult,
	NurtureCreateInboxPayload,
	NurtureEmailDraft,
	NurtureEmailDraftPayload,
	NurtureInboundEmailInbox,
	NurtureInboundEmailMessage,
	NurtureInboundEmailSendPayload,
	NurtureInboundSMSSendPayload,
} from '../types/nurture.js';
import type {
	ActionCenterBulkAction,
	ActionCenterBulkResponse,
	ActionCenterCountsResponse,
	ActionCenterResponse,
} from '../types/actionCenter.js';
import type {
	ReserveBookingInput,
	ReservePolicyGroup,
	ReserveRoomAssignmentInput,
	ReservePolicyChargesWrite,
	ReserveVerifySlotRequest,
	ReserveVerifySlotResponse,
} from '../types/reserve.js';
import type { ReservePanelCommandResult, ReservePanelAttributionSummary } from '../types/reservePanels.js';
import type { RoomSearchIntent, RoomSearchParse, RoomSearchResult } from '../types/roomsearch.js';
import type {
	ThriveCollectionsSummary,
	ThriveFilterOptions,
	ThriveItemsPage,
	ThriveItemsFilter,
	ThriveItemDetail,
	ThriveBalanceResponse,
	ThriveBalanceBySubjectResponse,
	ThriveTransferReport,
	ThriveFlagThresholdsPatch,
	ThriveThresholdsView,
	ThrivePlan,
	ThrivePlanItem,
	ThrivePlanAssignee,
	CreateThrivePlanInput,
} from '../thrive/collections.js';
import { THRIVE_NOT_ENABLED_EVENT, THRIVE_NOT_ENABLED_MESSAGE } from '../thrive/not-enabled.js';
import type { ThriveSnooze, ThriveSnoozedItem, ThriveSnoozeRequest } from '../thrive/snooze.js';
import type { ThriveArchiveSummary, ThriveArchiveItemsPage, ThriveArchiveItemsFilter, ThriveArchiveItemDetail, ThriveArchiveThresholds } from '../thrive/archive.js';
import type { ThriveTopicsSummary, TopicSetSummary, TopicSetPatch, TopicSetTrendPoint } from '../thrive/topics.js';
import type { ThriveDiscoverSummary, ThriveDiscoverItemsPage, ThriveDiscoverItemsFilter, ThriveDiscoverMatchRow, ThriveDiscoverMatchResult } from '../thrive/discover.js';
import type { PeerComparisonOptions } from '../types/peer.js';
import type {
	ThrivePatronSummary,
	ThrivePatronGroupsResponse,
	ThrivePatronGroup,
	ThrivePatronGroupPatch,
	ThrivePatronGroupCreate,
	ThrivePatronGroupPreview,
	ThriveGroupVocabulary,
	ThrivePatronMovementResponse,
	ThrivePatronListFilter,
	ThrivePatronListResponse,
	ThrivePatronPersonGroups,
	ThrivePatronHouseholdsSummary,
	ThrivePatronHouseholdsPage,
} from '../thrive/patrons.js';
import type { ThriveDemographicsResponse } from '../thrive/demographics.js';
import type { ThriveDeskActivity, ThriveOutreachActivity } from '../thrive/operations.js';
import type { ThriveCommunityMapResponse } from '../thrive/community-map.js';
import type {
	ArrSeries,
	ArrSummary,
	BillingAccount,
	BillingCurrency,
	BillingDataResponse,
	BillingListResponse,
	BillingProduct,
	BillingProductPrice,
	BillingRenewals,
	RenewalHorizonFilter,
	CohortReport,
	AmendPreview,
	AmendRequest,
	AmendResult,
	Contract,
	ContractCloseOutReason,
	ContractDeleteInventory,
	InvoiceTimingRequest,
	InvoiceTimingPreview,
	InvoiceTimingResult,
	ContractsByPartnerResponse,
	MarkCustomerLostPreview,
	MarkCustomerLostResult,
	ContractEvent,
	ContractInvoiceRow,
	ContractLine,
	ContractRevenueRow,
	ContractScheduleRow,
	ContractTerm,
	MetricsInterval,
	MovementReport,
	MovementSeriesInterval,
	MovementSeriesReport,
	OneTimeRevenue,
	PriceBook,
	NewContractRequest,
	NewContractResult,
	NewContractReview,
	Reconciliation,
	MaxioReportedFigures,
	MaxioFiguresPatch,
	MaxioFiguresResponse,
	UnmatchedResponse,
	CustomerResolution,
	CreatePartnerRequest,
	CreatedPartner,
	NearMatchPartner,
	WaterfallReport,
} from '../types/billing.js';
import type { BillingCustomerDetailWire, BillingCustomerSummary } from '../types/billing-customer.js';
import type { MidTermPreview, MidTermRequest, MidTermResult } from '../types/billing-midterm.js';
import type {
	BillingDocument,
	BillingDocumentClause,
	BillingDocumentClauseList,
	BillingDocumentCreate,
	BillingDocumentKind,
	BillingDocumentStatus,
	BillingDocumentTerm,
} from '../types/billing-documents.js';
import type {
	AccountingPeriod,
	BillingInvoiceSettings,
	BillingInvoiceSettingsPatch,
	QBOSentStatusResult,
	ApplyCreditNoteResult,
	ClosePack,
	CorrectionsRegister,
	BalanceCheck,
	ContractBalanceDetail,
	CreditNoteDetail,
	CreditNoteListEnvelope,
	RefundCreditNoteResult,
	Invoice,
	InvoiceListEnvelope,
	InvoiceRunRequest,
	InvoiceDatesResult,
	InvoiceDraftPatch,
	InvoiceLineEdit,
	DraftAddChargeBody,
	DraftChangeFigures,
	DraftChangePriceBody,
	InvoiceContractCheck,
	PeriodContractCheck,
	InvoiceRunResult,
	InvoiceSendResult,
	InvoiceSendRequest,
	AccountRecipients,
	AddressCheck,
	InvoiceTestSendResult,
	InvoiceViews,
	PartnerFinancials,
	RevenueItem,
} from '../types/billing-invoices.js';
import type {
	AgingReport,
	BillingReminderSettings,
	BillingReminderSettingsMeta,
	BillingReminderSettingsPatch,
	BillingTemplatePreview,
	BillingSendTestRequest,
	BillingSendTestResponse,
	BillingSendLog,
	QBOBankAccount,
	ReceivablesPayment,
	RecordPaymentRequest,
	PaymentReceiptOptions,
	PaymentReceiptSendResult,
	SendActionResponse,
	Statement,
} from '../types/billing-receivables.js';
import type {
	DepositBatch,
	DepositBatchDetail,
	DepositBatchScan,
	DepositBatchStatus,
	DepositConfirmResult,
	DepositItemInput,
	DepositReadResult,
} from '../types/billing-deposits.js';
import type {
	BankActivityFilter,
	BankActivityPage,
	BankMatchInput,
	BankSyncResult,
	BankLineRule,
	BankLineRuleInput,
	BankRuleActiveResponse,
	BankRuleApplyResult,
	BankRulePreview,
	BankRuleReleaseResult,
	BankRuleSaveResponse,
	BankRuleSuggestion,
	BankTransaction,
	BankTransactionDetail,
	UnrecordedPaymentsPage,
} from '../types/billing-bank-activity.js';
import type { PathwayState } from '../types/pathway.js';
import type { PatronFieldPolicy, PatronFieldPolicyPatch } from '../types/library.js';
import type { LibraryAISettings, LibraryAISettingsPatch } from '../types/library.js';
import type { CommsSendingHours, CommsSendingHoursChange } from '../types/comms.js';

import type { QBOAuthorizeURL, QBOConnectionStatus } from '../types/qbo.js';
import type {
	Agent,
	AgentConfig,
	AgentDetail,
	AgentHostHealth,
	ClaudeAccount,
	ClaudeAccountInput,
	ClaudeAccountListing,
	AgentListing,
	AgentRegistry,
	AgentVersion,
	ActivateResult as AgentActivateResult,
	CompanyPolicy,
	HostCredentialResult,
	SaveResult as AgentSaveResult,
	SlackAppResult,
	SlackConfigStatus,
	ApprovalDecision,
	ApprovalView,
	ApprovalsAppStatus,
	RecurringAction,
	RecurringPage,
	RecurringTaskView,
	MissionBudgetPatch,
	MissionCostRollup,
	MissionDetail,
	MissionListPage,
	Playbook,
	PlaybookDetail,
	PlaybookListing,
	PlaybookSaved,
	PlaybookWrite,
} from '../types/agentPlatform.js';
import type {
	DecisionCalibration,
	DecisionCompareReport,
	WorkflowMetrics,
	DecisionEngine,
	DecisionEngineInput,
	DecisionEnginesPage,
	WorkflowDecision,
	WorkflowDecisionDetail,
	WorkflowRunDetail,
	WorkflowStepBash,
	WorkflowRunListPage,
	WorkflowInboxPage,
	WorkflowInboxAnswer,
	WorkflowHumanAnswered,
	WorkflowCreated,
	WorkflowDefinition,
	WorkflowDefinitionDetail,
	WorkflowVersionRow,
	WorkflowVersionDiff,
	WorkflowDraftSaved,
	WorkflowPerson,
	WorkflowImportResult,
	WorkflowRunStarted,
} from '../types/workflows.js';
import type { FlowDocument, FlowNodeTypeView, FlowVocabulary } from '../flow/types.js';
import type { DraftPayload } from '../flow/flowDocument.js';
import type { GitHubAdminView, GitHubLinkView, GitHubRepo, GitHubRepoMetrics, GitHubRepoPatch, WorkflowChoice } from '../types/github.js';
import type {
	TrainingCourseCard,
	TrainingCourseDetail,
	TrainingSectionProgress,
	TrainingServedQuestion,
	TrainingSectionCheckResponse,
	TrainingScenario,
	TrainingScenarioStepResult,
	TrainingAttemptStart,
	TrainingAttemptAnswers,
	TrainingResults,
	TrainingAttemptSummary,
	TrainingBoard,
	TrainingAdminUserDetail,
} from '../types/training.js';
import type {
	FeedbackAdminListParams,
	FeedbackAdminPatch,
	FeedbackAdminStats,
	FeedbackAgentDispatchResult,
	FeedbackCreateInput,
	FeedbackEditInput,
	FeedbackFixNoteInput,
	FeedbackListEnvelope,
	FeedbackMediaUploadResult,
	FeedbackMessage,
	FeedbackMessageInput,
	FeedbackReclassifyInput,
	FeedbackReport,
	FeedbackReportKind,
	FeedbackReportRow,
	FeedbackRetestInput,
	FeedbackReviewInput,
	FeedbackReviewVerdict,
	FeedbackScopeNoteInput,
	FeedbackStatus,
	FeedbackStatusCounts,
} from '../types/feedback.js';
import { ApiError, type ApiFieldError } from './apiError.js';
import { connectionMonitor } from './connection.js';
// Impersonation (docs/impersonation-plan.md). impersonation.ts also imports
// `api` from this file (to make the actual /impersonation/* calls) — a
// deliberate two-way reference, safe because neither side touches the
// other's exports at module-evaluation time, only from inside function
// bodies that run after the whole module graph has finished loading (the
// same reasoning that lets connection.ts's connectionMonitor be consulted
// from deep inside request() below). Types are `import type` so they add
// zero runtime coupling; the four value imports are the header/event
// plumbing request() needs on every call.
import {
	clearImpersonationSession,
	impersonationHeaderValue,
	IMPERSONATION_HEADER,
	IMPERSONATION_ENDED_CODE,
	IMPERSONATION_READONLY_CODE,
	IMPERSONATION_READONLY_EVENT,
} from './impersonation.js';
import { legacyClientId } from './legacy-client.js';
import { activeCardholderTerm } from '../utils/terminology.js';

export { ApiError } from './apiError.js';

// Desk log wave 2, the "this came from Desk" signal (docs/modules/desk.md,
// "Request headers"). Exported so getDeskLogReportExportCSV's own raw
// fetch (below, bypassing request()) can attach them too.
export const SURFACE_HEADER = 'X-COS-Surface';
export const SERVICE_POINT_HEADER = 'X-COS-Service-Point';
// Desk-branch-ui contract, the branch id (locations.id) Desk log's
// RecordActivity fallback uses when the transaction's own module can't
// supply one (e.g. Lend). Same shape as the two headers above.
export const BRANCH_HEADER = 'X-COS-Branch';

// Relative by default, because the SPA is same-origin with its API: the
// production ALB routes /api/* on os.communico.* straight to the backend, so
// there is no CORS, no preflight, and the sessionStorage bearer never crosses an
// origin. That is strictly better than an absolute base while both live behind
// one load balancer.
//
// It stops being possible the moment the bundle is served from CloudFront, which
// cannot be same-origin with the API — that is what api.communico.co/v5 exists
// for (infra/event-core/api_host.tf; the backend answers on it via
// middleware.RewriteAPIVersionPrefix). Setting window.__COS_API_BASE__ to
// 'https://api.communico.co/v5' before this module loads is the whole switch.
//
// A RUNTIME override rather than a build-time VITE_ var on purpose: one image is
// currently correct in every environment, and baking the origin in would trade
// that away for nothing. Also note switching it makes every call cross-origin,
// so COS_CORS_ORIGINS must already name the app's origin — and the refresh
// cookie keeps working because os.communico.co and api.communico.co are the same
// SITE (registrable domain communico.co), so its SameSite=Lax is satisfied and
// tryRefresh's existing credentials:'include' is sufficient.
declare global {
	interface Window {
		__COS_API_BASE__?: string;
	}
}

/**
 * Where the API lives, as a prefix every call is built on.
 *
 * `/v5` — RELATIVE, and that is the load-bearing part. The backend answers on
 * both /v5 and /api/v1 (middleware.RewriteAPIVersionPrefix), so a relative /v5
 * needs nothing environment-specific: each layer that already forwards `/api`
 * simply also forwards `/v5` — the vite dev + preview proxies, the container's
 * Caddyfile, and the production ALB, which derives its list BY PARSING that
 * Caddyfile. Editing one file covers dev, the container and AWS at once.
 *
 * An absolute 'https://api.communico.co/v5' would have been the obvious reading
 * of "move the API to v5" and is the wrong one while the app is served from the
 * same origin: it makes every call cross-origin for no gain, and — the real
 * problem — a hostname baked into the bundle means LOCAL DEV TALKS TO
 * PRODUCTION. Relative cannot do that.
 *
 * It becomes right the day the bundle is served from CloudFront, which cannot be
 * same-origin with the API. Then set window.__COS_API_BASE__ before this module
 * loads; nothing else changes, including the WebSocket (see apiWebSocketURL).
 * COS_CORS_ORIGINS must name the app's origin first, and the refresh cookie
 * keeps working because os.communico.co and api.communico.co are the same SITE
 * (registrable domain communico.co), so its SameSite=Lax is satisfied.
 *
 * /api/v1 is NOT retired and is not going to be: the backend aliases rather than
 * moves, so the 31 e2e specs, the Reserve Panels PWA and public/sw.js all keep
 * working unchanged. Retiring it would be churn across ~40 files buying nothing.
 */
export const API_BASE = (typeof window !== 'undefined' && window.__COS_API_BASE__) || '/v5';

/**
 * The WebSocket URL for an API path — `apiWebSocketURL('/ws')`.
 *
 * Not string concatenation, unlike every REST call above, because a ws:// URL
 * cannot be relative: it must carry a scheme and a host. Resolving API_BASE
 * against the document collapses both forms — a bare path and a full origin —
 * into one absolute URL, so the socket follows the API base automatically and
 * the CloudFront switch does not need a second, easily-forgotten edit.
 *
 * The previous form hardcoded window.location.host, which is correct only while
 * the API is same-origin — exactly the assumption the base exists to remove.
 */
export function apiWebSocketURL(path: string): string {
	const url = new URL(API_BASE + path, window.location.href);
	url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
	return url.toString();
}
const TOKEN_KEY = 'cos.access_token';
// Stage 27 — refresh_token now lives in an httpOnly cookie set by the
// backend's /auth/login proxy (forwarded from microservice-auth). The
// SPA doesn't read or store it directly. tryRefresh() POSTs to
// /auth/refresh with credentials:'include' so the browser sends the
// cookie automatically. REFRESH_KEY is retired; sessionStorage cleanup
// happens on logout via setToken(null).
const EXPIRES_AT_KEY = 'cos.expires_at';

interface ApiResponse<T> {
	data?: T;
	error?: string;
	message?: string;
	status?: number;
	// The server-422 → field-level error contract: an optional per-field
	// breakdown a validation-aware handler adds alongside the unchanged
	// "message" — see ApiError.fields (services/apiError.ts).
	fields?: ApiFieldError[];
	// The optimistic-concurrency 409 contract — see ApiError.currentRevision.
	// Both keys travel together (the handler writes them as a pair), so
	// `conflict` is what gates reading `current_revision`.
	conflict?: boolean;
	current_revision?: number;
	// FR-60's extension of the same contract for the roster shift editor
	// — see ApiError.current/.changedByName/.changedAt.
	current?: Record<string, unknown>;
	changed_by_name?: string;
	changed_at?: string;
	// The impersonation read-only-write contract — see ApiError.code.
	code?: string;
	// Paul's ruling on confirmation (roster copy/apply tools) — see
	// ApiError.publishedWeeks/.wouldCreate. Travels alongside
	// `code: "roster_published_week_confirm"`.
	weeks?: string[];
	would_create?: number;
}

// Stage 24 — AI quality gate response shape. Mirrors
// service.QualityCheck on the backend.
export interface TicketQualityCheck {
	score: number;                                // 0-100 overall
	category_scores: Record<string, number>;      // {clarity, specificity, reproducibility}
	suggestions: Array<{ category: string; text: string }>;
	severity_hint: 'good' | 'fair' | 'needs_improvement';
	duration_ms: number;
	cached?: boolean;
}

// Stage 56 — staff user as returned by GET /users (the AuthHandler's
// UserSummary shape). Backs the @-mention autocomplete + task assignee.
export interface MentionUser {
	id: number;
	email: string;
	first_name: string;
	last_name: string;
	role?: string;
	level?: number;
}

// Stage 88c — the calendar event write body (POST/PATCH /calendar/events).
export interface CalendarEventWrite {
	title: string;
	start: string; // ISO
	end: string; // ISO
	all_day?: boolean;
	description?: string;
	location?: string;
	attendees?: string[]; // emails
}

// Stage 88d — the smart-scheduler query body (POST /calendar/schedule).
export interface ScheduleQuery {
	invitee_ids: number[];
	duration_min: number;
	from: string; // ISO
	to: string; // ISO
	preferred_tod?: 'morning' | 'afternoon' | 'either';
	avoid_weekdays?: number[]; // 0=Mon … 6=Sun
}

interface PartnerFilter {
	status?: string;
	// The list's status filter: partner | former_partner | other, comma
	// separated. Absent means all three; the Partners page always sends partner by default.
	lifecycle?: string;
	ils_type?: string;
	partner_tier?: string;
	happiness_score?: string;
	// Stage 48.2 — health_status drill-through from partner_health_summary
	// (red/yellow/green). Distinct from the numeric health_score.
	health?: string;
	search?: string;
	sort?: string;
	start?: number;
	limit?: number;
	// Stage 19f: backed by `has_modules_in_implementation=1` query
	// param. Used by the staff /implementation redirect (Stage 19e).
	has_modules_in_implementation?: number;
	// Stage 47d.2.1 Theme 6 PA-01: opt-in to hide ~10K HubSpot-imported
	// partners with empty name. Off by default.
	hide_unnamed?: number;
	// Stage 60f — admin escape hatch: include review-flagged (non
	// customer/other lifecycle) partners in the list. Honored only for
	// admin-tier callers; silently ignored otherwise.
	include_review?: boolean;
	// Partner round 1 (Marcie) B2 — the advanced-filter builder. A JSON
	// array of {field, op, values} conditions (ANDed), compiled server-side
	// against the whitelisted-column contract. Malformed → 422.
	filters?: string;
}

// Stage 60b — /prospects list filters (PLS libraries not yet linked to a partner).
interface ProspectFilter {
	year?: number;
	states?: string;   // comma-separated state abbreviations
	search?: string;
	pop_min?: number;
	pop_max?: number;
	locale?: string;   // urban | suburban | rural
	tech?: string;     // low | medium | high
	linked?: string;   // yes | no (Stage 60b; superseded by engagement)
	engagement?: string; // Stage 60f Show filter: engaged | imls | "" (all)
	sort?: string;
	start?: number;
	limit?: number;
}

interface TicketFilter {
	status?: string;
	// Staff only: none of these statuses (a comma-separated list); a board's first lane uses it.
	exclude_status?: string;
	priority?: string;
	// Stage 48.1b — age-bucket drill-through from open_tickets_aging:
	// "0-7" | "8-30" | "31-90" | "90plus".
	age?: string;
	// Stage 48.2 — product_area drill-through from tickets_by_product_area.
	product_area?: string;
	assignee_id?: number;
	// Marcie R4 — the "Unassigned" summary stat-tile filter (assignee_id IS NULL,
	// non-terminal). Serialised as unassigned=true; the handler reads 1/true.
	unassigned?: boolean;
	// Which kind of ticket: 'support' (the default when left out) or
	// 'engineering'. The server forces support for a library user, and the ticket
	// screens never send it for one.
	kind?: string;
	// Support desk, staff only: '0' keeps tickets with no engineering ticket linked
	// (sent with the With Engineering status); '1' keeps the ones that have one.
	has_engineering_link?: string;
	// Engineering "Open" tile: every ticket that is not Done and not Closed.
	// Serialised as open=1 (the contract's spelling).
	open?: string;
	// Staff only: support tickets with a "worked on in" link to an engineering
	// ticket that has been handed back (sent as engineering_back=1).
	engineering_back?: string;
	// Staff only, both kinds: tickets raised by this person.
	reporter_id?: number;
	// Staff only, engineering (26 October 2026): holder is who holds the ticket, a
	// comma-separated list of agent, product, engineer and reporter; mode is agent or
	// engineer. with_reporter=1 is the With the reporter tile: not finished and (Awaiting
	// Reporter Action or held by the reporter). quiet=1 is an agent ticket nothing has
	// happened on for a working day; waiting_on=me the caller's own tickets they hold or
	// that are quiet.
	holder?: string;
	mode?: string;
	with_reporter?: string;
	quiet?: string;
	waiting_on?: string;
	needs_attention?: string;
	work_section?: string;
	// Staff only, with sort=rank: the page starts just after this ticket's own place in backlog order
	// (a board lane's cursor).
	after_id?: number;
	project_id?: number;
	partner_id?: number;
	// Stage 39b live-smoke fix — `type` accepts a single token
	// or a comma-separated list. The backend handler splits on
	// comma; the URL serialiser here just passes the string
	// through (toQuery URL-encodes commas as %2C but the
	// backend's strings.Split is tolerant — accepts both
	// "bug,feature" and "bug%2Cfeature").
	type?: string;
	// Marcie arc #40 Stage 2b — module / support-priority / tag list filters.
	module_id?: number;
	support_priority_id?: number;
	label_id?: number;
	// Marcie R9 (1g) — all tickets a contact is on.
	contact_id?: number;
	// Marcie R5 (B3) — free-text compound search (ticket # / Freshdesk # / title /
	// partner name / contact name+email). Sent as `q` — the param the list handler
	// reads (handler/ticket.go: Search: c.Query("q")) and the same one the
	// command-palette ticket search uses.
	q?: string;
	sort?: string;
	start?: number;
	limit?: number;
	// Planning (contract section 11 d, e): an engineering ticket's release, exact;
	// and only tickets changed since an RFC 3339 time (both kinds).
	release?: string;
	updated_since?: string;
}

interface ProjectFilter {
	status?: string;
	// Stage 42b — Type filter now accepts comma-separated values
	// (the backend handler reads multi-value via splitCSV).
	type?: string;
	// Stage 42b — multi-value health-status filter; "yellow,red"
	// powers the cos-project-list "needs attention" preset.
	health_status?: string;
	owner_id?: number;
	// Stage 42b — partner_id shortcut filter (independent of the
	// scope-enforcing partner restriction the backend service
	// adds for level-3 callers via project_partners).
	partner_id?: number;
	sort?: string;
	start?: number;
	limit?: number;
}

// Stage 35b — Deals client surface. Mirrors backend DealFilter in
// repository/deal.go. start/limit (offset-based) matches the rest
// of the api.ts pattern; the component layer converts to page/
// pageSize as needed for its UI.
interface DealFilter {
	pipeline?: string;
	stage?: string;
	owner_id?: number;
	partner_id?: number;
	search?: string;
	sort?: string;
	start?: number;
	limit?: number;
}

// The chosen library for the by-inbox Email routes: the server refuses an
// inbox that is not in it (Nurture QA C-7). Absent for a library's own login.
function libraryClientIdOrUndefined(): number | undefined {
	const cid = legacyClientId();
	return cid > 0 ? cid : undefined;
}

function inboxLibraryQuery(): string {
	const cid = legacyClientId();
	return cid > 0 ? `?client_id=${cid}` : "";
}

/**
 * Every /lend/* request names the chosen library, from this one place so no
 * Lend method can forget it. Communico staff have no library of their own and
 * the server needs the pick; for a library's own staff it must equal their own
 * library, which their pick always is. Absent when nothing is chosen (the
 * server then answers "Choose a library first.").
 */
export function withLendLibrary(path: string, libraryId: number): string {
	if (!(path === '/lend' || path.startsWith('/lend/') || path.startsWith('/lend?'))) return path;
	if (!(libraryId > 0)) return path;
	const q = path.indexOf('?');
	const base = q === -1 ? path : path.slice(0, q);
	const params = new URLSearchParams(q === -1 ? '' : path.slice(q + 1));
	params.set('client_id', String(libraryId));
	return `${base}?${params.toString()}`;
}

function toQuery(filter: Record<string, unknown>): string {
	const params = new URLSearchParams();
	for (const [k, v] of Object.entries(filter)) {
		if (v !== undefined && v !== null && v !== '') {
			params.set(k, String(v));
		}
	}
	const qs = params.toString();
	return qs ? `?${qs}` : '';
}

// Wave 4, the Benchmarks peer-group picker's three dimensions, shaped to
// the backend's query param names (population_band/state/sort).
function peerComparisonQuery(options: PeerComparisonOptions): Record<string, unknown> {
	return {
		population_band: options.populationBand,
		state: options.state,
		sort: options.sortByBudget ? 'budget' : undefined,
	};
}

// Stage 68e-7b — the "View as partner" preview suffix. Appends
// ?as=customer so the server returns the client_visible-only projection
// (staff-only; only ever narrows the result).
function asCustomerSuffix(asCustomer: boolean): string {
	return asCustomer ? '?as=customer' : '';
}

// The cursor of a support-mail list page: the id of a line the page SHOWED, whether or not it has since left the list.
export type SupportMailCursor = { after: number } | { before: number };
function supportMailCursorQuery(c?: SupportMailCursor): string {
	if (!c) return '';
	return 'after' in c ? `&after=${encodeURIComponent(String(c.after))}` : `&before=${encodeURIComponent(String(c.before))}`;
}

class ApiClient {
	private token: string | null = null;
	// Desk log wave 2, "this came from Desk" signal (docs/modules/desk.md).
	// Set while <cos-desk-page> is mounted (surface) and while a service
	// point is chosen in its header (servicePoint); read synchronously by
	// request() below, the same shape as the impersonation header a few
	// lines down. Neither ever reaches localStorage, page-session state
	// only, cleared on disconnect.
	private surface: string | null = null;
	private servicePointId: number | null = null;
	private branchId: number | null = null;

	constructor() {
		// Restore token on page load. sessionStorage is scoped to the tab so a
		// fresh tab/window starts unauthenticated, which is the desired default.
		try {
			const t = sessionStorage.getItem(TOKEN_KEY);
			if (t) this.token = t;
		} catch {
			// sessionStorage may be unavailable (private mode, SSR). Treat as logged out.
		}
	}

	setToken(token: string): void {
		this.token = token;
		try { sessionStorage.setItem(TOKEN_KEY, token); } catch { /* ignore */ }
	}

	clearToken(): void {
		this.token = null;
		try {
			sessionStorage.removeItem(TOKEN_KEY);
			sessionStorage.removeItem(EXPIRES_AT_KEY);
			// Stage 27 — the refresh cookie is httpOnly; the server-side
			// /auth/logout call clears it via Set-Cookie. We don't touch
			// it from JS.
		} catch { /* ignore */ }
		// Impersonation (docs/impersonation-plan.md) — a local-only clear,
		// deliberately not a server call (see clearImpersonationSession's own
		// doc comment for why). Runs on every path that clears the token:
		// explicit sign-out (app.ts's _onLogout calls this), and the
		// 401-expired path below — either way the token that would carry
		// the impersonation header is gone, so holding onto a stale session
		// id here would be silent, orphaned state for whoever uses this tab
		// next.
		clearImpersonationSession();
	}

	isAuthenticated(): boolean {
		return this.token !== null;
	}

	// Desk log wave 2 request headers, X-COS-Surface and X-COS-Service-Point
	// (docs/modules/desk.md, "Request headers"). setSurface("desk") on
	// connect / null on disconnect; setServicePoint tracks the Desk header's
	// service-point picker, cleared (null) when nothing is chosen. "No
	// service point" is a valid choice, not a loading state.
	setSurface(surface: 'desk' | null): void {
		this.surface = surface;
	}

	setServicePoint(id: number | null): void {
		this.servicePointId = id && id > 0 ? id : null;
	}

	// Desk-branch-ui contract, X-COS-Branch (docs/modules/desk.md). Same
	// shape as setSurface/setServicePoint above: set to the Desk page's
	// resolved branch on load and on every branch change, cleared on
	// disconnect. Positive integer only, mirroring setServicePoint.
	setBranch(id: number | null): void {
		this.branchId = id && id > 0 ? id : null;
	}

	// Stage 30 — exposes the bearer token so the agent SSE client can
	// build its own fetch (the standard request() helper auto-parses
	// JSON, which doesn't apply to a streaming response).
	getToken(): string | null {
		return this.token;
	}

	// A8 (partner round 1, Marcie) — adopt an existing session in a NEW TAB.
	//
	// The access token lives in sessionStorage, which is tab-scoped by design
	// (and must NOT move to localStorage — an XSS-readable token is exactly
	// what that rule prevents). But the Stage 27 refresh token is an httpOnly
	// cookie, and cookies ARE shared across tabs — so a fresh tab can mint its
	// own access token from the cookie without the token ever being persisted
	// anywhere JS-readable across tabs.
	//
	// Returns true when this tab now holds a valid access token. False means
	// no usable cookie (absent / expired / revoked) → the caller sends the
	// user to /login, exactly as before.
	async restoreSession(): Promise<boolean> {
		if (this.token) return true;
		return this.tryRefresh();
	}

	// Live access token for an embedded surface that reuses this session
	// (the Community SPA — see cos-community-page). It reads the current
	// in-memory token, minting one from the httpOnly refresh cookie if
	// absent (fresh tab), and with force=true rotates first — used when a
	// downstream proxied request 401s so the retry carries a fresh token.
	// Reuses the existing tryRefresh machinery (no duplicate refresh path).
	async accessTokenForEmbed(force = false): Promise<string | null> {
		if (force || !this.token) await this.tryRefresh();
		return this.token;
	}

	// Stage 27 — refresh attempt invoked by the 401 handler when the
	// expired-token signal is present. POSTs to /auth/refresh with
	// credentials:'include' so the browser sends the httpOnly refresh
	// cookie set by /auth/login. The backend proxies to
	// microservice-auth /refresh and returns a new access token (and a
	// new refresh cookie via Set-Cookie). The SPA never reads or writes
	// the refresh token directly — it lives in the cookie jar.
	//
	// Returns false (force re-login) when:
	//   - the cookie is absent or expired (browser drops it; backend
	//     returns 401)
	//   - the refresh endpoint returns 401 (refresh expired/revoked)
	//   - any network / parse error
	/** The last refresh attempt failed because the request got no answer at all (offline), as
	 *  opposed to the server refusing it. Lets the app keep a registered Desk PC on /desk, which can
	 *  say "sign in when the connection returns", rather than bouncing to a /login that cannot load. */
	refreshFailedOnNetwork = false;

	private async tryRefresh(): Promise<boolean> {
		this.refreshFailedOnNetwork = false;
		try {
			const r = await fetch(`${API_BASE}/auth/refresh`, {
				method: 'POST',
				credentials: 'include',
			});
			if (!r.ok) return false;
			const body = await r.json() as { access_token?: string; expires_in?: number };
			if (!body.access_token) return false;
			this.setToken(body.access_token);
			if (body.expires_in) {
				try {
					sessionStorage.setItem(EXPIRES_AT_KEY, String(Date.now() + body.expires_in * 1000));
				} catch { /* noop */ }
			}
			return true;
		} catch (err) {
			this.refreshFailedOnNetwork = err instanceof TypeError;
			return false;
		}
	}

	// Stage 27 logout: hits /auth/logout with the bearer token AND the
	// refresh cookie (credentials:'include'). The backend revokes the
	// JTI in Redis and clears the refresh cookie via Set-Cookie. Errors
	// are swallowed — the user is signing out either way; both
	// revocation and cookie-clear are best-effort.
	async logout(): Promise<void> {
		if (this.token) {
			try {
				await fetch(`${API_BASE}/auth/logout`, {
					method: 'POST',
					credentials: 'include',
					headers: { 'Authorization': `Bearer ${this.token}` },
				});
			} catch { /* swallow */ }
		}
		this.clearToken();
	}

	// Headers for a multipart POST, which cannot go through request() because it
	// must not set a JSON Content-Type (the browser supplies the boundary). It
	// carries the same identity headers request() does: the bearer token and,
	// during an impersonation session, X-COS-Impersonate, without which the
	// server would run a mutating upload as the operator and skip the session's
	// read-only enforcement.
	private multipartHeaders(): Record<string, string> {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const impersonationId = impersonationHeaderValue();
		if (impersonationId) headers[IMPERSONATION_HEADER] = impersonationId;
		if (this.surface) headers[SURFACE_HEADER] = this.surface;
		if (this.servicePointId) headers[SERVICE_POINT_HEADER] = String(this.servicePointId);
		if (this.branchId) headers[BRANCH_HEADER] = String(this.branchId);
		return headers;
	}

	private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
		// The library chosen when this request was SENT: a refusal that lands
		// after staff switch library must not be applied to the new one.
		const libraryAtStart = legacyClientId();
		path = withLendLibrary(path, libraryAtStart);
		const headers: Record<string, string> = {
			'Content-Type': 'application/json',
			...(options.headers as Record<string, string> || {}),
		};

		if (this.token) {
			headers['Authorization'] = `Bearer ${this.token}`;
		}

		// Impersonation (docs/impersonation-plan.md) — attached here, the
		// ONE place this class builds its headers, rather than at each of
		// the ~7000 individual call sites below. A synchronous
		// sessionStorage read (see impersonationHeaderValue's own doc
		// comment), so it costs nothing on the overwhelming majority of
		// requests where no session is held.
		const impersonationId = impersonationHeaderValue();
		if (impersonationId) {
			headers[IMPERSONATION_HEADER] = impersonationId;
		}

		// Desk log wave 2, sent on every request while set (see setSurface/
		// setServicePoint's own doc comments above).
		if (this.surface) {
			headers[SURFACE_HEADER] = this.surface;
		}
		if (this.servicePointId) {
			headers[SERVICE_POINT_HEADER] = String(this.servicePointId);
		}
		if (this.branchId) {
			headers[BRANCH_HEADER] = String(this.branchId);
		}

		const method = (options.method || 'GET').toUpperCase();
		const isMutating = method !== 'GET' && method !== 'HEAD';

		let response: Response;
		try {
			response = await fetch(`${API_BASE}${path}`, {
				...options,
				headers,
			});
		} catch (err) {
			// fetch() itself rejected — offline, DNS, TLS, a dropped
			// connection mid-flight. Distinct from an HTTP error response
			// (handled below): there is no status here at all.
			connectionMonitor.reportNetworkFailure();
			// Mark mutating requests explicitly (ApiError.retriableOffline)
			// so callers can react to "this write didn't happen because
			// we're offline" without string-sniffing err.message — see
			// ApiError's own doc comment. GET/HEAD failures pass through
			// unmarked, unchanged from prior behaviour (userMessage()
			// already recognises the raw network-error message text).
			if (isMutating) {
				throw new ApiError(0, err instanceof Error ? err.message : 'Network request failed', true);
			}
			throw err;
		}
		// Any response at all — even a non-2xx one — proves the
		// connection is real, clearing a stale offline flag the
		// browser's own 'online' event might not have fired for.
		connectionMonitor.reportSuccess();

		if (response.status === 401) {
			// fleet/thrive-w1-shell — a request sent with NO token to begin
			// with (this.token was never set) is an anonymous caller hitting
			// an endpoint that requires auth, not a session that expired:
			// there was no session. This fires for a component that makes a
			// best-effort authenticated call regardless of context (e.g.
			// cos-report-tile's cardholderTermForCurrentLibrary(), which already
			// degrades to a safe default on any failure) when mounted on a
			// genuinely public page (/s/:token). Never clear a token that
			// doesn't exist, and never fire the global logout redirect for
			// a session that was never there to expire.
			if (!this.token) {
				throw new ApiError(401, 'Authentication required');
			}
			// Stage 17: distinguish expired (refresh might work) from
			// revoked / invalid (must re-login). RFC 6750 §3 — the
			// WWW-Authenticate header carries error_description=expired
			// when the token's exp claim is in the past, and =revoked
			// (or absent) for any other 401 reason.
			const wwwAuth = response.headers.get('WWW-Authenticate') || '';
			const isExpired = /error_description="?expired"?/i.test(wwwAuth);
			let recovered = false;
			if (isExpired && this.token) {
				const refreshed = await this.tryRefresh();
				if (refreshed) {
					// Retry original request once with the new token. Its own answer is
					// what the caller gets, whatever the status: a 422 is a 422 and a
					// 200 is a 200. Only a second 401 signs the person out.
					headers['Authorization'] = `Bearer ${this.token}`;
					const retry = await fetch(`${API_BASE}${path}`, { ...options, headers });
					if (retry.status !== 401) {
						response = retry;
						recovered = true;
					}
				}
			}
			if (!recovered) {
				this.clearToken();
				window.dispatchEvent(new CustomEvent('cos:auth:expired'));
				throw new ApiError(401, 'Authentication expired');
			}
		}

		if (response.status === 204) {
			return undefined as T;
		}

		// Read the body as text first, then parse defensively. A non-2xx (or a
		// gateway 502/504) can arrive with an EMPTY or non-JSON body — e.g. a
		// transient failure while the backend is reloading, or a slow query the
		// proxy times out. Calling response.json() on that throws
		// "Unexpected end of JSON input", which surfaced as a raw, scary error
		// (the ticket-list sort-flip crash Marcie hit, R5). Parsing text keeps
		// the status authoritative so every endpoint degrades to a clean,
		// retryable ApiError instead.
		const raw = await response.text();
		let body: ApiResponse<T> | undefined;
		if (raw) {
			try {
				body = JSON.parse(raw) as ApiResponse<T>;
			} catch {
				// Body was non-JSON (a proxy error page). The status drives the error.
				throw new ApiError(response.status || 500, `Request failed (${response.status || 'network'})`);
			}
		}

		if (!response.ok) {
			// Impersonation (docs/impersonation-plan.md) — the read-only
			// enforcement is server-side, never a hidden button, so a write
			// attempted under a read-only session can come back from ANY
			// call site in the app. Broadcasting here (rather than leaving
			// each caller to check err.code) is what lets the banner react
			// regardless of which page triggered the refusal; the ApiError
			// thrown below still carries `.code` too, for a caller that
			// wants to react locally instead.
			if (response.status === 403 && body?.code === IMPERSONATION_READONLY_CODE) {
				window.dispatchEvent(
					new CustomEvent(IMPERSONATION_READONLY_EVENT, { detail: { path } }),
				);
			}
			// A library without Thrive: the server refuses every /thrive read.
			// Broadcast it so the Thrive shell shows its "not enabled" state
			// whichever screen asked (see thrive/not-enabled.ts).
			if (response.status === 403 && body?.message === THRIVE_NOT_ENABLED_MESSAGE) {
				window.dispatchEvent(new CustomEvent(THRIVE_NOT_ENABLED_EVENT, { detail: { path, clientId: libraryAtStart } }));
			}
			// The session is over (stopped elsewhere, expired, or in-flight
			// when Stop landed). Drop the local session so the stale header
			// stops riding on subsequent requests — but leave the TOKEN
			// alone: the operator is still perfectly signed in as
			// themselves, and clearing it here is what used to bounce them
			// to /login (see IMPERSONATION_ENDED_CODE).
			if (response.status === 403 && body?.code === IMPERSONATION_ENDED_CODE) {
				clearImpersonationSession();
			}
			// The lending service (proxied under /lend/*) wraps its errors as
			// {error: {code, message}}; COS's own envelope is flat. Unwrap the
			// nested form so ApiError.message is always a string and the code
			// still rides .code, never "[object Object]".
			const nested = body?.error && typeof body.error === "object" ? (body.error as { code?: unknown; message?: unknown }) : null;
			const nestedMessage = typeof nested?.message === "string" ? nested.message : undefined;
			const nestedCode = typeof nested?.code === "string" ? nested.code : undefined;
			throw new ApiError(
				response.status,
				body?.message || nestedMessage || (typeof body?.error === "string" ? body.error : undefined) || `Request failed (${response.status})`,
				false,
				body?.fields,
				body?.conflict === true && typeof body.current_revision === "number" ? body.current_revision : undefined,
				typeof body?.code === "string" ? body.code : nestedCode,
				body?.conflict === true ? body.current : undefined,
				body?.conflict === true && typeof body.changed_by_name === "string" ? body.changed_by_name : undefined,
				body?.conflict === true && typeof body.changed_at === "string" ? body.changed_at : undefined,
				body?.code === "roster_published_week_confirm" ? body.weeks : undefined,
				body?.code === "roster_published_week_confirm" ? body.would_create : undefined,
				body && typeof body === "object" ? (body as unknown as Record<string, unknown>) : undefined,
			);
		}

		return (body ?? (undefined as T)) as T;
	}

	// Auth
	async getMe() {
		return this.request('/auth/me');
	}

	// Impersonation (docs/impersonation-plan.md). Thin wrappers only — the
	// session lifecycle (sessionStorage, the change event, the read-only →
	// allow-writes two-step) lives in services/impersonation.ts, which is
	// what every consumer (the dialog, the banner, the shell's menu) calls
	// instead of these directly.
	// client_id is the legacy clientsV2 id of the library chosen in the nav
	// picker, and BOTH of these endpoints require it: Communico staff have
	// no tenant of their own, so "which library's users" is not derivable
	// from the caller's own token. Read at call time from the same session
	// state the picker writes — importing it at module scope would be a
	// cycle (legacy-client.ts imports `api` from this file), exactly as the
	// impersonation.ts import above already notes.
	async getImpersonationCandidates(q: string) {
		const params = new URLSearchParams();
		if (q) params.set('q', q);
		params.set('client_id', String(legacyClientId()));
		return this.request(`/impersonation/candidates?${params.toString()}`);
	}

	// write_mode rides the start call so "begin already allowing changes"
	// is one request. The server still defaults to read when it is absent,
	// and allow-writes remains the mid-session escalation path.
	async startImpersonation(targetUserId: number, reason: string, allowWrites = false) {
		const body: Record<string, unknown> = { target_user_id: targetUserId, reason };
		if (allowWrites) body.write_mode = 'write';
		return this.request(`/impersonation/start?client_id=${encodeURIComponent(String(legacyClientId()))}`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	async stopImpersonation(): Promise<void> {
		await this.request('/impersonation/stop', { method: 'POST' });
	}

	async allowImpersonationWrites(reason: string) {
		return this.request('/impersonation/allow-writes', {
			method: 'PATCH',
			body: JSON.stringify({ reason }),
		});
	}

	async getCurrentImpersonation() {
		return this.request('/impersonation/current');
	}

	// Circulate ILS — host context for the embedded staff UI (tenant,
	// guard level, staff identity). The workspaces themselves talk to
	// the backend's /circulation/* proxy directly.
	async getCirculateContext(): Promise<{
		enabled: boolean;
		products: string[];
		client_id?: string;
		guard_level: number;
		staff_id?: string;
		staff_name?: string;
		level: number;
	}> {
		return this.request('/circulate/context') as Promise<{
			enabled: boolean;
			products: string[];
			client_id?: string;
			guard_level: number;
			staff_id?: string;
			staff_name?: string;
			level: number;
		}>;
	}

	// Circulate org units, wave 3b's branch-mapping admin
	// (docs/modules/branches-and-hours.md). Proxied through
	// /api/v1/circulation/org-units, gated on the Circulate entitlement
	// (org units don't exist without Circulate). List envelope is a bare
	// array, not { items }, matching what the proxy forwards verbatim.
	async getCirculationOrgUnits() {
		return this.request(`/circulation/org-units${toQuery({ client_id: libraryClientIdOrUndefined() })}`);
	}
	/** `communicoLocationId: null` clears the mapping, a normal action, not
	 *  a destructive one. */
	async setOrgUnitCommunicoLocation(orgUnitId: string, communicoLocationId: number | null) {
		return this.request(`/circulation/org-units/${encodeURIComponent(orgUnitId)}/communico-location${toQuery({ client_id: libraryClientIdOrUndefined() })}`, {
			method: 'PUT',
			body: JSON.stringify({ communicoLocationId }),
		});
	}

	// Community — host context for the embedded Community SPA (whether the
	// module is enabled for this deployment + the ws / media hosts). The
	// SPA itself talks to the backend's /community/api/* proxy directly.
	async getCommunityContext(): Promise<{
		enabled: boolean;
		ws_url: string | null;
		media_base: string | null;
	}> {
		return this.request('/community/context') as Promise<{
			enabled: boolean;
			ws_url: string | null;
			media_base: string | null;
		}>;
	}

	// Preserve (digital archives) — staff cataloguing surface. The
	// anonymous discovery reads live at /preserve/public/* and are
	// consumed by Explore via graph-api, never by this client.
	async getPreserveCollections(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/collections${toQuery(filter)}`);
	}

	async createPreserveCollection(data: Record<string, unknown>) {
		return this.request('/preserve/collections', { method: 'POST', body: JSON.stringify(data) });
	}

	async updatePreserveCollection(id: number, patch: Record<string, unknown>) {
		return this.request(`/preserve/collections/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	/** Staff "rebuild now" for one collection's Similar items shelf; runs in the background (202). */
	async rebuildPreserveSimilar(collectionId: number) {
		return this.request(`/preserve/collections/${collectionId}/similar/rebuild`, { method: 'POST' });
	}

	// List-vs-detail field display (bucket-2 #6).
	async getPreserveFieldCatalog(collectionId: number) {
		return this.request(`/preserve/collections/${collectionId}/field-catalog`);
	}

	async getPreserveFieldDisplay(collectionId: number) {
		return this.request(`/preserve/collections/${collectionId}/field-display`);
	}

	async setPreserveFieldDisplay(collectionId: number, fd: { list: string[]; detail: string[] }) {
		return this.request(`/preserve/collections/${collectionId}/field-display`, {
			method: 'PUT',
			body: JSON.stringify(fd),
		});
	}

	// Project Updates news CMS (bucket-2 #7).
	async getPreserveUpdates(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/updates${toQuery(filter)}`);
	}

	async createPreserveUpdate(data: Record<string, unknown>) {
		return this.request('/preserve/updates', { method: 'POST', body: JSON.stringify(data) });
	}

	async patchPreserveUpdate(id: number, patch: Record<string, unknown>) {
		return this.request(`/preserve/updates/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deletePreserveUpdate(id: number) {
		return this.request(`/preserve/updates/${id}`, { method: 'DELETE' });
	}

	async setPreserveUpdateVideo(id: number, url: string) {
		return this.request(`/preserve/updates/${id}/video`, { method: 'PUT', body: JSON.stringify({ url }) });
	}

	async uploadPreserveUpdateImage(id: number, file: File) {
		const form = new FormData();
		form.append('file', file);
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/preserve/updates/${id}/image`, { method: 'POST', headers, body: form });
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `Upload failed (${resp.status})`);
		}
		return resp.status === 204 ? {} : resp.json();
	}

	// Exhibits — curated multi-page narratives (bucket-2 #8).
	async getPreserveExhibits(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/exhibits${toQuery(filter)}`);
	}
	async getPreserveExhibit(id: number) {
		return this.request(`/preserve/exhibits/${id}`);
	}
	async createPreserveExhibit(data: Record<string, unknown>) {
		return this.request('/preserve/exhibits', { method: 'POST', body: JSON.stringify(data) });
	}
	async patchPreserveExhibit(id: number, patch: Record<string, unknown>) {
		return this.request(`/preserve/exhibits/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	async deletePreserveExhibit(id: number) {
		return this.request(`/preserve/exhibits/${id}`, { method: 'DELETE' });
	}
	async uploadPreserveExhibitCover(id: number, file: File) {
		const form = new FormData();
		form.append('file', file);
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/preserve/exhibits/${id}/cover`, { method: 'POST', headers, body: form });
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `Upload failed (${resp.status})`);
		}
		return resp.status === 204 ? {} : resp.json();
	}
	async createPreserveExhibitPage(exhibitId: number, data: Record<string, unknown>) {
		return this.request(`/preserve/exhibits/${exhibitId}/pages`, { method: 'POST', body: JSON.stringify(data) });
	}
	async patchPreserveExhibitPage(id: number, patch: Record<string, unknown>) {
		return this.request(`/preserve/exhibit-pages/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	async deletePreserveExhibitPage(id: number) {
		return this.request(`/preserve/exhibit-pages/${id}`, { method: 'DELETE' });
	}
	async createPreserveExhibitBlock(pageId: number, data: Record<string, unknown>) {
		return this.request(`/preserve/exhibit-pages/${pageId}/blocks`, { method: 'POST', body: JSON.stringify(data) });
	}
	async patchPreserveExhibitBlock(id: number, patch: Record<string, unknown>) {
		return this.request(`/preserve/exhibit-blocks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	async deletePreserveExhibitBlock(id: number) {
		return this.request(`/preserve/exhibit-blocks/${id}`, { method: 'DELETE' });
	}
	async importPreserveExhibits(exhibits: unknown[]) {
		return this.request('/preserve/exhibits/import', { method: 'POST', body: JSON.stringify({ exhibits }) });
	}
	async createPreserveExhibitBlockItem(blockId: number, data: Record<string, unknown>) {
		return this.request(`/preserve/exhibit-blocks/${blockId}/items`, { method: 'POST', body: JSON.stringify(data) });
	}
	async patchPreserveExhibitBlockItem(id: number, patch: Record<string, unknown>) {
		return this.request(`/preserve/exhibit-block-items/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	async deletePreserveExhibitBlockItem(id: number) {
		return this.request(`/preserve/exhibit-block-items/${id}`, { method: 'DELETE' });
	}

	async getPreserveItems(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/items${toQuery(filter)}`);
	}

	// fleet/preserve-ui-frontend — gap 3 (facet counts) + gap 1 (result-set
	// traversal) + gap E (in-item OCR hit list).
	async getPreserveItemFacets(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/items/facets${toQuery(filter)}`);
	}

	async getPreserveItemNeighbors(id: number, filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/items/${id}/neighbors${toQuery(filter)}`);
	}

	async getPreserveItemTextHits(id: number, q: string) {
		return this.request(`/preserve/items/${id}/text-hits${toQuery({ q })}`);
	}

	async getPreserveItem(id: number) {
		return this.request(`/preserve/items/${id}`);
	}

	async createPreserveItem(data: Record<string, unknown>) {
		return this.request('/preserve/items', { method: 'POST', body: JSON.stringify(data) });
	}

	async updatePreserveItem(id: number, patch: Record<string, unknown>) {
		return this.request(`/preserve/items/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deletePreserveItem(id: number) {
		return this.request(`/preserve/items/${id}`, { method: 'DELETE' });
	}

	async addPreserveTag(itemId: number, data: Record<string, unknown>) {
		return this.request(`/preserve/items/${itemId}/tags`, { method: 'POST', body: JSON.stringify(data) });
	}

	async removePreserveTag(tagId: number) {
		return this.request(`/preserve/tags/${tagId}`, { method: 'DELETE' });
	}

	// ── Batch-entry pack (P0) ──────────────────────────────────────
	//
	// Create up to 100 items in one request. `defaults` fill any field an
	// individual item omits (every item needs at minimum its own `title`;
	// `defaults.collection_id` is required since every item needs one).
	// Response is partial-success shaped: `created` carries the original
	// `items[]` index alongside the new id, `errors` the same for failures.
	async batchCreatePreserveItems(payload: {
		defaults: Record<string, unknown>;
		items: Record<string, unknown>[];
	}) {
		return this.request('/preserve/items/batch', { method: 'POST', body: JSON.stringify(payload) });
	}

	// Bulk-edit up to 200 existing items in one request: `set` fields apply
	// to every id (tri-state — a field the caller didn't touch is simply
	// absent here, never sent as a value), `add_tags` appends the same tag
	// set to every id, `status_action` runs one gated status transition
	// across the set. Partial-success shaped: `updated` ids that
	// succeeded, `errors` the rest (an illegal transition, a validation
	// failure) each with their own id + message.
	async bulkUpdatePreserveItems(payload: {
		item_ids: number[];
		set?: Record<string, unknown>;
		add_tags?: { tag_type: string; tag_text: string }[];
		status_action?: 'submit_for_review' | 'publish' | 'withdraw' | 'back_to_draft';
	}) {
		return this.request('/preserve/items/bulk', { method: 'PATCH', body: JSON.stringify(payload) });
	}

	// Hard-delete up to 200 existing items in one request — legal only from
	// draft or withdrawn (see utils/preserveStatus.ts's canDeleteStatus).
	// Partial-success shaped like the bulk-edit sibling above: `deleted`
	// ids that succeeded, `errors` the rest.
	async bulkDeletePreserveItems(payload: { item_ids: number[] }) {
		return this.request('/preserve/items/bulk-delete', { method: 'POST', body: JSON.stringify(payload) });
	}

	// Add several tags to ONE item in a single round trip — the response
	// carries the item's full, updated tag list, so (unlike the single
	// addPreserveTag above) no follow-up item refetch is ever needed.
	async batchTagPreserveItem(
		itemId: number,
		tags: { tag_type: string; tag_text: string; authority_uri?: string; role?: string }[],
	) {
		return this.request(`/preserve/items/${itemId}/tags/batch`, {
			method: 'POST',
			body: JSON.stringify({ tags }),
		});
	}

	// Duplicate an item into a new draft (no files copied). Returns the
	// full new item — the caller opens it straight into the editor.
	async clonePreserveItem(itemId: number, opts: { include_metadata?: boolean; include_tags?: boolean } = {}) {
		return this.request(`/preserve/items/${itemId}/clone`, { method: 'POST', body: JSON.stringify(opts) });
	}

	// ── Media Platform ────────────────────────────────────────────
	//
	// One library behind every picker in COS. Browsing also CATALOGS:
	// a file control-panel wrote appears the first time somebody looks
	// at its folder, so the listing may report `catalogued` > 0.

	async getMediaTree(params: Record<string, unknown> = {}) {
		return this.request(`/media/tree${toQuery(params)}`);
	}

	async getMediaFiles(params: Record<string, unknown> = {}) {
		return this.request(`/media/files${toQuery(params)}`);
	}

	async getMediaUsage(assetId: number) {
		return this.request(`/media/files/${assetId}/usage`);
	}

	async recordMediaUsage(assetId: number, consumer: string, consumerId: string, field: string) {
		return this.request(`/media/files/${assetId}/usage`, {
			method: 'POST',
			body: JSON.stringify({ consumer, consumer_id: consumerId, field }),
		});
	}

	async createMediaFolder(parent: string, name: string, partnerId?: number) {
		return this.request('/media/folders', {
			method: 'POST',
			body: JSON.stringify({ parent, name, partner_id: partnerId ?? 0 }),
		});
	}

	async setMediaFavorite(assetId: number, on: boolean) {
		return this.request(`/media/files/${assetId}/favorite`, { method: on ? 'PUT' : 'DELETE' });
	}

	/**
	 * Delete is usage-checked. Without `force` the server answers 409 and
	 * returns the usage list, so the caller can name the pages that would
	 * break rather than warning vaguely. The bytes are always retained —
	 * legacy may still be serving them.
	 */
	async deleteMediaFile(assetId: number, force = false) {
		return this.request(`/media/files/${assetId}${force ? '?force=true' : ''}`, { method: 'DELETE' });
	}

	/**
	 * Multipart upload. Omitting `onCollision` is deliberate: the server
	 * answers 409 so the UI can ask "replace, or keep both?" instead of
	 * the service guessing on the user's behalf.
	 */
	/**
	 * Crop a file, replacing it in place.
	 *
	 * Destructive by decision: a legacy page references an image by filename,
	 * so a crop written anywhere else would be invisible to it. The pre-crop
	 * original is kept as a backup rendition, which is the only undo.
	 */
	async cropMediaAsset(id: number, rect: { x: number; y: number; w: number; h: number }) {
		return this.request(`/media/files/${id}/crop`, {
			method: 'POST',
			body: JSON.stringify(rect),
		});
	}

	/** Put the pre-crop original back. */
	async restoreMediaOriginal(id: number) {
		return this.request(`/media/files/${id}/restore-original`, { method: 'POST' });
	}

	/** Save a file's description and framing. */
	async updateMediaAsset(
		id: number,
		patch: { alt_text?: string; title?: string; focal_x?: number; focal_y?: number },
	) {
		return this.request(`/media/files/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	/**
	 * Rename a file, keeping it in the same folder. 409 asks the caller to
	 * pick a different name or pass `keep_both`, matching Upload's clash
	 * contract minus `replace` (see MediaRelocateCollisionPolicy).
	 */
	async renameMediaFile(id: number, name: string, onCollision?: 'keep_both') {
		return this.request(`/media/files/${id}/rename`, {
			method: 'POST',
			body: JSON.stringify({ name, on_collision: onCollision }),
		});
	}

	/** Move a file into a different folder, keeping its name. Same collision contract as rename. */
	async moveMediaFile(id: number, folder: string, onCollision?: 'keep_both') {
		return this.request(`/media/files/${id}/move`, {
			method: 'POST',
			body: JSON.stringify({ folder, on_collision: onCollision }),
		});
	}

	async uploadMediaFile(
		folder: string,
		file: File,
		opts: { partnerId?: number; clientId?: number; onCollision?: 'replace' | 'keep_both' } = {},
	) {
		const form = new FormData();
		form.append('file', file);
		form.append('folder', folder);
		if (opts.partnerId) form.append('partner_id', String(opts.partnerId));
		if (opts.clientId) form.append('client_id', String(opts.clientId));
		if (opts.onCollision) form.append('on_collision', opts.onCollision);
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/media/upload`, { method: 'POST', headers, body: form });
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `Upload failed (${resp.status})`);
		}
		return resp.json();
	}

	// Preserve Stage 2/3 — files, metadata, templates, relationships,
	// donors, CSV import.
	async uploadPreserveFile(itemId: number, file: File) {
		const form = new FormData();
		form.append('file', file);
		const headers = this.multipartHeaders();
		const resp = await fetch(`${API_BASE}/preserve/items/${itemId}/files`, { method: 'POST', headers, body: form });
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `Upload failed (${resp.status})`);
		}
		return resp.json();
	}

	// Bearer-authenticated blob fetch (an <img src> can't carry the
	// header — the prospect-CSV Blob pattern).
	async getPreserveFileBlob(fileId: number): Promise<Blob> {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/preserve/files/${fileId}/content`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `File fetch failed (${resp.status})`);
		return resp.blob();
	}

	async deletePreserveFile(fileId: number) {
		return this.request(`/preserve/files/${fileId}`, { method: 'DELETE' });
	}

	// Batch thumbnail fetch (base64) — the item list's one-round-trip
	// alternative to getPreserveFileBlob per row.
	async getPreserveThumbnails(fileIds: number[]): Promise<Record<string, { mime_type: string; data: string }>> {
		if (fileIds.length === 0) return {};
		const r = (await this.request(`/preserve/thumbnails?ids=${fileIds.join(',')}`)) as {
			data: Record<string, { mime_type: string; data: string }>;
		};
		return r.data ?? {};
	}

	// Attach an external media embed (YouTube/SoundCloud) — a role='embed'
	// file with no stored blob; the server parses/validates the URL.
	async addPreserveEmbed(itemId: number, provider: string, url: string) {
		return this.request(`/preserve/items/${itemId}/embeds`, {
			method: 'POST',
			body: JSON.stringify({ provider, url }),
		});
	}

	async setPreserveItemMetadata(itemId: number, values: Record<string, unknown>[]) {
		return this.request(`/preserve/items/${itemId}/metadata`, { method: 'PUT', body: JSON.stringify(values) });
	}

	async getPreserveTemplates(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/templates${toQuery(filter)}`);
	}

	async getPreserveTemplate(id: number) {
		return this.request(`/preserve/templates/${id}`);
	}

	async createPreserveTemplate(data: Record<string, unknown>) {
		return this.request('/preserve/templates', { method: 'POST', body: JSON.stringify(data) });
	}

	async updatePreserveTemplate(id: number, data: Record<string, unknown>) {
		return this.request(`/preserve/templates/${id}`, { method: 'PUT', body: JSON.stringify(data) });
	}

	// P1 entry-ergonomics — reorder a SAVED template's fields (drag or the
	// keyboard-accessible up/down buttons in the admin editor drive this one
	// path). `field_ids` is the complete new permutation; the response is
	// the template's full ordered field list.
	async reorderPreserveTemplateFields(templateId: number, fieldIds: number[]) {
		return this.request(`/preserve/templates/${templateId}/fields/order`, {
			method: 'PUT',
			body: JSON.stringify({ field_ids: fieldIds }),
		});
	}

	// P1 entry-ergonomics — prior-usage autocomplete (tag text / metadata
	// value / physical location), partner-scoped + frequency-ordered.
	async getPreserveSuggestions(params: {
		kind: 'tag_text' | 'metadata_value' | 'physical_location';
		q: string;
		tag_type?: string;
		property_uri?: string;
		limit?: number;
	}) {
		return this.request(`/preserve/suggest${toQuery(params as unknown as Record<string, unknown>)}`);
	}

	async addPreserveRelationship(itemId: number, data: Record<string, unknown>) {
		return this.request(`/preserve/items/${itemId}/relationships`, { method: 'POST', body: JSON.stringify(data) });
	}

	async removePreserveRelationship(id: number) {
		return this.request(`/preserve/relationships/${id}`, { method: 'DELETE' });
	}

	async getPreserveDonors(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/donors${toQuery(filter)}`);
	}

	async createPreserveDonor(data: Record<string, unknown>) {
		return this.request('/preserve/donors', { method: 'POST', body: JSON.stringify(data) });
	}

	async updatePreserveDonor(id: number, data: Record<string, unknown>) {
		return this.request(`/preserve/donors/${id}`, { method: 'PUT', body: JSON.stringify(data) });
	}

	// P3 — entity hubs (aggregated preserve_tags, "person"/"place"/...) + the
	// unlinked-items report (the Orphanarium). Read-only aggregation; a
	// 1-indexed `page` param (not the items list's own `start=` offset).
	async getPreserveEntities(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/entities${toQuery(filter)}`);
	}

	async getPreserveEntityItems(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/entities/items${toQuery(filter)}`);
	}

	async getPreserveEntitySummary(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/entities/summary${toQuery(filter)}`);
	}

	async getPreserveUnlinkedItems(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/items/unlinked${toQuery(filter)}`);
	}

	// --- Library settings (branches / opening hours / branch groups /
	// pickup locations) — docs/branches-port-plan.md. Communico staff pass
	// client_id; a library admin's tenant is forced server-side, so these
	// helpers never send one for them (the page omits it).

	async getLibraryContext(clientId?: number) {
		return this.request(`/library/context${toQuery({ client_id: clientId })}`);
	}

	// Cardholder terminology (patrons / customers / members) — a global
	// per-partner setting, chosen when Communico onboards a partner. Read
	// is open to any scoped library caller; the write is Communico-staff-
	// only server-side (its one edit home is this Library settings page).
	async getLibraryCardholderTerminology(clientId?: number) {
		return this.request(`/library/terminology${toQuery({ client_id: clientId })}`);
	}

	// COS-native single-tenant surfaces (Nurture, Comms Hub, CID, Preserve,
	// Reserve, Lend) know a COS partner_id, not the legacy client_id above —
	// the backend resolves partner_id -> partners.client_id itself, so
	// callers here never need the legacy id.
	async getLibraryCardholderTerminologyForPartner(partnerId: number) {
		return this.request(`/library/terminology${toQuery({ partner_id: partnerId })}`);
	}

	// The no-param form — for a caller with no local partner_id/client_id at
	// all (e.g. a library admin on a COS partner_id-space surface, where the
	// server infers their tenant from claims rather than the caller ever
	// picking or being handed an id). Communico staff with no partner/client
	// chosen get the same 422 the scoped id forms already surface.
	async getLibraryCardholderTerminologySelf() {
		return this.request(`/library/terminology`);
	}

	// Which staff see which patron fields in lists and exports
	// (backend/internal/service/patron_field_policy.go). Read is any library
	// staff (screens that blank a column read it), write is the library admin.
	async getLibraryPatronFieldPolicy(clientId?: number): Promise<PatronFieldPolicy> {
		return this.request<PatronFieldPolicy>(`/library/patron-field-policy${toQuery({ client_id: clientId })}`);
	}

	async setLibraryPatronFieldPolicy(patch: PatronFieldPolicyPatch, clientId?: number): Promise<PatronFieldPolicy> {
		return this.request<PatronFieldPolicy>(`/library/patron-field-policy${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(patch),
		});
	}

	async getLibraryPatronDataSetupState(clientId?: number): Promise<PathwayState> {
		return this.request<PathwayState>(`/library/patron-field-policy/setup-state${toQuery({ client_id: clientId })}`);
	}

	// The library AI switch: the ai_enabled legacy client setting, default on
	// (backend/internal/service/library_ai.go). Read is any library
	// staff (the Reserve search box hides itself off it); write is Communico
	// staff only.
	async getLibraryAISettings(clientId?: number): Promise<LibraryAISettings> {
		return this.request<LibraryAISettings>(`/library/ai-settings${toQuery({ client_id: clientId })}`);
	}

	async setLibraryAISettings(patch: LibraryAISettingsPatch, clientId?: number): Promise<LibraryAISettings> {
		return this.request<LibraryAISettings>(`/library/ai-settings${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(patch),
		});
	}

	async setLibraryCardholderTerminology(term: string, clientId?: number) {
		return this.request(`/library/terminology${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ term }),
		});
	}

	// Library webhooks (docs/modules/webhooks.md). clientId is the shell
	// picker's value for Communico staff; a library admin sends none and the
	// server forces their own library.
	async getWebhooks(clientId?: number) {
		return this.request(`/webhooks${toQuery({ client_id: clientId })}`);
	}

	async getWebhook(id: number, clientId?: number) {
		return this.request(`/webhooks/${id}${toQuery({ client_id: clientId })}`);
	}

	async createWebhook(body: Record<string, unknown>, clientId?: number) {
		return this.request(`/webhooks${toQuery({ client_id: clientId })}`, { method: 'POST', body: JSON.stringify(body) });
	}

	async updateWebhook(id: number, body: Record<string, unknown>, clientId?: number) {
		return this.request(`/webhooks/${id}${toQuery({ client_id: clientId })}`, { method: 'PUT', body: JSON.stringify(body) });
	}

	async deleteWebhook(id: number, clientId?: number) {
		return this.request(`/webhooks/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	async rotateWebhookSecret(id: number, clientId?: number) {
		return this.request(`/webhooks/${id}/rotate-secret${toQuery({ client_id: clientId })}`, { method: 'POST' });
	}

	async sendWebhookTest(id: number, clientId?: number) {
		return this.request(`/webhooks/${id}/test${toQuery({ client_id: clientId })}`, { method: 'POST' });
	}

	async getWebhookDeliveries(id: number, filter: Record<string, unknown> = {}, clientId?: number) {
		return this.request(`/webhooks/${id}/deliveries${toQuery({ ...filter, client_id: clientId })}`);
	}

	async redeliverWebhook(id: number, deliveryId: number, clientId?: number) {
		return this.request(`/webhooks/${id}/deliveries/${deliveryId}/redeliver${toQuery({ client_id: clientId })}`, { method: 'POST' });
	}

	async getWebhookEvents() {
		return this.request('/webhooks/events');
	}

	async getLibraryBranches(filter: Record<string, unknown> = {}) {
		return this.request(`/library/branches${toQuery(filter)}`);
	}

	async getLibraryBranch(id: number, clientId?: number) {
		return this.request(`/library/branches/${id}${toQuery({ client_id: clientId })}`);
	}

	async createLibraryBranch(data: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/branches${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateLibraryBranch(id: number, data: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/branches/${id}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async getLibraryBranchReferences(id: number, clientId?: number) {
		return this.request(`/library/branches/${id}/references${toQuery({ client_id: clientId })}`);
	}

	async deleteLibraryBranch(id: number, clientId?: number) {
		return this.request(`/library/branches/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	async getLibraryBranchGroups(clientId?: number) {
		return this.request(`/library/branch-groups${toQuery({ client_id: clientId })}`);
	}

	async createLibraryBranchGroup(name: string, clientId?: number) {
		return this.request(`/library/branch-groups${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ name }),
		});
	}

	async renameLibraryBranchGroup(id: number, name: string, clientId?: number) {
		return this.request(`/library/branch-groups/${id}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ name }),
		});
	}

	async deleteLibraryBranchGroup(id: number, clientId?: number) {
		return this.request(`/library/branch-groups/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	async assignLibraryBranchGroup(groupId: number, branchIds: number[], clientId?: number) {
		return this.request(`/library/branch-groups/assign${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ group_id: groupId, branch_ids: branchIds }),
		});
	}

	// scope is "default" (the client-wide hours) or a branch id.
	async getLibraryHours(scope: string, clientId?: number) {
		return this.request(`/library/hours/${scope}${toQuery({ client_id: clientId })}`);
	}

	async saveLibraryHours(scope: string, days: unknown[], clientId?: number) {
		return this.request(`/library/hours/${scope}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ days }),
		});
	}

	async resetLibraryHours(scope: string, clientId?: number) {
		return this.request(`/library/hours/${scope}/reset${toQuery({ client_id: clientId })}`, { method: 'POST' });
	}

	async createLibraryHoursException(scope: string, data: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/hours/${scope}/exceptions${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateLibraryHoursException(scope: string, id: number, data: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/hours/${scope}/exceptions/${id}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteLibraryHoursException(scope: string, id: number, clientId?: number) {
		return this.request(`/library/hours/${scope}/exceptions/${id}${toQuery({ client_id: clientId })}`, {
			method: 'DELETE',
		});
	}

	async getLibraryHoursConflicts(scope: string, data: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/hours/${scope}/conflicts${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async getLibraryHolidays(clientId?: number) {
		return this.request(`/library/holidays${toQuery({ client_id: clientId })}`);
	}

	// key = the holiday being replaced (omit to create).
	async saveLibraryHoliday(data: Record<string, unknown>, key?: string, clientId?: number) {
		return this.request(`/library/holidays${toQuery({ client_id: clientId, key })}`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async deleteLibraryHoliday(key: string, clientId?: number) {
		return this.request(`/library/holidays/${encodeURIComponent(key)}${toQuery({ client_id: clientId })}`, {
			method: 'DELETE',
		});
	}

	async getReserveAvailability(filter: Record<string, unknown> = {}) {
		return this.request(`/library/reserve/availability${toQuery(filter)}`);
	}

	// --- Reserve natural-language room search -------------------------
	// docs/stage-briefs/reserve-nl-search-brief.md. searchRooms/
	// parseRoomSearch/verifyReserveSlot are the typed client surface for
	// the three request shapes in backend/internal/roomsearch/types.go +
	// service.ReserveVerifySlot — additive, following the same
	// client_id-suffix convention every other Reserve method above uses
	// (Communico staff pass it explicitly; library staff are locked to
	// their own and it's dropped by toQuery's undefined check).

	/**
	 * Runs one search: the validated intent in, ranked candidates plus
	 * relaxed alternatives plus the resolved interpretation out. POST
	 * with a JSON body — RoomSearchIntent nests objects and arrays
	 * (dates, times, equipment[], branch_ids[]) that don't round-trip
	 * through toQuery's flat key=value query-string serialisation.
	 */
	async searchRooms(intent: RoomSearchIntent, clientId?: number): Promise<RoomSearchResult> {
		return this.request<RoomSearchResult>(`/library/reserve/search${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(intent),
		});
	}

	/**
	 * Parses a natural-language sentence into a RoomSearchIntent plus any
	 * unresolved gaps. Ambiguity is a typed result here (RoomSearchParse's
	 * own gaps array), never a thrown error — see that type's doc comment.
	 *
	 * currentBranchId is the branch the caller is already working at (Desk
	 * always knows this from its own screen) — a DEFAULT ONLY, used when
	 * the sentence names no branch. A branch named in the sentence still
	 * wins, and an id outside this library's own branches is ignored
	 * server-side rather than trusted; see the backend handler's doc
	 * comment for the full priority order.
	 */
	async parseRoomSearch(sentence: string, clientId?: number, currentBranchId?: number): Promise<RoomSearchParse> {
		return this.request<RoomSearchParse>(`/library/reserve/parse${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({
				sentence,
				...(currentBranchId ? { current_branch_id: currentBranchId } : {}),
			}),
		});
	}

	/**
	 * Re-checks ONE search candidate live, immediately before the booking
	 * form opens (the brief's §5.3 confirm-time re-check — a courtesy
	 * check, not the authoritative one, which still lives in the write
	 * transaction). A 409 means the slot was just taken; detect it with
	 * `err instanceof ApiError && err.status === 409` — NEVER a string
	 * match on the message, which the client doesn't status-prefix (see
	 * apiError.ts's own doc comment on exactly this mistake).
	 */
	async verifyReserveSlot(slot: ReserveVerifySlotRequest, clientId?: number): Promise<ReserveVerifySlotResponse> {
		return this.request<ReserveVerifySlotResponse>(
			`/library/reserve/verify-slot${toQuery({ client_id: clientId })}`,
			{ method: 'POST', body: JSON.stringify(slot) },
		);
	}

	// --- Reserve booking writes (slice 2b) ---------------------------

	async getReserveBookingClasses(clientId?: number) {
		return this.request(`/library/reserve/booking-classes${toQuery({ client_id: clientId })}`);
	}

	// --- Booking-class admin CRUD (docs/reserve-groundtruth.md §7) ---

	async createReserveBookingClass(data: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/reserve/booking-classes${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateReserveBookingClass(id: number, data: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/reserve/booking-classes/${id}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteReserveBookingClass(id: number, clientId?: number) {
		return this.request(`/library/reserve/booking-classes/${id}${toQuery({ client_id: clientId })}`, {
			method: 'DELETE',
		});
	}

	// --- Booking-class policy editor (design.md §2.3) -----------------

	// The class + its default policy + every named policy + every room's
	// resolved assignment, in one round trip — what the class-rules
	// editor opens on.
	async getReserveBookingClassDetail(id: number, clientId?: number) {
		return this.request(`/library/reserve/booking-classes/${id}${toQuery({ client_id: clientId })}`);
	}

	// Updates ANY policy group's full field set — the default profile, a
	// named policy, or a room-specific override; they share one wire shape.
	async updateReservePolicyGroup(groupId: number, data: Partial<ReservePolicyGroup>, clientId?: number) {
		return this.request(`/library/reserve/policy-groups/${groupId}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	// Reads ONE policy group's full rule set — the load path for editing a
	// room override that already existed when the class detail was fetched
	// (the detail response carries assignments, not every override's full
	// fields, and re-issuing {assignment:"custom"} would recreate it blank).
	async getReservePolicyGroup(groupId: number, clientId?: number) {
		return this.request(`/library/reserve/policy-groups/${groupId}${toQuery({ client_id: clientId })}`);
	}

	// Creates a new type=1 named, reusable policy on this class.
	async createReserveNamedPolicy(classId: number, data: { name: string }, clientId?: number) {
		return this.request(`/library/reserve/booking-classes/${classId}/policies${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	// Deletes a named policy or a room's custom override (never the
	// default profile — the backend refuses that). Every room pointed at
	// it falls back to the default policy; the response names how many.
	async deleteReservePolicyGroup(groupId: number, clientId?: number) {
		return this.request(`/library/reserve/policy-groups/${groupId}${toQuery({ client_id: clientId })}`, {
			method: 'DELETE',
		});
	}

	// Sets one room's policy assignment for one class — default / a named
	// policy / a fresh custom override (the response carries the group a
	// "named"/"custom" write now resolves to).
	async setReserveClassRoomAssignment(
		classId: number,
		roomId: number,
		data: ReserveRoomAssignmentInput,
		clientId?: number,
	) {
		return this.request(
			`/library/reserve/booking-classes/${classId}/rooms/${roomId}${toQuery({ client_id: clientId })}`,
			{
				method: 'PUT',
				body: JSON.stringify(data),
			},
		);
	}

	// The per-policy equipment + layout fee sheet — every asset/layout the
	// client has, with this group's own cost (0 where never priced).
	async getReservePolicyCharges(groupId: number, clientId?: number) {
		return this.request(`/library/reserve/policy-groups/${groupId}/charges${toQuery({ client_id: clientId })}`);
	}

	// Replace-set both charge families for this policy group in one call.
	async saveReservePolicyCharges(groupId: number, data: ReservePolicyChargesWrite, clientId?: number) {
		return this.request(`/library/reserve/policy-groups/${groupId}/charges${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async getReserveBooking(id: number, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}${toQuery({ client_id: clientId })}`);
	}

	// The "Reservation details" panel's history log (feedback #183) —
	// roombooking_log entries for one booking.
	async getReserveBookingHistory(id: number, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/history${toQuery({ client_id: clientId })}`);
	}

	async createReserveBooking(data: ReserveBookingInput, clientId?: number) {
		return this.request(`/library/reserve/bookings${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	/**
	 * Every gate, real tenant, nothing written — the create form's live
	 * preview. `signal` lets the caller abort a stale request when the
	 * form's fields change again before this one resolves.
	 */
	async previewReserveBooking(data: ReserveBookingInput, clientId?: number, signal?: AbortSignal) {
		return this.request(
			`/library/reserve/bookings${toQuery({ client_id: clientId, dry_run: true })}`,
			{ method: 'POST', body: JSON.stringify(data), signal },
		);
	}

	async updateReserveBooking(id: number, data: ReserveBookingInput, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	// B20 — the three-way edit. "This booking only" keeps using
	// updateReserveBooking above; these two are this_and_future/all only.
	async getReserveSeriesEditImpact(id: number, scope: SeriesEditScope, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/series-edit-impact${toQuery({ client_id: clientId, scope })}`);
	}

	async editReserveSeries(id: number, scope: SeriesEditScope, data: ReserveBookingInput, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/series-edit${toQuery({ client_id: clientId, scope })}`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	/**
	 * Toggle a booking's no-show flag. Reversible — legacy's own menu offers
	 * both directions — and it never touches status: no_show is an independent
	 * flag that overrides the status WORD, not a status value.
	 */
	async setReserveBookingNoShow(id: number, noShow: boolean, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/no-show${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ no_show: noShow }),
		});
	}

	async cancelReserveBooking(id: number, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/cancel${toQuery({ client_id: clientId })}`, {
			method: 'POST',
		});
	}

	/**
	 * The staff half of "end early" — a librarian, standing at a desk with
	 * no panel device, signing a booking's room out for the rest of the
	 * slot on the day of. Returns `{booking, state, already_ended}`
	 * (service.ReserveEndEarlyResult) — `already_ended` is a no-op, never
	 * an error, so a repeat click reports the same outcome rather than a
	 * failure.
	 */
	async endReserveBookingEarly(id: number, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/end-early${toQuery({ client_id: clientId })}`, {
			method: 'POST',
		});
	}

	/**
	 * The desk Rooms tab's "Add time" action — the durations (minutes,
	 * ascending) currently free, never one the write below would then
	 * refuse. Returns `{options: number[]}` (service.ReserveExtendOptionsResult)
	 * — always an array, empty when nothing fits right now.
	 */
	async getReserveBookingExtendOptions(id: number, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/extend-options${toQuery({ client_id: clientId })}`);
	}

	/**
	 * Extend a booking by one of the offered ladder durations. minutes MUST
	 * be one of the values GET .../extend-options returned — a 422 (a stale
	 * offer, or the ladder itself) carries a message written for a
	 * librarian; surface it verbatim, not through userMessage().
	 */
	async extendReserveBooking(id: number, minutes: number, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/extend${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ minutes }),
		});
	}

	/**
	 * The delays the desk's "Running late" action may offer for a booking
	 * — always an array, empty when no later start fits.
	 */
	async getReserveBookingDelayOptions(id: number, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/delay-options${toQuery({ client_id: clientId })}`);
	}

	/**
	 * Shift a booking later for a late arrival, keeping its duration.
	 * minutes MUST be one of the values GET .../delay-options returned — a
	 * 422 (a stale offer, or the ladder itself) carries a message written
	 * for a librarian; surface it verbatim, not through userMessage().
	 */
	async delayReserveBooking(id: number, minutes: number, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/delay${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ minutes }),
		});
	}

	/**
	 * The desk Rooms tab's "Move" action, step 1 — rooms free for THIS
	 * booking's own day+window at its branch, excluding its current room.
	 * An empty list means genuinely nothing else is free, not a load
	 * failure.
	 */
	async getReserveMoveCandidates(id: number, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/move-candidates${toQuery({ client_id: clientId })}`);
	}

	/**
	 * Whether the move dialog's Notify switch can work for this booking
	 * (`can_notify`, with a `reason` when not). Read before the move; it
	 * sends nothing.
	 */
	async getReserveMoveNotice(id: number, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/move-notice${toQuery({ client_id: clientId })}`);
	}

	/**
	 * Move a booking into a different room, with the librarian's own
	 * choice of whether to notify the patron. A 422 (the room is no
	 * longer free, or this is an Attend-owned booking) carries a message
	 * written for a librarian — surface it verbatim, not through
	 * userMessage().
	 */
	async moveReserveBooking(id: number, roomId: number, notify: boolean, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/move${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ room_id: roomId, notify }),
		});
	}

	// Reserve payments (docs/reserve-groundtruth.md §8) — bookkeeping-only
	// refund of a booking's paid offline/pay-later charges. Gated
	// server-side on the SEPARATE `refundrooms` permission; never touches
	// a card charge.
	async refundReserveBooking(id: number, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/refund${toQuery({ client_id: clientId })}`, {
			method: 'POST',
		});
	}

	// Reserve approval queue (docs/reserve-groundtruth.md) — bookings that
	// landed Awaiting approval instead of Approved.

	async getReserveApprovalQueue(clientId?: number) {
		return this.request(`/library/reserve/approvals${toQuery({ client_id: clientId })}`);
	}

	async approveReserveBooking(id: number, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/approve${toQuery({ client_id: clientId })}`, {
			method: 'POST',
		});
	}

	async declineReserveBooking(id: number, reason: string, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/decline${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ reason }),
		});
	}

	// The mediated CANCELLATION decision (docs/reserve-groundtruth.md) — a
	// patron's own cancellation request (status 4) and the refund it can
	// leave pending (status 5). Distinct from approve/declineReserveBooking
	// above, which act on the Awaiting-approval (0) vocabulary only.

	async approveReserveCancellation(id: number, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/approve-cancellation${toQuery({ client_id: clientId })}`, {
			method: 'POST',
		});
	}

	async declineReserveCancellation(id: number, reason: string, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/decline-cancellation${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ reason }),
		});
	}

	async completeReserveCancellationRefund(id: number, clientId?: number) {
		return this.request(`/library/reserve/bookings/${id}/complete-refund${toQuery({ client_id: clientId })}`, {
			method: 'POST',
		});
	}

	// Reserve notice templates (Comms Hub contract §14) — the LEGACY
	// `notifications` store the reminder crons already read
	// (docs/platform/legacy-reminders-groundtruth.md). Same absence
	// semantics as Attend's: there is no reset endpoint — a missing row
	// means the notice is NOT SENT, and writing the Communico default
	// (via a normal save) is what turns it on. Deleting would silence
	// it, so there is nothing to delete. Mirrors listAttendNoticeTemplates
	// / saveAttendNoticeTemplate exactly, one module prefix later.
	async listReserveNoticeTemplates(clientId: number) {
		return this.request(`/library/reserve/notice-templates${toQuery({ client_id: clientId })}`);
	}

	async saveReserveNoticeTemplate(clientId: number, type: string, tmpl: Record<string, string>) {
		return this.request(
			`/library/reserve/notice-templates/${encodeURIComponent(type)}${toQuery({ client_id: clientId })}`,
			{ method: 'PUT', body: JSON.stringify(tmpl) },
		);
	}

	// Reserve Panels — the admin surface over room-booking devices
	// (docs/reserve-panels/reserve-panels-spec.md §7). Staff-facing REST;
	// scoped server-side via RequireLevelScoped (reads level 4, writes
	// level 3) per the panels contract — no client_id param, the
	// Roster/Preserve/Lend COS-native admin convention (unlike the
	// legacy client_id-scoped Library surfaces above).
	//
	// CORRECTION (2026-08-09, found in a live smoke): that parenthetical was
	// wrong and so were these signatures. These routes resolve their tenant
	// through the SHARED libraryClientID helper (handler/library.go:41), which
	// takes the tenant from a library user's own claims but reads `?client_id=`
	// from the QUERY STRING for Communico staff -- on every method, writes
	// included. Without it the whole section 422s, but ONLY for Communico
	// staff; a library admin never sees it because their tenant is implied.
	// That is the third time this class has bitten in this codebase (the Comms
	// Hub Phase 5b embed and the Roster notice-template editor were the first
	// two), and it is invisible to exactly the people who test first.
	//
	// Every method below therefore takes an explicit clientId and appends it as
	// a query param. Callers pass legacyClientId(); a library user's value is
	// ignored server-side, so sending it is harmless and keeps one code path.
	async getReservePanels(filter: Record<string, unknown> = {}) {
		return this.request(`/library/reserve/panels${toQuery(filter)}`);
	}

	async claimReservePanel(clientId: number, data: Record<string, unknown>) {
		return this.request(`/library/reserve/panels/claim${toQuery({ client_id: clientId })}`, { method: 'POST', body: JSON.stringify(data) });
	}

	async updateReservePanel(clientId: number, id: number, data: Record<string, unknown>) {
		return this.request(`/library/reserve/panels/${id}${toQuery({ client_id: clientId })}`, { method: 'PUT', body: JSON.stringify(data) });
	}

	async unpairReservePanel(clientId: number, id: number) {
		return this.request(`/library/reserve/panels/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	// Both return a ReservePanelCommandResult, never a bare 204 — a panel is
	// behind NAT with no inbound path, so this call can only ever QUEUE the
	// command. See that type's doc comment for why the caller must render
	// panel_status/last_seen_at rather than assuming the action happened.
	async identifyReservePanel(clientId: number, id: number) {
		return this.request<ReservePanelCommandResult>(`/library/reserve/panels/${id}/identify${toQuery({ client_id: clientId })}`, { method: 'POST' });
	}

	async restartReservePanel(clientId: number, id: number) {
		return this.request<ReservePanelCommandResult>(`/library/reserve/panels/${id}/restart${toQuery({ client_id: clientId })}`, { method: 'POST' });
	}

	async getReservePanelProfiles(filter: Record<string, unknown> = {}) {
		return this.request(`/library/reserve/panels/profiles${toQuery(filter)}`);
	}

	// Attribution reporting (spec §11, §14 P2) — same clientScope()
	// convention as every other read above.
	async getReservePanelAttribution(from: string, to: string, filter: Record<string, unknown> = {}) {
		return this.request<ReservePanelAttributionSummary>(
			`/library/reserve/panels/attribution${toQuery({ ...filter, from, to })}`,
		);
	}

	async createReservePanelProfile(clientId: number, data: Record<string, unknown>) {
		return this.request(`/library/reserve/panels/profiles${toQuery({ client_id: clientId })}`, { method: 'POST', body: JSON.stringify(data) });
	}

	async updateReservePanelProfile(clientId: number, id: number, data: Record<string, unknown>) {
		return this.request(`/library/reserve/panels/profiles/${id}${toQuery({ client_id: clientId })}`, { method: 'PUT', body: JSON.stringify(data) });
	}

	async deleteReservePanelProfile(clientId: number, id: number) {
		return this.request(`/library/reserve/panels/profiles/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	// The bookings LIST — docs/reserve-bookings-list-contract.md. A date-range
	// / filter / search / paginated query, distinct from getReserveAvailability
	// (one branch, one day, a grid) and getReserveApprovalQueue (future-only,
	// unmediated statuses only). `filter` is a ReserveBookingFilters; array
	// values (status/booking_type/branch_id) stringify to CSV via toQuery's
	// `String(v)`, matching the contract's CSV-of-ints param shape — EXCEPT
	// `room_id`, which the contract sends as REPEATED params
	// (attendMultiInt64's own shape on the backend, feedback #194 — a CSV
	// value like "5,8" fails strconv.ParseInt there and is silently
	// dropped, not parsed), so it's pulled out and appended separately.
	// `signal` lets the caller abort a stale request when a newer filter
	// change has superseded it (the Cmd+K palette's own abort-on-keystroke
	// pattern, cos-command-palette.ts) — see cos-reserve-bookings-list.ts's
	// `_load`.
	async getReserveBookings(filter: Record<string, unknown>, signal?: AbortSignal) {
		const { room_id, ...rest } = filter;
		const params = new URLSearchParams();
		for (const [k, v] of Object.entries(rest)) {
			if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
		}
		const roomIds = Array.isArray(room_id)
			? room_id
			: room_id !== undefined && room_id !== null && room_id !== ''
				? [room_id]
				: [];
		for (const id of roomIds) params.append('room_id', String(id));
		const qs = params.toString();
		return this.request(`/library/reserve/bookings${qs ? `?${qs}` : ''}`, signal ? { signal } : {});
	}

	async getLibraryHolidaySuggestions(filter: Record<string, unknown> = {}) {
		return this.request(`/library/holiday-suggestions${toQuery(filter)}`);
	}

	async bulkAddLibraryHolidays(body: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/holidays/bulk${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	// The cross-module open/closed read. Any module about to schedule
	// something asks HERE rather than reading the hours rows itself —
	// the precedence is easy to get subtly wrong.
	async getLibraryHoursStatus(params: {
		date: string;
		to?: string;
		branch_id?: number;
		start?: string;
		end?: string;
		client_id?: number;
	}) {
		return this.request(`/library/hours-status${toQuery(params as Record<string, unknown>)}`);
	}

	// --- rooms (Reserve setup) --------------------------------------

	async getLibraryRooms(filter: Record<string, unknown> = {}) {
		return this.request(`/library/rooms${toQuery(filter)}`);
	}

	async getLibraryRoom(id: number, clientId?: number) {
		return this.request(`/library/rooms/${id}${toQuery({ client_id: clientId })}`);
	}

	async createLibraryRoom(data: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/rooms${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateLibraryRoom(id: number, data: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/rooms/${id}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async getLibraryRoomReferences(id: number, clientId?: number) {
		return this.request(`/library/rooms/${id}/references${toQuery({ client_id: clientId })}`);
	}

	async deleteLibraryRoom(id: number, clientId?: number) {
		return this.request(`/library/rooms/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	async getLibraryRoomHours(id: number, clientId?: number) {
		return this.request(`/library/rooms/${id}/hours${toQuery({ client_id: clientId })}`);
	}

	async saveLibraryRoomHours(id: number, days: unknown[], clientId?: number) {
		return this.request(`/library/rooms/${id}/hours${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ days }),
		});
	}

	async resetLibraryRoomHours(id: number, clientId?: number) {
		return this.request(`/library/rooms/${id}/hours/reset${toQuery({ client_id: clientId })}`, {
			method: 'POST',
		});
	}

	async createLibraryRoomException(id: number, data: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/rooms/${id}/hours/exceptions${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async deleteLibraryRoomException(id: number, excId: number, clientId?: number) {
		return this.request(
			`/library/rooms/${id}/hours/exceptions/${excId}${toQuery({ client_id: clientId })}`,
			{ method: 'DELETE' },
		);
	}

	async getLibraryLayoutTypes(clientId?: number) {
		return this.request(`/library/layout-types${toQuery({ client_id: clientId })}`);
	}

	async getLibraryRoomLayouts(id: number, clientId?: number) {
		return this.request(`/library/rooms/${id}/layouts${toQuery({ client_id: clientId })}`);
	}

	async saveLibraryRoomLayouts(id: number, layouts: unknown[], clientId?: number) {
		return this.request(`/library/rooms/${id}/layouts${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ layouts }),
		});
	}

	async getLibraryEquipment(filter: Record<string, unknown> = {}) {
		return this.request(`/library/equipment${toQuery(filter)}`);
	}

	async getLibraryRoomEquipment(id: number, clientId?: number) {
		return this.request(`/library/rooms/${id}/equipment${toQuery({ client_id: clientId })}`);
	}

	async saveLibraryRoomEquipment(id: number, equipmentIds: number[], clientId?: number) {
		return this.request(`/library/rooms/${id}/equipment${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ equipment_ids: equipmentIds }),
		});
	}

	async getLibraryRoomCheckInQR(id: number, clientId?: number) {
		return this.request(`/library/rooms/${id}/checkin-qr${toQuery({ client_id: clientId })}`);
	}

	async createLibraryEquipment(body: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/equipment${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	async updateLibraryEquipment(id: number, body: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/equipment/${id}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(body),
		});
	}

	async getLibraryEquipmentReferences(id: number, clientId?: number) {
		return this.request(`/library/equipment/${id}/references${toQuery({ client_id: clientId })}`);
	}

	async deleteLibraryEquipment(id: number, clientId?: number) {
		return this.request(`/library/equipment/${id}${toQuery({ client_id: clientId })}`, {
			method: 'DELETE',
		});
	}

	// The controlled vocabulary of equipment names — Communico-managed
	// defaults plus this library's own additions.
	async getLibraryEquipmentCatalog(filter: Record<string, unknown> = {}) {
		return this.request(`/library/equipment-catalog${toQuery(filter)}`);
	}

	async createLibraryEquipmentCatalogItem(body: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/equipment-catalog${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	async updateLibraryEquipmentCatalogItem(
		id: number,
		body: Record<string, unknown>,
		clientId?: number,
	) {
		return this.request(`/library/equipment-catalog/${id}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(body),
		});
	}

	async deleteLibraryEquipmentCatalogItem(id: number, clientId?: number) {
		return this.request(`/library/equipment-catalog/${id}${toQuery({ client_id: clientId })}`, {
			method: 'DELETE',
		});
	}

	async getLibraryPickupLocations(filter: Record<string, unknown> = {}) {
		return this.request(`/library/pickup-locations${toQuery(filter)}`);
	}

	async createLibraryPickupLocation(data: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/pickup-locations${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateLibraryPickupLocation(id: number, data: Record<string, unknown>, clientId?: number) {
		return this.request(`/library/pickup-locations/${id}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteLibraryPickupLocation(id: number, clientId?: number) {
		return this.request(`/library/pickup-locations/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	async getPreserveBranches(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/branches${toQuery(filter)}`);
	}

	async createPreserveBranch(data: Record<string, unknown>) {
		return this.request('/preserve/branches', { method: 'POST', body: JSON.stringify(data) });
	}

	async updatePreserveBranch(id: number, data: Record<string, unknown>) {
		return this.request(`/preserve/branches/${id}`, { method: 'PUT', body: JSON.stringify(data) });
	}

	async getPreserveUsage(partnerId: number) {
		return this.request(`/preserve/usage?partner_id=${partnerId}`);
	}

	// Community engagement (P2) — the moderation worklist. `filter` carries
	// the same partner_id/client_id tenant-scoping keys as every other
	// Preserve list endpoint, plus status/kind/page/limit.
	async getPreserveContributions(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/contributions${toQuery(filter)}`);
	}

	async getPreserveContribution(id: number) {
		return this.request(`/preserve/contributions/${id}`);
	}

	// Bearer-authenticated blob fetch for the PENDING original — an
	// <img src> can't carry the header (mirrors getPreserveFileBlob).
	async getPreserveContributionFileBlob(id: number): Promise<Blob> {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/preserve/contributions/${id}/file`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `File fetch failed (${resp.status})`);
		return resp.blob();
	}

	async approvePreserveContribution(id: number, payload: Record<string, unknown> = {}) {
		return this.request(`/preserve/contributions/${id}/approve`, { method: 'POST', body: JSON.stringify(payload) });
	}

	// message is REQUIRED — the contributor may retry.
	async rejectPreserveContribution(id: number, message: string) {
		return this.request(`/preserve/contributions/${id}/reject`, { method: 'POST', body: JSON.stringify({ message }) });
	}

	// message is optional — decline is permanent.
	async declinePreserveContribution(id: number, message = '') {
		return this.request(`/preserve/contributions/${id}/decline`, { method: 'POST', body: JSON.stringify({ message }) });
	}

	// filter carries partner_id OR client_id — the same tenant-scoping
	// convention as getPreserveContributions/getPreserveItems, since a
	// Communico staffer may have only a legacy client picked.
	async getPreserveEngagementSettings(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/engagement-settings${toQuery(filter)}`);
	}

	async updatePreserveEngagementSettings(patch: Record<string, unknown>) {
		return this.request('/preserve/engagement-settings', { method: 'PUT', body: JSON.stringify(patch) });
	}

	// preserve-setup-screen — per-partner cataloguing policy (which content
	// types are offered, the collection-field-addition policy, and what
	// happens when an item's type changes). Not yet backed by the server —
	// see cos-preserve-admin-cataloguing.ts's header comment for the exact
	// contract these expect; the endpoint path here is this frontend's own
	// choice (the given contract only names the settings FIELDS, not a
	// route), following the getPreserveEngagementSettings shape above.
	async getPreserveCataloguingSettings(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/cataloguing-settings${toQuery(filter)}`);
	}

	async updatePreserveCataloguingSettings(patch: Record<string, unknown>) {
		return this.request('/preserve/cataloguing-settings', { method: 'PUT', body: JSON.stringify(patch) });
	}

	// ── Preserve oral history ────────────────────────────────────
	// Direct-to-storage upload for long audio and video, which would not
	// fit the 64 MiB API body limit: presign, PUT to the object store,
	// then register. On a backend with no object store (local disk) the
	// presign answers 501 and this falls back to the ordinary upload.
	async uploadPreserveAV(itemId: number, file: File, onProgress?: (pct: number) => void) {
		let presign: { upload_url: string; key: string; headers?: Record<string, string> };
		try {
			presign = await this.request(`/preserve/items/${itemId}/av/presign`, {
				method: 'POST',
				body: JSON.stringify({ filename: file.name, size: file.size }),
			});
		} catch (e) {
			if (e instanceof ApiError && e.status === 501) return this.uploadPreserveFile(itemId, file);
			throw e;
		}
		await new Promise<void>((resolve, reject) => {
			const xhr = new XMLHttpRequest();
			xhr.open('PUT', presign.upload_url);
			// The URL signs the content type (and the size, which the browser
			// sends itself as Content-Length): send exactly what was signed.
			for (const [name, value] of Object.entries(presign.headers ?? {})) xhr.setRequestHeader(name, value);
			xhr.upload.onprogress = (ev) => {
				if (onProgress && ev.lengthComputable) onProgress(Math.round((ev.loaded / ev.total) * 100));
			};
			xhr.onload = () =>
				xhr.status >= 200 && xhr.status < 300
					? resolve()
					: reject(new ApiError(xhr.status, `Upload to storage failed (${xhr.status})`));
			xhr.onerror = () => reject(new ApiError(0, 'Upload to storage failed: the connection was interrupted'));
			xhr.send(file);
		});
		return this.request(`/preserve/items/${itemId}/av/complete`, {
			method: 'POST',
			body: JSON.stringify({ key: presign.key, filename: file.name }),
		});
	}
	async getPreserveRecording(itemId: number) {
		return this.request(`/preserve/items/${itemId}/recording`);
	}
	async retallyPreserveRecording(itemId: number) {
		return this.request(`/preserve/items/${itemId}/recording/retally`, { method: 'POST' });
	}
	async getPreservePlayUrl(fileId: number) {
		return this.request<{ url: string }>(`/preserve/files/${fileId}/play-url`);
	}
	async transcribePreserveFile(fileId: number) {
		return this.request(`/preserve/files/${fileId}/transcribe`, { method: 'POST' });
	}
	async editPreserveTranscriptLine(lineId: number, text: string) {
		return this.request(`/preserve/transcript-lines/${lineId}`, { method: 'PATCH', body: JSON.stringify({ text }) });
	}
	async resolvePreserveTranscriptClash(lineId: number, keepMine: boolean) {
		return this.request(`/preserve/transcript-lines/${lineId}/resolve`, { method: 'POST', body: JSON.stringify({ keep_mine: keepMine }) });
	}
	async renamePreserveSpeaker(transcriptId: number, key: string, name: string) {
		return this.request(`/preserve/transcripts/${transcriptId}/speakers/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify({ name }) });
	}
	async markPreserveTranscriptReviewed(transcriptId: number) {
		return this.request(`/preserve/transcripts/${transcriptId}/reviewed`, { method: 'POST' });
	}
	// recordings come a page at a time by keyset cursor: pass the previous
	// page's next_after as `after` to continue (0 or omitted for the first page).
	async getPreserveOralReview(collectionId: number, after = 0, limit = 0) {
		return this.request(`/preserve/collections/${collectionId}/oral-review${toQuery({ after: after || undefined, limit: limit || undefined })}`);
	}
	async getPreserveOralTerm(collectionId: number, termId: number) {
		return this.request<{ candidates: unknown[] }>(`/preserve/collections/${collectionId}/oral-terms/${termId}`);
	}
	async decidePreserveOralTerm(collectionId: number, termId: number, decision: 'accepted' | 'rejected') {
		return this.request<{ changed: number }>(`/preserve/collections/${collectionId}/oral-terms/${termId}/decision`, { method: 'POST', body: JSON.stringify({ decision }) });
	}
	async tallyPreserveCollection(collectionId: number) {
		return this.request<{ recordings: number; candidates: number }>(`/preserve/collections/${collectionId}/tally`, { method: 'POST' });
	}
	async decidePreserveTagCandidate(candidateId: number, decision: 'accepted' | 'rejected') {
		return this.request(`/preserve/tag-candidates/${candidateId}/decision`, { method: 'POST', body: JSON.stringify({ decision }) });
	}
	async getPreserveTranscriptionSettings(collectionId: number) {
		return this.request(`/preserve/collections/${collectionId}/transcription`);
	}
	async setPreserveTranscriptionSettings(collectionId: number, enabled: boolean, language: string) {
		return this.request(`/preserve/collections/${collectionId}/transcription`, { method: 'PUT', body: JSON.stringify({ enabled, language }) });
	}
	async getPreserveVocabulary(partnerId?: number) {
		return this.request<{ terms: unknown[]; starter_vocabulary_size: number }>(`/preserve/vocabulary${toQuery({ partner_id: partnerId })}`);
	}
	async createPreserveVocabularyTerm(term: Record<string, unknown>) {
		return this.request('/preserve/vocabulary', { method: 'POST', body: JSON.stringify(term) });
	}
	async updatePreserveVocabularyTerm(id: number, term: Record<string, unknown>) {
		return this.request(`/preserve/vocabulary/${id}`, { method: 'PUT', body: JSON.stringify(term) });
	}
	async deletePreserveVocabularyTerm(id: number) {
		return this.request(`/preserve/vocabulary/${id}`, { method: 'DELETE' });
	}
	async copyPreserveStarterVocabulary(partnerId?: number) {
		return this.request<{ added: number; skipped_existing: number }>('/preserve/vocabulary/starter', { method: 'POST', body: JSON.stringify({ partner_id: partnerId ?? 0 }) });
	}
	async importPreserveVocabularyCSV(file: File, partnerId?: number) {
		const form = new FormData();
		form.append('file', file);
		if (partnerId) form.append('partner_id', String(partnerId));
		const headers = this.multipartHeaders();
		const resp = await fetch(`${API_BASE}/preserve/vocabulary/import`, { method: 'POST', headers, body: form });
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `Import failed (${resp.status})`);
		}
		return resp.json();
	}

	async updatePreserveLimits(limits: Record<string, unknown>) {
		return this.request('/preserve/limits', { method: 'PUT', body: JSON.stringify(limits) });
	}

	// Communico-staff estate-wide Preserve AI cost report — spend by
	// partner for one calendar month, heaviest first, with an estate
	// total ("what did Preserve cost us"). month is "YYYY-MM"; omitted
	// defaults to the current month server-side.
	async getPreserveAISpend(month?: string) {
		return this.request(`/preserve/ai-spend${month ? `?month=${month}` : ''}`);
	}

	// Whether "Suggest fields" can run for the picked library, asked before
	// anything is created so the screen can decline to offer it.
	// feature: "suggest" (the default, metadata suggestions) or "speech".
	async getPreserveAIStatus(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/ai-status${toQuery(filter)}`);
	}

	// describedText (fleet/describe-ui) — the cataloguer's own plain-sentence
	// description; optional, so a suggest call with no description (an
	// existing item's re-scan, or a not-yet-wired backend) is unaffected.
	async suggestPreserveMetadata(itemId: number, describedText?: string) {
		const body = describedText && describedText.trim() ? JSON.stringify({ described_text: describedText }) : undefined;
		return this.request(`/preserve/items/${itemId}/ai-suggest`, { method: 'POST', ...(body ? { body } : {}) });
	}

	// Entry-side value control — the controlled rights vocabulary (a closed,
	// static 20-entry list; no params) + its conformance summary (how many
	// of this partner's — or, unscoped, the whole platform's — items carry
	// a rights_uri right now).
	async getPreserveRightsVocabulary() {
		return this.request('/preserve/rights-vocabulary');
	}

	async getPreserveRightsSummary(filter: Record<string, unknown> = {}) {
		return this.request(`/preserve/rights/summary${toQuery(filter)}`);
	}

	// External authority control (LCSH/LCNAF/TGM/Getty AAT+TGN/Wikidata) —
	// purely advisory typeahead for tag entry. Never blocks: a caller that
	// fails or returns nothing just gets an empty result list.
	async searchPreserveAuthority(params: { q: string; tag_type?: string; sources?: string; limit?: number }) {
		return this.request(`/preserve/authority/search${toQuery(params as unknown as Record<string, unknown>)}`);
	}

	// Internet Archive import (Communico staff; admin-gated on the server).
	async getPreserveIAImports() {
		return this.request('/preserve/ia-imports');
	}

	async getPreserveIAMapping() {
		return this.request('/preserve/ia-imports/mapping');
	}

	async planPreserveIAImport(collectionId: number, iaCollection: string) {
		return this.request('/preserve/ia-imports', { method: 'POST', body: JSON.stringify({ collection_id: collectionId, ia_collection: iaCollection }) });
	}

	async getPreserveIAImport(id: number) {
		return this.request(`/preserve/ia-imports/${id}`);
	}

	async startPreserveIAImport(id: number, confirmed: boolean) {
		return this.request(`/preserve/ia-imports/${id}/start`, { method: 'POST', body: JSON.stringify({ confirmed }) });
	}

	async controlPreserveIAImport(id: number, action: 'pause' | 'resume' | 'cancel' | 'retry-failures') {
		return this.request(`/preserve/ia-imports/${id}/${action}`, { method: 'POST' });
	}

	async importPreserveCSV(collectionId: number, file: File) {
		const form = new FormData();
		form.append('file', file);
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/preserve/collections/${collectionId}/import`, { method: 'POST', headers, body: form });
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `Import failed (${resp.status})`);
		}
		return resp.json();
	}

	// CID (Community Information Directory) — staff curation surface
	// (/api/v1/cid/*). The anonymous discovery reads live at
	// /cid/public/* and are not called from here.
	async resolveCIDPartner(clientId: number) {
		return this.request(`/cid/resolve-partner${toQuery({ client_id: clientId })}`);
	}

	async getCIDAgencies(params: Record<string, unknown> = {}) {
		return this.request(`/cid/agencies${toQuery(params)}`);
	}

	async getCIDAgency(id: number) {
		return this.request(`/cid/agencies/${id}`);
	}

	async createCIDAgency(body: unknown) {
		return this.request('/cid/agencies', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateCIDAgency(id: number, patch: unknown) {
		return this.request(`/cid/agencies/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteCIDAgency(id: number) {
		return this.request(`/cid/agencies/${id}`, { method: 'DELETE' });
	}

	async setCIDAgencyTerms(id: number, termIds: number[]) {
		return this.request(`/cid/agencies/${id}/terms`, {
			method: 'PUT',
			body: JSON.stringify({ term_ids: termIds }),
		});
	}

	async setCIDAgencyHours(id: number, hours: unknown[]) {
		return this.request(`/cid/agencies/${id}/hours`, {
			method: 'PUT',
			body: JSON.stringify({ hours }),
		});
	}

	async getCIDTerms(params: Record<string, unknown> = {}) {
		return this.request(`/cid/terms${toQuery(params)}`);
	}

	async createCIDTerm(body: unknown) {
		return this.request('/cid/terms', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateCIDTerm(id: number, patch: unknown) {
		return this.request(`/cid/terms/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteCIDTerm(id: number) {
		return this.request(`/cid/terms/${id}`, { method: 'DELETE' });
	}

	async getCIDAlerts(params: Record<string, unknown> = {}) {
		return this.request(`/cid/alerts${toQuery(params)}`);
	}

	async createCIDAlert(body: unknown) {
		return this.request('/cid/alerts', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateCIDAlert(id: number, patch: unknown) {
		return this.request(`/cid/alerts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteCIDAlert(id: number) {
		return this.request(`/cid/alerts/${id}`, { method: 'DELETE' });
	}

	async getCIDHotTopics(params: Record<string, unknown> = {}) {
		return this.request(`/cid/hot-topics${toQuery(params)}`);
	}

	async createCIDHotTopic(body: unknown) {
		return this.request('/cid/hot-topics', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateCIDHotTopic(id: number, patch: unknown) {
		return this.request(`/cid/hot-topics/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteCIDHotTopic(id: number) {
		return this.request(`/cid/hot-topics/${id}`, { method: 'DELETE' });
	}

	async getCIDCategories(params: Record<string, unknown> = {}) {
		return this.request(`/cid/categories${toQuery(params)}`);
	}

	async createCIDCategory(body: unknown) {
		return this.request('/cid/categories', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateCIDCategory(id: number, patch: unknown) {
		return this.request(`/cid/categories/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteCIDCategory(id: number) {
		return this.request(`/cid/categories/${id}`, { method: 'DELETE' });
	}

	async getCIDSections(params: Record<string, unknown> = {}) {
		return this.request(`/cid/sections${toQuery(params)}`);
	}

	async createCIDSection(body: unknown) {
		return this.request('/cid/sections', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateCIDSection(id: number, patch: unknown) {
		return this.request(`/cid/sections/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteCIDSection(id: number) {
		return this.request(`/cid/sections/${id}`, { method: 'DELETE' });
	}

	async getCIDLinks(params: Record<string, unknown> = {}) {
		return this.request(`/cid/links${toQuery(params)}`);
	}

	async createCIDLink(body: unknown) {
		return this.request('/cid/links', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateCIDLink(id: number, patch: unknown) {
		return this.request(`/cid/links/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteCIDLink(id: number) {
		return this.request(`/cid/links/${id}`, { method: 'DELETE' });
	}

	async getCIDQuickResources(params: Record<string, unknown> = {}) {
		return this.request(`/cid/quick-resources${toQuery(params)}`);
	}

	async createCIDQuickResource(body: unknown) {
		return this.request('/cid/quick-resources', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateCIDQuickResource(id: number, patch: unknown) {
		return this.request(`/cid/quick-resources/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteCIDQuickResource(id: number) {
		return this.request(`/cid/quick-resources/${id}`, { method: 'DELETE' });
	}

	// Measure (analytics platform) — COS-native staff surface
	// (/api/v1/measure/*). Stage 1: the validation dashboard reads.
	// build-contract-wave2.md Seat B #5 — an optional clientId (the shell
	// picker's legacy id) lets a Communico-staff caller resolve which COS
	// partner the response's `partner_id` names; a library-tier caller's
	// own tenant resolves server-side regardless (Scope wins) and never
	// needs this param.
	async getMeasureContext(filter: { clientId?: number } = {}) {
		return this.request(`/measure/context${toQuery({ client_id: filter.clientId })}`);
	}

	// Read-only branch ids and names for Thrive's pickers; open to library
	// staff without the Library Settings branches right.
	async getMeasureBranches(filter: Record<string, unknown> = {}) {
		return this.request(`/measure/branches${toQuery(filter)}`);
	}

	// The platform-wide read every Google Maps surface makes before
	// mounting a map (docs/geocoding-spec.md section 0a). See
	// utils/googleMaps.ts.
	async getMapsConfig(): Promise<{ enabled: boolean; api_key?: string }> {
		return this.request(`/config/maps`);
	}

	async getMeasureOverview() {
		return this.request(`/measure/overview`);
	}

	async getMeasureCoverage() {
		return this.request(`/measure/coverage`);
	}

	async getMeasureVolume(days = 14) {
		return this.request(`/measure/volume?days=${days}`);
	}

	async getMeasureMetrics() {
		return this.request(`/measure/metrics`);
	}

	// zone: 'library' buckets the days in the library's own time zone (the
	// server resolves it), which is what the period model's windows use.
	async getMeasureMetricSeries(key: string, days = 14, filter: { clientId?: number; zone?: 'library' } = {}) {
		return this.request(`/measure/metrics/${encodeURIComponent(key)}/series${toQuery({ days, client_id: filter.clientId, tz: filter.zone })}`);
	}

	// Manual KPI entry (measure-manual-kpis brief) — the numbers no
	// instrumented pipeline captures. Postgres-only; never reaches the
	// warehouse. partnerId is required for an unrestricted (Communico
	// staff) WRITE; a library-tier caller's own tenant resolves
	// server-side and the param is ignored.
	async getManualMetrics(partnerId?: number, includeArchived = false) {
		return this.request(`/measure/manual-metrics${toQuery({ partner_id: partnerId, include_archived: includeArchived || undefined })}`);
	}

	async createManualMetric(body: {
		partner_id?: number;
		name: string;
		unit?: string;
		description?: string;
		period?: string;
	}) {
		return this.request(`/measure/manual-metrics`, { method: 'POST', body: JSON.stringify(body) });
	}

	async updateManualMetric(id: number, patch: { name?: string; unit?: string; description?: string; archived?: boolean }) {
		return this.request(`/measure/manual-metrics/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async getManualMetricValues(id: number) {
		return this.request(`/measure/manual-metrics/${id}/values`);
	}

	async upsertManualMetricValue(id: number, input: { period_date: string; value: number; note?: string }) {
		return this.request(`/measure/manual-metrics/${id}/values`, { method: 'PUT', body: JSON.stringify(input) });
	}

	// The Today page's one round trip (measure corkscrew contract §pulse).
	// A restricted (library-staff) caller's tenant resolves server-side; an
	// unrestricted (Communico staff) caller must name one — callers pass
	// legacyClientId() as clientId (the shell picker's value; the server
	// crosswalks it to the partner), per this file's own picker precedent.
	//
	// period/compare/from/to are the shared period model
	// (measure/period.ts ↔ backend internal/measureperiod) — omitted
	// entirely when the caller sends no period, preserving the pre-period
	// response shape exactly.
	async getMeasurePulse(
		params: { partnerId?: number; clientId?: number; branch?: string | null; period?: string; compare?: string; from?: string; to?: string } = {},
	) {
		const qs = toQuery({
			partner_id: params.partnerId,
			client_id: params.clientId,
			branch: params.branch || undefined,
			period: params.period,
			compare: params.compare,
			from: params.from,
			to: params.to,
		});
		return this.request(`/measure/pulse${qs}`);
	}

	// Today's checkouts by branch (Measure corkscrew wave 2, seat C) —
	// sourced from the SAME warehouse metric + day bucket as the pulse
	// Checkouts tile above, so the two can never independently disagree.
	// Replaces the old circulate-live read (services/circulateReports.ts),
	// which was a genuinely different system with no branch-name field.
	async getMeasureCheckoutsByBranch(params: { partnerId?: number; clientId?: number } = {}) {
		const qs = toQuery({ partner_id: params.partnerId, client_id: params.clientId });
		return this.request(`/measure/pulse/checkouts-by-branch${qs}`);
	}

	// The range sibling (Measure corkscrew wave 2, seat B) — e.g. "checkouts
	// by branch, last 30 days" for the Circulation drill's L2 Physical view,
	// rather than a single day's total. Same warehouse source, same shape,
	// replacing the L2 by-branch card's own prior read of Circulate's own
	// live reporting API (services/circulateReports.ts).
	//
	// from/to (YYYY-MM-DD, inclusive) name the EXACT window the caller's own
	// KPI card resolved to (comparePeriods() over a day-bucketed series) —
	// the server can't safely re-derive this window on its own (see the
	// backend's LocationBreakdownForPartnerDateRange doc for why a rolling
	// or calendar-aligned-to-now guess can disagree with a data-driven
	// "last N populated days" window by a full day's events). Omitting both
	// falls back to the backend's own plain last-30-days default.
	// zone: 'library' counts the days in the library's own time zone, for a
	// window the server's period model resolved (the Overview and Circulation).
	async getMeasureCheckoutsByBranchRange(params: { partnerId?: number; clientId?: number; from?: string; to?: string; zone?: 'library' } = {}) {
		const qs = toQuery({ partner_id: params.partnerId, client_id: params.clientId, from: params.from, to: params.to, tz: params.zone });
		return this.request(`/measure/pulse/checkouts-by-branch-range${qs}`);
	}

	// The corkscrew (build-contract.md) — Programs (Attend) and Spaces
	// (Reserve) drills. Every level is `RequireLevelScoped(3)`. Library
	// staff resolve server-side from their own scope; Communico staff pass
	// legacyClientId() as clientId, same as the pulse read above.
	async getMeasureProgramsSummary(
		filter: { from?: string; to?: string; branch?: string; clientId?: number; period?: string; compare?: string } = {},
	) {
		const { clientId, ...rest } = filter;
		return this.request(`/measure/programs/summary${toQuery({ ...rest, client_id: clientId })}`);
	}

	async getMeasureProgramsCategory(
		id: number | string,
		filter: { from?: string; to?: string; branch?: string; clientId?: number; period?: string; compare?: string } = {},
	) {
		const { clientId, ...rest } = filter;
		return this.request(`/measure/programs/categories/${encodeURIComponent(String(id))}${toQuery({ ...rest, client_id: clientId })}`);
	}

	async getMeasureProgramsSeries(eventId: number | string, filter: { clientId?: number } = {}) {
		return this.request(`/measure/programs/series/${encodeURIComponent(String(eventId))}${toQuery({ client_id: filter.clientId })}`);
	}

	async getMeasureProgramsOccurrence(timeId: number | string, filter: { clientId?: number } = {}) {
		return this.request(`/measure/programs/occurrences/${encodeURIComponent(String(timeId))}${toQuery({ client_id: filter.clientId })}`);
	}

	async getMeasureSpacesSummary(
		filter: { from?: string; to?: string; branch?: string; clientId?: number; period?: string; compare?: string } = {},
	) {
		const { clientId, ...rest } = filter;
		return this.request(`/measure/spaces/summary${toQuery({ ...rest, client_id: clientId })}`);
	}

	// Ticketed-program revenue (Measure corkscrew wave 2, seat C, Part 2) —
	// gross/outstanding/net, by program and by branch. Same from/to/branch/
	// clientId contract as every other Programs read on this page, so a
	// future period picker driving getMeasureProgramsSummary drives this
	// for free.
	async getMeasureProgramsRevenue(filter: { from?: string; to?: string; branch?: string; clientId?: number; period?: string; compare?: string } = {}) {
		const { clientId, ...rest } = filter;
		return this.request(`/measure/programs/revenue${toQuery({ ...rest, client_id: clientId })}`);
	}

	// Community Reach — Phase 0 (docs/stage-briefs/measure-community-reach-
	// brief.md §5). System-wide library-card penetration from IMLS PLS, no
	// patron data involved. `partnerId` is a COS partners.id (NOT a legacy
	// client_id, unlike every other Measure endpoint above) — an
	// unrestricted (Communico staff) caller with no partnerId gets a 422
	// "choose a library first"; a restricted (library-tier) caller's own
	// tenant resolves regardless.
	async getMeasureCommunityReach(filter: { partnerId?: number } = {}) {
		return this.request(`/measure/community/reach${toQuery({ partner_id: filter.partnerId })}`);
	}

	// Patron-base aggregate — churn-band distribution, lifecycle mix, and
	// how many persons have ever been scored by Nurture's intelligence
	// engine. Aggregate counts only, never a person-level fact.
	// Unrestricted (Communico staff) callers get every tenant combined,
	// unsuppressed; a restricted (library-tier) caller's own tenant is
	// small-cell suppressed.
	// partnerId NARROWS an unrestricted (Communico staff) caller to the
	// library named in the shell picker. Omit it for the deliberate
	// cross-tenant staff view. A restricted (library-tier) caller is forced
	// onto its own tenant server-side and this value is ignored.
	async getMeasureNurtureOverview(filter: { partnerId?: number } = {}) {
		return this.request(`/measure/nurture/overview${toQuery({ partner_id: filter.partnerId })}`);
	}

	// Survey results rollup — response rate + rating averages, per
	// dispatched survey. Never opens/clicks (deliberately unsurfaced).
	// partnerId narrows a staff caller to the named library — same contract
	// as getMeasureNurtureOverview above.
	async getMeasureSurveysOverview(filter: { partnerId?: number } = {}) {
		return this.request(`/measure/surveys/overview${toQuery({ partner_id: filter.partnerId })}`);
	}

	// ---------------------------------------------------------------
	// Thrive collection intelligence (fleet/thrive-w2-ui) — the
	// collectionHQ/LibraryIQ read model (thrive_item_flags, thrive_balance).
	// This wave codes against the shared wave-2 contract (three fleet seats
	// building in parallel), not an observed response.
	// ---------------------------------------------------------------

	// Thrive collections and plans are partner scoped. A restricted caller
	// is pinned to their own partner by the backend; a Communico-staff
	// caller must name the partner explicitly (the rule every other partner
	// read follows), so it is resolved from /measure/context for the client
	// currently picked in the shell. Resolved per request, never cached,
	// because the client picker can change between calls.
	// Public: Thrive screens that talk to a partner-scoped endpoint outside
	// this file (dashboard share, Metrics you enter) use it too.
	async thrivePartnerId(): Promise<number | undefined> {
		try {
			const clientId = legacyClientId() || undefined;
			const ctx = (await this.getMeasureContext({ clientId })) as { partner_id?: number };
			return ctx.partner_id || undefined;
		} catch {
			return undefined;
		}
	}

	async getThriveCollectionsSummary(filter: { branch?: string } = {}): Promise<ThriveCollectionsSummary> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/summary${toQuery({ branch: filter.branch, partner_id })}`);
	}

	/** The material types and collections this library actually holds, with counts. */
	async getThriveCollectionsFilters(): Promise<ThriveFilterOptions> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/filters${toQuery({ partner_id })}`);
	}

	async listThriveCollectionsItems(filter: ThriveItemsFilter = {}): Promise<ThriveItemsPage> {
		const partner_id = await this.thrivePartnerId();
		return this.request(
			`/thrive/collections/items${toQuery({
				partner_id,
				flag: filter.flag,
				branch: filter.branch,
				collection: filter.collection,
				material_type: filter.material_type,
				exclude_core: filter.exclude_core || undefined,
				exclude_last_copy: filter.exclude_last_copy || undefined,
				cursor: filter.cursor,
				limit: filter.limit,
			})}`,
		);
	}

	// source disambiguates item_key when it matches more than one row (a
	// catalogue item and a Library of Things item sharing a barcode,
	// migration 000495/000496): pass the row's own ThriveCollectionItem.
	// source back once a list read has reported one. Omitting it behaves
	// as before when item_key matches exactly one row, and 409s (naming
	// every matching source) when it matches more than one.
	async getThriveCollectionsItem(itemKey: string, source?: string): Promise<ThriveItemDetail> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/items/${encodeURIComponent(itemKey)}${toQuery({ partner_id, source })}`);
	}

	// Maintain/Curate Snooze (docs/modules/thrive.md "Snooze"): hide one item
	// from its library's flag lists until a date. Archive items use source
	// 'archive' and their id as the key.
	async listThriveSnoozes(kind: 'collection' | 'archive' = 'collection'): Promise<{ items: ThriveSnoozedItem[] }> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/snoozes${toQuery({ kind, partner_id })}`);
	}

	async getThriveSnooze(itemKey: string, source?: string): Promise<{ snooze: ThriveSnooze | null; today?: string }> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/snoozes/item${toQuery({ item_key: itemKey, source, partner_id })}`);
	}

	async putThriveSnooze(body: ThriveSnoozeRequest): Promise<{ snooze: ThriveSnooze }> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/snoozes${toQuery({ partner_id })}`, { method: 'PUT', body: JSON.stringify(body) });
	}

	async deleteThriveSnooze(itemKey: string, source?: string): Promise<void> {
		const partner_id = await this.thrivePartnerId();
		await this.request(`/thrive/collections/snoozes${toQuery({ item_key: itemKey, source, partner_id })}`, { method: 'DELETE' });
	}

	async getThriveCollectionsBalance(by: 'collection' = 'collection'): Promise<ThriveBalanceResponse> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/balance${toQuery({ by, partner_id })}`);
	}

	// Wave 4, the same matrix, keyed by subject bucket (Dewey hundred /
	// material type + audience) instead of collection code. A separate method
	// rather than a `by` overload on getThriveCollectionsBalance above: the two
	// responses carry different row shapes (subject_bucket vs collection_code),
	// see ThriveBalanceBySubjectResponse's own doc comment.
	async getThriveCollectionsBalanceBySubject(): Promise<ThriveBalanceBySubjectResponse> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/balance${toQuery({ by: 'subject', partner_id })}`);
	}

	/** The balance report: how many copies to move from which branch to which,
	 *  for one collection (or subject), or all of them when `group` is empty. */
	async getThriveBalanceReport(by: 'collection' | 'subject', group = ''): Promise<ThriveTransferReport> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/balance/report${toQuery({ by, group: group || undefined, partner_id })}`);
	}

	/** Makes ONE transfer plan from the same report. The server recomputes the
	 *  report, so nothing but the choice of report is sent. A second identical
	 *  request within a minute is a 409 whose body carries `plan_id`. */
	async createThriveBalanceTransfersPlan(input: { by: 'collection' | 'subject'; group: string; title: string; snapshot_date?: string }): Promise<ThrivePlan> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/plans/balance-transfers`, { method: 'POST', body: JSON.stringify({ ...input, partner_id }) });
	}

	async getThriveCollectionsSettings(): Promise<ThriveThresholdsView> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/settings${toQuery({ partner_id })}`);
	}

	/** Sends only the numbers that changed (a patch, never a full replace). */
	async putThriveCollectionsSettings(thresholds: ThriveFlagThresholdsPatch): Promise<ThriveThresholdsView> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/settings${toQuery({ partner_id })}`, { method: 'PUT', body: JSON.stringify({ thresholds }) });
	}

	/** Puts every number back to the shipped default. */
	async resetThriveCollectionsSettings(): Promise<ThriveThresholdsView> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/settings/reset${toQuery({ partner_id })}`, { method: 'POST' });
	}

	// ---------------------------------------------------------------
	// Collections > Topics (docs/modules/thrive.md "Topics"): the
	// collection by subject bucket, plus library-defined named topic sets
	// tracked over time.
	// ---------------------------------------------------------------

	async getThriveTopics(): Promise<ThriveTopicsSummary> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/topics${toQuery({ partner_id })}`);
	}

	async getThriveTopicSets(): Promise<{ sets: TopicSetSummary[] }> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/topics/sets${toQuery({ partner_id })}`);
	}

	async putThriveTopicSet(key: string, patch: TopicSetPatch): Promise<TopicSetSummary> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/topics/sets/${encodeURIComponent(key)}${toQuery({ partner_id })}`, {
			method: 'PUT',
			body: JSON.stringify(patch),
		});
	}

	async deleteThriveTopicSet(key: string): Promise<void> {
		const partner_id = await this.thrivePartnerId();
		await this.request(`/thrive/collections/topics/sets/${encodeURIComponent(key)}${toQuery({ partner_id })}`, { method: 'DELETE' });
	}

	async getThriveTopicSetTrend(key: string, weeks = 12): Promise<{ trend: TopicSetTrendPoint[] }> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/collections/topics/sets/${encodeURIComponent(key)}/trend${toQuery({ partner_id, weeks })}`);
	}

	// ---------------------------------------------------------------
	// Thrive Wave 5 - digital archives (docs/modules/thrive.md "Digital
	// archives"): the same read-model shape as the physical collection
	// engine above, served from /thrive/archive instead of
	// /thrive/collections.
	// ---------------------------------------------------------------

	async getThriveArchiveSummary(): Promise<ThriveArchiveSummary> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/archive/summary${toQuery({ partner_id })}`);
	}

	async listThriveArchiveItems(filter: ThriveArchiveItemsFilter = {}): Promise<ThriveArchiveItemsPage> {
		const partner_id = await this.thrivePartnerId();
		return this.request(
			`/thrive/archive/items${toQuery({
				partner_id,
				flag: filter.flag,
				branch: filter.branch,
				collection: filter.collection,
				material_type: filter.material_type,
				cursor: filter.cursor,
				limit: filter.limit,
			})}`,
		);
	}

	async getThriveArchiveItem(itemId: number | string): Promise<ThriveArchiveItemDetail> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/archive/items/${encodeURIComponent(String(itemId))}${toQuery({ partner_id })}`);
	}

	async getThriveArchiveSettings(): Promise<{ thresholds: ThriveArchiveThresholds }> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/archive/settings${toQuery({ partner_id })}`);
	}

	async putThriveArchiveSettings(thresholds: ThriveArchiveThresholds): Promise<{ thresholds: ThriveArchiveThresholds }> {
		return this.request(`/thrive/archive/settings`, { method: 'PUT', body: JSON.stringify({ thresholds }) });
	}

	// ---------------------------------------------------------------
	// Collections > Discover (docs/modules/thrive.md "Discover"): the
	// buy-side read model over the same intel snapshot extract. Settings
	// are the EXISTING /thrive/collections/settings endpoints above
	// (holds_per_copy_target, acceptable_wait_days) -- Discover has none
	// of its own.
	// ---------------------------------------------------------------

	async getThriveDiscoverSummary(): Promise<ThriveDiscoverSummary> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/discover/summary${toQuery({ partner_id })}`);
	}

	async listThriveDiscoverItems(filter: ThriveDiscoverItemsFilter = {}): Promise<ThriveDiscoverItemsPage> {
		const partner_id = await this.thrivePartnerId();
		return this.request(
			`/thrive/discover/items${toQuery({
				partner_id,
				branch: filter.branch,
				holds_on_nothing: filter.holds_on_nothing || undefined,
			})}`,
		);
	}

	async matchThriveDiscoverList(rows: ThriveDiscoverMatchRow[]): Promise<{ results: ThriveDiscoverMatchResult[] }> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/discover/match${toQuery({ partner_id })}`, { method: 'POST', body: JSON.stringify({ rows }) });
	}

	// ---------------------------------------------------------------
	// Thrive action plans (fleet/thrive-w2-ui contract; seat C owns the
	// backend). POST materialises items from the flag read model above
	// through an ItemFlagSource interface — coded against the contract.
	// ---------------------------------------------------------------

	async createThrivePlan(input: CreateThrivePlanInput): Promise<ThrivePlan> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/plans`, { method: 'POST', body: JSON.stringify({ ...input, partner_id }) });
	}

	async listThrivePlans(): Promise<ThrivePlan[]> {
		const partner_id = await this.thrivePartnerId();
		const res = await this.request<{ data: ThrivePlan[]; total: number }>(`/thrive/plans${toQuery({ partner_id })}`);
		return res.data ?? [];
	}

	/** The library's own active logins a plan can be assigned to (never Communico staff). */
	async listThrivePlanAssignees(): Promise<ThrivePlanAssignee[]> {
		const partner_id = await this.thrivePartnerId();
		const res = await this.request<{ data: ThrivePlanAssignee[] }>(`/thrive/plans/assignees${toQuery({ partner_id })}`);
		return res.data ?? [];
	}

	async renameThrivePlan(id: number | string, title: string): Promise<ThrivePlan> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/plans/${encodeURIComponent(String(id))}`, { method: 'PATCH', body: JSON.stringify({ title, partner_id }) });
	}

	/** Stops a plan: it stays on the list, greyed, with its items and outcomes. */
	async cancelThrivePlan(id: number | string, reason?: string): Promise<ThrivePlan> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/plans/${encodeURIComponent(String(id))}/cancel`, { method: 'POST', body: JSON.stringify({ reason: reason ?? '', partner_id }) });
	}

	/** Removes a plan nobody has acted on; the server answers 422 for any other. */
	async deleteThrivePlan(id: number | string): Promise<void> {
		const partner_id = await this.thrivePartnerId();
		await this.request(`/thrive/plans/${encodeURIComponent(String(id))}${toQuery({ partner_id })}`, { method: 'DELETE' });
	}

	async getThrivePlan(id: number | string): Promise<ThrivePlan> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/plans/${encodeURIComponent(String(id))}${toQuery({ partner_id })}`);
	}

	// source disambiguates item_key when it matches more than one row in
	// this plan (a catalogue item and a Library of Things item sharing a
	// barcode, migration 000495/000496): pass the row's own
	// ThrivePlanItem.source back once it has reported one. Omitting it
	// behaves as before when item_key matches exactly one row, and 409s
	// (naming every matching source) when it matches more than one.
	async patchThrivePlanItem(planId: number | string, itemKey: string, patch: { outcome: string; to_branch?: string; source?: string }): Promise<{ item: ThrivePlanItem; note?: string; write_back_error?: string }> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/plans/${encodeURIComponent(String(planId))}/items/${encodeURIComponent(itemKey)}`, {
			method: 'PATCH',
			body: JSON.stringify({ ...patch, partner_id }),
		});
	}

	async assignThrivePlan(planId: number | string, branchUserIds: number[]): Promise<ThrivePlan> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/plans/${encodeURIComponent(String(planId))}/assign`, {
			method: 'POST',
			body: JSON.stringify({ branch_user_ids: branchUserIds, partner_id }),
		});
	}

	async scheduleThrivePlan(planId: number | string, schedule: { every: string; occurrences?: number; start?: string }): Promise<ThrivePlan> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/plans/${encodeURIComponent(String(planId))}/schedule`, {
			method: 'POST',
			body: JSON.stringify({ ...schedule, partner_id }),
		});
	}

	// ---------------------------------------------------------------
	// Thrive patrons and community (fleet/thrive-w3-ui) — the Savannah-
	// clusters read model (thrive_patron_groups/_labels/_daily). Coded
	// against the shared wave-3 contract; the backend seat builds these
	// endpoints in parallel, the same "contract not observed response"
	// discipline as the wave-2 collection intelligence methods above.
	// ---------------------------------------------------------------

	async getThrivePatronsSummary(): Promise<ThrivePatronSummary> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/patrons/summary${toQuery({ partner_id })}`);
	}

	async getThrivePatronGroups(): Promise<ThrivePatronGroupsResponse> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/patrons/groups${toQuery({ partner_id })}`);
	}

	async putThrivePatronGroup(key: string, patch: ThrivePatronGroupPatch): Promise<ThrivePatronGroup> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/patrons/groups/${encodeURIComponent(key)}${toQuery({ partner_id })}`, {
			method: 'PUT',
			body: JSON.stringify(patch),
		});
	}

	async getThrivePatronGroupVocabulary(): Promise<ThriveGroupVocabulary> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/patrons/groups/vocabulary${toQuery({ partner_id })}`);
	}

	async createThrivePatronGroup(body: ThrivePatronGroupCreate): Promise<ThrivePatronGroup> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/patrons/groups${toQuery({ partner_id })}`, { method: 'POST', body: JSON.stringify(body) });
	}

	/** A full-library count; `signal` lets the caller cancel one the next edit has superseded. */
	async previewThrivePatronGroup(rule: Record<string, unknown>, signal?: AbortSignal): Promise<ThrivePatronGroupPreview> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/patrons/groups/preview${toQuery({ partner_id })}`, {
			method: 'POST',
			body: JSON.stringify({ rule, enabled: true }),
			signal,
		});
	}

	/** Put the library's own groups in this order, all in one write. */
	async reorderThrivePatronGroups(keys: string[]): Promise<void> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/patrons/groups/order${toQuery({ partner_id })}`, { method: 'PUT', body: JSON.stringify({ keys }) });
	}

	async deleteThrivePatronGroup(key: string): Promise<void> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/patrons/groups/${encodeURIComponent(key)}${toQuery({ partner_id })}`, { method: 'DELETE' });
	}

	async getThrivePatronsMovement(weeks = 12): Promise<ThrivePatronMovementResponse> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/patrons/movement${toQuery({ weeks, partner_id })}`);
	}

	async getThrivePatronsList(filter: ThrivePatronListFilter = {}): Promise<ThrivePatronListResponse> {
		const partner_id = await this.thrivePartnerId();
		return this.request(
			`/thrive/patrons/list${toQuery({
				partner_id,
				lifecycle: filter.lifecycle,
				label: filter.label,
				age_band: filter.age_band,
				branch: filter.branch,
				registered_within_days: filter.registered_within_days,
				include_no_card: filter.include_no_card,
				cursor: filter.cursor,
				limit: filter.limit,
			})}`,
		);
	}

	async getThrivePatronGroupsFor(personId: number | string, knownPartnerId = 0): Promise<ThrivePatronPersonGroups> {
		// The record already knows its library; asking the Measure context for
		// it again fails (400) when no library is picked. With no library at
		// all there is nothing to ask: say "not worked out yet" without a request.
		const partner_id = knownPartnerId > 0 ? knownPartnerId : await this.thrivePartnerId();
		if (!partner_id) return { computed: false, lifecycle: "", labels: [], age_band: "", household: null };
		return this.request(`/thrive/patrons/${encodeURIComponent(String(personId))}/groups${toQuery({ partner_id })}`);
	}

	async getThrivePatronHouseholds(opts: { cursor?: string; limit?: number } = {}): Promise<ThrivePatronHouseholdsPage> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/patrons/households${toQuery({ partner_id, cursor: opts.cursor, limit: opts.limit })}`);
	}

	async getThrivePatronHouseholdsSummary(): Promise<ThrivePatronHouseholdsSummary> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/patrons/households/summary${toQuery({ partner_id })}`);
	}

	// Demographics (wave 6, fleet/thrive-demographics): borrowers vs the
	// community, banded the same way, at the geography Community Reach's
	// opt-in resolves. See thrive/demographics.ts for the full contract note.
	async getThriveDemographics(): Promise<ThriveDemographicsResponse> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/patrons/demographics${toQuery({ partner_id })}`);
	}

	// Community map (fleet/thrive-community-map). Coded against
	// thrive/community-map.ts's documented contract; the
	// fleet/thrive-community-data seat builds the real endpoint in
	// parallel, same discipline as the wave-2/3 methods above.
	async getThriveCommunityMap(): Promise<ThriveCommunityMapResponse> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/patrons/community-map${toQuery({ partner_id })}`);
	}

	// The REAL existing endpoint (measure-geo-consent brief, live today) -
	// not a contract guess. Staff-only (RequireAdminRole) at the router;
	// a partner-tier caller gets a real 403 the component must expect.
	async setCommunityReachOptIn(optedIn: boolean): Promise<{ partner_id: number; opted_in: boolean }> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/measure/admin/community-reach/opt-in`, {
			method: 'PUT',
			body: JSON.stringify({ partner_id, opted_in: optedIn }),
		});
	}

	// ---------------------------------------------------------------
	// Thrive Operations, Desk and Outreach (Wave 4, seat A). This
	// seat's own backend, see docs/modules/thrive.md.
	// ---------------------------------------------------------------

	async getThriveOperationsDeskActivity(weeks = 12): Promise<ThriveDeskActivity> {
		const partner_id = await this.thrivePartnerId();
		return this.request(`/thrive/operations/desk/summary${toQuery({ weeks, partner_id })}`);
	}

	/** With a period (last-30d, this-quarter, last-12-months or custom with from and to) the server answers for that window; without one, for the trailing months. */
	async getThriveOperationsOutreachActivity(months = 12, period?: { period: string; from?: string; to?: string }): Promise<ThriveOutreachActivity> {
		const partner_id = await this.thrivePartnerId();
		const window = period ? { period: period.period, from: period.from, to: period.to } : { months };
		return this.request(`/thrive/operations/outreach/summary${toQuery({ ...window, partner_id })}`);
	}

	// Lend point-in-time snapshot — a SNAPSHOT, never a trend. An
	// unrestricted (Communico staff) caller with no partnerId gets a
	// 422 "choose a library first"; a restricted (library-tier)
	// caller's own tenant resolves regardless (the Community Reach
	// contract).
	async getMeasureLendOverview(filter: { partnerId?: number } = {}) {
		return this.request(`/measure/lend/overview${toQuery({ partner_id: filter.partnerId })}`);
	}

	// Community staff-network tile — Community's own analytics API,
	// surfaced read-only. Staff audience across libraries, not patrons.
	async getMeasureCommunityForum() {
		return this.request(`/measure/community-forum`);
	}

	// Community Reach Phase 1, Zone 1 (measure-geo-consent brief) — the
	// per-library geo-consent opt-in (admin-only). Off by default; a
	// partner with no row reads opted_in: false.
	async getMeasureCommunityGeoOptIn(partnerId: number) {
		return this.request(`/measure/admin/community-reach/opt-in?partner_id=${partnerId}`);
	}

	async setMeasureCommunityGeoOptIn(partnerId: number, optedIn: boolean) {
		return this.request(`/measure/admin/community-reach/opt-in`, {
			method: "PUT",
			body: JSON.stringify({ partner_id: partnerId, opted_in: optedIn }),
		});
	}

	// Nurture (CRM) — COS-native staff surface (/api/v1/nurture/*).
	// Stage 1: the record spine (persons + identities + activity ledger).
	// ── Communications Hub (the staff console) ──────────────────

	// The desk lookup: whatever the person at the counter can give you.
	// partnerId is required for Communico staff and ignored for library
	// staff (their own partner always wins, server-side).
	async getCommsRecipient(identifier: string, partnerId = 0, start = 0, limit = 50, personId = 0) {
		const params = new URLSearchParams({ identifier, start: String(start), limit: String(limit) });
		if (personId > 0) params.set("person_id", String(personId));
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		return this.request(`/comms-hub/recipient?${params.toString()}`);
	}

	async getCommsMessages(partnerId = 0, start = 0, limit = 50) {
		const params = new URLSearchParams({ start: String(start), limit: String(limit) });
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		return this.request(`/comms-hub/messages?${params.toString()}`);
	}

	async getCommsCoverage(partnerId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		const qs = params.toString();
		return this.request(`/comms-hub/coverage${qs ? `?${qs}` : ""}`);
	}

	// Sending hours: library staff are held to their own library server-side;
	// Communico staff name one by the picker's client id.
	async getCommsSendingHours(clientId?: number, partnerId?: number): Promise<CommsSendingHours> {
		return this.request<CommsSendingHours>(`/comms-hub/sending-hours${toQuery({ client_id: clientId, partner_id: partnerId })}`);
	}

	async saveCommsSendingHours(types: CommsSendingHoursChange[], clientId?: number, partnerId?: number): Promise<CommsSendingHours> {
		return this.request<CommsSendingHours>(`/comms-hub/sending-hours${toQuery({ client_id: clientId, partner_id: partnerId })}`, {
			method: 'PUT',
			body: JSON.stringify({ types }),
		});
	}

	// Offline Desk (desk-offline-contract.md). FROZEN for the parallel seats:
	// the engine and the Desk screens both call these, and the backend
	// matches them. clientId is the legacy clientsV2 id, branchId the
	// anywhereme.locations id, as for the rest of Desk.
	async getDeskOfflineEligibility(clientId: number): Promise<DeskOfflineEligibility> {
		return this.request(`/desk/offline/eligibility?${new URLSearchParams({ client_id: String(clientId) })}`);
	}

	async registerDeskOfflineDevice(clientId: number, branchId: number, label: string): Promise<DeskOfflineDevice> {
		return this.request(`/desk/offline/devices`, {
			method: "POST",
			body: JSON.stringify({ client_id: clientId, branch_id: branchId, label }),
		});
	}

	async getDeskOfflineDevices(clientId: number): Promise<{ devices: DeskOfflineDevice[] }> {
		return this.request(`/desk/offline/devices?${new URLSearchParams({ client_id: String(clientId) })}`);
	}

	async revokeDeskOfflineDevice(clientId: number, deviceId: string): Promise<DeskOfflineDevice> {
		return this.request(`/desk/offline/devices/${encodeURIComponent(deviceId)}/revoke`, {
			method: "POST",
			body: JSON.stringify({ client_id: clientId }),
		});
	}

	async checkDeskOfflineDevice(clientId: number, deviceId: string): Promise<{ status: "active" | "revoked" }> {
		return this.request(
			`/desk/offline/devices/${encodeURIComponent(deviceId)}/check?${new URLSearchParams({ client_id: String(clientId) })}`,
		);
	}

	async pingDeskOffline(clientId: number, signal?: AbortSignal): Promise<DeskOfflinePing> {
		return this.request(`/desk/offline/ping?${new URLSearchParams({ client_id: String(clientId) })}`, { signal });
	}

	async getDeskOfflineSnapshot<T = unknown>(
		clientId: number,
		deviceId: string,
		section: "rules" | "cards" | "items",
		cursor: string | null = null,
		limit = 2000,
		signal?: AbortSignal,
	): Promise<DeskOfflineSnapshotPage<T>> {
		const params = new URLSearchParams({ client_id: String(clientId), device_id: deviceId, section, limit: String(limit) });
		if (cursor) params.set("cursor", cursor);
		return this.request(`/desk/offline/snapshot?${params}`, { signal });
	}

	async postDeskOfflineBatch(
		clientId: number,
		deviceId: string,
		entries: DeskOfflineWireEntry[],
	): Promise<{ results: DeskOfflineResult[] }> {
		return this.request(`/desk/offline/batches`, {
			method: "POST",
			body: JSON.stringify({ client_id: clientId, device_id: deviceId, entries }),
		});
	}

	async getDeskOfflineFollowups(
		clientId: number,
		branchId: number,
		list: "exceptions" | "pulls",
		state: "open" | "done" = "open",
		cursor: string | null = null,
		limit = 50,
	): Promise<{ items: DeskOfflineFollowup[]; next_cursor: string | null }> {
		const params = new URLSearchParams({
			client_id: String(clientId),
			branch_id: String(branchId),
			list,
			state,
			limit: String(limit),
		});
		if (cursor) params.set("cursor", cursor);
		return this.request(`/desk/offline/followups?${params}`);
	}

	async getDeskOfflineFollowupsSummary(clientId: number, branchId: number): Promise<DeskOfflineFollowupSummary> {
		return this.request(
			`/desk/offline/followups/summary?${new URLSearchParams({ client_id: String(clientId), branch_id: String(branchId) })}`,
		);
	}

	async markDeskOfflineFollowupDone(clientId: number, followupId: number, note = ""): Promise<DeskOfflineFollowup> {
		return this.request(`/desk/offline/followups/${followupId}/done`, {
			method: "POST",
			body: JSON.stringify({ client_id: clientId, note }),
		});
	}
	// End of Offline Desk block.

	// Offline Desk phase 3 (docs/stage-briefs/desk-offline-phase3-contract.md). FROZEN for
	// the parallel seats: A owns pending, exports and deleted registrations; B owns unlock holders.
	async reportDeskOfflinePending(clientId: number, deviceId: string, counts: Record<string, number>): Promise<{ status: "active" | "revoked" }> {
		return this.request(`/desk/offline/devices/${encodeURIComponent(deviceId)}/pending`, {
			method: "POST",
			body: JSON.stringify({ client_id: clientId, counts }),
		});
	}

	async recordDeskOfflineExport(clientId: number, deviceId: string, record: DeskOfflineExportRecord): Promise<void> {
		await this.request(`/desk/offline/devices/${encodeURIComponent(deviceId)}/exports`, {
			method: "POST",
			body: JSON.stringify({ client_id: clientId, ...record }),
		});
	}

	async reportDeskOfflineDeletedRegistrations(clientId: number, deviceId: string, body: DeskOfflineDeletedRegistrations): Promise<void> {
		await this.request(`/desk/offline/devices/${encodeURIComponent(deviceId)}/deleted-registrations`, {
			method: "POST",
			body: JSON.stringify({ client_id: clientId, ...body }),
		});
	}

	async getDeskOfflineUnlockHolders(clientId: number, deviceId: string): Promise<{ holders: DeskOfflineUnlockHolder[] }> {
		return this.request(
			`/desk/offline/devices/${encodeURIComponent(deviceId)}/unlock-holders?${new URLSearchParams({ client_id: String(clientId) })}`,
		);
	}

	async addDeskOfflineUnlockHolder(clientId: number, deviceId: string, expiresAt: string): Promise<DeskOfflineUnlockHolder> {
		return this.request(`/desk/offline/devices/${encodeURIComponent(deviceId)}/unlock-holders`, {
			method: "POST",
			body: JSON.stringify({ client_id: clientId, expires_at: expiresAt }),
		});
	}

	async clearDeskOfflineUnlockHolder(clientId: number, deviceId: string, staffUserId: number): Promise<void> {
		await this.request(
			`/desk/offline/devices/${encodeURIComponent(deviceId)}/unlock-holders/${staffUserId}?${new URLSearchParams({ client_id: String(clientId) })}`,
			{ method: "DELETE" },
		);
	}
	// End of Offline Desk phase 3 block.

	// Desk payments (phase 2 contract, docs/stage-briefs/desk-offline-phase2-contract.md
	// section 6). FROZEN for the parallel seats. Amounts are decimal strings with two places.
	async getDeskPatronAccount(clientId: number, patronId: string): Promise<DeskPatronAccount> {
		return this.request(
			`/desk/patron/${encodeURIComponent(patronId)}/account?${new URLSearchParams({ client_id: String(clientId) })}`,
		);
	}

	/** What the signed-in library's ILS can do through Desk (GET /desk/capabilities). A failed read means
	 *  "unknown", and the screens then hide nothing. */
	async getDeskCapabilities(clientId = 0): Promise<DeskCapabilities> {
		const params = new URLSearchParams();
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/desk/capabilities${qs ? `?${qs}` : ""}`);
	}

	async getDeskPaymentTypes(clientId: number): Promise<{ types: DeskPaymentType[] }> {
		return this.request(`/desk/payment-types?${new URLSearchParams({ client_id: String(clientId) })}`);
	}

	// branchId is the desk's own branch, for an ILS that records where a payment was taken; 0 leaves it out.
	async postDeskPayment(clientId: number, patronId: string, amount: string, paymentMethod: string, branchId = 0): Promise<DeskPaymentResult> {
		const params = new URLSearchParams({ client_id: String(clientId) });
		if (branchId > 0) params.set("branch_id", String(branchId));
		return this.request(
			`/desk/patron/${encodeURIComponent(patronId)}/payment?${params}`,
			{ method: "POST", body: JSON.stringify({ amount, payment_method: paymentMethod }) },
		);
	}
	// End of Desk payments block.

	// Added after the contract froze (24 October 2026): finds the patron an offline follow-up is
	// about, live from Circulate, for Desk's Patron look up. The follow-up stores no card number.
	async getDeskOfflineFollowupPatron(clientId: number, followupId: number): Promise<{ barcode: string; display_name: string }> {
		return this.request(
			`/desk/offline/followups/${followupId}/patron?${new URLSearchParams({ client_id: String(clientId) })}`,
		);
	}

	// Desk (docs/desk-plan.md §5) — the front-of-house identity spine.
	// One call, several sources, staff confirm everything; `patron_database:
	// false` is a first-class answer, not an empty result. partnerId is
	// required for Communico staff and ignored for library staff (their
	// own partner always wins, server-side) — same contract as the Comms
	// Hub lookup above.
	// `clientId` is the LEGACY clientsV2 id the left-nav library picker
	// supplies — the backend resolves it to a partner when no partner_id
	// is given. Desk's branch/hours/rooms half is keyed by that legacy id
	// while its identity half is keyed by the COS partner id, so a page
	// that can name only one of the two must still be able to look a
	// patron up (feedback: "asking to choose a library when I am already
	// on Seasons and the Falltown branch").
	async getDeskLookup(q: string, partnerId = 0, clientId = 0) {
		const params = new URLSearchParams({ q });
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/desk/lookup?${params.toString()}`);
	}

	// Zone 1's "Right now at <branch>" aggregate. `branch_id` is required
	// by the backend (handler.DeskHandler.Now) — this endpoint is scoped
	// by anywhereme.locations.id, not by the COS partner_id Desk's own
	// lookup picker resolves, so a bare partnerId (what this call used to
	// send) is silently ignored server-side and produces exactly the
	// "branch_id is required" 422 docs/desk-plan.md's branch-context wave
	// closes. `clientId` mirrors `library/context.ts`'s `libraryClientId()`
	// / Attend's `attendClientID` convention: >0 for a Communico staffer
	// (the shared nav client picker's pick), 0/omitted for library staff
	// (their own tenant is inferred server-side and the param is ignored).
	async getDeskNow(branchId = 0, clientId = 0) {
		const params = new URLSearchParams();
		if (branchId > 0) params.set("branch_id", String(branchId));
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/desk/now${qs ? `?${qs}` : ""}`);
	}

	// Desk board (shared board-redesign contract), for the "Today at this
	// branch" stat card and the What's-on tab's per-tile counts. A missing
	// counter arrives as `null`, never a placeholder zero; the caller
	// omits that cell rather than showing it. A 404 or other error here
	// hides the whole card, never a red banner: this is a nice-to-have
	// summary, not the day's own list.
	async getDeskBoardCounts(branchId: number, clientId = 0) {
		return this.request(`/desk/board/counts${toQuery({ branch_id: branchId, client_id: clientId })}`);
	}

	// The What's-on "Needs something" card: not-arrived room bookings past
	// their hold time, events whose check-in has opened, and full events
	// carrying a waiting list. Always asked for TODAY's branch-local date
	// (this is a right-now fact, not a fortnight one), same tenant gate as
	// every other Desk board read.
	async getDeskWhatsOnAttention(branchId: number, date: string, clientId = 0) {
		return this.request(`/desk/whats-on/attention${toQuery({ branch_id: branchId, client_id: clientId, date })}`);
	}

	// Wave 2's person view, Event registrations tab: a resolved person's
	// own Attend registrations, across every event — matched by whichever
	// of their own identifiers (email, phone, library card) the caller
	// already knows, never a name. `identifiers` rides as repeated
	// `identifier=` params (docs/desk-plan.md §1's genuinely-new backend —
	// Attend never had a person-first, cross-event read before this).
	// partnerId + clientId mirror getDeskLookup exactly (same tenant gate,
	// same legacy-client fallback).
	async getDeskPersonRegistrations(identifiers: string[], partnerId = 0, clientId = 0) {
		const params = new URLSearchParams();
		for (const id of identifiers) params.append("identifier", id);
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/desk/registrations?${params.toString()}`);
	}

	// The Desk Customer details tab's full-record read/write pair —
	// straight through Circulate. clientId mirrors every other Desk call:
	// the shared left-nav picker's legacy client id, required for
	// Communico staff, ignored (locked server-side) for library staff.
	/** Turns a Communico One person id back into the candidate Look up would have shown (a logged
	 *  question's person link). 404 when the person is gone or belongs to another library. */
	async getDeskPersonCandidate(personId: number, partnerId = 0, clientId = 0): Promise<DeskCandidate> {
		const params = new URLSearchParams({ person_id: String(personId) });
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/desk/person-candidate?${params.toString()}`);
	}

	async getDeskPatron(patronId: string, clientId = 0) {
		const params = new URLSearchParams({ patron_id: patronId });
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/desk/patron?${params.toString()}`);
	}

	async updateDeskPatron(patronId: string, body: Record<string, unknown>, clientId = 0, branchId = 0) {
		const params = new URLSearchParams();
		if (clientId > 0) params.set("client_id", String(clientId));
		if (branchId > 0) params.set("branch_id", String(branchId));
		const qs = params.toString();
		return this.request(`/desk/patron/${encodeURIComponent(patronId)}${qs ? `?${qs}` : ""}`, {
			method: "PUT",
			body: JSON.stringify(body),
		});
	}

	// Patron PIN status/set/clear — the account-link credential. There is
	// no read-back: only a status flag, a replace, and a clear. Never log
	// the pin value passed to setDeskPatronPIN.
	// A cardholder's current loans and holds by Circulate cardholder id, the
	// person view's Checked out and Holds reads for a login that cannot read
	// the Nurture contact record.
	async getDeskPatronLoans(patronId: string, clientId = 0) {
		const params = new URLSearchParams();
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/desk/patron/${encodeURIComponent(patronId)}/loans${qs ? `?${qs}` : ""}`);
	}

	async getDeskPatronPINStatus(patronId: string, clientId = 0) {
		const params = new URLSearchParams();
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/desk/patron/${encodeURIComponent(patronId)}/pin${qs ? `?${qs}` : ""}`);
	}

	/** Accounts linked to a patron (family, school, organization), both
	 *  directions. Read only; linking stays on the Circulate staff screen. */
	async getDeskLinkedAccounts(patronId: string, clientId = 0) {
		const params = new URLSearchParams();
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/desk/patron/${encodeURIComponent(patronId)}/linked-accounts${qs ? `?${qs}` : ""}`);
	}

	/** Active blocks on a cardholder: reason, what each stops, expiry,
	 *  description and any staff note. */
	async getDeskPatronBlocks(patronId: string, clientId = 0) {
		const params = new URLSearchParams();
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/desk/patron/${encodeURIComponent(patronId)}/blocks${qs ? `?${qs}` : ""}`);
	}

	/** The library's reasons for overriding a block at checkout. */
	async getDeskOverrideReasons(clientId = 0) {
		const params = new URLSearchParams();
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/desk/override-reasons${qs ? `?${qs}` : ""}`);
	}

	async setDeskPatronPIN(patronId: string, pin: string, clientId = 0) {
		const params = new URLSearchParams();
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/desk/patron/${encodeURIComponent(patronId)}/pin${qs ? `?${qs}` : ""}`, {
			method: "PUT",
			body: JSON.stringify({ pin }),
		});
	}

	async clearDeskPatronPIN(patronId: string, clientId = 0) {
		const params = new URLSearchParams();
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/desk/patron/${encodeURIComponent(patronId)}/pin${qs ? `?${qs}` : ""}`, {
			method: "DELETE",
		});
	}

	// The checked-out table's Renew action. A block comes back 200 with
	// renewed:false — never an error — so callers read the body rather
	// than catching for the refusal case.
	async renewDeskLoan(patronId: string, loanId: string, clientId = 0, branchId = 0) {
		const params = new URLSearchParams();
		if (clientId > 0) params.set("client_id", String(clientId));
		if (branchId > 0) params.set("branch_id", String(branchId));
		const qs = params.toString();
		return this.request(
			`/desk/patron/${encodeURIComponent(patronId)}/loans/${encodeURIComponent(loanId)}/renew${qs ? `?${qs}` : ""}`,
			{ method: "POST" },
		);
	}

	// This tenant's patron-type vocabulary (Circulate's admin-managed
	// patron categories) — feeds the Customer details tab's patron-type
	// picker (and the issue-card form's).
	async getDeskPatronTypes(clientId = 0) {
		const params = new URLSearchParams();
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/desk/patron-types${qs ? `?${qs}` : ""}`);
	}

	// clientId is the shell picker's legacy client id (Communico staff
	// only, resolved server-side) — the nurture-picker-deletion fix. A
	// restricted (partner-tier) caller's own tenant always wins.
	// partnerId, when known (a record's own library), asks about that library
	// instead of the shell's choice.
	async getNurtureContext(clientId = 0, partnerId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/nurture/context${qs ? `?${qs}` : ""}`);
	}

	async getNurturePersons(q = "", start = 0, limit = 50, lifecycle = "", contactType = "", sort = "", partnerId = 0, clientId = 0) {
		const params = new URLSearchParams();
		// partner_id narrows to one library; 0/absent = every library the
		// caller may see. The Audience list stays answerable across
		// libraries for Communico staff — the filter exists so the
		// per-library contact-type categories mean something.
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		if (q) params.set("q", q);
		if (lifecycle) params.set("lifecycle", lifecycle);
		if (contactType) params.set("contact_type", contactType);
		if (sort) params.set("sort", sort);
		params.set("start", String(start));
		params.set("limit", String(limit));
		return this.request(`/nurture/persons?${params.toString()}`);
	}

	async getNurturePerson(id: number) {
		return this.request(`/nurture/persons/${id}`);
	}

	// Manual contact creation (Seat A). Body: { partner_id, display_name,
	// first_name?, last_name?, email?, phone?, home_branch?,
	// contact_type_id, email_consent? }.
	async createNurturePerson(body: Record<string, unknown>) {
		return this.request(`/nurture/persons`, { method: "POST", body: JSON.stringify(body) });
	}

	// Contact types (Seat A) — a partner's taxonomy (system rows first).
	async getNurtureContactTypes(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/contact-types?${params.toString()}`);
	}

	async setNurturePersonContactType(id: number, contactTypeId: number) {
		return this.request(`/nurture/persons/${id}/contact-type`, {
			method: "POST",
			body: JSON.stringify({ contact_type_id: contactTypeId }),
		});
	}

	// customer-record-v2 rebuild — the Personal-details inline-edit rail.
	// `patch` carries only the field(s) being saved (a save is per field).
	async updateNurturePersonProfile(id: number, patch: Record<string, string>) {
		return this.request(`/nurture/persons/${id}/profile`, {
			method: "PATCH",
			body: JSON.stringify(patch),
		});
	}

	// customer-record-v2 fidelity pass — the "Coming up" panel. Sourced
	// from Reserve/Attend directly (never the Activity Ledger, which
	// carries no start-time field); future-only, cancelled excluded,
	// ascending by starts_at.
	async getNurtureUpcoming(id: number) {
		return this.request(`/nurture/persons/${id}/upcoming`);
	}

	// Contact types admin (RequireAdminRole).
	async createNurtureContactType(partnerId: number, label: string, allowFiles: boolean, clientId = 0) {
		return this.request(`/nurture/admin/contact-types`, {
			method: "POST",
			body: JSON.stringify({
				...(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
				label,
				allow_files: allowFiles,
			}),
		});
	}

	async updateNurtureContactType(id: number, body: Record<string, unknown>) {
		return this.request(`/nurture/admin/contact-types/${id}`, { method: "PATCH", body: JSON.stringify(body) });
	}

	async archiveNurtureContactType(id: number) {
		return this.request(`/nurture/admin/contact-types/${id}/archive`, { method: "POST" });
	}

	// A2-4: the type new contacts get when nobody picks one.
	async setNurtureContactTypeDefault(id: number) {
		return this.request(`/nurture/admin/contact-types/${id}/default`, { method: "POST" });
	}

	async reactivateNurtureContactType(id: number) {
		return this.request(`/nurture/admin/contact-types/${id}/reactivate`, { method: "POST" });
	}

	async getNurtureTimeline(id: number, start = 0, limit = 50, module = "") {
		const params = new URLSearchParams({ start: String(start), limit: String(limit) });
		if (module) params.set("module", module);
		return this.request(`/nurture/persons/${id}/timeline?${params.toString()}`);
	}

	// Nurture fix A2-2/A2-3: a person's activity, newest first, paged by the
	// server's own cursor (`next_cursor`), never an offset. `module` is one
	// kind or a comma-separated list.
	async getNurtureTimelinePage(id: number, opts: { cursor?: string; module?: string; limit?: number } = {}) {
		const params = new URLSearchParams({ limit: String(opts.limit ?? 50) });
		if (opts.cursor) params.set("cursor", opts.cursor);
		if (opts.module) params.set("module", opts.module);
		return this.request(`/nurture/persons/${id}/timeline?${params.toString()}`);
	}

	async getNurturePersonILS(id: number) {
		return this.request(`/nurture/persons/${id}/ils`);
	}

	// Front-desk mutations (record-frontdesk-mutations) — real check-out /
	// renew / renew-all / take-payment writes against this person's
	// linked circulate patron. renewNurturePersonLoan renews one loan
	// (loanId) or every open loan (all=true, loanId ignored).
	async renewNurturePersonLoan(id: number, loanId: string, all = false) {
		return this.request(`/nurture/persons/${id}/ils/renew`, {
			method: "POST",
			body: JSON.stringify({ loan_id: loanId, all }),
		});
	}

	async checkoutNurturePersonItem(id: number, itemBarcode: string) {
		return this.request(`/nurture/persons/${id}/ils/checkout`, {
			method: "POST",
			body: JSON.stringify({ item_barcode: itemBarcode }),
		});
	}

	async payNurturePersonAccount(id: number, amount: number, paymentMethod: string) {
		return this.request(`/nurture/persons/${id}/ils/pay`, {
			method: "POST",
			body: JSON.stringify({ amount, payment_method: paymentMethod }),
		});
	}

	// Contact files (Seat C) — gated server-side on the person's contact
	// type allowing files; a 403 carries message "files_not_allowed",
	// which the files tile checks to hide itself entirely.
	async getNurturePersonFiles(personId: number) {
		return this.request(`/nurture/persons/${personId}/files`);
	}

	async uploadNurturePersonFile(personId: number, file: File) {
		const form = new FormData();
		form.append('file', file);
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/nurture/persons/${personId}/files`, {
			method: 'POST',
			headers,
			body: form,
		});
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `Upload failed (${resp.status})`);
		}
		return resp.json();
	}

	async deleteNurturePersonFile(personId: number, fileId: number) {
		return this.request(`/nurture/persons/${personId}/files/${fileId}`, { method: 'DELETE' });
	}

	// Bearer-authed download (Stage 44a precedent — a plain <a href>
	// would 401 since the token lives in sessionStorage, not a cookie).
	async downloadNurturePersonFile(personId: number, fileId: number, fallbackFilename: string) {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(
			`${API_BASE}/nurture/persons/${personId}/files/${fileId}/download`,
			{ headers },
		);
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `Download failed (${resp.status})`);
		}
		let filename = fallbackFilename;
		const disp = resp.headers.get('Content-Disposition') || '';
		const match = disp.match(/filename="?([^";]+)"?/i);
		if (match) filename = match[1];

		const blob = await resp.blob();
		const url = URL.createObjectURL(blob);
		try {
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
		} finally {
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		}
	}

	// Consent (Stage 6, §14) — staff-set marketing consent. Body:
	// { channel, status, legal_basis?, source_detail? }. Returns the
	// upserted NurtureConsent row.
	// Contact housekeeping (nurture fix A-3, A-4, C-6): delete, merge,
	// consent history and the staff a task can be given to. Own block so
	// merges with the other seats' api.ts changes stay clean.
	async deleteNurturePerson(id: number) {
		return this.request(`/nurture/persons/${id}`, { method: "DELETE" });
	}

	/** Fold `mergeId` into `keepId`; `keepId` is the record that survives. */
	async mergeNurturePersons(keepId: number, mergeId: number) {
		return this.request(`/nurture/persons/${keepId}/merge`, { method: "POST", body: JSON.stringify({ merge_id: mergeId }) });
	}

	async getNurtureConsentHistory(id: number) {
		return this.request(`/nurture/persons/${id}/consent-history`);
	}

	async getNurtureTaskOwners(partnerId: number, clientId = 0) {
		return this.request(`/nurture/task-owners${toQuery({ partner_id: partnerId, client_id: clientId > 0 ? clientId : undefined })}`);
	}

	async setNurtureConsent(id: number, body: Record<string, unknown>) {
		return this.request(`/nurture/persons/${id}/consent`, {
			method: "POST",
			body: JSON.stringify(body),
		});
	}

	// Segments (Stage 3) — saved dynamic audiences.
	// clientId narrows the list to one library (the Partner-nav pick) —
	// the nurture-picker-deletion fix. Omitted/0 keeps the historical
	// unscoped-for-staff behaviour.
	async getNurtureSegments(clientId = 0) {
		const params = new URLSearchParams();
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/nurture/segments${qs ? `?${qs}` : ""}`);
	}

	async getNurtureSegment(id: number) {
		return this.request(`/nurture/segments/${id}`);
	}

	async createNurtureSegment(body: Record<string, unknown>) {
		return this.request(`/nurture/segments`, { method: "POST", body: JSON.stringify(body) });
	}

	async updateNurtureSegment(id: number, body: Record<string, unknown>) {
		return this.request(`/nurture/segments/${id}`, { method: "PATCH", body: JSON.stringify(body) });
	}

	async archiveNurtureSegment(id: number) {
		return this.request(`/nurture/segments/${id}/archive`, { method: "POST" });
	}

	async previewNurtureSegment(partnerId: number, rules: unknown, clientId = 0) {
		return this.request(`/nurture/segments/preview`, {
			method: "POST",
			body: JSON.stringify({
				...(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
				rules,
			}),
		});
	}

	async getNurtureSegmentMembers(id: number, start = 0, limit = 50) {
		return this.request(`/nurture/segments/${id}/members?start=${start}&limit=${limit}`);
	}

	// The Audiences redesign (nurture-audience-builder brief) — the
	// home's folders, the builder's gallery + patron buckets, and the
	// estimate rail's consent split.
	async getNurtureSegmentFolders(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/segments/folders?${params.toString()}`);
	}

	async createNurtureSegmentFolder(partnerId: number, name: string, clientId = 0) {
		return this.request(`/nurture/segments/folders`, {
			method: "POST",
			body: JSON.stringify({
				...(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
				name,
			}),
		});
	}

	async renameNurtureSegmentFolder(id: number, name: string) {
		return this.request(`/nurture/segments/folders/${id}`, { method: "PATCH", body: JSON.stringify({ name }) });
	}

	async deleteNurtureSegmentFolder(id: number) {
		return this.request(`/nurture/segments/folders/${id}`, { method: "DELETE" });
	}

	// The merged recipe gallery — the code-defined registry plus this
	// library's own shared recipes (nurture-recipe-sharing brief).
	async getNurtureSegmentRecipes(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/segments/recipes?${params.toString()}`);
	}

	async shareNurtureSegmentRecipe(partnerId: number, segmentId: number, title: string, description: string, category: string, clientId = 0) {
		return this.request(`/nurture/segments/recipes/share`, {
			method: "POST",
			body: JSON.stringify({
				...(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
				segment_id: segmentId,
				title,
				description,
				category,
			}),
		});
	}

	async deleteNurtureSegmentRecipe(id: number) {
		return this.request(`/nurture/segments/recipes/${id}`, { method: "DELETE" });
	}

	async getNurtureBucketCatalog(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/segments/bucket-catalog?${params.toString()}`);
	}

	async setNurtureBucketLabels(partnerId: number, labels: unknown[], clientId = 0) {
		return this.request(`/nurture/segments/bucket-labels`, {
			method: "PUT",
			body: JSON.stringify({
				...(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
				labels,
			}),
		});
	}

	// purpose (the quick-email brief) — "" (default, marketing) or
	// "service"; lets the quick-email composer preview a saved
	// audience's breakdown under the purpose picked, before any
	// campaign exists.
	async previewNurtureSegmentBreakdown(partnerId: number, rules: unknown, clientId = 0, purpose = "") {
		return this.request(`/nurture/segments/preview-breakdown`, {
			method: "POST",
			body: JSON.stringify({
				...(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
				rules,
				...(purpose ? { purpose } : {}),
			}),
		});
	}

	// Which audiences feed a live automation. Served by a SIBLING
	// workstream — the Audiences home treats a 404/error as "no refs",
	// so the badge simply doesn't render until that endpoint lands.
	// journey-segment-trigger brief — the "used by automation" derivation
	// (non-archived segment-trigger journeys + in-flight campaign sends).
	async getNurtureSegmentAutomationRefs(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/segments/automation-refs?${params.toString()}`);
	}

	// Campaigns (Converse Stage 1) — consent-gated audience; NO send.
	// clientId (the campaigns/journeys-scope fix, the getNurtureSegments
	// precedent) is the shell picker's legacy client id. 0 (unrestricted
	// staff naming no library) returns every library's campaigns; a
	// restricted caller ignores it entirely (the backend forces its own
	// tenant).
	async getNurtureCampaigns(clientId = 0) {
		const params = new URLSearchParams();
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/nurture/campaigns${qs ? `?${qs}` : ""}`);
	}

	async getNurtureCampaign(id: number) {
		return this.request(`/nurture/campaigns/${id}`);
	}

	async createNurtureCampaign(body: Record<string, unknown>) {
		return this.request(`/nurture/campaigns`, { method: "POST", body: JSON.stringify(body) });
	}

	async updateNurtureCampaign(id: number, body: Record<string, unknown>) {
		return this.request(`/nurture/campaigns/${id}`, { method: "PATCH", body: JSON.stringify(body) });
	}

	async archiveNurtureCampaign(id: number) {
		return this.request(`/nurture/campaigns/${id}/archive`, { method: "POST" });
	}

	async getNurtureCampaignAudience(id: number, start = 0, limit = 50) {
		return this.request(`/nurture/campaigns/${id}/audience?start=${start}&limit=${limit}`);
	}

	async inspectNurtureCampaignRecipient(id: number, personId: number) {
		return this.request(`/nurture/campaigns/${id}/audience/${personId}`);
	}

	// Converse Stage 2 — the campaign send.
	async sendNurtureCampaign(id: number) {
		return this.request(`/nurture/campaigns/${id}/send`, { method: "POST" });
	}

	async getNurtureCampaignSends(id: number) {
		return this.request(`/nurture/campaigns/${id}/sends`);
	}

	// campaign-scheduling brief — schedule a draft campaign to send at a
	// future instant (the library's own wall clock), optionally spread
	// across a window; cancel clears it.
	async scheduleNurtureCampaign(id: number, body: { send_at_local: string; send_window_hours?: number }) {
		return this.request(`/nurture/campaigns/${id}/schedule`, { method: "POST", body: JSON.stringify(body) });
	}

	async cancelNurtureCampaignSchedule(id: number) {
		return this.request(`/nurture/campaigns/${id}/schedule`, { method: "DELETE" });
	}

	// §11.4 — the Outcomes panel's rollup read.
	async getNurtureCampaignOutcomes(id: number) {
		return this.request(`/nurture/campaigns/${id}/outcomes`);
	}

	// A/B testing (2026-08-21, Paul: winner by clicks) — the per-variant
	// results (assigned/unique clickers/click rate + decision state).
	async getNurtureCampaignABResults(id: number) {
		return this.request(`/nurture/campaigns/${id}/ab-results`);
	}

	// Click tracking (2026-08-21) — total + per-link click counts.
	async getNurtureCampaignClicks(id: number) {
		return this.request(`/nurture/campaigns/${id}/clicks`);
	}

	// Surveys v2 email distribution — the campaign's linked survey's
	// completion status (nil when the document has no survey block),
	// manual resend to non-completers, and the automatic-reminder
	// schedule.
	async getNurtureCampaignSurveyStatus(id: number) {
		return this.request(`/nurture/campaigns/${id}/survey-status`);
	}

	async resendNurtureCampaignSurvey(id: number, scope?: "non_completers" | "non_openers") {
		return this.request(`/nurture/campaigns/${id}/survey-resend`, {
			method: "POST",
			...(scope ? { body: JSON.stringify({ scope }) } : {}),
		});
	}

	async updateNurtureCampaignSurveyReminder(id: number, body: { days: number | null; max: number }) {
		return this.request(`/nurture/campaigns/${id}/survey-reminder`, { method: "PUT", body: JSON.stringify(body) });
	}

	// Email library (fleet/email-library) — an email is its own asset, in
	// its own library. The Email Studio edits an email; a campaign (or,
	// later, a journey send_email step) REFERENCES one via email_id on
	// the campaign payload. Kept as one contiguous block per the brief.
	// clientId is the shell picker's legacy client id (staff only) — the
	// nurture-picker-deletion pattern every other Nurture list follows.
	// A restricted (library) caller's own tenant always wins regardless
	// of what's passed.
	// The journey/survey event-type picker: the distinct event types this
	// library's Activity Ledger has actually seen for one module.
	async getNurtureEventTypes(partnerId: number, module: string) {
		const params = new URLSearchParams({ partner_id: String(partnerId), module });
		return this.request(`/nurture/event-types?${params.toString()}`);
	}

	async getNurtureEmails(partnerId = 0, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/nurture/emails${qs ? `?${qs}` : ""}`);
	}

	async getNurtureEmail(id: number) {
		return this.request(`/nurture/emails/${id}`);
	}

	async createNurtureEmail(body: Record<string, unknown>) {
		return this.request(`/nurture/emails`, { method: "POST", body: JSON.stringify(body) });
	}

	async updateNurtureEmail(id: number, body: Record<string, unknown>) {
		return this.request(`/nurture/emails/${id}`, { method: "PATCH", body: JSON.stringify(body) });
	}

	async deleteNurtureEmail(id: number) {
		return this.request(`/nurture/emails/${id}`, { method: "DELETE" });
	}

	// The old /nurture/campaigns/:id/studio route's redirect target —
	// lifts a campaign's legacy body_doc (or a blank canvas, absent one)
	// into its own email asset and attaches it. IDEMPOTENT: a campaign
	// that already has email_id just returns that same asset.
	async liftCampaignEmail(campaignId: number) {
		return this.request(`/nurture/campaigns/${campaignId}/lift-email`, { method: "POST" });
	}

	// Email Studio (§10.3) stage 2 — stateless compile preview. No
	// campaign id: previews a draft before it's ever saved. partnerId IS
	// required (stage 3) so a collection_carousel block can resolve.
	async previewEmailDoc(bodyDoc: Record<string, unknown>, partnerId: number, clientId = 0) {
		return this.request(`/nurture/email-studio/preview`, {
			method: "POST",
			body: JSON.stringify({ partner_id: partnerId, ...(clientId > 0 ? { client_id: clientId } : {}), body_doc: bodyDoc }),
		});
	}

	// Email Studio (§10.3) stage 3 — the collection_carousel block's
	// picker: partnerId's CUSTOM collections only.
	async listEmailStudioCollections(partnerId: number) {
		return this.request(`/nurture/email-studio/collections?partner_id=${partnerId}`);
	}

	// Email Studio v2 — the canvas editor's three read-only lookups.
	// merge-tags and templates are SERVED (rather than mirrored by hand
	// in TypeScript) so the editor's menus and the server's validator
	// read one definition.
	async listEmailStudioMergeTags() {
		return this.request(`/nurture/email-studio/merge-tags`);
	}

	async listEmailStudioTemplates() {
		return this.request(`/nurture/email-studio/templates`);
	}

	// The absolute public image base a chosen Media Manager asset's
	// storage_key is appended to. Empty base = this library has no
	// resolvable public image host, which the editor reports rather than
	// guessing a URL.
	async getEmailStudioMediaContext(partnerId: number) {
		return this.request(`/nurture/email-studio/media-context?partner_id=${partnerId}`);
	}

	// Per-library email templates (fleet/email-templates) — "Save as
	// template" snapshots of a library's own designed emails. Kept as
	// one contiguous block with the email-library methods above, per
	// the same nurture-picker-deletion pattern (partnerId/clientId).
	async getNurtureEmailTemplates(partnerId = 0, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/nurture/email-templates${qs ? `?${qs}` : ""}`);
	}

	async createNurtureEmailTemplate(emailId: number, name: string, description: string) {
		return this.request(`/nurture/email-templates`, {
			method: "POST",
			body: JSON.stringify({ email_id: emailId, name, description }),
		});
	}

	async archiveNurtureEmailTemplate(id: number) {
		return this.request(`/nurture/email-templates/${id}/archive`, { method: "POST" });
	}

	// Automated Journeys (§10.5) — the workflow engine + canvas.
	// clientId (the campaigns/journeys-scope fix, the getNurtureSegments
	// precedent) is the shell picker's legacy client id. 0 (unrestricted
	// staff naming no library) returns every library's journeys; a
	// restricted caller ignores it entirely (the backend forces its own
	// tenant).
	async getNurtureJourneys(clientId = 0) {
		const params = new URLSearchParams();
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/nurture/journeys${qs ? `?${qs}` : ""}`);
	}

	async getNurtureJourney(id: number) {
		return this.request(`/nurture/journeys/${id}`);
	}

	async createNurtureJourney(body: Record<string, unknown>) {
		return this.request(`/nurture/journeys`, { method: "POST", body: JSON.stringify(body) });
	}

	async updateNurtureJourney(id: number, body: Record<string, unknown>) {
		return this.request(`/nurture/journeys/${id}`, { method: "PATCH", body: JSON.stringify(body) });
	}

	async getNurtureJourneyActivationPreview(id: number) {
		return this.request(`/nurture/journeys/${id}/activation-preview`);
	}

	async setNurtureJourneyStatus(id: number, status: string) {
		return this.request(`/nurture/journeys/${id}/status`, { method: "POST", body: JSON.stringify({ status }) });
	}

	async getNurtureJourneyDetail(id: number) {
		return this.request(`/nurture/journeys/${id}/detail`);
	}

	// The canvas's per-node overlay (journey-canvas-v2 brief, Part 3).
	// partner_id is a cross-check, not a lookup key — a mismatch reads
	// as not found.
	async getNurtureJourneyNodeStats(id: number, partnerId: number) {
		return this.request(`/nurture/journeys/${id}/node-stats?partner_id=${partnerId}`);
	}

	// Surveys (§13, v1 spine) — definitions + results. Dispatch itself
	// is backend-only (the ingest path); there is no send endpoint here.
	// clientId (surveys-scope brief, the getNurtureSegments precedent) is
	// the shell picker's legacy client id. 0 (unrestricted staff naming
	// no library) returns every library's surveys; a restricted caller
	// ignores it entirely (the backend forces its own tenant).
	async getNurtureSurveys(clientId = 0) {
		const params = new URLSearchParams();
		if (clientId > 0) params.set("client_id", String(clientId));
		// Archived surveys come back too so the list's Archived filter can
		// show them; the "All" view hides them on the screen.
		params.set("include_archived", "true");
		const qs = params.toString();
		return this.request(`/nurture/surveys${qs ? `?${qs}` : ""}`);
	}

	async getNurtureSurvey(id: number) {
		return this.request(`/nurture/surveys/${id}`);
	}

	async createNurtureSurvey(body: Record<string, unknown>) {
		return this.request(`/nurture/surveys`, { method: "POST", body: JSON.stringify(body) });
	}

	async updateNurtureSurvey(id: number, body: Record<string, unknown>) {
		return this.request(`/nurture/surveys/${id}`, { method: "PATCH", body: JSON.stringify(body) });
	}

	async activateNurtureSurvey(id: number) {
		return this.request(`/nurture/surveys/${id}/activate`, { method: "POST" });
	}

	async archiveNurtureSurvey(id: number, archived = true) {
		return this.request(`/nurture/surveys/${id}/archive`, {
			method: "POST",
			body: JSON.stringify({ archived }),
		});
	}

	async getNurtureSurveyResults(id: number) {
		return this.request(`/nurture/surveys/${id}/results`);
	}

	// The Responses tab (surveys-section-ui) — individual responses,
	// paginated (limit max 100, per the pinned contract). filter is
	// results-v2 wave 2's server-side q/max_rating/after_reminder
	// narrowing — every field optional, so an omitted `filter` is
	// byte-identical to the pre-filter call.
	async getNurtureSurveyResponses(
		id: number,
		limit = 50,
		offset = 0,
		filter?: { q?: string; maxRating?: number; afterReminder?: boolean },
	) {
		const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
		if (filter?.q) params.set("q", filter.q);
		if (filter?.maxRating != null) params.set("max_rating", String(filter.maxRating));
		if (filter?.afterReminder) params.set("after_reminder", "true");
		return this.request(`/nurture/surveys/${id}/responses?${params.toString()}`);
	}

	// The builder's stateless "see how this looks on screen" preview
	// (Paul) — renders a draft (name/description/questions) through the
	// SAME engine the live patron-facing form uses, without saving it.
	// No survey id: a fresh, never-saved draft can be previewed too.
	async previewNurtureSurvey(body: Record<string, unknown>) {
		return this.request(`/nurture/surveys/preview`, { method: "POST", body: JSON.stringify(body) });
	}

	// The rule-builder / journey-canvas survey picker.
	async getNurtureSurveysForPartner(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/surveys/for-partner?${params.toString()}`);
	}

	// The person record's Surveys tile.
	async getPersonSurveyDispatches(personId: number) {
		return this.request(`/nurture/persons/${personId}/surveys`);
	}

	// §8 admin foundation — the Permissions & visibility section
	// (RequireAdminRole; nurture-split-permissions).
	async getNurturePartnerSettings(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/admin/settings?${params.toString()}`);
	}

	async setNurturePartnerSettings(partnerId: number, readingHistoryAccess: string, clientId = 0) {
		return this.request(`/nurture/admin/settings`, {
			method: "PUT",
			body: JSON.stringify({
				...(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
				reading_history_access: readingHistoryAccess,
			}),
		});
	}

	// Bucket THRESHOLDS (nurture-bucket-thresholds brief) — sibling to
	// the bucket-label rename overlay's endpoints above.
	async getNurtureBucketThresholds(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/admin/bucket-thresholds?${params.toString()}`);
	}

	async setNurtureBucketThresholds(
		partnerId: number,
		t: {
			engagement_high: number;
			engagement_mid: number;
			churn_high: number;
			churn_mid: number;
			at_risk_after_days: number;
			lapsed_after_days: number;
			inactive_after_days: number;
		},
		clientId = 0,
	) {
		return this.request(`/nurture/admin/bucket-thresholds`, {
			method: "PUT",
			body: JSON.stringify({
				...(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
				...t,
			}),
		});
	}

	async resetNurtureBucketThresholds(partnerId: number, clientId = 0) {
		return this.request(`/nurture/admin/bucket-thresholds/reset`, {
			method: "POST",
			body: JSON.stringify(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
		});
	}

	async previewNurtureBucketThresholds(
		partnerId: number,
		t: {
			engagement_high: number;
			engagement_mid: number;
			churn_high: number;
			churn_mid: number;
			at_risk_after_days: number;
			lapsed_after_days: number;
			inactive_after_days: number;
		},
		clientId = 0,
	) {
		return this.request(`/nurture/admin/bucket-thresholds/preview`, {
			method: "POST",
			body: JSON.stringify({
				...(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
				...t,
			}),
		});
	}

	// Card-conversion journey settings (card-conversion brief, Part 2) —
	// a sibling of the reading-history setting above, on the same table.
	async getNurtureCardConversionSettings(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/admin/card-conversion-settings?${params.toString()}`);
	}

	async setNurtureCardConversionSettings(partnerId: number, purpose: string, cardSignupUrl: string, clientId = 0) {
		return this.request(`/nurture/admin/card-conversion-settings`, {
			method: "PUT",
			body: JSON.stringify({
				...(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
				purpose,
				card_signup_url: cardSignupUrl,
			}),
		});
	}

	// Email brand kit (fleet/email-brand-kit brief) — the per-library
	// Email Studio header/footer override. /nurture/admin/brand-kit.
	async getNurtureBrandKit(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/admin/brand-kit?${params.toString()}`);
	}

	async setNurtureBrandKit(
		partnerId: number,
		kit: {
			library_name: string;
			logo_url: string;
			primary_color: string;
			address_lines: string;
			facebook_url: string;
			instagram_url: string;
		},
		clientId = 0,
	) {
		return this.request(`/nurture/admin/brand-kit`, {
			method: "PUT",
			body: JSON.stringify({
				...(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
				...kit,
			}),
		});
	}

	async resetNurtureBrandKit(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/admin/brand-kit?${params.toString()}`, { method: "DELETE" });
	}

	// Header & footer PRESETS (fleet/brand-presets) — a small named set
	// per kind, on top of the single Brand kit default above. The Studio
	// swap panel reads the staff-facing list; the admin grid manages it.
	async getNurtureBrandPresets(partnerId: number) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		return this.request(`/nurture/brand-presets?${params.toString()}`);
	}

	async getNurtureAdminBrandPresets(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/admin/brand-presets?${params.toString()}`);
	}

	async createNurtureBrandPreset(body: Record<string, unknown>) {
		return this.request(`/nurture/admin/brand-presets`, { method: "POST", body: JSON.stringify(body) });
	}

	async updateNurtureBrandPreset(id: number, body: Record<string, unknown>) {
		return this.request(`/nurture/admin/brand-presets/${id}`, { method: "PATCH", body: JSON.stringify(body) });
	}

	async deleteNurtureBrandPreset(id: number) {
		return this.request(`/nurture/admin/brand-presets/${id}`, { method: "DELETE" });
	}

	// Opt-in email-open tracking (surveys-v2-form-and-opens brief) — a
	// sibling of the reading-history setting above, on the same table.
	async getNurtureEmailOpenTrackingSettings(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/admin/email-open-tracking?${params.toString()}`);
	}

	async setNurtureEmailOpenTrackingSettings(partnerId: number, enabled: boolean, clientId = 0) {
		return this.request(`/nurture/admin/email-open-tracking`, {
			method: "PUT",
			body: JSON.stringify({
				...(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
				enabled,
			}),
		});
	}

	// Custom fields per contact type (nurture-custom-fields brief).
	async getNurturePersonCustomFields(personId: number) {
		return this.request(`/nurture/persons/${personId}/custom-fields`);
	}

	async setNurturePersonCustomFields(personId: number, values: Record<string, unknown>) {
		return this.request(`/nurture/persons/${personId}/custom-fields`, {
			method: "PUT",
			body: JSON.stringify({ values }),
		});
	}

	// One contact type's active field definitions, on the patrons
	// capability (#150): the create-contact modal's read. Record-side
	// callers use this; the segment/journey builders keep the
	// marketing-gated cross-type list below.
	async getNurtureContactTypeCustomFields(partnerId: number, typeKey: string, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/contact-types/${encodeURIComponent(typeKey)}/custom-fields?${params.toString()}`);
	}

	// The segment/journey condition builder's cross-type field picker
	// (marketing capability).
	async getNurtureCustomFieldsForBuilder(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/custom-fields?${params.toString()}`);
	}

	// Segment attributes (nurture-segment-attributes brief) — the closed
	// activity-attribute registry + the two value pickers behind it.
	async getNurtureAttributeRegistry(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/segments/attribute-registry?${params.toString()}`);
	}

	async getNurtureAttributeOptions(partnerId: number, module: string, key: string, clientId = 0) {
		const params = new URLSearchParams({ module, key });
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/segments/attribute-options?${params.toString()}`);
	}

	async getNurturePatronTypeOptions(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/segments/patron-types?${params.toString()}`);
	}

	// Admin — the field-definition CRUD (RequireAdminRole).
	async getNurtureAdminCustomFields(partnerId: number, contactTypeKey: string, clientId = 0) {
		const params = new URLSearchParams({ contact_type_key: contactTypeKey });
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/admin/custom-fields?${params.toString()}`);
	}

	async createNurtureAdminCustomField(body: Record<string, unknown>) {
		return this.request(`/nurture/admin/custom-fields`, { method: "POST", body: JSON.stringify(body) });
	}

	async updateNurtureAdminCustomField(id: number, body: Record<string, unknown>) {
		return this.request(`/nurture/admin/custom-fields/${id}`, { method: "PATCH", body: JSON.stringify(body) });
	}

	async archiveNurtureAdminCustomField(id: number, archived: boolean) {
		return this.request(`/nurture/admin/custom-fields/${id}/archive`, {
			method: "POST",
			body: JSON.stringify({ archived }),
		});
	}

	async reorderNurtureAdminCustomFields(partnerId: number, contactTypeKey: string, orderedIds: number[], clientId = 0) {
		return this.request(`/nurture/admin/custom-fields/reorder`, {
			method: "POST",
			body: JSON.stringify({
				...(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
				contact_type_key: contactTypeKey,
				ordered_ids: orderedIds,
			}),
		});
	}

	// §4.4 staff workspace — notes, tasks + tags (nurture-staff-workspace).
	async getNurturePersonNotes(personId: number) {
		return this.request(`/nurture/persons/${personId}/notes`);
	}

	async createNurturePersonNote(personId: number, body: string) {
		return this.request(`/nurture/persons/${personId}/notes`, {
			method: "POST",
			body: JSON.stringify({ body }),
		});
	}

	async setNurturePersonNotePin(personId: number, noteId: number, pinned: boolean) {
		return this.request(`/nurture/persons/${personId}/notes/${noteId}/${pinned ? "pin" : "unpin"}`, { method: "POST" });
	}

	async deleteNurturePersonNote(personId: number, noteId: number) {
		return this.request(`/nurture/persons/${personId}/notes/${noteId}`, { method: "DELETE" });
	}

	// One-to-one patron email (patron-email-thread build) — sent through
	// the Communications Hub, Reply-To pointed at the library's own
	// inbound-email address so a reply lands back on this same record.
	// idempotencyKey is CALLER-supplied (the composer mints a fresh UUID
	// on mount and a new one after every successful send) — required;
	// the backend 422s a missing/malformed one rather than generating
	// its own, which would defeat the double-click protection.
	async sendNurturePersonEmail(personId: number, subject: string, body: string, idempotencyKey: string, bodyDoc?: unknown) {
		return this.request(`/nurture/persons/${personId}/email`, {
			method: "POST",
			// body_doc (optional) is the formatted message; body stays its plain-text version.
			body: JSON.stringify({ subject, body, idempotency_key: idempotencyKey, ...(bodyDoc ? { body_doc: bodyDoc } : {}) }),
		});
	}

	async getNurturePersonTasks(personId: number) {
		return this.request(`/nurture/persons/${personId}/tasks`);
	}

	async createNurturePersonTask(personId: number, body: Record<string, unknown>) {
		return this.request(`/nurture/persons/${personId}/tasks`, { method: "POST", body: JSON.stringify(body) });
	}

	async setNurturePersonTaskStatus(personId: number, taskId: number, completed: boolean) {
		return this.request(`/nurture/persons/${personId}/tasks/${taskId}/${completed ? "complete" : "reopen"}`, { method: "POST" });
	}

	// §9.4 work queue (nurture-work-queue brief) — aggregates open tasks
	// + churn-attention items. partnerId omitted = every partner;
	// assignee "me"/"all" (default "all" server-side).
	async getNurtureWorkQueue(partnerId?: number, assignee?: "me" | "all", clientId = 0) {
		return this.request(
			`/nurture/work-queue${toQuery({ partner_id: partnerId, assignee, client_id: clientId > 0 ? clientId : undefined })}`,
		);
	}

	// §9.3 inbound email — the platform's first inbound rail
	// (docs/stage-briefs/nurture-unified-inbox-audit.md). Inbound-email-v2
	// Stage 3: every route is scoped by inbox_id — a NAMED inbox the
	// caller is an explicit member of — never partner_id (a library can
	// have several inboxes; access is granted per inbox, not derived
	// from a shared library pick).
	async getNurtureInboundEmail(inboxId: number, status?: string, start = 0, limit = 50) {
		return this.request(`/nurture/inbound-email${toQuery({ inbox_id: inboxId, status, start, limit, client_id: libraryClientIdOrUndefined() })}`);
	}

	// Nurture fix C2-5: one page of an inbox by cursor (empty = the newest).
	async getNurtureInboundEmailPage(inboxId: number, cursor = "", limit = 100) {
		return this.request(`/nurture/inbound-email${toQuery({ inbox_id: inboxId, paged: 1, cursor, limit, client_id: libraryClientIdOrUndefined() })}`);
	}

	async getNurtureInboundEmailMessage(inboxId: number, id: number) {
		return this.request(`/nurture/inbound-email/${id}${toQuery({ inbox_id: inboxId, client_id: libraryClientIdOrUndefined() })}`);
	}

	async assignNurtureInboundEmail(inboxId: number, id: number, userId?: number) {
		return this.request(`/nurture/inbound-email/${id}/assign${toQuery({ inbox_id: inboxId, client_id: libraryClientIdOrUndefined() })}`, {
			method: "POST",
			body: JSON.stringify(userId ? { user_id: userId } : {}),
		});
	}

	async closeNurtureInboundEmail(inboxId: number, id: number) {
		return this.request(`/nurture/inbound-email/${id}/close${toQuery({ inbox_id: inboxId, client_id: libraryClientIdOrUndefined() })}`, { method: "POST" });
	}

	async reopenNurtureInboundEmail(inboxId: number, id: number) {
		return this.request(`/nurture/inbound-email/${id}/reopen${toQuery({ inbox_id: inboxId, client_id: libraryClientIdOrUndefined() })}`, { method: "POST" });
	}

	async linkNurtureInboundEmail(inboxId: number, id: number, personId: number) {
		return this.request(`/nurture/inbound-email/${id}/link${toQuery({ inbox_id: inboxId, client_id: libraryClientIdOrUndefined() })}`, {
			method: "POST",
			body: JSON.stringify({ person_id: personId }),
		});
	}

	// Correspondence purge (fleet/email-purge brief) — two actions on a
	// person's own comms-module correspondence. "Remove" is everyday and
	// reversible (re-linking a message via linkNurtureInboundEmail
	// restores it); "delete" is a PERMANENT data-subject erasure and
	// 403s for anyone who isn't super_admin (the backend's
	// mw.RequireSuperAdmin() router gate — the billing-contract-delete
	// precedent). Both take the same message-id-array body and return
	// the same shape.
	async removeNurtureCorrespondence(personId: number, messageIds: number[]) {
		return this.request(`/nurture/persons/${personId}/correspondence/remove`, {
			method: "POST",
			body: JSON.stringify({ message_ids: messageIds }),
		}) as Promise<NurtureCorrespondencePurgeResult>;
	}

	async deleteNurtureCorrespondencePermanently(personId: number, messageIds: number[]) {
		return this.request(`/nurture/persons/${personId}/correspondence/delete`, {
			method: "POST",
			body: JSON.stringify({ message_ids: messageIds }),
		}) as Promise<NurtureCorrespondencePurgeResult>;
	}

	// The standalone Email surface's per-inbox tab list: the NAMED inboxes
	// the caller is an explicit member of (comms_inbox_members), inside the
	// library chosen in the shell picker ONLY (Nurture QA C-7, 29 October
	// 2026, reversing the earlier "never tied to the picker" rule: a staff
	// member in two libraries must not see one library's mail while working
	// in the other). With no library chosen the server returns nothing.
	async getNurtureInboundEmailInboxes() {
		return this.request(`/nurture/inbound-email/inboxes${inboxLibraryQuery()}`) as Promise<{ inboxes: NurtureInboundEmailInbox[] }>;
	}

	// The compose/reply/forward send — direct to Mailgun, never through
	// the Communications Hub (see the backend's own doc comment on
	// NurtureService.SendInboundEmail). A 422 here carries the backend's
	// own actionable copy (sending disabled / the pre-production guard /
	// validation) — the errText pin applies, same as every other Nurture
	// 422 surface.
	//
	// Stage 2 — files (attachments) switches the request to
	// multipart/form-data: a "payload" field carrying this same JSON,
	// plus one "attachments" file part per file (the backend's
	// SendInboundEmail handler branches on Content-Type). No files still
	// goes through the plain JSON path, unchanged.
	async sendNurtureInboundEmail(payload: NurtureInboundEmailSendPayload, files?: File[]) {
		if (files && files.length > 0) {
			const form = new FormData();
			form.append("payload", JSON.stringify(payload));
			for (const f of files) form.append("attachments", f);
			const headers: Record<string, string> = {};
			if (this.token) headers["Authorization"] = `Bearer ${this.token}`;
			const resp = await fetch(`${API_BASE}/nurture/inbound-email/send${inboxLibraryQuery()}`, { method: "POST", headers, body: form });
			if (!resp.ok) {
				const b = (await resp.json().catch(() => ({}))) as { message?: string };
				throw new ApiError(resp.status, b.message || `Send failed (${resp.status})`);
			}
			return resp.json() as Promise<NurtureInboundEmailMessage>;
		}
		return this.request(`/nurture/inbound-email/send${inboxLibraryQuery()}`, {
			method: "POST",
			body: JSON.stringify(payload),
		}) as Promise<NurtureInboundEmailMessage>;
	}

	// The SMS rail's reply send — direct to Twilio, same 422 posture as
	// the email send (errText applies).
	async sendNurtureInboundSMS(payload: NurtureInboundSMSSendPayload) {
		return this.request(`/nurture/inbound-email/send-sms${inboxLibraryQuery()}`, {
			method: "POST",
			body: JSON.stringify(payload),
		}) as Promise<NurtureInboundEmailMessage>;
	}

	// Stage 2 — attachment download (bearer-authed Blob, the
	// downloadNurturePersonFile precedent: a plain <a href> would 401
	// since the token lives in sessionStorage, not a cookie). A 404 with
	// message "attachment not retained" means it was recorded as
	// metadata only (oversized at capture, or storage unconfigured).
	async downloadNurtureInboundEmailAttachment(attachmentId: number, fallbackFilename: string) {
		const headers: Record<string, string> = {};
		if (this.token) headers["Authorization"] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/nurture/inbound-email/attachments/${attachmentId}${inboxLibraryQuery()}`, { headers });
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `Download failed (${resp.status})`);
		}
		let filename = fallbackFilename;
		const disp = resp.headers.get("Content-Disposition") || "";
		const match = disp.match(/filename="?([^";]+)"?/i);
		if (match) filename = match[1];
		const blob = await resp.blob();
		const url = URL.createObjectURL(blob);
		try {
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
		} finally {
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		}
	}

	// Inbound email v2, Stage 2 — server-side per-user compose drafts.
	// The caller's own drafts, only for the chosen library's inboxes (C-7).
	async getNurtureEmailDrafts() {
		return this.request(`/nurture/email-drafts${inboxLibraryQuery()}`) as Promise<{ drafts: NurtureEmailDraft[] }>;
	}
	async createNurtureEmailDraft(payload: NurtureEmailDraftPayload) {
		return this.request(`/nurture/email-drafts${inboxLibraryQuery()}`, {
			method: "POST",
			body: JSON.stringify(payload),
		}) as Promise<NurtureEmailDraft>;
	}
	async updateNurtureEmailDraft(id: number, payload: NurtureEmailDraftPayload) {
		return this.request(`/nurture/email-drafts/${id}${inboxLibraryQuery()}`, {
			method: "PUT",
			body: JSON.stringify(payload),
		}) as Promise<NurtureEmailDraft>;
	}
	async deleteNurtureEmailDraft(id: number) {
		return this.request(`/nurture/email-drafts/${id}`, { method: "DELETE" });
	}

	// Inbound email v2, Stage 3 — the partner-admin "Email inboxes"
	// section. Admin-gated CRUD over comms_inboxes + comms_inbox_members.
	async getNurtureAdminInboxes(partnerId = 0, clientId = 0) {
		return this.request(
			`/nurture/admin/inboxes${toQuery({ partner_id: partnerId || undefined, client_id: clientId > 0 ? clientId : undefined })}`,
		) as Promise<{ inboxes: NurtureCommsInboxAdminRow[] }>;
	}
	async createNurtureAdminInbox(payload: NurtureCreateInboxPayload) {
		return this.request("/nurture/admin/inboxes", {
			method: "POST",
			body: JSON.stringify(payload),
		}) as Promise<NurtureCommsInboxAdminRow>;
	}
	async renameNurtureAdminInbox(id: number, displayName: string) {
		return this.request(`/nurture/admin/inboxes/${id}`, {
			method: "PATCH",
			body: JSON.stringify({ display_name: displayName }),
		}) as Promise<NurtureCommsInboxAdminRow>;
	}
	async setNurtureAdminInboxActive(id: number, active: boolean) {
		return this.request(`/nurture/admin/inboxes/${id}`, {
			method: "PATCH",
			body: JSON.stringify({ active }),
		}) as Promise<NurtureCommsInboxAdminRow>;
	}
	async getNurtureAdminInboxLinkableUsers(partnerId = 0, clientId = 0) {
		return this.request(
			`/nurture/admin/inboxes/linkable-users${toQuery({ partner_id: partnerId || undefined, client_id: clientId > 0 ? clientId : undefined })}`,
		) as Promise<{ users: NurtureCommsInboxLinkableUser[] }>;
	}
	async getNurtureAdminInboxMembers(inboxId: number) {
		return this.request(`/nurture/admin/inboxes/${inboxId}/members`) as Promise<{ members: NurtureCommsInboxMember[] }>;
	}
	async addNurtureAdminInboxMember(inboxId: number, userId: number) {
		return this.request(`/nurture/admin/inboxes/${inboxId}/members`, {
			method: "POST",
			body: JSON.stringify({ user_id: userId }),
		});
	}
	async removeNurtureAdminInboxMember(inboxId: number, userId: number) {
		return this.request(`/nurture/admin/inboxes/${inboxId}/members/${userId}`, { method: "DELETE" });
	}

	// The staff-readable ACTIVE tag catalog (the picker) — distinct from
	// the admin CRUD below.
	async getNurtureTagCatalog(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/tags?${params.toString()}`);
	}

	async applyNurturePersonTag(personId: number, tagId: number) {
		return this.request(`/nurture/persons/${personId}/tags`, {
			method: "POST",
			body: JSON.stringify({ tag_id: tagId }),
		});
	}

	async removeNurturePersonTag(personId: number, tagId: number) {
		return this.request(`/nurture/persons/${personId}/tags/${tagId}`, { method: "DELETE" });
	}

	// §8.1 tag vocabulary admin CRUD (RequireAdminRole).
	async getNurtureAdminTags(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/admin/tags?${params.toString()}`);
	}

	async createNurtureAdminTag(body: Record<string, unknown>) {
		return this.request(`/nurture/admin/tags`, { method: "POST", body: JSON.stringify(body) });
	}

	async updateNurtureAdminTag(id: number, body: Record<string, unknown>) {
		return this.request(`/nurture/admin/tags/${id}`, { method: "PATCH", body: JSON.stringify(body) });
	}

	// §4.5/§6.3 organisations & roles (nurture-orgs-roles brief).
	async getNurtureOrgTypeCatalog(partnerId: number, clientId = 0) {
		// Never send partner_id=0: an unresolved library is left out of the address.
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/org-types?${params.toString()}`);
	}

	async getNurtureAdminOrgTypes(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/admin/org-types?${params.toString()}`);
	}

	async createNurtureAdminOrgType(body: Record<string, unknown>) {
		return this.request(`/nurture/admin/org-types`, { method: "POST", body: JSON.stringify(body) });
	}

	async updateNurtureAdminOrgType(id: number, body: Record<string, unknown>) {
		return this.request(`/nurture/admin/org-types/${id}`, { method: "PATCH", body: JSON.stringify(body) });
	}

	async getNurtureRoleCatalog(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/roles?${params.toString()}`);
	}

	async getNurturePersonRoles(personId: number) {
		return this.request(`/nurture/persons/${personId}/roles`);
	}

	async applyNurturePersonRole(personId: number, roleId: number) {
		return this.request(`/nurture/persons/${personId}/roles`, {
			method: "POST",
			body: JSON.stringify({ role_id: roleId }),
		});
	}

	async removeNurturePersonRole(personId: number, roleId: number) {
		return this.request(`/nurture/persons/${personId}/roles/${roleId}`, { method: "DELETE" });
	}

	async getNurtureAdminRoles(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/admin/roles?${params.toString()}`);
	}

	async createNurtureAdminRole(body: Record<string, unknown>) {
		return this.request(`/nurture/admin/roles`, { method: "POST", body: JSON.stringify(body) });
	}

	async updateNurtureAdminRole(id: number, body: Record<string, unknown>) {
		return this.request(`/nurture/admin/roles/${id}`, { method: "PATCH", body: JSON.stringify(body) });
	}

	async getNurtureOrgPartners() {
		return this.request(`/nurture/orgs/partners`);
	}

	async getNurtureOrgOptions(partnerId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (partnerId > 0) params.set("partner_id", String(partnerId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/nurture/orgs/options?${params.toString()}`);
	}

	async getNurtureOrgs(partnerId: number, filter: Record<string, unknown> = {}, clientId = 0) {
		return this.request(
			`/nurture/orgs${toQuery({ partner_id: partnerId > 0 ? partnerId : undefined, client_id: clientId > 0 ? clientId : undefined, ...filter })}`,
		);
	}

	async getNurtureOrg(id: number) {
		return this.request(`/nurture/orgs/${id}`);
	}

	async createNurtureOrg(body: Record<string, unknown>) {
		return this.request(`/nurture/orgs`, { method: "POST", body: JSON.stringify(body) });
	}

	async updateNurtureOrg(id: number, body: Record<string, unknown>) {
		return this.request(`/nurture/orgs/${id}`, { method: "PATCH", body: JSON.stringify(body) });
	}

	async archiveNurtureOrg(id: number, partnerId: number, clientId = 0) {
		return this.request(`/nurture/orgs/${id}/archive`, {
			method: "POST",
			body: JSON.stringify(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
		});
	}

	async reactivateNurtureOrg(id: number, partnerId: number, clientId = 0) {
		return this.request(`/nurture/orgs/${id}/reactivate`, {
			method: "POST",
			body: JSON.stringify(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
		});
	}

	async getNurtureOrgAffiliations(orgId: number) {
		return this.request(`/nurture/orgs/${orgId}/affiliations`);
	}

	async getNurturePersonAffiliations(personId: number) {
		return this.request(`/nurture/persons/${personId}/affiliations`);
	}

	async createNurturePersonAffiliation(personId: number, body: Record<string, unknown>) {
		return this.request(`/nurture/persons/${personId}/affiliations`, { method: "POST", body: JSON.stringify(body) });
	}

	async deleteNurturePersonAffiliation(personId: number, affiliationId: number) {
		return this.request(`/nurture/persons/${personId}/affiliations/${affiliationId}`, { method: "DELETE" });
	}

	// §9.6 the Boardroom — meetings, agendas, minutes, publishing.
	async getBoardBodies(partnerId: number, includeArchived = false, clientId = 0) {
		return this.request(
			`/nurture/board/bodies${toQuery({ partner_id: partnerId, include_archived: includeArchived, client_id: clientId > 0 ? clientId : undefined })}`,
		);
	}

	async createBoardBody(partnerId: number, name: string, clientId = 0) {
		return this.request(`/nurture/board/bodies`, {
			method: "POST",
			body: JSON.stringify({
				...(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
				name,
			}),
		});
	}

	async updateBoardBody(id: number, partnerId: number, patch: Record<string, unknown>, clientId = 0) {
		return this.request(`/nurture/board/bodies/${id}`, {
			method: "PATCH",
			body: JSON.stringify({
				...(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
				...patch,
			}),
		});
	}

	async archiveBoardBody(id: number, partnerId: number, clientId = 0) {
		return this.request(`/nurture/board/bodies/${id}/archive`, {
			method: "POST",
			body: JSON.stringify(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
		});
	}

	async reactivateBoardBody(id: number, partnerId: number, clientId = 0) {
		return this.request(`/nurture/board/bodies/${id}/reactivate`, {
			method: "POST",
			body: JSON.stringify(partnerId > 0 ? { partner_id: partnerId } : clientId > 0 ? { client_id: clientId } : {}),
		});
	}

	async getBoardTrustees(bodyId: number) {
		return this.request(`/nurture/board/bodies/${bodyId}/trustees`);
	}

	async getBoardMeetings(bodyId: number, q?: string, start = 0) {
		return this.request(`/nurture/board/meetings${toQuery({ body_id: bodyId, q, start: start > 0 ? start : undefined })}`);
	}

	async getBoardMeeting(id: number) {
		return this.request(`/nurture/board/meetings/${id}`);
	}

	async createBoardMeeting(body: Record<string, unknown>) {
		return this.request(`/nurture/board/meetings`, { method: "POST", body: JSON.stringify(body) });
	}

	async updateBoardMeeting(id: number, patch: Record<string, unknown>) {
		return this.request(`/nurture/board/meetings/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
	}

	async advanceBoardMeetingStatus(id: number, status: string) {
		return this.request(`/nurture/board/meetings/${id}/status`, { method: "POST", body: JSON.stringify({ status }) });
	}

	async unpublishBoardMeeting(id: number, reason: string) {
		return this.request(`/nurture/board/meetings/${id}/unpublish`, { method: "POST", body: JSON.stringify({ reason }) });
	}

	// Multipart upload — the uploadPreserveFile bearer-fetch shape (no
	// cos-file-upload primitive exists; every upload is a raw
	// FormData + fetch, per-feature).
	async uploadBoardDocument(meetingId: number, kind: string, file: File) {
		const form = new FormData();
		form.append("kind", kind);
		form.append("file", file);
		const headers: Record<string, string> = {};
		if (this.token) headers["Authorization"] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/nurture/board/meetings/${meetingId}/documents`, { method: "POST", headers, body: form });
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `Upload failed (${resp.status})`);
		}
		return resp.json();
	}

	async approveBoardMinutes(docId: number) {
		return this.request(`/nurture/board/documents/${docId}/approve-minutes`, { method: "POST" });
	}

	async renameBoardDocument(docId: number, filename: string) {
		return this.request(`/nurture/board/documents/${docId}`, { method: "PATCH", body: JSON.stringify({ filename }) });
	}

	async deleteBoardDocument(docId: number) {
		return this.request(`/nurture/board/documents/${docId}`, { method: "DELETE" });
	}

	// Bearer-authenticated blob fetch — the prospect-CSV / getPreserveFileBlob
	// pattern (a plain <a href> can't carry the Authorization header).
	async getBoardDocumentBlob(docId: number): Promise<Blob> {
		const headers: Record<string, string> = {};
		if (this.token) headers["Authorization"] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/nurture/board/documents/${docId}/content`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `Document fetch failed (${resp.status})`);
		return resp.blob();
	}

	// Trustee voting on motions: the meeting's structured agenda
	// (distinct from the uploaded agenda PDF) plus the staff tally.
	// Casting a vote itself is anonymous, on the trustee packet page.
	async getBoardAgendaItems(meetingId: number) {
		return this.request(`/nurture/board/meetings/${meetingId}/agenda-items`);
	}

	async createBoardAgendaItem(meetingId: number, body: Record<string, unknown>) {
		return this.request(`/nurture/board/meetings/${meetingId}/agenda-items`, { method: "POST", body: JSON.stringify(body) });
	}

	async updateBoardAgendaItem(itemId: number, patch: Record<string, unknown>) {
		return this.request(`/nurture/board/agenda-items/${itemId}`, { method: "PATCH", body: JSON.stringify(patch) });
	}

	async deleteBoardAgendaItem(itemId: number) {
		return this.request(`/nurture/board/agenda-items/${itemId}`, { method: "DELETE" });
	}

	async getBoardMotionTallies(meetingId: number) {
		return this.request(`/nurture/board/meetings/${meetingId}/motion-tallies`);
	}

	// Voting in the meeting, run by the clerk on one motion at a time.
	async openBoardVoting(itemId: number) {
		return this.request(`/nurture/board/agenda-items/${itemId}/open-voting`, { method: "POST" });
	}

	async reopenBoardVoting(itemId: number) {
		return this.request(`/nurture/board/agenda-items/${itemId}/reopen-voting`, { method: "POST" });
	}

	async closeBoardVoting(itemId: number, body: { outcome: string; mover_person_id?: number; seconder_person_id?: number }) {
		return this.request(`/nurture/board/agenda-items/${itemId}/close-voting`, { method: "POST", body: JSON.stringify(body) });
	}

	async recordBoardVote(itemId: number, personId: number, choice: string) {
		return this.request(`/nurture/board/agenda-items/${itemId}/votes`, {
			method: "POST",
			body: JSON.stringify({ person_id: personId, choice }),
		});
	}

	// Each trustee's own packet link for a meeting. Every read mints fresh
	// tokens, so call it when the box is shown, not ahead of time.
	async getBoardTrusteeLinks(meetingId: number) {
		return this.request(`/nurture/board/meetings/${meetingId}/trustee-links`);
	}

	// How the meeting reads on the public page, worded by the page's own
	// rules on the server, at any status.
	async getBoardPublicPreview(meetingId: number) {
		return this.request(`/nurture/board/meetings/${meetingId}/public-preview`);
	}

	// Emails the links through the packet-ready notice's path. No ids means
	// every trustee with an email address.
	async sendBoardTrusteeLinks(meetingId: number, personIds: number[] = []) {
		return this.request(`/nurture/board/meetings/${meetingId}/trustee-links/send`, {
			method: "POST",
			body: JSON.stringify(personIds.length ? { person_ids: personIds } : {}),
		});
	}

	async getBoardSettings(partnerId: number, clientId = 0) {
		return this.request(
			`/nurture/board/settings${toQuery({ partner_id: partnerId, client_id: clientId > 0 ? clientId : undefined })}`,
		);
	}

	// The Boardroom's own settings write, open to a library admin for their
	// own library. Send only the boxes being changed: a box left out keeps
	// its stored value.
	async setBoardSettings(settings: Record<string, unknown>) {
		return this.request(`/nurture/board/settings`, { method: "PUT", body: JSON.stringify(settings) });
	}

	// The Notices card's three pickers. The Boardroom serves its own read
	// because the Nurture role, audience and user lists stay closed to a
	// library login.
	async getBoardPickers(partnerId: number, clientId = 0) {
		return this.request(
			`/nurture/board/pickers${toQuery({ partner_id: partnerId, client_id: clientId > 0 ? clientId : undefined })}`,
		);
	}

	// The ANONYMOUS public read, called from the staff page so a librarian can
	// see exactly what a visitor sees after pressing Publish. It is the same
	// endpoint Explore/Create consume — published + public + in-retention-window
	// only, enforced in the repository query — so a preview that comes back
	// empty is the honest answer, not a bug. Tenant rides ?client_id= (the
	// documented fallback when there's no X-Client-ID header to send).
	async getPublicBoardMeeting(id: number, clientId: number) {
		return this.request(`/nurture/board/public/meetings/${id}${toQuery({ client_id: clientId })}`);
	}

	// Roster (staff rostering) — COS-native staff surface (/api/v1/roster/*).
	// List envelopes are { data, total }.
	async getRosterDashboard(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/dashboard${toQuery(filter)}`);
	}

	// Setup pathway (docs/stage-briefs/setup-pathway-spec.md) — one read
	// per module, returning every declared step with its live-derived
	// state and summary. Shared shape: see types/pathway.ts.
	async getRosterSetupState(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/setup-state${toQuery(filter)}`);
	}

	// Implementation mode ("When setup is over") — an explicit
	// declaration in either direction, never a threshold the client
	// computes. Both return the same shape as getRosterSetupState.
	async declareRosterLive(body: Record<string, unknown>) {
		return this.request('/roster/setup-state/declare-live', { method: 'POST', body: JSON.stringify(body) });
	}

	async returnRosterToImplementing(body: Record<string, unknown>) {
		return this.request('/roster/setup-state/return-to-implementing', { method: 'POST', body: JSON.stringify(body) });
	}

	// Implementation status admin screen ("Going live one branch at a
	// time") — the library's own mode plus every branch's, own or
	// inherited, in one read regardless of branch count.
	async getRosterImplementationOverview(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/setup-state/overview${toQuery(filter)}`);
	}

	async getRosterBranches(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/branches${toQuery(filter)}`);
	}

	async createRosterBranch(data: Record<string, unknown>) {
		return this.request('/roster/branches', { method: 'POST', body: JSON.stringify(data) });
	}

	async updateRosterBranch(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/branches/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async getRosterServicePoints(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/service-points${toQuery(filter)}`);
	}

	async createRosterServicePoint(data: Record<string, unknown>) {
		return this.request('/roster/service-points', { method: 'POST', body: JSON.stringify(data) });
	}

	async updateRosterServicePoint(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/service-points/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async getRosterWorkers(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/workers${toQuery(filter)}`);
	}

	async getRosterWorker(id: number) {
		return this.request(`/roster/workers/${id}`);
	}

	async createRosterWorker(data: Record<string, unknown>) {
		return this.request('/roster/workers', { method: 'POST', body: JSON.stringify(data) });
	}

	async updateRosterWorker(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/workers/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async replaceRosterWorkerSkills(id: number, skills: Record<string, unknown>[]) {
		return this.request(`/roster/workers/${id}/skills`, { method: 'PUT', body: JSON.stringify(skills) });
	}

	// Profile history (CLC row 01) — read-only, over the existing audit
	// log; see RosterService.WorkerHistory's doc comment.
	async getRosterWorkerHistory(id: number, opts: { start?: number; limit?: number } = {}) {
		return this.request(`/roster/workers/${id}/history${toQuery(opts)}`);
	}

	// Designated-schedule versions (CLC row 07).
	async getRosterWorkerDesignatedSchedules(id: number) {
		return this.request(`/roster/workers/${id}/designated-schedules`);
	}

	async addRosterWorkerDesignatedSchedule(id: number, data: Record<string, unknown>) {
		return this.request(`/roster/workers/${id}/designated-schedules`, { method: 'POST', body: JSON.stringify(data) });
	}

	// Roster W4b — the Contact & HR tab, only ever reachable when the
	// partner has opted into hr_fields_enabled (server-enforced).
	async updateRosterWorkerHR(id: number, hr: Record<string, unknown>) {
		return this.request(`/roster/workers/${id}/hr`, { method: 'PUT', body: JSON.stringify(hr) });
	}

	async getRosterSkills(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/skills${toQuery(filter)}`);
	}

	async createRosterSkill(data: Record<string, unknown>) {
		return this.request('/roster/skills', { method: 'POST', body: JSON.stringify(data) });
	}

	async updateRosterSkill(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/skills/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	// Roster people import (the setup pathway's "Import people" step):
	// upload proposes a mapping + previews (writes nothing); commit writes
	// exactly what the confirmed mapping resolves to. Multipart, like
	// importPreserveCSV above — this.request always forces
	// Content-Type: application/json, which would break the multipart
	// boundary, so these bypass it.
	private async postRosterPeopleImportForm(path: string, file: File, mapping?: RosterPeopleImportMapping, partnerId?: number) {
		const form = new FormData();
		form.append('file', file);
		if (mapping) form.append('mapping', JSON.stringify(mapping));
		if (partnerId) form.append('partner_id', String(partnerId));
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}${path}`, { method: 'POST', headers, body: form });
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `Request failed (${resp.status})`);
		}
		return resp.json();
	}

	async previewRosterPeopleImport(file: File, mapping?: RosterPeopleImportMapping, partnerId?: number) {
		return this.postRosterPeopleImportForm('/roster/people-import/preview', file, mapping, partnerId);
	}

	async commitRosterPeopleImport(file: File, mapping: RosterPeopleImportMapping, partnerId?: number) {
		return this.postRosterPeopleImportForm('/roster/people-import/commit', file, mapping, partnerId);
	}

	// Roster schedule-workbook import (wave 17 screen): same multipart
	// shape as people import above, plus the year/month the workbook
	// covers, which the backend needs before it can read a single cell.
	private async postRosterScheduleImportForm(
		path: string,
		file: File,
		year: number,
		month: number,
		mapping?: RosterScheduleImportMapping,
		partnerId?: number,
	) {
		const form = new FormData();
		form.append('file', file);
		form.append('year', String(year));
		form.append('month', String(month));
		if (mapping) form.append('mapping', JSON.stringify(mapping));
		if (partnerId) form.append('partner_id', String(partnerId));
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}${path}`, { method: 'POST', headers, body: form });
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `Request failed (${resp.status})`);
		}
		return resp.json();
	}

	async previewRosterScheduleImport(file: File, year: number, month: number, mapping?: RosterScheduleImportMapping, partnerId?: number) {
		return this.postRosterScheduleImportForm('/roster/schedule-import/preview', file, year, month, mapping, partnerId);
	}

	async commitRosterScheduleImport(file: File, year: number, month: number, mapping: RosterScheduleImportMapping, partnerId?: number) {
		return this.postRosterScheduleImportForm('/roster/schedule-import/commit', file, year, month, mapping, partnerId);
	}

	async getRosterScheduleImportRuns(partnerId?: number) {
		return this.request(`/roster/schedule-import/runs${toQuery({ partner_id: partnerId })}`);
	}

	// Schedule-import vocabulary admin (wave 20, CLC row 30): the same
	// code-mapping/location-alias/PTO tables above, readable and
	// editable ahead of a first upload. putRosterScheduleVocabulary is a
	// PARTIAL patch: only the codes/aliases present are upserted.
	async getRosterScheduleVocabulary(partnerId?: number) {
		return this.request(`/roster/schedule-import/vocabulary${toQuery({ partner_id: partnerId })}`);
	}

	async putRosterScheduleVocabulary(body: Record<string, unknown>) {
		return this.request('/roster/schedule-import/vocabulary', { method: 'PUT', body: JSON.stringify(body) });
	}

	async deleteRosterScheduleVocabularyCode(code: string, partnerId?: number) {
		return this.request(`/roster/schedule-import/vocabulary/codes/${encodeURIComponent(code)}${toQuery({ partner_id: partnerId })}`, {
			method: 'DELETE',
		});
	}

	async deleteRosterScheduleVocabularyAlias(alias: string, partnerId?: number) {
		return this.request(`/roster/schedule-import/vocabulary/aliases/${encodeURIComponent(alias)}${toQuery({ partner_id: partnerId })}`, {
			method: 'DELETE',
		});
	}

	// Style mappings (wave 22, CLC rows 30/43): a fill colour or the
	// strikethrough flag taught a meaning, the same vocabulary shape as
	// codes/aliases above. signalValue is "" for the strikethrough
	// signal.
	async deleteRosterScheduleVocabularyStyleMapping(signalKind: string, signalValue: string, partnerId?: number) {
		return this.request(
			`/roster/schedule-import/vocabulary/styles/${encodeURIComponent(signalKind)}${toQuery({ value: signalValue, partner_id: partnerId })}`,
			{ method: 'DELETE' },
		);
	}

	async getRosterShifts(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/shifts${toQuery(filter)}`);
	}

	// Retrofit R3 — the real per-week publish-state row (branch_id/from/to).
	async getRosterWeeks(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/weeks${toQuery(filter)}`);
	}

	async createRosterShift(data: Record<string, unknown>) {
		return this.request('/roster/shifts', { method: 'POST', body: JSON.stringify(data) });
	}

	async updateRosterShift(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/shifts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	// FR-60 per-shift history (read-only): who changed what, when.
	async getRosterShiftHistory(id: number) {
		return this.request(`/roster/shifts/${id}/history`);
	}

	// Row 60 live conflict awareness: a light poll of current state (and
	// who last changed it) for the shifts an open editor or fill panel
	// still cares about — so a conflict is noticed before a save is
	// attempted, not only on its 409.
	async getRosterShiftsLive(ids: number[]) {
		return this.request(`/roster/shifts/live${toQuery({ ids: ids.join(',') })}`);
	}

	async deleteRosterShift(id: number) {
		return this.request(`/roster/shifts/${id}`, { method: 'DELETE' });
	}

	// ack mirrors publishRosterRange's own contract (ack/flag_count/reason)
	// rather than a second shape. wave 30 item 3: single-shift publish
	// consults the same publishing-policy gate a week publish does.
	async publishRosterShift(id: number, ack?: { flagCount?: number; reason: string }) {
		const query = ack ? toQuery({ ack: true, flag_count: ack.flagCount ?? 0, reason: ack.reason }) : '';
		return this.request(`/roster/shifts/${id}/publish${query}`, { method: 'POST' });
	}

	// The open-shift broadcast's re-notify + the Open Shifts board's own
	// telemetry (roster W4c).
	async renotifyRosterOpenShift(id: number) {
		return this.request(`/roster/shifts/${id}/renotify`, { method: 'POST' });
	}

	async getRosterOpenShiftTelemetry(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/open-shifts/telemetry${toQuery(filter)}`);
	}

	// Targeted Ask (ledger R-F) — asks one named worker about an open
	// shift, distinct from Assign and from the open-shift broadcast: the
	// shift stays open until claimed or filled regardless of the outcome.
	async askRosterShiftWorker(id: number, workerId: number) {
		return this.request(`/roster/shifts/${id}/ask`, { method: 'POST', body: JSON.stringify({ worker_id: workerId }) });
	}

	// The dashboard's narrative Alerts & Actions feed (roster W4c).
	async getRosterAlerts(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/alerts${toQuery(filter)}`);
	}

	async publishRosterRange(filter: Record<string, unknown>) {
		return this.request(`/roster/publish${toQuery(filter)}`, { method: 'POST' });
	}

	// Weekly exception digest (FR-64) — computed fresh every GET; PATCH
	// saves a manager's edits without sending; POST /send queues the
	// current body (excluded_keys + manager_note) through the Hub.
	async getRosterWeekDigest(filter: Record<string, unknown>) {
		return this.request(`/roster/digest${toQuery(filter)}`);
	}

	async saveRosterWeekDigestDraft(filter: Record<string, unknown>, body: { excluded_keys: string[]; manager_note: string }) {
		return this.request(`/roster/digest${toQuery(filter)}`, { method: 'PATCH', body: JSON.stringify(body) });
	}

	async sendRosterWeekDigest(filter: Record<string, unknown>, body: { excluded_keys: string[]; manager_note: string }) {
		return this.request(`/roster/digest/send${toQuery(filter)}`, { method: 'POST', body: JSON.stringify(body) });
	}

	async copyRosterWeek(filter: Record<string, unknown>) {
		return this.request(`/roster/copy-week${toQuery(filter)}`, { method: 'POST' });
	}

	// Desk-across-days copy (CLC rows 39/72) — a service point's shifts
	// for a day (or a week) copied onto other chosen days (or weeks).
	async copyRosterDesk(body: Record<string, unknown>) {
		return this.request('/roster/copy-desk', { method: 'POST', body: JSON.stringify(body) });
	}

	// Review flag (CLC row 72): clearing one copied/generated shift by
	// hand, or the week header's "Mark all reviewed" bulk action.
	async markRosterShiftReviewed(id: number) {
		return this.request(`/roster/shifts/${id}/mark-reviewed`, { method: 'POST' });
	}

	async markRosterWeekReviewed(filter: Record<string, unknown>) {
		return this.request(`/roster/plan/mark-reviewed${toQuery(filter)}`, { method: 'POST' });
	}

	// TEMPLATES WIRE BEGIN - roster week templates (fleet/roster-templates-b).
	// Pinned-guess wire contract; the backend seat (fleet/roster-templates-a)
	// builds these against the same spec but the exact method names / DTO
	// nesting may shift before merge - reconcile against the real handlers.
	async listRosterTemplates(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/templates${toQuery(filter)}`);
	}

	async createRosterTemplateFromWeek(data: Record<string, unknown>) {
		return this.request('/roster/templates/from-week', { method: 'POST', body: JSON.stringify(data) });
	}

	async applyRosterTemplate(id: number, data: Record<string, unknown>) {
		return this.request(`/roster/templates/${id}/apply`, { method: 'POST', body: JSON.stringify(data) });
	}

	async renameRosterTemplate(id: number, name: string) {
		return this.request(`/roster/templates/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) });
	}

	async deleteRosterTemplate(id: number) {
		return this.request(`/roster/templates/${id}`, { method: 'DELETE' });
	}
	// TEMPLATES WIRE END

	// ROTATIONS (roster-rotations-spec.md, phases 1-2): a library-defined
	// cycle of N weeks, applied forward the same way a week template is.
	// No UI consumes these yet (phase 3); the setup step and admin screen
	// are a later stage.
	async listRosterRotations(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/rotations${toQuery(filter)}`);
	}

	async createRosterRotation(data: Record<string, unknown>) {
		return this.request('/roster/rotations', { method: 'POST', body: JSON.stringify(data) });
	}

	async getRosterRotation(id: number) {
		return this.request(`/roster/rotations/${id}`);
	}

	async updateRosterRotation(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/rotations/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteRosterRotation(id: number) {
		return this.request(`/roster/rotations/${id}`, { method: 'DELETE' });
	}

	async applyRosterRotation(id: number, data: Record<string, unknown>) {
		return this.request(`/roster/rotations/${id}/apply`, { method: 'POST', body: JSON.stringify(data) });
	}

	// Row 3: rewrite one rotation week's shifts after creation.
	async replaceRosterRotationWeekShifts(id: number, weekIndex: number, shifts: unknown[]) {
		return this.request(`/roster/rotations/${id}/weeks/${weekIndex}`, {
			method: 'PUT',
			body: JSON.stringify({ shifts }),
		});
	}

	async getRosterRotationWeekIndex(id: number, workerId: number, date: string) {
		return this.request(`/roster/rotations/${id}/week-index${toQuery({ worker_id: workerId, date })}`);
	}

	async assignRosterRotationWorker(id: number, data: Record<string, unknown>) {
		return this.request(`/roster/rotations/${id}/workers`, { method: 'POST', body: JSON.stringify(data) });
	}

	async listRosterRotationWorkers(id: number) {
		return this.request(`/roster/rotations/${id}/workers`);
	}

	async removeRosterRotationWorker(id: number, assignmentId: number) {
		return this.request(`/roster/rotations/${id}/workers/${assignmentId}`, { method: 'DELETE' });
	}

	// CLC requirement record rows 3/19: a worker's fairness offset into
	// the rotation's OWN sub-pattern, patched independently of their base
	// assignment (offsetWeeks null clears it).
	async setRosterRotationWorkerSubPatternOffset(id: number, assignmentId: number, offsetWeeks: number | null) {
		return this.request(`/roster/rotations/${id}/workers/${assignmentId}/sub-pattern-offset`, {
			method: 'PATCH',
			body: JSON.stringify({ sub_pattern_offset_weeks: offsetWeeks }),
		});
	}

	// A rotation's optional second, independently-clocked pattern over a
	// subset of its weekdays (CLC requirement record rows 3/19). Full
	// rewrite, the same shape replaceRosterRotationWeekShifts uses.
	async replaceRosterRotationSubPattern(id: number, data: Record<string, unknown>) {
		return this.request(`/roster/rotations/${id}/sub-pattern`, { method: 'PUT', body: JSON.stringify(data) });
	}

	async removeRosterRotationSubPattern(id: number) {
		return this.request(`/roster/rotations/${id}/sub-pattern`, { method: 'DELETE' });
	}

	// What the base pattern and its sub-pattern would produce together
	// over a date range, before anything is applied.
	async previewRosterRotationComposite(id: number, from: string, weeks: number) {
		return this.request(`/roster/rotations/${id}/preview${toQuery({ from, weeks })}`);
	}

	// A rotation's own linked-day rules (CLC row 3), distinct from the
	// partner-wide ones on the compliance rule pack.
	async listRosterRotationLinkedDayPairs(id: number) {
		return this.request(`/roster/rotations/${id}/linked-day-pairs`);
	}

	async replaceRosterRotationLinkedDayPairs(id: number, pairs: unknown[]) {
		return this.request(`/roster/rotations/${id}/linked-day-pairs`, {
			method: 'PUT',
			body: JSON.stringify({ pairs }),
		});
	}

	async proposeRosterRotationOffsets(cycleWeeks: number, workerIds: number[]) {
		return this.request('/roster/rotations/propose-offsets', {
			method: 'POST',
			body: JSON.stringify({ cycle_weeks: cycleWeeks, worker_ids: workerIds }),
		});
	}

	async replaceRosterShiftAssignments(id: number, assignments: Record<string, unknown>[]) {
		return this.request(`/roster/shifts/${id}/assignments`, { method: 'PUT', body: JSON.stringify(assignments) });
	}

	// Exception-aware generation (CLC row 67): the persisted run/exception
	// trail from a week-template or rotation Apply, kept for a manager to
	// return to from the Plan space.
	async listRosterGenerationRuns(branchId: number) {
		return this.request(`/roster/generation-runs${toQuery({ branch_id: branchId })}`);
	}

	async getRosterGenerationRunExceptions(runId: number) {
		return this.request(`/roster/generation-runs/${runId}/exceptions`);
	}

	async resolveRosterGenerationException(id: number) {
		return this.request(`/roster/generation-exceptions/${id}/resolve`, { method: 'POST' });
	}

	// Worker transfers (CLC row 67): a dated home-branch change generation
	// consults so it can open the position at the old branch.
	async listRosterWorkerTransfers(workerId: number) {
		return this.request(`/roster/workers/${workerId}/transfers`);
	}

	async createRosterWorkerTransfer(workerId: number, data: Record<string, unknown>) {
		return this.request(`/roster/workers/${workerId}/transfers`, { method: 'POST', body: JSON.stringify(data) });
	}

	// Per-branch staffing target override (CLC row 68).
	async listRosterCoverageTargets(partnerId?: number) {
		return this.request(`/roster/coverage-targets${toQuery(partnerId ? { partner_id: partnerId } : {})}`);
	}

	async setRosterCoverageTarget(branchId: number, targetPct: number | null) {
		return this.request('/roster/coverage-targets', {
			method: 'PUT',
			body: JSON.stringify({ branch_id: branchId, target_pct: targetPct }),
		});
	}

	// Live workload counters by category (CLC row 68).
	async getRosterCoverageCounters(branchId: number, date: string) {
		return this.request(`/roster/coverage/counters${toQuery({ branch_id: branchId, date })}`);
	}

	// Breaks (roster-breaks-spec.md phase 1: model + coverage, no
	// placement UI yet). All three return the parent shift with its
	// current breaks composed, the assignments-endpoint convention.
	async placeRosterBreak(shiftId: number, data: Record<string, unknown>) {
		return this.request(`/roster/shifts/${shiftId}/breaks`, { method: 'POST', body: JSON.stringify(data) });
	}

	async moveRosterBreak(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/breaks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async removeRosterBreak(id: number) {
		return this.request(`/roster/breaks/${id}`, { method: 'DELETE' });
	}

	async getRosterShiftClaims(id: number) {
		return this.request(`/roster/shifts/${id}/claims`);
	}

	async createRosterClaim(shiftId: number, data: Record<string, unknown>) {
		return this.request(`/roster/shifts/${shiftId}/claims`, { method: 'POST', body: JSON.stringify(data) });
	}

	async approveRosterClaim(id: number) {
		return this.request(`/roster/claims/${id}/approve`, { method: 'POST' });
	}

	async declineRosterClaim(id: number) {
		return this.request(`/roster/claims/${id}/decline`, { method: 'POST' });
	}

	async getRosterLeaveTypes(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/leave-types${toQuery(filter)}`);
	}

	async createRosterLeaveType(data: Record<string, unknown>) {
		return this.request('/roster/leave-types', { method: 'POST', body: JSON.stringify(data) });
	}

	async updateRosterLeaveType(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/leave-types/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async getRosterLeaveRequests(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/leave-requests${toQuery(filter)}`);
	}

	async createRosterLeaveRequest(data: Record<string, unknown>) {
		return this.request('/roster/leave-requests', { method: 'POST', body: JSON.stringify(data) });
	}

	async decideRosterLeaveRequest(id: number, decision: 'approve' | 'decline' | 'cancel') {
		return this.request(`/roster/leave-requests/${id}/${decision}`, { method: 'POST' });
	}

	// CLC row 61 — balance verification: flips awaiting_verification
	// back to pending so a manager's normal decision can proceed.
	async verifyRosterLeaveRequest(id: number, body: { verified_balance_hours?: number | null; note?: string }) {
		return this.request(`/roster/leave-requests/${id}/verify`, { method: 'POST', body: JSON.stringify(body) });
	}

	// CLC row 06 — HR entering leave that already happened.
	async recordRosterLeaveRequest(data: Record<string, unknown>) {
		return this.request('/roster/leave-requests/record', { method: 'POST', body: JSON.stringify(data) });
	}

	// CLC row 06 — the WORKER's own withdraw, only while pending or
	// awaiting_verification.
	async withdrawMyRosterLeaveRequest(id: number) {
		return this.request(`/roster/me/leave-requests/${id}/withdraw`, { method: 'POST' });
	}

	// CLC row 61 — leave attachments. Manager/verifier surface only for
	// list/download; upload is offered both here and on /me.
	async getRosterLeaveAttachments(leaveRequestId: number) {
		return this.request(`/roster/leave-requests/${leaveRequestId}/attachments`);
	}

	// Wave 10 — the requester's own read (needed to know WHICH file
	// they're removing); sees only their own request's attachments.
	async getMyRosterLeaveAttachments(leaveRequestId: number) {
		return this.request(`/roster/me/leave-requests/${leaveRequestId}/attachments`);
	}

	async uploadRosterLeaveAttachment(leaveRequestId: number, file: File, ownRequest = false) {
		const form = new FormData();
		form.append('file', file);
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const path = ownRequest
			? `/roster/me/leave-requests/${leaveRequestId}/attachments`
			: `/roster/leave-requests/${leaveRequestId}/attachments`;
		const resp = await fetch(`${API_BASE}${path}`, { method: 'POST', headers, body: form });
		if (!resp.ok) {
			const text = await resp.text();
			throw new ApiError(resp.status, text || `Upload failed (${resp.status})`);
		}
		return resp.json();
	}

	async downloadRosterLeaveAttachment(leaveRequestId: number, attachmentId: number, fallbackFilename: string) {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(
			`${API_BASE}/roster/leave-requests/${leaveRequestId}/attachments/${attachmentId}/download`,
			{ headers },
		);
		if (!resp.ok) {
			const text = await resp.text();
			throw new ApiError(resp.status, text || `Download failed (${resp.status})`);
		}
		let filename = fallbackFilename;
		const disp = resp.headers.get('Content-Disposition') || '';
		const match = disp.match(/filename="?([^";]+)"?/i);
		if (match) filename = match[1];
		const blob = await resp.blob();
		const url = URL.createObjectURL(blob);
		try {
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
		} finally {
			// Revoke after a beat to let the browser kick off the save;
			// revoking too early aborts the download in some browsers.
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		}
	}

	// CLC row 61, wave 10 — deleting an attachment. The requester's own
	// route (ownRequest=true) only succeeds while the request is still
	// pending or awaiting verification; the manager/verifier route
	// (ownRequest=false) works at any status.
	async deleteRosterLeaveAttachment(leaveRequestId: number, attachmentId: number, ownRequest = false) {
		const path = ownRequest
			? `/roster/me/leave-requests/${leaveRequestId}/attachments/${attachmentId}`
			: `/roster/leave-requests/${leaveRequestId}/attachments/${attachmentId}`;
		return this.request(path, { method: 'DELETE' });
	}

	// CLC row 61: library-tracked leave balances (HR's own read/edit;
	// the worker's own balances ride GET /roster/me instead).
	async getRosterWorkerLeaveBalances(workerId: number) {
		return this.request(`/roster/workers/${workerId}/leave-balances`);
	}

	async setRosterWorkerLeaveBalance(workerId: number, leaveTypeId: number, openingBalanceDays: number) {
		return this.request(`/roster/workers/${workerId}/leave-balances/${leaveTypeId}`, {
			method: 'PUT',
			body: JSON.stringify({ opening_balance_days: openingBalanceDays }),
		});
	}

	async getRosterWorkerLeaveBalanceAdjustments(workerId: number, leaveTypeId: number) {
		return this.request(`/roster/workers/${workerId}/leave-balances/${leaveTypeId}/adjustments`);
	}

	async createRosterWorkerLeaveBalanceAdjustment(
		workerId: number,
		leaveTypeId: number,
		data: { kind: 'accrual' | 'correction'; amount_days: number; note?: string; effective_on: string },
	) {
		return this.request(`/roster/workers/${workerId}/leave-balances/${leaveTypeId}/adjustments`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	// CLC row 46 — linked-day pairs.
	async getRosterLinkedDayPairs(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/linked-day-pairs${toQuery(filter)}`);
	}

	async createRosterLinkedDayPair(filter: Record<string, unknown>, data: Record<string, unknown>) {
		return this.request(`/roster/linked-day-pairs${toQuery(filter)}`, { method: 'POST', body: JSON.stringify(data) });
	}

	async deleteRosterLinkedDayPair(id: number) {
		return this.request(`/roster/linked-day-pairs/${id}`, { method: 'DELETE' });
	}

	// Roster manager R2 — call-in absences (a RECORDED fact, distinct
	// from a leave request's planned ask).
	async getRosterAbsences(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/absences${toQuery(filter)}`);
	}

	async createRosterAbsence(data: Record<string, unknown>) {
		return this.request('/roster/absences', { method: 'POST', body: JSON.stringify(data) });
	}

	// Limited-time-off days (leavelimit-w26, CLC row 30): a plain
	// per-branch, per-date marker, advisory only.
	async getRosterLimitedTimeOffDays(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/limited-time-off-days${toQuery(filter)}`);
	}

	async createRosterLimitedTimeOffDay(data: Record<string, unknown>) {
		return this.request('/roster/limited-time-off-days', { method: 'POST', body: JSON.stringify(data) });
	}

	async updateRosterLimitedTimeOffDay(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/limited-time-off-days/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteRosterLimitedTimeOffDay(id: number) {
		return this.request(`/roster/limited-time-off-days/${id}`, { method: 'DELETE' });
	}

	async getRosterComplianceFlags(filter: Record<string, unknown>) {
		return this.request(`/roster/compliance/flags${toQuery(filter)}`);
	}

	// Roster commitments spine (Plan's commitments rail — roster-rethink §2).
	async getRosterCommitments(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/commitments${toQuery(filter)}`);
	}

	async createRosterCommitment(data: Record<string, unknown>) {
		return this.request('/roster/commitments', { method: 'POST', body: JSON.stringify(data) });
	}

	async updateRosterCommitment(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/commitments/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	// FR-53: cause is the required structured reason (see
	// RosterCommitmentChangeCause), reason an optional free-text note.
	async cancelRosterCommitment(id: number, data: Record<string, unknown>) {
		return this.request(`/roster/commitments/${id}/cancel`, { method: 'POST', body: JSON.stringify(data) });
	}

	// Moves a commitment to a new time (and optionally branch) IN PLACE —
	// same id, never a new commitment. See RosterCommitmentReschedule.
	async rescheduleRosterCommitment(id: number, data: Record<string, unknown>) {
		return this.request(`/roster/commitments/${id}/reschedule`, { method: 'POST', body: JSON.stringify(data) });
	}

	async getRosterCommitment(id: number) {
		return this.request(`/roster/commitments/${id}`);
	}

	async replaceRosterCommitmentWorkers(id: number, workers: Record<string, unknown>[]) {
		return this.request(`/roster/commitments/${id}/workers`, { method: 'PUT', body: JSON.stringify(workers) });
	}

	async importAttendCommitments(data: Record<string, unknown>) {
		return this.request('/roster/commitments/import/attend', { method: 'POST', body: JSON.stringify(data) });
	}

	// FR-66: how many appointment slots a branch can offer, from its own
	// staffing — a read a booking surface could consume.
	async getRosterAppointmentCapacity(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/coverage/appointment-capacity${toQuery(filter)}`);
	}

	async getRosterCommitmentCutoffPolicy(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/commitment-cutoff-policy${toQuery(filter)}`);
	}

	async putRosterCommitmentCutoffPolicy(body: Record<string, unknown>) {
		return this.request('/roster/commitment-cutoff-policy', { method: 'PUT', body: JSON.stringify(body) });
	}

	// FR-52 — honest re-imports: a re-imported field that disagrees with
	// a manager's own edit lands here instead of silently overwriting it.
	async getRosterCommitmentImportDisagreements(id: number) {
		return this.request(`/roster/commitments/${id}/import-disagreements`);
	}

	async resolveRosterCommitmentImportDisagreement(id: number, disagreementId: number, resolution: 'theirs' | 'ours') {
		return this.request(`/roster/commitments/${id}/import-disagreements/${disagreementId}/resolve`, {
			method: 'POST',
			body: JSON.stringify({ resolution }),
		});
	}

	// FR-62 — staff-submitted commitment requests.
	async requestMyCommitment(data: Record<string, unknown>) {
		return this.request('/roster/me/commitment-requests', { method: 'POST', body: JSON.stringify(data) });
	}

	async approveRosterCommitmentRequest(id: number) {
		return this.request(`/roster/commitments/${id}/approve-request`, { method: 'POST' });
	}

	async declineRosterCommitmentRequest(id: number, reason: string) {
		return this.request(`/roster/commitments/${id}/decline-request`, { method: 'POST', body: JSON.stringify({ reason }) });
	}

	// CLC row 66 — appointment capacity as a rule, beside the desk settings.
	async getRosterAppointmentCapacityPolicy(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/appointment-capacity-policy${toQuery(filter)}`);
	}

	async putRosterAppointmentCapacityPolicy(body: Record<string, unknown>) {
		return this.request('/roster/appointment-capacity-policy', { method: 'PUT', body: JSON.stringify(body) });
	}

	// CLC row 66 — every rule configured for a branch (the branch-wide
	// one plus any per-desk ones).
	async getRosterAppointmentCapacityPolicies(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/appointment-capacity-policies${toQuery(filter)}`);
	}

	// CLC row 66: remove one desk's own rule, reverting it to the
	// branch-wide one.
	async deleteRosterAppointmentCapacityPolicy(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/appointment-capacity-policy${toQuery(filter)}`, { method: 'DELETE' });
	}

	// Off-desk time targets (CLC row 16).
	async getRosterOffDeskClassificationDefaults(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/offdesk-classification-defaults${toQuery(filter)}`);
	}

	async putRosterOffDeskClassificationDefault(body: Record<string, unknown>) {
		return this.request('/roster/offdesk-classification-defaults', { method: 'PUT', body: JSON.stringify(body) });
	}

	async deleteRosterOffDeskClassificationDefault(employmentBasis: string, filter: Record<string, unknown> = {}) {
		return this.request(`/roster/offdesk-classification-defaults/${employmentBasis}${toQuery(filter)}`, { method: 'DELETE' });
	}

	async scheduleRosterOffDeskTime(body: Record<string, unknown>) {
		return this.request('/roster/schedule-offdesk-time', { method: 'POST', body: JSON.stringify(body) });
	}

	// Schedule horizons + locks (CLC row 47).
	async getRosterScheduleHorizonPolicy(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/schedule-horizon-policy${toQuery(filter)}`);
	}

	async putRosterScheduleHorizonPolicy(body: Record<string, unknown>) {
		return this.request('/roster/schedule-horizon-policy', { method: 'PUT', body: JSON.stringify(body) });
	}

	async getRosterWeekHorizonStatus(filter: Record<string, unknown>) {
		return this.request(`/roster/schedule-horizon-status${toQuery(filter)}`);
	}

	// Travel-time policy (FR-34, bounded).
	async getRosterTravelTimePolicy(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/travel-time-policy${toQuery(filter)}`);
	}

	async putRosterTravelTimePolicy(body: Record<string, unknown>) {
		return this.request('/roster/travel-time-policy', { method: 'PUT', body: JSON.stringify(body) });
	}

	// Visiting-shift approval (FR-34, bounded).
	async approveRosterVisitingShift(shiftId: number) {
		return this.request(`/roster/shifts/${shiftId}/approve-visit`, { method: 'POST' });
	}

	// Publication versions (CLC rows 23, 48, 71).
	async getRosterWeekPublications(filter: Record<string, unknown>) {
		return this.request(`/roster/week-publications${toQuery(filter)}`);
	}

	async getRosterWeekPublicationDiff(id: number) {
		return this.request(`/roster/week-publications/${id}/diff`);
	}

	async restoreRosterWeekPublication(id: number, reason: string) {
		return this.request(`/roster/week-publications/${id}/restore`, { method: 'POST', body: JSON.stringify({ reason }) });
	}

	// PDF exports (CLC row 27).
	async getRosterWeekSheetPDF(branchId: number, weekStart: string, versionId?: number, groupByTeam?: boolean): Promise<Blob> {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const params = new URLSearchParams();
		if (versionId) params.set('version_id', String(versionId));
		if (groupByTeam) params.set('group_by_team', 'true');
		const qs = params.toString() ? `?${params.toString()}` : '';
		const resp = await fetch(`${API_BASE}/roster/weeks/${branchId}/${weekStart}/print.pdf${qs}`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `Week sheet PDF failed (${resp.status})`);
		return resp.blob();
	}

	async getRosterMySchedulePDF(from: string, to: string): Promise<Blob> {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/roster/me/schedule.pdf?from=${from}&to=${to}`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `Schedule PDF failed (${resp.status})`);
		return resp.blob();
	}

	// Digest recipients + send history (CLC row 64).
	async getRosterDigestRecipientPolicy(filter: Record<string, unknown>) {
		return this.request(`/roster/digest-recipients${toQuery(filter)}`);
	}

	async putRosterDigestRecipientPolicy(body: Record<string, unknown>) {
		return this.request('/roster/digest-recipients', { method: 'PUT', body: JSON.stringify(body) });
	}

	async getRosterDigestSends(filter: Record<string, unknown>) {
		return this.request(`/roster/digest-sends${toQuery(filter)}`);
	}

	async overrideRosterFlag(id: number, reason: string) {
		return this.request(`/roster/compliance/flags/${id}/override`, {
			method: 'POST',
			body: JSON.stringify({ reason }),
		});
	}

	async reevaluateRosterCompliance(filter: Record<string, unknown>) {
		return this.request(`/roster/compliance/reevaluate${toQuery(filter)}`, { method: 'POST' });
	}

	async getRosterCoverageDay(filter: Record<string, unknown>) {
		return this.request(`/roster/coverage/day${toQuery(filter)}`);
	}

	async getRosterCoverageRange(filter: Record<string, unknown>) {
		return this.request(`/roster/coverage/range${toQuery(filter)}`);
	}

	async getRosterCoverageNow(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/coverage/now${toQuery(filter)}`);
	}

	async getRosterCoverCandidates(filter: Record<string, unknown>) {
		return this.request(`/roster/cover-candidates${toQuery(filter)}`);
	}

	// Repair plans (row 44) + same-day mode (row 58).
	async getRosterRepairPlans(filter: Record<string, unknown>) {
		return this.request(`/roster/repair-plans${toQuery(filter)}`);
	}

	async applyRosterRepairPlan(body: Record<string, unknown>) {
		return this.request('/roster/repair-plans/apply', { method: 'POST', body: JSON.stringify(body) });
	}

	async getRosterSameDayModeStatus(filter: Record<string, unknown>) {
		return this.request(`/roster/same-day/status${toQuery(filter)}`);
	}

	async getRosterCoverageRules(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/coverage-rules${toQuery(filter)}`);
	}

	async createRosterCoverageRule(body: Record<string, unknown>) {
		return this.request('/roster/coverage-rules', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateRosterCoverageRule(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/coverage-rules/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteRosterCoverageRule(id: number) {
		return this.request(`/roster/coverage-rules/${id}`, { method: 'DELETE' });
	}

	// Commitment-driven coverage tiers (CLC requirement record row 43).
	async getRosterCommitmentCoverageTiers(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/commitment-coverage-tiers${toQuery(filter)}`);
	}

	async createRosterCommitmentCoverageTier(body: Record<string, unknown>) {
		return this.request('/roster/commitment-coverage-tiers', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateRosterCommitmentCoverageTier(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/commitment-coverage-tiers/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteRosterCommitmentCoverageTier(id: number) {
		return this.request(`/roster/commitment-coverage-tiers/${id}`, { method: 'DELETE' });
	}

	// Staffing priorities (FR-45) — a branch's declared understaffed-mode
	// cut order + per-level minimums.
	async getRosterStaffingPriorities(filter: Record<string, unknown>) {
		return this.request(`/roster/staffing-priorities${toQuery(filter)}`);
	}

	async replaceRosterStaffingPriorities(body: Record<string, unknown>) {
		return this.request('/roster/staffing-priorities', { method: 'PUT', body: JSON.stringify(body) });
	}

	// The library-wide default plan (row 45) a branch without its own
	// plan follows — same admin screen, one level up.
	async getRosterDefaultStaffingPriorities(filter: Record<string, unknown>) {
		return this.request(`/roster/staffing-priorities/default${toQuery(filter)}`);
	}

	async replaceRosterDefaultStaffingPriorities(body: Record<string, unknown>) {
		return this.request('/roster/staffing-priorities/default', { method: 'PUT', body: JSON.stringify(body) });
	}

	// Reduced service / emergency marking (row 71), set on an already-
	// published week without republishing.
	async setRosterWeekServiceLevel(branchId: number, weekStart: string, body: Record<string, unknown>) {
		return this.request(`/roster/weeks/${branchId}/${weekStart}/service-level`, { method: 'POST', body: JSON.stringify(body) });
	}

	async getRosterOperationalRoles(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/operational-roles${toQuery(filter)}`);
	}

	async createRosterOperationalRole(body: Record<string, unknown>) {
		return this.request('/roster/operational-roles', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateRosterOperationalRole(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/operational-roles/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteRosterOperationalRole(id: number) {
		return this.request(`/roster/operational-roles/${id}`, { method: 'DELETE' });
	}

	async getRosterDepartments(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/departments${toQuery(filter)}`);
	}

	async createRosterDepartment(body: Record<string, unknown>) {
		return this.request('/roster/departments', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateRosterDepartment(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/departments/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async putRosterDepartmentManagers(id: number, body: Record<string, unknown>) {
		return this.request(`/roster/departments/${id}/managers`, { method: 'PUT', body: JSON.stringify(body) });
	}

	async getRosterTaskTagCategories() {
		return this.request('/roster/task-tags/categories');
	}

	async getRosterTaskTags(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/task-tags${toQuery(filter)}`);
	}

	async createRosterTaskTag(body: Record<string, unknown>) {
		return this.request('/roster/task-tags', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateRosterTaskTag(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/task-tags/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	// Saved Plan view (CLC row 04, FR-04).
	async putRosterDepartmentPlanView(id: number, body: Record<string, unknown>) {
		return this.request(`/roster/departments/${id}/plan-view`, { method: 'PUT', body: JSON.stringify(body) });
	}

	async getMyManagedRosterDepartmentPlanView(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/departments/my-plan-view${toQuery(filter)}`);
	}

	async getRosterSubPools(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/sub-pools${toQuery(filter)}`);
	}

	async createRosterSubPool(body: Record<string, unknown>) {
		return this.request('/roster/sub-pools', { method: 'POST', body: JSON.stringify(body) });
	}

	async getRosterSubPool(id: number) {
		return this.request(`/roster/sub-pools/${id}`);
	}

	async updateRosterSubPool(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/sub-pools/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteRosterSubPool(id: number) {
		return this.request(`/roster/sub-pools/${id}`, { method: 'DELETE' });
	}

	async addRosterSubPoolMember(poolId: number, body: Record<string, unknown>) {
		return this.request(`/roster/sub-pools/${poolId}/members`, { method: 'POST', body: JSON.stringify(body) });
	}

	async getRosterSubPoolMembers(poolId: number) {
		return this.request(`/roster/sub-pools/${poolId}/members`);
	}

	async updateRosterSubPoolMember(poolId: number, memberId: number, patch: Record<string, unknown>) {
		return this.request(`/roster/sub-pools/${poolId}/members/${memberId}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async removeRosterSubPoolMember(poolId: number, memberId: number) {
		return this.request(`/roster/sub-pools/${poolId}/members/${memberId}`, { method: 'DELETE' });
	}

	async getRosterPresenters(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/presenters${toQuery(filter)}`);
	}

	async createRosterPresenter(body: Record<string, unknown>) {
		return this.request('/roster/presenters', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateRosterPresenter(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/presenters/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async getRosterBookings(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/bookings${toQuery(filter)}`);
	}

	async getRosterBooking(id: number) {
		return this.request(`/roster/bookings/${id}`);
	}

	async createRosterBooking(body: Record<string, unknown>) {
		return this.request('/roster/bookings', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateRosterBooking(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/bookings/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async generateRosterInvoice(bookingId: number) {
		return this.request(`/roster/bookings/${bookingId}/invoice`, { method: 'POST' });
	}

	async transitionRosterInvoice(id: number, status: 'sent' | 'paid' | 'void') {
		return this.request(`/roster/invoices/${id}/${status}`, { method: 'POST' });
	}

	async getRosterInvoiceSettings(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/invoice-settings${toQuery(filter)}`);
	}

	async putRosterInvoiceSettings(body: Record<string, unknown>) {
		return this.request('/roster/invoice-settings', { method: 'PUT', body: JSON.stringify(body) });
	}

	// R5a — reports + cost visibility.
	async getRosterHoursReport(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/reports/hours${toQuery(filter)}`);
	}

	async getRosterOvertimeReport(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/reports/overtime${toQuery(filter)}`);
	}

	// Department rollup (FR-33 item 2).
	async getRosterDepartmentRollup(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/reports/department-hours${toQuery(filter)}`);
	}

	async getRosterLeaveReport(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/reports/leave${toQuery(filter)}`);
	}

	async getRosterCredentialExpiryReport(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/reports/credentials${toQuery(filter)}`);
	}

	// Workforce analytics (roster-rethink Insights space, Workforce tab).
	// Admin + hr_analytics_enabled gated server-side; a 403 means the
	// gate is off, not a broken read.
	async getRosterStabilityReport(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/reports/stability${toQuery(filter)}`);
	}

	async getRosterAbsenceReport(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/reports/absence${toQuery(filter)}`);
	}

	async getRosterPunctualityReport(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/reports/punctuality${toQuery(filter)}`);
	}

	// Workload percentage, weekend fairness, and change volume (CLC row
	// 28) - same double gate as the three reports above.
	async getRosterWorkloadReport(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/reports/workload${toQuery(filter)}`);
	}

	async getRosterWeekendFairnessReport(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/reports/weekend-fairness${toQuery(filter)}`);
	}

	async getRosterChangeVolumeReport(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/reports/change-volume${toQuery(filter)}`);
	}

	async getRosterSettings(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/settings${toQuery(filter)}`);
	}

	// Presentation-only subset (week start, departments, secondary
	// coverage, hours figures visibility) open to a day-to-day manager,
	// unlike getRosterSettings above (administrator-only).
	async getRosterPresentationSettings(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/settings/presentation${toQuery(filter)}`);
	}

	async putRosterSettings(body: Record<string, unknown>) {
		return this.request('/roster/settings', { method: 'PUT', body: JSON.stringify(body) });
	}

	// Bearer-fetched CSV (the prospect-CSV / getDashboardTileCSV precedent
	// — the token lives in sessionStorage, so a plain <a href> would 401).
	private async _rosterReportCSV(path: string, params: Record<string, unknown>): Promise<Blob> {
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers['Authorization'] = `Bearer ${tok}`;
		const resp = await fetch(`${API_BASE}${path}${toQuery(params)}`, { headers });
		if (!resp.ok) {
			// A 422 here (e.g. the payroll export's refused-workers guard)
			// carries the actual reason in the body — never collapse it to
			// a bare status code the way a generic failure would.
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `CSV export failed (${resp.status})`);
		}
		return resp.blob();
	}

	async getRosterHoursReportCSV(filter: Record<string, unknown> = {}): Promise<Blob> {
		return this._rosterReportCSV('/roster/reports/hours/export.csv', filter);
	}

	async getRosterOvertimeReportCSV(filter: Record<string, unknown> = {}): Promise<Blob> {
		return this._rosterReportCSV('/roster/reports/overtime/export.csv', filter);
	}

	async getRosterLeaveReportCSV(filter: Record<string, unknown> = {}): Promise<Blob> {
		return this._rosterReportCSV('/roster/reports/leave/export.csv', filter);
	}

	async getRosterCredentialExpiryReportCSV(filter: Record<string, unknown> = {}): Promise<Blob> {
		return this._rosterReportCSV('/roster/reports/credentials/export.csv', filter);
	}

	async getRosterWorkloadReportCSV(filter: Record<string, unknown> = {}): Promise<Blob> {
		return this._rosterReportCSV('/roster/reports/workload/export.csv', filter);
	}

	async getRosterWeekendFairnessReportCSV(filter: Record<string, unknown> = {}): Promise<Blob> {
		return this._rosterReportCSV('/roster/reports/weekend-fairness/export.csv', filter);
	}

	async getRosterChangeVolumeReportCSV(filter: Record<string, unknown> = {}): Promise<Blob> {
		return this._rosterReportCSV('/roster/reports/change-volume/export.csv', filter);
	}

	// Payroll pay-code export (Stage 7, second half). Both routes are
	// RequireAdminRole — a non-admin caller 403s (the same admin-only
	// surface as get/putRosterSettings, not the reports above).
	async getRosterPayrollSummary(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/payroll/summary${toQuery(filter)}`);
	}

	// The propose-before-download preview (FR-30/FR-31): who would be
	// included, how many hours, and who would be refused and why —
	// before a file exists.
	async getRosterPayrollPreview(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/payroll/preview${toQuery(filter)}`);
	}

	async getRosterPayrollExportCSV(filter: Record<string, unknown> = {}): Promise<Blob> {
		return this._rosterReportCSV('/roster/payroll/export.csv', filter);
	}

	async getRosterPayrollCodeMappings(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/payroll/code-mappings${toQuery(filter)}`);
	}

	async putRosterPayrollCodeMappings(body: Record<string, unknown>) {
		return this.request('/roster/payroll/code-mappings', { method: 'PUT', body: JSON.stringify(body) });
	}

	// Roster Stage 6a — worker <-> platform-login linkage (admin) + the
	// My Roster self-service surface for linked library staff.
	async getRosterLinkableUsers(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/linkable-users${toQuery(filter)}`);
	}

	async linkRosterWorkerLogin(workerId: number, userId: number) {
		return this.request(`/roster/workers/${workerId}/link-login`, {
			method: 'POST',
			body: JSON.stringify({ user_id: userId }),
		});
	}

	async unlinkRosterWorkerLogin(workerId: number) {
		return this.request(`/roster/workers/${workerId}/unlink-login`, { method: 'POST' });
	}

	// Roster R7 (§8.2) — issuing/rotating a worker's or branch's iCal
	// calendar-feed subscription URL. Both admin-curation (LinkWorkerLogin
	// gate); the caller's OWN feed is also issued implicitly inside
	// GET /roster/me (getMyRoster below).
	async issueRosterWorkerIcalFeed(workerId: number) {
		return this.request(`/roster/workers/${workerId}/ical`, { method: 'POST' });
	}

	async rotateRosterWorkerIcalFeed(workerId: number) {
		return this.request(`/roster/workers/${workerId}/ical/rotate`, { method: 'POST' });
	}

	// The worker landing page's own three reads. The manager-tier routes
	// beside them answer about anybody; these three only ever answer about
	// the person signed in, so a restricted login can use them.
	async getMyRosterRotation(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/me/rotation${toQuery(filter)}`);
	}

	async getMyRosterWeekChanges(filter: Record<string, unknown>) {
		return this.request(`/roster/me/week-changes${toQuery(filter)}`);
	}

	async rotateMyRosterIcalFeed() {
		return this.request('/roster/me/ical/rotate', { method: 'POST' });
	}

	async issueRosterBranchIcalFeed(branchId: number) {
		return this.request(`/roster/branches/${branchId}/ical`, { method: 'POST' });
	}

	async rotateRosterBranchIcalFeed(branchId: number) {
		return this.request(`/roster/branches/${branchId}/ical/rotate`, { method: 'POST' });
	}

	async getMyRoster(from: string, to: string) {
		return this.request(`/roster/me?from=${from}&to=${to}`);
	}

	async claimRosterShiftAsMe(shiftId: number, note = '') {
		return this.request(`/roster/me/claims/${shiftId}`, {
			method: 'POST',
			body: JSON.stringify({ note }),
		});
	}

	async requestMyRosterLeave(data: Record<string, unknown>) {
		return this.request('/roster/me/leave-requests', { method: 'POST', body: JSON.stringify(data) });
	}

	// Reports-to wave (CLC rows 25/48) — a worker's explicit
	// acknowledgement of one of their own published shifts.
	async acknowledgeMyRosterShift(shiftId: number) {
		return this.request(`/roster/me/shifts/${shiftId}/acknowledge`, { method: 'POST' });
	}

	// Phase 3 (roster-breaks-spec.md, employee selection, FR-58): a
	// worker choosing their own break on one of THEIR OWN shifts — the
	// self-service twin of placeRosterBreak/removeRosterBreak above,
	// gated server-side by roster_break_policy's placement mode per kind.
	async getMyRosterBreakPolicy(shiftId: number) {
		return this.request(`/roster/me/shifts/${shiftId}/break-policy`);
	}

	async getMyRosterBreakSafeWindows(shiftId: number, body: Record<string, unknown>) {
		return this.request(`/roster/me/shifts/${shiftId}/breaks/safe-windows`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	async placeMyRosterBreak(shiftId: number, data: Record<string, unknown>) {
		return this.request(`/roster/me/shifts/${shiftId}/breaks`, { method: 'POST', body: JSON.stringify(data) });
	}

	async removeMyRosterBreak(id: number) {
		return this.request(`/roster/me/breaks/${id}`, { method: 'DELETE' });
	}

	// Roster Stage 6b — availability rules + shift swaps.
	async getRosterWorkerAvailability(workerId: number) {
		return this.request(`/roster/workers/${workerId}/availability`);
	}

	async replaceRosterWorkerAvailability(workerId: number, rules: unknown[]) {
		return this.request(`/roster/workers/${workerId}/availability`, {
			method: 'PUT',
			body: JSON.stringify(rules),
		});
	}

	// CLC row 50 - per-worker confidential-reason manager grants + the read audit.
	async getRosterConfidentialReasonManagers(workerId: number) {
		return this.request(`/roster/workers/${workerId}/confidential-reason-managers`);
	}

	async replaceRosterConfidentialReasonManagers(workerId: number, managerUserIds: number[]) {
		return this.request(`/roster/workers/${workerId}/confidential-reason-managers`, {
			method: 'PUT',
			body: JSON.stringify({ manager_user_ids: managerUserIds }),
		});
	}

	// CLC row 50's remaining gap - narrowing ONE confidential availability
	// rule (keyed by rule_key) rather than every reason on the worker.
	async getRosterAvailabilityRuleConfidentialReasonManagers(workerId: number, ruleKey: string) {
		return this.request(`/roster/workers/${workerId}/availability/${encodeURIComponent(ruleKey)}/confidential-reason-managers`);
	}

	async replaceRosterAvailabilityRuleConfidentialReasonManagers(workerId: number, ruleKey: string, managerUserIds: number[]) {
		return this.request(`/roster/workers/${workerId}/availability/${encodeURIComponent(ruleKey)}/confidential-reason-managers`, {
			method: 'PUT',
			body: JSON.stringify({ manager_user_ids: managerUserIds }),
		});
	}

	async getRosterConfidentialReasonReads(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/confidential-reason-reads${toQuery(filter)}`);
	}

	async replaceMyRosterAvailability(rules: unknown[]) {
		return this.request('/roster/me/availability', { method: 'PUT', body: JSON.stringify(rules) });
	}

	// CLC requirement record row 36 — reading a staff member's outside
	// calendar. Self-service under /me (identity forced from the login
	// link); the manager side is the worker panel's Availability tab.
	async getMyRosterCalendarFeeds() {
		return this.request('/roster/me/calendar-feeds');
	}

	async createMyRosterCalendarFeed(data: Record<string, unknown>) {
		return this.request('/roster/me/calendar-feeds', { method: 'POST', body: JSON.stringify(data) });
	}

	async updateMyRosterCalendarFeed(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/me/calendar-feeds/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteMyRosterCalendarFeed(id: number) {
		return this.request(`/roster/me/calendar-feeds/${id}`, { method: 'DELETE' });
	}

	async getRosterWorkerCalendarFeeds(workerId: number) {
		return this.request(`/roster/workers/${workerId}/calendar-feeds`);
	}

	async createRosterWorkerCalendarFeed(workerId: number, data: Record<string, unknown>) {
		return this.request(`/roster/workers/${workerId}/calendar-feeds`, { method: 'POST', body: JSON.stringify(data) });
	}

	async updateRosterWorkerCalendarFeed(workerId: number, feedId: number, patch: Record<string, unknown>) {
		return this.request(`/roster/workers/${workerId}/calendar-feeds/${feedId}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteRosterWorkerCalendarFeed(workerId: number, feedId: number) {
		return this.request(`/roster/workers/${workerId}/calendar-feeds/${feedId}`, { method: 'DELETE' });
	}

	// trade (CLC row 24) carries the optional kind/slice/target/give-back
	// fields — an omitted trade leaves today's whole-shift, one-way offer
	// unchanged.
	async offerMyRosterSwap(shiftId: number, toWorkerId: number, note = '', trade: Record<string, unknown> = {}) {
		return this.request('/roster/me/swaps', {
			method: 'POST',
			body: JSON.stringify({ shift_id: shiftId, to_worker_id: toWorkerId, note, ...trade }),
		});
	}

	async respondMyRosterSwap(swapId: number, action: 'accept' | 'decline' | 'cancel') {
		return this.request(`/roster/me/swaps/${swapId}/${action}`, { method: 'POST' });
	}

	async getRosterSwaps(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/swaps${toQuery(filter)}`);
	}

	async decideRosterSwap(swapId: number, decision: 'approve' | 'decline') {
		return this.request(`/roster/swaps/${swapId}/${decision}`, { method: 'POST' });
	}

	// CLC row 24: a named colleague's published shifts, for a trade's
	// extension/two-way picker.
	async getMyRosterColleagueShifts(workerId: number, from: string, to: string) {
		return this.request(`/roster/me/colleagues/${workerId}/shifts${toQuery({ from, to })}`);
	}

	// CLC row 56: the open-shift board — partial offers, holds, expiry.
	async getRosterShiftOffers(shiftId: number) {
		return this.request(`/roster/shifts/${shiftId}/offers`);
	}

	// The worker's own read (level 4): live offers on an open shift of
	// their library, for the landing's hold/claim controls.
	async getMyRosterShiftOffers(shiftId: number) {
		return this.request(`/roster/me/shifts/${shiftId}/offers`);
	}

	async createRosterShiftOffer(shiftId: number, body: Record<string, unknown> = {}) {
		return this.request(`/roster/shifts/${shiftId}/offers`, { method: 'POST', body: JSON.stringify(body) });
	}

	async cancelRosterShiftOffer(offerId: number) {
		return this.request(`/roster/offers/${offerId}/cancel`, { method: 'POST' });
	}

	async holdMyRosterOffer(offerId: number) {
		return this.request(`/roster/me/offers/${offerId}/hold`, { method: 'POST' });
	}

	async claimMyRosterOffer(offerId: number, note = '') {
		return this.request(`/roster/me/offers/${offerId}/claim`, { method: 'POST', body: JSON.stringify({ note }) });
	}

	// CLC row 51 — the most recent sequential ask run for a shift (who
	// was asked, from which pool, and when), for the board's "N asked"
	// expansion. 404s honestly for a shift nobody has ever run one
	// against.
	async getRosterSequentialAsk(shiftId: number) {
		return this.request(`/roster/shifts/${shiftId}/sequential-ask`);
	}

	// The latest ask run of every shift in the library, in one read: the
	// whole Approvals queue's ask state without one request per open shift.
	// A shift nobody ever asked a pool about has no entry.
	async getRosterSequentialAsks(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/sequential-asks${toQuery(filter)}`);
	}

	// Row 51, wave 9: the board's own "Ask a pool" dialog. Start a
	// sequential ask against an ordered pool chain (pool_ids, the first
	// entry becomes the run's current pool, the rest queue as backups).
	async startRosterSequentialAsk(shiftId: number, body: Record<string, unknown>) {
		return this.request(`/roster/shifts/${shiftId}/sequential-ask`, { method: 'POST', body: JSON.stringify(body) });
	}

	async stopRosterSequentialAsk(askId: number) {
		return this.request(`/roster/sequential-asks/${askId}/stop`, { method: 'POST' });
	}

	// Roster notice templates — the copy a library controls (Comms Hub
	// contract §14). The list carries every notice's effective copy, its
	// built-in default, and its token palette in one round-trip.
	async listRosterNoticeTemplates(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/notice-templates${toQuery(filter)}`);
	}

	async saveRosterNoticeTemplate(
		kind: string,
		filter: Record<string, unknown>,
		tmpl: Record<string, string>,
	) {
		return this.request(
			`/roster/notice-templates/${encodeURIComponent(kind)}${toQuery(filter)}`,
			{ method: 'PUT', body: JSON.stringify(tmpl) },
		);
	}

	// Reset DELETES the library's row, returning it to the built-in
	// default — so the library resumes receiving default improvements.
	async resetRosterNoticeTemplate(kind: string, filter: Record<string, unknown> = {}) {
		return this.request(
			`/roster/notice-templates/${encodeURIComponent(kind)}${toQuery(filter)}`,
			{ method: 'DELETE' },
		);
	}

	async getRosterRulePack(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/rule-pack${toQuery(filter)}`);
	}

	async updateRosterRulePack(filter: Record<string, unknown>, rules: unknown[]) {
		return this.request(`/roster/rule-pack${toQuery(filter)}`, {
			method: 'PUT',
			body: JSON.stringify({ rules }),
		});
	}

	async getRosterEnforcementPolicy(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/enforcement-policy${toQuery(filter)}`);
	}

	async putRosterEnforcementPolicy(body: Record<string, unknown>) {
		return this.request('/roster/enforcement-policy', { method: 'PUT', body: JSON.stringify(body) });
	}

	async getRosterBreakPolicy(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/break-policy${toQuery(filter)}`);
	}

	async putRosterBreakPolicy(body: Record<string, unknown>) {
		return this.request('/roster/break-policy', { method: 'PUT', body: JSON.stringify(body) });
	}

	async getRosterPayPeriodCycles() {
		return this.request('/roster/pay-periods/cycles');
	}

	async getRosterPayPeriodPolicy(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/pay-period-policy${toQuery(filter)}`);
	}

	async putRosterPayPeriodPolicy(body: Record<string, unknown>) {
		return this.request('/roster/pay-period-policy', { method: 'PUT', body: JSON.stringify(body) });
	}

	async getRosterPayPeriodReport(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/reports/pay-period${toQuery(filter)}`);
	}

	async addRosterPayPeriodAdjustment(body: Record<string, unknown>) {
		return this.request('/roster/reports/pay-period/adjustment', { method: 'POST', body: JSON.stringify(body) });
	}

	async getRosterPayPeriodSheet(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/reports/pay-period-sheet${toQuery(filter)}`);
	}

	async getRosterPayPeriodSheetCSV(filter: Record<string, unknown> = {}): Promise<Blob> {
		return this._rosterReportCSV('/roster/reports/pay-period-sheet/export.csv', filter);
	}

	async getRosterBreakSafeWindows(shiftId: number, body: Record<string, unknown>) {
		return this.request(`/roster/shifts/${shiftId}/breaks/safe-windows`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	// Review states for a week (CLC requirement record row 21).
	async submitRosterWeekForReview(branchId: number, weekStart: string) {
		return this.request(`/roster/weeks/${branchId}/${weekStart}/submit-review`, { method: 'POST' });
	}

	async approveRosterWeek(branchId: number, weekStart: string) {
		return this.request(`/roster/weeks/${branchId}/${weekStart}/approve`, { method: 'POST' });
	}

	async getRosterWeeksPendingApproval(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/weeks-pending-approval${toQuery(filter)}`);
	}

	async getRosterReviewRequirements(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/review-requirements${toQuery(filter)}`);
	}

	async putRosterReviewRequirement(body: Record<string, unknown>) {
		return this.request('/roster/review-requirements', { method: 'PUT', body: JSON.stringify(body) });
	}

	// Row 21's other two change_kinds enforced: how many shift changes
	// and leave decisions in a branch's date window still need review,
	// and clearing the leave side in bulk (the shift side reuses the
	// existing mark-reviewed actions above).
	async getRosterPendingReviewCounts(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/reviews/pending${toQuery(filter)}`);
	}

	async markRosterLeaveDecisionsReviewed(body: Record<string, unknown>) {
		return this.request('/roster/reviews/leave-decisions/mark-reviewed', { method: 'POST', body: JSON.stringify(body) });
	}

	// Narrower rights and temporary delegation (CLC requirement record row 22).
	async getRosterCapabilityGrants(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/capability-grants${toQuery(filter)}`);
	}

	async grantRosterCapability(body: Record<string, unknown>) {
		return this.request('/roster/capability-grants', { method: 'POST', body: JSON.stringify(body) });
	}

	async delegateRosterCapability(body: Record<string, unknown>) {
		return this.request('/roster/capability-grants/delegate', { method: 'POST', body: JSON.stringify(body) });
	}

	async revokeRosterCapabilityGrant(id: number) {
		return this.request(`/roster/capability-grants/${id}/revoke`, { method: 'POST' });
	}

	// Self-service "hand over a right" screen (CLC row 22).
	async getMyRosterHeldCapabilities() {
		return this.request('/roster/capability-grants/mine');
	}

	async getMyRosterDelegations() {
		return this.request('/roster/capability-grants/mine/delegations');
	}

	// A level-4-reachable colleague picker (CLC rows 22 and 50) - the
	// SAME read as getRosterLinkableUsers, at a route that stays open to
	// a level-3/level-4 caller (see router.go's own comment for why this
	// is a separate route from /linkable-users).
	async getMyRosterColleagueLogins(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/my-colleague-logins${toQuery(filter)}`);
	}

	// Manager notice delegation (CLC requirement record row 25).
	async getMyRosterManagerDelegations() {
		return this.request('/roster/me/manager-delegations');
	}

	async setMyRosterManagerDelegation(body: Record<string, unknown>) {
		return this.request('/roster/me/manager-delegations', { method: 'POST', body: JSON.stringify(body) });
	}

	async getRosterManagerDelegations(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/manager-delegations${toQuery(filter)}`);
	}

	async createRosterManagerDelegation(body: Record<string, unknown>) {
		return this.request('/roster/manager-delegations', { method: 'POST', body: JSON.stringify(body) });
	}

	async revokeRosterManagerDelegation(id: number) {
		return this.request(`/roster/manager-delegations/${id}/revoke`, { method: 'POST' });
	}

	// Phase 3 (roster-breaks-spec.md, employee selection, FR-58): a
	// manager decides a worker's REQUESTED break exactly where every
	// other approval queue decides (decideRosterLeaveRequest/decideRosterSwap
	// above) — same "/:id/:decision" shape.
	async decideRosterBreak(id: number, decision: 'approve' | 'decline') {
		return this.request(`/roster/breaks/${id}/${decision}`, { method: 'POST' });
	}

	// Volunteer applications — staff review of the public signup queue.
	async getRosterVolunteerApplications(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/volunteer-applications${toQuery(filter)}`);
	}

	async approveRosterVolunteerApplication(id: number, note = '') {
		return this.request(`/roster/volunteer-applications/${id}/approve`, {
			method: 'POST',
			body: JSON.stringify({ note }),
		});
	}

	async declineRosterVolunteerApplication(id: number, note = '') {
		return this.request(`/roster/volunteer-applications/${id}/decline`, {
			method: 'POST',
			body: JSON.stringify({ note }),
		});
	}

	// Time clock (Stage 9) — tap-to-clock physical stickers + a manual
	// path gated behind manager approval. No anonymous surface (unlike
	// the volunteer signup above) — every route here is authed.
	async getRosterClockTags(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/clock-tags${toQuery(filter)}`);
	}

	async createRosterClockTag(data: Record<string, unknown>) {
		return this.request('/roster/clock-tags', { method: 'POST', body: JSON.stringify(data) });
	}

	async updateRosterClockTag(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/clock-tags/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	// The tap card's one read — resolves the tag + the caller's linked
	// worker, open session, and today's shift.
	// One narrowly-scoped SVG-QR renderer (reuses the backend's existing
	// go-qrcode helper — see broadcast_carousel_qr.go): returns
	// `{svg: string}` so the printable sheet can inline the markup into a
	// standalone `window.open` document rather than pointing an <img> at
	// an authenticated endpoint (which would 401 — COS auth is a bearer
	// token, not a cookie). Scoped server-side to /clock/ URLs only.
	async getRosterClockTagQR(url: string) {
		return this.request(`/roster/qr${toQuery({ url })}`);
	}

	async getRosterClockTagContext(token: string) {
		return this.request(`/roster/clock/tags/${encodeURIComponent(token)}/context`);
	}

	async punchRosterClock(token: string, direction: 'in' | 'out', channel: 'nfc' | 'qr') {
		return this.request('/roster/clock/punch', {
			method: 'POST',
			body: JSON.stringify({ token, direction, channel }),
		});
	}

	// Who's-in-now — every open session for a partner (optionally a branch).
	async getRosterClockBoard(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/clock/board${toQuery(filter)}`);
	}

	async getRosterTimeEntries(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/time-entries${toQuery(filter)}`);
	}

	// Manager-keyed direct entry — always approved immediately.
	async createRosterTimeEntry(data: Record<string, unknown>) {
		return this.request('/roster/time-entries', { method: 'POST', body: JSON.stringify(data) });
	}

	// Manager amend — always leaves the entry approved, reviewer stamped.
	async updateRosterTimeEntry(id: number, patch: Record<string, unknown>) {
		return this.request(`/roster/time-entries/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async approveRosterTimeEntry(id: number, note = '') {
		return this.request(`/roster/time-entries/${id}/approve`, {
			method: 'POST',
			body: JSON.stringify({ note }),
		});
	}

	async rejectRosterTimeEntry(id: number, note = '') {
		return this.request(`/roster/time-entries/${id}/reject`, {
			method: 'POST',
			body: JSON.stringify({ note }),
		});
	}

	// Rostered-vs-actual weekly variance — the Time clock tab's timesheet table.
	async getRosterTimeEntryVariance(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/time-entries/variance${toQuery(filter)}`);
	}

	async getMyRosterTimeEntries(filter: Record<string, unknown> = {}) {
		return this.request(`/roster/me/time-entries${toQuery(filter)}`);
	}

	// Self-service missed-punch — always pending (a manager decides).
	async createMyRosterTimeEntry(data: Record<string, unknown>) {
		return this.request('/roster/me/time-entries', { method: 'POST', body: JSON.stringify(data) });
	}

	// Close an open session or fix a not-yet-decided one — always
	// (re-)flips the entry to pending.
	async patchMyRosterTimeEntry(id: number, data: Record<string, unknown>) {
		return this.request(`/roster/me/time-entries/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
	}

	// Outreach (Bookmobile) — COS-native staff surface (/api/v1/outreach/*).
	// Vehicles, stops, ordered route templates; schedules and trips are
	// below the visit calls. List envelopes are { data, total }.
	async getOutreachVehicles(filter: Record<string, unknown> = {}) {
		return this.request(`/outreach/vehicles${toQuery(filter)}`);
	}
	async getOutreachVehicle(id: number) {
		return this.request(`/outreach/vehicles/${id}`);
	}
	async createOutreachVehicle(data: Record<string, unknown>) {
		return this.request('/outreach/vehicles', { method: 'POST', body: JSON.stringify(data) });
	}
	async updateOutreachVehicle(id: number, patch: Record<string, unknown>) {
		return this.request(`/outreach/vehicles/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	async deleteOutreachVehicle(id: number) {
		return this.request(`/outreach/vehicles/${id}`, { method: 'DELETE' });
	}
	async getOutreachVehicleOutages(id: number) {
		return this.request(`/outreach/vehicles/${id}/outages`);
	}
	async createOutreachVehicleOutage(id: number, data: Record<string, unknown>) {
		return this.request(`/outreach/vehicles/${id}/outages`, { method: 'POST', body: JSON.stringify(data) });
	}
	async deleteOutreachVehicleOutage(id: number, outageId: number) {
		return this.request(`/outreach/vehicles/${id}/outages/${outageId}`, { method: 'DELETE' });
	}
	async getOutreachStops(filter: Record<string, unknown> = {}) {
		return this.request(`/outreach/stops${toQuery(filter)}`);
	}
	async getOutreachStop(id: number) {
		return this.request(`/outreach/stops/${id}`);
	}
	async createOutreachStop(data: Record<string, unknown>) {
		return this.request('/outreach/stops', { method: 'POST', body: JSON.stringify(data) });
	}
	async updateOutreachStop(id: number, patch: Record<string, unknown>) {
		return this.request(`/outreach/stops/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	async deleteOutreachStop(id: number) {
		return this.request(`/outreach/stops/${id}`, { method: 'DELETE' });
	}
	/**
	 * Ask the free US Census geocoder for a PROPOSED coordinate for a stop
	 * address. Nothing is stored by this call — the staffer accepts the pin
	 * (geo_source 'census') or drops their own (geo_source 'manual'), and the
	 * stop write is what persists it. 404 when the address does not match,
	 * 422 when country is not US (the geocoder is US-only; drop a pin
	 * instead). Google is deliberately not a source here.
	 */
	/**
	 * The CRM organisations offered as visit hosts, for this partner only.
	 * Served by Outreach rather than Nurture on purpose: Nurture's own org
	 * routes are Communico-staff-only, and a library admin running Outreach
	 * has to be able to fill in this field.
	 */
	async getOutreachHostOrganisations(filter: Record<string, unknown> = {}) {
		return this.request(`/outreach/host-organisations${toQuery(filter)}`);
	}
	/** Adds an organisation to the CRM from the visit form. */
	async createOutreachHostOrganisation(body: Record<string, unknown>) {
		return this.request('/outreach/host-organisations', { method: 'POST', body: JSON.stringify(body) });
	}
	async geocodeOutreachStop(body: Record<string, unknown>) {
		return this.request('/outreach/stops/geocode', { method: 'POST', body: JSON.stringify(body) });
	}
	async getOutreachRoutes(filter: Record<string, unknown> = {}) {
		return this.request(`/outreach/routes${toQuery(filter)}`);
	}
	async getOutreachRoute(id: number) {
		return this.request(`/outreach/routes/${id}`);
	}
	async createOutreachRoute(data: Record<string, unknown>) {
		return this.request('/outreach/routes', { method: 'POST', body: JSON.stringify(data) });
	}
	async updateOutreachRoute(id: number, patch: Record<string, unknown>) {
		return this.request(`/outreach/routes/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	async deleteOutreachRoute(id: number) {
		return this.request(`/outreach/routes/${id}`, { method: 'DELETE' });
	}
	async replaceOutreachRouteStops(id: number, stops: Record<string, unknown>[]) {
		return this.request(`/outreach/routes/${id}/stops`, { method: 'PUT', body: JSON.stringify(stops) });
	}

	// Outreach Visits (Paul, 2026-08-23) — the two-kind booking (internal =
	// a group comes to us, external = we go out). List envelope is
	// { visits, total }, NOT { data, total } like vehicles/stops/routes —
	// a deliberately fixed, different shape (see types/outreach.ts).
	async getOutreachVisits(filter: Record<string, unknown> = {}) {
		return this.request(`/outreach/visits${toQuery(filter)}`);
	}
	async getOutreachVisit(id: number) {
		return this.request(`/outreach/visits/${id}`);
	}
	async createOutreachVisit(data: Record<string, unknown>) {
		return this.request('/outreach/visits', { method: 'POST', body: JSON.stringify(data) });
	}
	async updateOutreachVisit(id: number, patch: Record<string, unknown>) {
		return this.request(`/outreach/visits/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	// Soft-delete: the backend sets status='cancelled' rather than removing
	// the row (docs/outreach-spec.md conventions — soft delete when in doubt).
	async cancelOutreachVisit(id: number) {
		return this.request(`/outreach/visits/${id}`, { method: 'DELETE' });
	}
	// The visit's own library's staff for the "Add staff" picker: id and
	// display name only, sorted by name. Outreach-only (the general user
	// list is refused to a library admin and lists Communico people).
	async getOutreachStaff(filter: Record<string, unknown> = {}) {
		return this.request(`/outreach/staff${toQuery(filter)}`);
	}
	// Full replace, per the fixed contract's body shape { staff: [...] } —
	// not a bare array.
	// Round three (1 November 2026): route schedules, dated trips, the
	// library's how-far-ahead setting. Contract types: types/outreach.ts.
	async getOutreachRouteSchedules(routeId: number) {
		return this.request(`/outreach/routes/${routeId}/schedules`);
	}
	async createOutreachRouteSchedule(routeId: number, body: Record<string, unknown>) {
		return this.request(`/outreach/routes/${routeId}/schedules`, { method: 'POST', body: JSON.stringify(body) });
	}
	async updateOutreachRouteSchedule(id: number, patch: Record<string, unknown>, scope: 'all_future' | 'this_and_future', from?: string) {
		return this.request(`/outreach/route-schedules/${id}${toQuery({ scope, from })}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	async deleteOutreachRouteSchedule(id: number, from?: string) {
		return this.request(`/outreach/route-schedules/${id}${toQuery({ from })}`, { method: 'DELETE' });
	}
	async getOutreachTrips(filter: Record<string, unknown>) {
		return this.request(`/outreach/trips${toQuery(filter)}`);
	}
	async getOutreachTrip(id: number) {
		return this.request(`/outreach/trips/${id}`);
	}
	async createOutreachTrip(routeId: number, body: { trip_date: string; start_time: string }) {
		return this.request(`/outreach/routes/${routeId}/trips`, { method: 'POST', body: JSON.stringify(body) });
	}
	async updateOutreachTrip(id: number, patch: Record<string, unknown>) {
		return this.request(`/outreach/trips/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	// `reason` is the staff-only note; `publicReasonId` is the reason the
	// library's website shows (required by the server on a public route).
	async cancelOutreachTrip(id: number, reason?: string, publicReasonId?: number | null) {
		const body: Record<string, unknown> = {};
		if (reason) body.reason = reason;
		if (publicReasonId != null) body.public_reason_id = publicReasonId;
		return this.request(`/outreach/trips/${id}/cancel`, { method: 'POST', body: JSON.stringify(body) });
	}
	// A Communico staff login has no library of its own, so the chosen
	// library goes in as partner_id (a library login ignores it).
	async getOutreachCancelReasons(partnerId?: number) {
		return this.request(`/outreach/cancel-reasons${toQuery({ partner_id: partnerId })}`);
	}
	// Cancel one visit with its two reasons: the PATCH the contract gives
	// (status plus public_cancel_reason_id and cancel_note).
	async cancelOutreachVisitWithReason(id: number, publicReasonId: number | null, note: string) {
		const body: Record<string, unknown> = { status: 'cancelled', cancel_note: note };
		if (publicReasonId != null) body.public_cancel_reason_id = publicReasonId;
		return this.request(`/outreach/visits/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
	}
	// Exactly what the public will see for the signed-in library.
	async getOutreachPublicPreview(filter: Record<string, unknown> = {}) {
		return this.request(`/outreach/public-preview/stops${toQuery(filter)}`);
	}
	// Card PDFs: fetched with the bearer (never a token in a URL), then the
	// caller opens the blob in a new tab.
	async getOutreachStopCardPDF(stopId: number, format: 'full' | 'half'): Promise<Blob> {
		return this.outreachPDF(`/outreach/stops/${stopId}/card.pdf?format=${format}`);
	}
	async getOutreachRouteCardsPDF(routeId: number, format: 'full' | 'half'): Promise<Blob> {
		return this.outreachPDF(`/outreach/routes/${routeId}/cards.pdf?format=${format}`);
	}
	// A PDF cannot go through request() (it parses JSON), so this carries the
	// same identity headers (impersonation included) and the same 401 rule: an
	// expired sign-in is refreshed and retried once, anything else signs out.
	private async outreachPDF(path: string): Promise<Blob> {
		let resp = await fetch(`${API_BASE}${path}`, { headers: this.multipartHeaders() });
		if (resp.status === 401 && this.token) {
			const isExpired = /error_description="?expired"?/i.test(resp.headers.get('WWW-Authenticate') || '');
			if (isExpired && (await this.tryRefresh())) {
				resp = await fetch(`${API_BASE}${path}`, { headers: this.multipartHeaders() });
			}
			if (resp.status === 401) {
				this.clearToken();
				window.dispatchEvent(new CustomEvent('cos:auth:expired'));
				throw new ApiError(401, 'Authentication expired');
			}
		}
		if (!resp.ok) {
			let message = '';
			try {
				message = ((await resp.json()) as { message?: string }).message ?? '';
			} catch {
				// not JSON; fall through to the plain message
			}
			throw new ApiError(resp.status, message || `The card could not be made (${resp.status}).`);
		}
		return resp.blob();
	}
	async reopenOutreachTrip(id: number) {
		return this.request(`/outreach/trips/${id}/reopen`, { method: 'POST' });
	}
	async keepOutreachTrip(id: number) {
		return this.request(`/outreach/trips/${id}/keep`, { method: 'POST' });
	}
	async getOutreachSettings(filter: Record<string, unknown> = {}) {
		return this.request(`/outreach/settings${toQuery(filter)}`);
	}
	async updateOutreachSettings(body: Record<string, unknown>, filter: Record<string, unknown> = {}) {
		return this.request(`/outreach/settings${toQuery(filter)}`, { method: 'PATCH', body: JSON.stringify(body) });
	}
	async setOutreachVisitStaff(id: number, staff: Record<string, unknown>[]) {
		return this.request(`/outreach/visits/${id}/staff`, { method: 'PUT', body: JSON.stringify({ staff }) });
	}

	// Lend (Library of Things) — native COS staff surface. Reaches circulate
	// through the backend's Preserve-symmetric /api/v1/lend/* proxy (tenant +
	// staff identity injected server-side from the JWT — the client sends no
	// X-Client-ID). The proxy does inject X-Staff-ID, but circulate's Lend
	// handler never reads it for checkout/checkin/maintenance attribution
	// (only its own audit middleware does, and that isn't wired onto Lend's
	// routes) — checkoutStaffId/checkinStaffId/idVerifiedBy attribution rides
	// an explicit `staffId` in the checkout/checkin bodies instead
	// (cos-lend-checkout.ts / cos-lend-checkin.ts, via
	// circulate-products.ts's getLendAttesterUUID — a synthetic UUID, since
	// circulate's Lend schema has no crosswalk from COS's legacy integer
	// staff id). See docs/lend-cos-groundtruth.md §3.2. List envelopes are
	// { items, ... }.
	async getLendDashboardStats() {
		return this.request('/lend/dashboard/stats');
	}
	async getLendThings(filter: Record<string, unknown> = {}) {
		return this.request(`/lend/things${toQuery(filter)}`);
	}
	async getLendThing(id: string) {
		return this.request(`/lend/things/${id}`);
	}
	async createLendThing(data: Record<string, unknown>) {
		return this.request('/lend/things', { method: 'POST', body: JSON.stringify(data) });
	}
	async updateLendThing(id: string, data: Record<string, unknown>) {
		return this.request(`/lend/things/${id}`, { method: 'PUT', body: JSON.stringify(data) });
	}
	async getLendThingPolicy(id: string) {
		return this.request(`/lend/things/${id}/policy`);
	}
	/** The ONE path that can change a Thing's item policy (transfers, holds,
	 *  hold pickup, permitted locations), the full-replace updateLendThing
	 *  above deliberately excludes it (anti-clobber; see
	 *  LendItemPolicyUpdate). A field sent as `null` means "clear the
	 *  override, inherit the category default". */
	async updateLendThingPickupPolicy(id: string, data: Record<string, unknown>) {
		return this.request(`/lend/things/${id}/policy`, { method: 'PUT', body: JSON.stringify(data) });
	}
	/** Float groups (Program admin's "small Float groups" section), the
	 *  Lend-native placeholder a "float_group" transfer policy points at. */
	async getLendFloatGroups() {
		return this.request('/lend/float-groups');
	}
	async createLendFloatGroup(data: Record<string, unknown>) {
		return this.request('/lend/float-groups', { method: 'POST', body: JSON.stringify(data) });
	}
	async updateLendFloatGroup(id: string, data: Record<string, unknown>) {
		return this.request(`/lend/float-groups/${id}`, { method: 'PUT', body: JSON.stringify(data) });
	}
	/** 204 on success; 409 FLOAT_GROUP_IN_USE while any item uses the group.
	 *  A Circulate without the route answers 404 or 405, which reads as "not
	 *  available yet" rather than an error page. */
	async deleteLendFloatGroup(id: string) {
		try {
			return await this.request(`/lend/float-groups/${id}`, { method: 'DELETE' });
		} catch (e) {
			if (e instanceof ApiError && (e.status === 405 || (e.status === 404 && e.code !== 'FLOAT_GROUP_NOT_FOUND'))) {
				throw new ApiError(e.status, 'Deleting float groups is not available yet.', false, undefined, undefined, 'FLOAT_GROUP_DELETE_UNAVAILABLE');
			}
			throw e;
		}
	}
	/** Staff holds: a hold for a borrower with no PIN, recorded against the
	 *  staff member. Only offered when getLendIlsCapabilities().staffHolds. */
	async placeLendStaffHold(data: { thingId: string; patronBarcode: string; pickupLocationId?: number | null }) {
		return this.request('/lend/staff-holds', { method: 'POST', body: JSON.stringify(data) });
	}
	async getLendStaffHolds(patronBarcode: string) {
		return this.request(`/lend/staff-holds${toQuery({ patronBarcode })}`);
	}
	async cancelLendStaffHold(id: string) {
		return this.request(`/lend/staff-holds/${id}/cancel`, { method: 'POST' });
	}
	/** What this tenant's ILS adapter can actually do, the bridge (SIP2)
	 *  can't create records, so a thing's instance list offers "Link by
	 *  barcode" instead of assuming a twin exists (canCreateItem false). */
	async getLendIlsCapabilities() {
		return this.request('/lend/ils/capabilities');
	}
	/** Records an existing ILS barcode as an instance's twin, the only way
	 *  to give a copy a twin under an adapter that can't create one itself. */
	async linkLendInstanceTwin(id: string, twinBarcode: string) {
		return this.request(`/lend/instances/${id}/link-twin`, { method: 'POST', body: JSON.stringify({ twinBarcode }) });
	}
	/** Wave 3b's Circulate item twin (docs/modules/lend.md's "3b" scope,
	 *  distinct from the SIP2-bridge twinRef above): whether this copy has a
	 *  real circulation.item_records twin, and when it doesn't, why not
	 *  ("circulate-twin" adapter not in use for this library, or the copy's
	 *  branch isn't mapped to an org unit). Never creates one, that's the
	 *  backfill command's job, not this screen's. */
	async getLendInstanceTwin(id: string) {
		return this.request(`/lend/instances/${id}/twin`);
	}
	async addLendMedia(thingId: string, data: Record<string, unknown>) {
		return this.request(`/lend/things/${thingId}/media`, { method: 'POST', body: JSON.stringify(data) });
	}
	async deleteLendMedia(mediaId: string) {
		return this.request(`/lend/media/${mediaId}`, { method: 'DELETE' });
	}
	async createLendInstance(data: Record<string, unknown>) {
		return this.request('/lend/instances', { method: 'POST', body: JSON.stringify(data) });
	}
	async getLendInstanceByBarcode(barcode: string) {
		return this.request(`/lend/instances/barcode/${encodeURIComponent(barcode)}`);
	}
	async updateLendInstance(id: string, data: Record<string, unknown>) {
		return this.request(`/lend/instances/${id}`, { method: 'PUT', body: JSON.stringify(data) });
	}
	async getLendCategories(opts: { includeInactive?: boolean } = {}) {
		return this.request(`/lend/categories${toQuery({ includeInactive: opts.includeInactive ? 'true' : undefined })}`);
	}
	/** The tenant's Communico locations (control panel Settings), active
	 *  only — a cataloger picks from these to say which building holds a
	 *  copy. Degrades to an empty list for a tenant not yet synced with
	 *  COS locations (see ils-lend-thing-form's own precedent). */
	async getLendLocations() {
		return this.request('/lend/locations');
	}
	async createLendCategory(data: Record<string, unknown>) {
		return this.request('/lend/categories', { method: 'POST', body: JSON.stringify(data) });
	}
	async updateLendCategory(id: string, data: Record<string, unknown>) {
		return this.request(`/lend/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) });
	}
	/** Apply a starter category (e.g. "game_hardware"). Idempotent per library:
	 *  a second call returns the existing category. */
	async applyLendCategoryPreset(key: string) {
		return this.request(`/lend/categories/presets/${encodeURIComponent(key)}/apply`, { method: 'POST' });
	}
	async getLendCollections() {
		return this.request('/lend/collections');
	}
	async createLendCollection(data: Record<string, unknown>) {
		return this.request('/lend/collections', { method: 'POST', body: JSON.stringify(data) });
	}
	async getLendCollection(slug: string) {
		return this.request(`/lend/collections/${encodeURIComponent(slug)}`);
	}
	async getLendWaivers() {
		return this.request('/lend/waivers');
	}
	async createLendWaiver(data: Record<string, unknown>) {
		return this.request('/lend/waivers', { method: 'POST', body: JSON.stringify(data) });
	}
	async signLendWaiver(waiverId: string, data: Record<string, unknown>) {
		return this.request(`/lend/waivers/${waiverId}/sign`, { method: 'POST', body: JSON.stringify(data) });
	}
	async getLendCheckoutRequirements(barcode: string, patronBarcode: string) {
		return this.request(`/lend/checkout/requirements${toQuery({ barcode, patronBarcode })}`);
	}
	async lendCheckout(data: Record<string, unknown>) {
		return this.request('/lend/loans/checkout', { method: 'POST', body: JSON.stringify(data) });
	}
	async lendCheckin(data: Record<string, unknown>) {
		return this.request('/lend/loans/checkin', { method: 'POST', body: JSON.stringify(data) });
	}
	// Lend-transit contract: the receiving branch rides X-COS-Branch (set
	// ambiently via setBranch, same as every other Desk request); clientId
	// follows the findDeskItem/deskCheckin convention (>0 for a Communico
	// staffer working a specific library, omitted otherwise).
	async receiveLendTransit(barcode: string, clientId = 0) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/lend/transits/receive${qs}`, { method: 'POST', body: JSON.stringify({ barcode }) });
	}
	/**
	 * The transfer label document for a transit, in the same slip-document
	 * shape Circulate's own print-slip handler returns (SlipService), a
	 * file download like getLendMarcExport, not JSON, so it needs the same
	 * manual bearer-authenticated fetch + Blob.
	 */
	async getLendTransitSlip(transitId: string): Promise<Blob> {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}${withLendLibrary(`/lend/transits/${transitId}/slip`, legacyClientId())}`, { method: 'POST', headers });
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `Transfer label failed (${resp.status})`);
		}
		return resp.blob();
	}
	async renewLendLoan(loanId: string) {
		return this.request(`/lend/loans/${loanId}/renew`, { method: 'POST' });
	}
	async getLendPatronLoans(patronId: string, filter: Record<string, unknown> = {}) {
		return this.request(`/lend/loans/patron/${patronId}${toQuery(filter)}`);
	}
	async getLendOverdueLoans(limit = 50, cursor = '') {
		return this.request(`/lend/loans-queries/overdue${toQuery({ limit, cursor: cursor || undefined })}`);
	}
	async lookupLendPatron(barcode: string) {
		return this.request(`/lend/patrons/lookup${toQuery({ barcode })}`);
	}
	async getLendPendingMaintenance(limit = 50, cursor = '') {
		return this.request(`/lend/maintenance/pending${toQuery({ limit, cursor: cursor || undefined })}`);
	}
	async completeLendMaintenance(id: string, data: Record<string, unknown>) {
		return this.request(`/lend/maintenance/${id}/complete`, { method: 'PUT', body: JSON.stringify(data) });
	}
	async getLendHeldDeposits(limit = 50, cursor = '') {
		return this.request(`/lend/deposits-queries/held${toQuery({ limit, cursor: cursor || undefined })}`);
	}
	async getLendPatronDeposits(patronId: string, filter: Record<string, unknown> = {}) {
		return this.request(`/lend/deposits/patron/${patronId}${toQuery(filter)}`);
	}
	async resolveLendDeposit(id: string, data: Record<string, unknown>) {
		return this.request(`/lend/deposits/${id}/resolve`, { method: 'POST', body: JSON.stringify(data) });
	}
	async getLendConfig() {
		return this.request('/lend/config');
	}
	async putLendConfig(data: Record<string, unknown>) {
		return this.request('/lend/config', { method: 'PUT', body: JSON.stringify(data) });
	}
	/**
	 * MARC export is a file download, not JSON. It must go through a
	 * bearer-authenticated fetch + Blob (an <a href> nav would 401 — the
	 * token lives in sessionStorage, not a cookie). Mirrors getPreserveFileBlob.
	 */
	async getLendMarcExport(variant: 'discovery' | 'full', format: 'mrc' | 'xml' = 'mrc'): Promise<Blob> {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}${withLendLibrary(`/lend/marc-export${toQuery({ variant, format })}`, legacyClientId())}`, { headers });
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `MARC export failed (${resp.status})`);
		}
		return resp.blob();
	}

	// Stage 76 — inbox aggregator + snooze + landing preference.
	async getInbox() {
		return this.request('/inbox/');
	}
	async snoozeInboxItem(entityType: string, entityId: string, until: string, reason?: string) {
		return this.request('/inbox/snooze', {
			method: 'POST',
			body: JSON.stringify({ entity_type: entityType, entity_id: entityId, until, reason }),
		});
	}
	async unsnoozeInboxItem(id: number) {
		return this.request(`/inbox/snooze/${id}`, { method: 'DELETE' });
	}
	// Widened for the Action Center (Part 5, O1 refinement) — library staff
	// (partner-tier) now get a choice too, between 'action-center' and
	// 'dashboard' (never 'inbox', which stays staff-only).
	async setLandingPage(page: 'inbox' | 'dashboard' | 'action-center') {
		return this.request('/me/preferences/landing-page', {
			method: 'PATCH',
			body: JSON.stringify({ landing_page: page }),
		});
	}

	// Action Center — docs/action-center-plan.md Part 6.4/6.5. `filters` is
	// already the wire-shaped query object from utils/actionCenterFilters.ts
	// (`buildTabQuery` here — this is the per-tab items call, always
	// carrying `sources=<activeTab>`) — this layer just joins it onto the
	// querystring, it doesn't interpret it.
	async getActionCenter(filters: Record<string, string | number> = {}): Promise<ActionCenterResponse> {
		return this.request<ActionCenterResponse>(`/action-center${toQuery(filters)}`);
	}
	// The all-sources overview call (`buildOverviewQuery` — deliberately
	// WITHOUT `sources=`/`mediation_groups=`): drives the tab strip's own
	// per-tab counts and the page header's across-ALL-sources total. The
	// shell's nav badge poll calls this too, with its own (unfiltered)
	// params.
	async getActionCenterCounts(filters: Record<string, string | number> = {}): Promise<ActionCenterCountsResponse> {
		return this.request<ActionCenterCountsResponse>(`/action-center/counts${toQuery(filters)}`);
	}
	async markActionCenterRead(keys: string[], clientId = 0) {
		return this.request(`/action-center/read${clientId > 0 ? toQuery({ client_id: clientId }) : ''}`, { method: 'POST', body: JSON.stringify({ keys }) });
	}
	async markAllActionCenterRead(clientId = 0) {
		return this.request(`/action-center/read-all${clientId > 0 ? toQuery({ client_id: clientId }) : ''}`, { method: 'POST' });
	}
	// Single-row actions reuse this with a one-element `keys` array — one
	// code path, per the wire contract (Part 6.4).
	async actionCenterBulk(
		action: ActionCenterBulkAction,
		keys: string[],
		note?: string,
		clientId = 0,
	): Promise<ActionCenterBulkResponse> {
		return this.request<ActionCenterBulkResponse>(`/action-center/bulk${clientId > 0 ? toQuery({ client_id: clientId }) : ''}`, {
			method: 'POST',
			body: JSON.stringify({ action, keys, note }),
		});
	}

	// A library user raises a ticket: the one call a library login may use to make
	// a ticket (contract section 13). The body is exactly the fields of that
	// section; COS decides everything else. Nothing a library user can reach posts
	// a whole ticket to POST /tickets, which stays staff only.
	async requestTicket(data: TicketRequestBody, signal?: AbortSignal): Promise<TicketRequestAnswer> {
		return this.request<TicketRequestAnswer>('/tickets/request', {
			method: 'POST',
			body: JSON.stringify(data),
			...(signal ? { signal } : {}),
		});
	}

	// The five statuses a library is shown, in lane order, with the wording the
	// server holds (contract section 12 h).
	async getPartnerStatuses(): Promise<PartnerStatusOption[]> {
		const res = await this.request<{ statuses?: PartnerStatusOption[] | null }>('/tickets/partner-statuses');
		return Array.isArray(res?.statuses) ? res.statuses : [];
	}

	// Stage 24 — AI quality gate. Returns null on 503 (fail-open: AI
	// down → form keeps working). Returns the parsed check otherwise.
	// 422 (input below threshold) bubbles as a thrown error; the caller
	// should debounce-gate on input length so users don't see it.
	async checkTicketQuality(data: { title: string; description: string; type: string }): Promise<TicketQualityCheck | null> {
		try {
			return await this.request('/tickets/quality-check', {
				method: 'POST', body: JSON.stringify(data),
			}) as TicketQualityCheck;
		} catch (err) {
			const msg = err instanceof Error ? err.message : '';
			if (msg.includes('quality check unavailable')) {
				return null; // fail-open
			}
			throw err;
		}
	}

	// Dashboard (Stage 42 — summary widgets; preserved for cos-dashboard-v1)
	async getDashboard() {
		return this.request('/dashboard');
	}

	// Dashboards + reports (Stage 48)
	// The library chosen in the nav rides along so Communico staff are offered
	// only the modules that library bought; a library's own login ignores it.
	async getReports() {
		return this.request(`/reports${toQuery({ client_id: libraryClientIdOrUndefined() })}`);
	}

	// Stage 3d — dashboard templates (entitlement-filtered) for "create from template".
	async getDashboardTemplates() {
		return this.request(`/dashboard-templates${toQuery({ client_id: libraryClientIdOrUndefined() })}`);
	}
	// Stage 3g — the closed module vocabulary for the custom-metric builder.
	async getCustomMetricModules() {
		return this.request(`/dashboards/custom-metric-modules${toQuery({ client_id: libraryClientIdOrUndefined() })}`);
	}
	// Stage 3k — render a custom-metric definition without persisting a tile
	// (the builder's live preview). Same scope + suppression path as the tile.
	async previewCustomMetric(body: Record<string, unknown>) {
		return this.request('/dashboards/custom-metric-preview', { method: 'POST', body: JSON.stringify(body) });
	}
	// Phase 3l — saved / named custom metrics (live-linked; COS Postgres).
	async getSavedMetrics() {
		return this.request('/dashboards/saved-metrics');
	}
	async createSavedMetric(body: Record<string, unknown>) {
		return this.request('/dashboards/saved-metrics', { method: 'POST', body: JSON.stringify(body) });
	}
	async updateSavedMetric(id: number, body: Record<string, unknown>) {
		return this.request(`/dashboards/saved-metrics/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
	}
	async deleteSavedMetric(id: number) {
		return this.request(`/dashboards/saved-metrics/${id}`, { method: 'DELETE' });
	}
	// Phase 3m — async report exports.
	async enqueueReport(dashboardId: number, format: string) {
		return this.request(`/dashboards/${dashboardId}/export`, { method: 'POST', body: JSON.stringify({ format }) });
	}
	async getReportJobs() {
		return this.request('/dashboards/report-jobs');
	}
	// A ready export downloads as a Blob (bearer-authed — a plain link would 401).
	async getReportDownload(jobId: number): Promise<Blob> {
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers['Authorization'] = `Bearer ${tok}`;
		const resp = await fetch(`${API_BASE}/dashboards/report-jobs/${jobId}/download`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `download failed (${resp.status})`);
		return resp.blob();
	}

	// Stage 92a — Files panel browse (staff-only; live Drive read).
	async getDriveDrives(): Promise<{ data: Array<{ drive_id: string; name: string }> }> {
		return this.request('/drive/drives');
	}
	async getDriveFiles(
		driveId = '',
		folderId = '',
		pageToken = '',
		sort = '',
	): Promise<{ data: DriveFileEntry[]; next_page_token?: string }> {
		const qs = new URLSearchParams();
		if (driveId) qs.set('drive_id', driveId);
		if (folderId) qs.set('folder_id', folderId);
		if (pageToken) qs.set('page_token', pageToken);
		if (sort) qs.set('sort', sort);
		const q = qs.toString();
		return this.request(`/drive/files${q ? `?${q}` : ''}`);
	}

	// Stage 92b — Files panel writes (staff-only).
	async uploadDriveFile(driveId: string, folderId: string, file: File): Promise<DriveFileEntry> {
		const form = new FormData();
		form.append('file', file);
		if (driveId) form.append('drive_id', driveId);
		if (folderId) form.append('folder_id', folderId);
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/drive/files`, { method: 'POST', headers, body: form });
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `Upload failed (${resp.status})`);
		}
		return resp.json();
	}
	async createDriveFolder(driveId: string, parent: string, name: string): Promise<DriveFileEntry> {
		return this.request('/drive/folders', {
			method: 'POST',
			body: JSON.stringify({ name, drive_id: driveId, parent }),
		});
	}
	async patchDriveFile(
		fileId: string,
		patch: { name?: string; parent?: string; source_parent?: string },
	): Promise<DriveFileEntry> {
		return this.request(`/drive/files/${encodeURIComponent(fileId)}`, {
			method: 'PATCH',
			body: JSON.stringify(patch),
		});
	}
	async deleteDriveFile(fileId: string): Promise<void> {
		return this.request(`/drive/files/${encodeURIComponent(fileId)}`, { method: 'DELETE' });
	}

	// Stage 92d — per-record Files tile.
	async getRecordFolder(recordType: string, recordId: number): Promise<{ folder_id: string; provisioned: boolean }> {
		return this.request(`/drive/records/${recordType}/${recordId}/folder`);
	}
	async provisionPartnerFolder(partnerId: number): Promise<{ folder_id: string; provisioned: boolean }> {
		return this.request(`/drive/partners/${partnerId}/folder`, { method: 'POST' });
	}
	async getDriveRecentFiles(driveId: string, folderId: string, limit = 10): Promise<{ data: DriveFileEntry[] }> {
		const qs = new URLSearchParams();
		if (driveId) qs.set('drive_id', driveId);
		if (folderId) qs.set('folder', folderId);
		qs.set('limit', String(limit));
		return this.request(`/drive/files/recent?${qs.toString()}`);
	}

	// Stage 92e — share / permissions (file OR folder; same endpoints).
	async getFilePermissions(fileId: string): Promise<{ data: DrivePermission[] }> {
		return this.request(`/drive/files/${encodeURIComponent(fileId)}/permissions`);
	}
	async grantFilePermission(
		fileId: string,
		body: { type: string; role: string; email?: string; notify?: boolean },
	): Promise<DrivePermission> {
		return this.request(`/drive/files/${encodeURIComponent(fileId)}/permissions`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}
	async revokeFilePermission(fileId: string, permissionId: string): Promise<void> {
		return this.request(
			`/drive/files/${encodeURIComponent(fileId)}/permissions/${encodeURIComponent(permissionId)}`,
			{ method: 'DELETE' },
		);
	}
	async updateFilePermissionRole(fileId: string, permissionId: string, role: string): Promise<DrivePermission> {
		return this.request(
			`/drive/files/${encodeURIComponent(fileId)}/permissions/${encodeURIComponent(permissionId)}`,
			{ method: 'PATCH', body: JSON.stringify({ role }) },
		);
	}

	// Stage 60a — IMLS PLS admin upload. Multipart, so it bypasses the JSON
	// request() helper. `preview` returns the schema delta without writing.
	async uploadPLS(file: File, preview = false) {
		const fd = new FormData();
		fd.append('file', file);
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers['Authorization'] = `Bearer ${tok}`;
		const resp = await fetch(`${API_BASE}/admin/pls/upload${preview ? '?preview=1' : ''}`, {
			method: 'POST',
			headers,
			body: fd,
		});
		const body = await resp.json().catch(() => ({}));
		if (!resp.ok) throw new ApiError(resp.status, body?.error || body?.message || `Upload failed (${resp.status})`);
		return body;
	}
	async getPLSUploads() {
		return this.request('/admin/pls/uploads');
	}
	async getDashboards() {
		return this.request('/dashboards');
	}
	async getDefaultDashboard() {
		return this.request(`/dashboards/default${toQuery({ client_id: libraryClientIdOrUndefined() })}`);
	}
	async getDashboardById(id: number) {
		return this.request(`/dashboards/${id}${toQuery({ client_id: libraryClientIdOrUndefined() })}`);
	}
	async createDashboard(body: Record<string, unknown>) {
		return this.request('/dashboards', { method: 'POST', body: JSON.stringify(body) });
	}
	async patchDashboard(id: number, patch: Record<string, unknown>) {
		return this.request(`/dashboards/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	async archiveDashboard(id: number) {
		return this.request(`/dashboards/${id}`, { method: 'DELETE' });
	}
	async favoriteDashboard(id: number) {
		return this.request(`/dashboards/${id}/favorite`, { method: 'POST' });
	}
	// Thrive share links (fleet/thrive-w1-shell) - mints (or re-mints) the
	// token behind the public, no-login /s/:token page. Returns
	// { token, url }; the url is what cos-copy-link renders and copies.
	// partnerId is required for an unrestricted (Communico staff) caller:
	// a share link is always scoped to one library's data, and staff carry
	// no partner of their own to default to (issue 118); omitted for a
	// library-tier caller, whose own scope already supplies it server-side.
	// opts.expiresInDays is 7, 30, 90 or 365 (the server refuses any other);
	// neverExpires stores no expiry; password is optional (8 to 72 characters).
	async shareDashboard(id: number, partnerId?: number, opts: { expiresInDays?: number; neverExpires?: boolean; password?: string } = {}) {
		const body: Record<string, unknown> = {};
		if (partnerId) body.partner_id = partnerId;
		if (opts.neverExpires) body.never_expires = true;
		else if (opts.expiresInDays) body.expires_in_days = opts.expiresInDays;
		if (opts.password) body.password = opts.password;
		return this.request(`/dashboards/${id}/share`, {
			method: 'POST',
			body: Object.keys(body).length ? JSON.stringify(body) : undefined,
		});
	}
	// The owner's list of this dashboard's links (no link text: only a hash is stored).
	async listDashboardShares(id: number) {
		return this.request(`/dashboards/${id}/share`);
	}
	async revokeDashboardShare(id: number, tokenId: number) {
		return this.request(`/dashboards/${id}/share/${tokenId}`, { method: 'DELETE' });
	}
	async addDashboardTile(id: number, tile: Record<string, unknown>) {
		return this.request(`/dashboards/${id}/tiles`, { method: 'POST', body: JSON.stringify(tile) });
	}
	async patchDashboardTile(id: number, tileId: number, patch: Record<string, unknown>) {
		return this.request(`/dashboards/${id}/tiles/${tileId}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	async deleteDashboardTile(id: number, tileId: number) {
		return this.request(`/dashboards/${id}/tiles/${tileId}`, { method: 'DELETE' });
	}
	// Stage 50 — persist a collision-resolved layout (N tiles) atomically.
	async bulkPositionTiles(id: number, positions: Array<Record<string, number>>) {
		return this.request(`/dashboards/${id}/tiles/bulk-position`, { method: 'POST', body: JSON.stringify(positions) });
	}
	async renderDashboardTile(id: number, tileId: number, config: Record<string, unknown> = {}) {
		return this.request(`/dashboards/${id}/tiles/${tileId}/render`, { method: 'POST', body: JSON.stringify(config) });
	}
	// Stage 3e-1 — tile CSV export. A bearer fetch + Blob (a plain <a href>
	// navigation would 401: the token lives in sessionStorage, not a cookie).
	async getDashboardTileCSV(id: number, tileId: number, dateRange = ''): Promise<Blob> {
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers['Authorization'] = `Bearer ${tok}`;
		const q = dateRange ? `?date_range=${encodeURIComponent(dateRange)}` : '';
		const resp = await fetch(`${API_BASE}/dashboards/${id}/tiles/${tileId}/export.csv${q}`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `CSV export failed (${resp.status})`);
		return resp.blob();
	}
	async getActivities(filter: Record<string, unknown> = {}) {
		return this.request(`/activities${toQuery(filter)}`);
	}

	// Partners
	async getPartners(filter: PartnerFilter = {}) {
		return this.request(`/partners${toQuery(filter as Record<string, unknown>)}`);
	}

	// Fix #39 — the live filter facets for the /partners list (DISTINCT stored
	// values per filterable column; the dropdowns derive from these, never
	// static enums — the ILS-class pin at the read side).
	async getPartnerFilterOptions(lifecycle?: string) {
		return this.request(`/partners/filter-options${lifecycle ? `?lifecycle=${encodeURIComponent(lifecycle)}` : ''}`);
	}

	// Partner map view (2026-08) — one row per customer partner + its
	// resolved (own > PLS) coordinate, or a null coordinate when neither
	// resolves. Staff surface; a restricted scope narrows server-side.
	async getPartnerMap() {
		return this.request(`/partners/map`);
	}

	// Stage 77 — asCustomer appends ?as=customer (the staff "View as: Partner"
	// preview), so the backend narrows ownership / promoted-extras out of the
	// shared payload — the data filter agrees with the rendering filter.
	async getPartner(id: number, asCustomer = false) {
		return this.request(`/partners/${id}${asCustomerSuffix(asCustomer)}`);
	}

	// Stage 71 — command-palette record search. Signal-aware (the palette
	// aborts in-flight searches on each keystroke). Partners filter on
	// `search` (name/domain ILIKE); tickets + contacts on `q`.
	async searchPartners(q: string, limit: number, signal?: AbortSignal) {
		return this.request(`/partners${toQuery({ search: q, limit })}`, signal ? { signal } : {});
	}
	// For Communico staff the search covers both kinds of ticket (`kind=all`) and
	// each result carries its reference; for anyone else the call is exactly what
	// it always was.
	async searchTickets(q: string, limit: number, signal?: AbortSignal) {
		const kind = isKnownCommunicoStaff() ? 'all' : undefined;
		return this.request(`/tickets${toQuery({ q, limit, kind })}`, signal ? { signal } : {});
	}
	async searchContacts(q: string, limit: number, signal?: AbortSignal) {
		return this.request(`/contacts${toQuery({ q, limit })}`, signal ? { signal } : {});
	}
	// Stage 82b — project name search (the @-anchor autocomplete).
	async searchProjects(q: string, limit: number, signal?: AbortSignal) {
		return this.request(`/projects${toQuery({ q, limit })}`, signal ? { signal } : {});
	}
	// Stage 82c — membership-gated channel name search (the #-anchor autocomplete).
	async searchChannels(q: string, limit: number, signal?: AbortSignal) {
		return this.request(`/chat/channels${toQuery({ q, limit })}`, signal ? { signal } : {});
	}
	// Stage 82c — conversations cross-referenced to a channel (visibility-floored).
	async channelCrossRefs(channelId: number) {
		return this.request(`/chat/channels/${channelId}/cross-refs`);
	}

	// Stage 39a — Partner Overview Card aggregate (spec §10.1).
	// Single round-trip envelope: partner + ticket counts + deal
	// summary + modules + contacts-by-role + annual reviews +
	// last-ticket-activity + control-panel deep-link + stubs.
	// See src/types/partner.ts for the response shape.
	async getPartnerOverview(id: number, asCustomer = false) {
		return this.request(`/partners/${id}/overview${asCustomerSuffix(asCustomer)}`);
	}

	async createPartner(data: Record<string, unknown>) {
		return this.request('/partners', { method: 'POST', body: JSON.stringify(data) });
	}

	async updatePartner(id: number, patch: Record<string, unknown>) {
		return this.request(`/partners/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deletePartner(id: number) {
		return this.request(`/partners/${id}`, { method: 'DELETE' });
	}

	// Stage 46d — partner-detail endpoints.
	async setPartnerHealth(id: number, status: string, reason = '') {
		return this.request(`/partners/${id}/health`, {
			method: 'PATCH',
			body: JSON.stringify(reason ? { status, reason } : { status }),
		});
	}
	async getPartnerImplementationRollup(id: number) {
		return this.request(`/partners/${id}/implementation-rollup`);
	}

	// Stage 60b — prospects (unlinked PLS libraries) + partner↔PLS link.
	async getProspects(filter: ProspectFilter = {}) {
		return this.request(`/prospects${toQuery(filter as Record<string, unknown>)}`);
	}
	async getProspectRecord(fscskey: string) {
		return this.request(`/prospects/${encodeURIComponent(fscskey)}`);
	}
	// Stage 60c — the categorized PLS metric catalog for the IMLS field-picker.
	async getProspectFields() {
		return this.request('/prospects/fields');
	}
	// Stage 60d — CSV export. Fetched with the bearer header (auth is a
	// sessionStorage token, NOT a cookie — a plain window.location navigation
	// would 401), returned as a Blob the caller turns into a download.
	// Marcie R12 — export the filtered tickets as CSV. params carries the same
	// filters the list uses (status/priority/partner_id/contact_id/type/q/…)
	// plus an optional created-at window (from/to, YYYY-MM-DD). Bearer-authed
	// Blob so the sessionStorage token rides (a plain link would 401).
	async getTicketsCSV(params: Record<string, string>): Promise<Blob> {
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers['Authorization'] = `Bearer ${tok}`;
		const qs = new URLSearchParams(params).toString();
		const resp = await fetch(`${API_BASE}/tickets/export.csv${qs ? `?${qs}` : ''}`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `CSV export failed (${resp.status})`);
		return resp.blob();
	}

	async getProspectCSV(fscskey: string): Promise<Blob> {
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers['Authorization'] = `Bearer ${tok}`;
		const resp = await fetch(`${API_BASE}/prospects/${encodeURIComponent(fscskey)}/export.csv`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `CSV export failed (${resp.status})`);
		return resp.blob();
	}
	// Stage 60e — IMLS peer comparison. Resolves up to 5 similar-sized same-state
	// peers for a partner (by id) or a PLS record (by fscskey). `options` (Thrive
	// Benchmarks, wave 4) is the peer-group picker's three refinement dimensions;
	// omitting it reproduces the original default exactly.
	async getPeerComparisonByPartner(id: number, options: PeerComparisonOptions = {}) {
		return this.request(`/peer-comparison/partner/${id}${toQuery(peerComparisonQuery(options))}`);
	}
	async getPeerComparisonByFscskey(fscskey: string, options: PeerComparisonOptions = {}) {
		return this.request(`/peer-comparison/fscskey/${encodeURIComponent(fscskey)}${toQuery(peerComparisonQuery(options))}`);
	}
	async linkPartnerPLS(id: number, fscskey: string) {
		return this.request(`/partners/${id}/link-pls`, { method: 'POST', body: JSON.stringify({ fscskey }) });
	}
	async getPLSMatchCandidates() {
		return this.request('/admin/pls/match-candidates');
	}
	// Stage 60h — admin batch-link suggestions: unlinked customer partners +
	// their top PLS candidates. Linking reuses linkPartnerPLS above.
	async getPLSLinkSuggestions() {
		return this.request('/admin/pls/link-suggestions');
	}
	async getPartnerRecentActivity(id: number, limit = 20, cursor = '') {
		return this.request(`/partners/${id}/recent-activity${toQuery({ limit, cursor })}`);
	}
	// Stage 95.6 — the right-zone engagement-glance tile (real-activity summary).
	async getPartnerEngagementGlance(id: number) {
		return this.request(`/partners/${id}/engagement-glance`);
	}

	// Stage 61a — tracked email store (staff-only; 404 for partner scopes).
	async getPartnerEmailThreads(id: number, limit = 50, cursor = '') {
		return this.request(`/partners/${id}/email-threads${toQuery({ limit, cursor })}`);
	}
	async getPartnerEmailThread(id: number, threadId: number) {
		return this.request(`/partners/${id}/email-threads/${threadId}`);
	}

	// Stage 61b — the /mail personal inbox (staff-only, owner-scoped).
	async getMailFolders() {
		return this.request('/mail/folders');
	}
	// Stage 64 — the Gmail tab rail (label catalog + live counts).
	async getMailLabels() {
		return this.request('/mail/labels');
	}
	// Stage 64 — multi-domain customers.
	async getPartnerDomains(partnerId: number) {
		return this.request(`/partners/${partnerId}/domains`);
	}
	async addPartnerDomain(partnerId: number, domain: string) {
		return this.request(`/partners/${partnerId}/domains`, {
			method: 'POST',
			body: JSON.stringify({ domain }),
		});
	}
	async deletePartnerDomain(partnerId: number, domainId: number) {
		return this.request(`/partners/${partnerId}/domains/${domainId}`, { method: 'DELETE' });
	}
	async getMailThreads(opts: { folder?: string; unread?: boolean; q?: string; limit?: number; cursor?: string } = {}) {
		return this.request(`/mail/threads${toQuery(opts as Record<string, unknown>)}`);
	}
	async getMailThread(threadId: string) {
		return this.request(`/mail/threads/${encodeURIComponent(threadId)}`);
	}
	// Stage 61c.5 — AI follow-up suggestions + meeting creation.
	async suggestMailActions(threadId: string) {
		return this.request(`/mail/threads/${encodeURIComponent(threadId)}/suggest-actions`, { method: 'POST' });
	}
	async createMailMeeting(body: { title: string; details?: string; start: string; duration_minutes: number; attendees: string[] }) {
		return this.request('/mail/meetings', { method: 'POST', body: JSON.stringify(body) });
	}
	// Stage 61c — retro-link a new contact's earlier tracked mail.
	async attributeMailContact(partnerId: number, email: string) {
		return this.request('/mail/attribute-contact', { method: 'POST', body: JSON.stringify({ partner_id: partnerId, email }) });
	}
	// Stage 61c — AI redraft (503 when the completer is off).
	async redraftMail(body: { draft_html: string; thread_context?: string }) {
		return this.request('/mail/redraft', { method: 'POST', body: JSON.stringify(body) });
	}
	// Stage 61c — the composer's read-only signature preview.
	async getMailSignature() {
		return this.request('/mail/signature');
	}
	// Stage 61c — send real email as the signed-in user.
	async sendMail(body: {
		to: string[]; cc?: string[]; bcc?: string[]; subject?: string; body_html: string;
		reply_to_gmail_thread_id?: string; partner_id?: number;
		attachments?: { filename: string; content_type: string; data_base64: string }[];
	}) {
		return this.request('/mail/send', { method: 'POST', body: JSON.stringify(body) });
	}
	async patchMailThread(threadId: string, body: { unread?: boolean; folder_id?: number | null; move_to?: string }) {
		return this.request(`/mail/threads/${encodeURIComponent(threadId)}`, { method: 'PATCH', body: JSON.stringify(body) });
	}
	async deleteMailThread(threadId: string, scope: 'inbox' | 'record' = 'inbox') {
		return this.request(`/mail/threads/${encodeURIComponent(threadId)}${scope === 'record' ? '?scope=record' : ''}`, { method: 'DELETE' });
	}
	async deletePartnerEmailThread(partnerId: number, threadId: number) {
		return this.request(`/partners/${partnerId}/email-threads/${threadId}`, { method: 'DELETE' });
	}
	async restoreMailThread(threadId: string) {
		return this.request(`/mail/threads/${encodeURIComponent(threadId)}/restore`, { method: 'POST' });
	}
	async linkMailThread(threadId: string, partnerId: number) {
		return this.request(`/mail/threads/${encodeURIComponent(threadId)}/link`, { method: 'POST', body: JSON.stringify({ partner_id: partnerId }) });
	}
	async unlinkMailThread(threadId: string) {
		return this.request(`/mail/threads/${encodeURIComponent(threadId)}/unlink`, { method: 'POST' });
	}
	async createMailFolder(name: string, parentId: number | null = null) {
		return this.request('/mail/folders', { method: 'POST', body: JSON.stringify({ name, parent_id: parentId }) });
	}
	async renameMailFolder(id: number, name: string, position = 0) {
		return this.request(`/mail/folders/${id}`, { method: 'PATCH', body: JSON.stringify({ name, position }) });
	}
	async deleteMailFolder(id: number) {
		return this.request(`/mail/folders/${id}`, { method: 'DELETE' });
	}

	// Stage 46.y — import scaffold reads (empty until Stage 47 populates).
	async getPartnerEmails(id: number, limit = 50, cursor = '') {
		return this.request(`/partners/${id}/emails${toQuery({ limit, cursor })}`);
	}
	async getPartnerFiles(id: number, limit = 50, cursor = '') {
		return this.request(`/partners/${id}/files${toQuery({ limit, cursor })}`);
	}
	async getPartnerCalls(id: number, limit = 50, cursor = '') {
		return this.request(`/partners/${id}/calls${toQuery({ limit, cursor })}`);
	}
	async getPartnerMeetings(id: number, limit = 50, cursor = '') {
		return this.request(`/partners/${id}/meetings${toQuery({ limit, cursor })}`);
	}
	async getPartnerTasks(id: number, limit = 50, cursor = '') {
		return this.request(`/partners/${id}/tasks${toQuery({ limit, cursor })}`);
	}
	// Stage 47c — partner notes (option-B typed table, mirrors tasks/etc.).
	async getPartnerNotes(id: number, limit = 50, cursor = '') {
		return this.request(`/partners/${id}/notes${toQuery({ limit, cursor })}`);
	}
	async getPartnerImportedExtras(id: number) {
		return this.request(`/partners/${id}/imported-extras`);
	}
	// Stage 56 — action affordances. COS-native create path for partner
	// activity; each writes a source='internal' row.
	async logPartnerCall(id: number, body: Record<string, unknown>) {
		return this.request(`/partners/${id}/calls`, { method: 'POST', body: JSON.stringify(body) });
	}
	async logPartnerMeeting(id: number, body: Record<string, unknown>) {
		return this.request(`/partners/${id}/meetings`, { method: 'POST', body: JSON.stringify(body) });
	}
	async addPartnerNote(id: number, body: Record<string, unknown>) {
		return this.request(`/partners/${id}/notes`, { method: 'POST', body: JSON.stringify(body) });
	}
	// Marcie 4 — pin/unpin a note to the top of the activity feed (staff-only).
	async pinPartnerNote(partnerId: number, noteId: number) {
		return this.request(`/partners/${partnerId}/notes/${noteId}/pin`, { method: 'POST' });
	}
	async unpinPartnerNote(partnerId: number, noteId: number) {
		return this.request(`/partners/${partnerId}/notes/${noteId}/unpin`, { method: 'POST' });
	}
	async addPartnerTask(id: number, body: Record<string, unknown>) {
		return this.request(`/partners/${id}/tasks`, { method: 'POST', body: JSON.stringify(body) });
	}
	// Stage 56 — staff directory for the @-mention autocomplete + task
	// assignee picker. Reuses the existing /users staff list (level-1,
	// capped server-side); the composer filters client-side.
	async getUsersForMention(): Promise<MentionUser[]> {
		return this.request<MentionUser[]>('/users');
	}
	async getTicketFreshdeskAttachments(id: number, limit = 50, cursor = '') {
		return this.request(`/tickets/${id}/freshdesk-attachments${toQuery({ limit, cursor })}`);
	}
	async getTicketTimeEntries(id: number, limit = 50, cursor = '') {
		return this.request(`/tickets/${id}/time-entries${toQuery({ limit, cursor })}`);
	}
	async getTicketImportedExtras(id: number) {
		return this.request(`/tickets/${id}/imported-extras`);
	}

	// Tickets
	async getMyTickets(start = 0, limit = 25) {
		return this.request(`/tickets/mine?start=${start}&limit=${limit}`);
	}

	async getTickets(filter: TicketFilter = {}) {
		return this.request(`/tickets${toQuery(filter as Record<string, unknown>)}`);
	}

	// Stage 78b.1 — optional ?as=customer (mirrors getPartner). No UI calls it
	// with true yet; it's here for symmetry + a future "Preview as customer"
	// affordance. Partner-tier callers are narrowed server-side regardless.
	async getTicket(id: number, asCustomer = false) {
		return this.request(`/tickets/${id}${asCustomerSuffix(asCustomer)}`);
	}

	async createTicket(data: Record<string, unknown>) {
		return this.request('/tickets', { method: 'POST', body: JSON.stringify(data) });
	}

	async updateTicket(id: number, patch: Record<string, unknown>) {
		return this.request(`/tickets/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	// Marcie R6 (C1) — replace-set the partner contacts attached to a ticket.
	// Staff-only; returns the ticket's resolved contact list.
	async setTicketContacts(ticketId: number, contactIds: number[]) {
		return this.request(`/tickets/${ticketId}/contacts`, {
			method: 'PUT',
			body: JSON.stringify({ contact_ids: contactIds }),
		});
	}

	async deleteTicket(id: number) {
		return this.request(`/tickets/${id}`, { method: 'DELETE' });
	}

	// Marcie arc #40 Stage 2 — the module taxonomy dropdown (live catalog, admin-
	// editable + is_platform-aware) and the Kanban gated-transition metadata.
	async getModules() {
		return this.request(`/modules`);
	}
	async getTicketStatusGates() {
		return this.request(`/tickets/status-gates`);
	}
	// Marcie arc #40 Stage 2 — per-ticket custom tags. Assign/remove return the
	// ticket's updated label set so the section refreshes its chips in place.
	async getTicketLabels(ticketId: number) {
		return this.request(`/tickets/${ticketId}/labels`);
	}
	async getLabelsCatalog() {
		return this.request(`/labels`);
	}
	async applyTicketLabel(ticketId: number, labelId: number) {
		return this.request(`/tickets/${ticketId}/labels`, {
			method: 'POST',
			body: JSON.stringify({ label_id: labelId }),
		});
	}
	async removeTicketLabel(ticketId: number, labelId: number) {
		return this.request(`/tickets/${ticketId}/labels/${labelId}`, { method: 'DELETE' });
	}

	// Comments (Stage 2)
	async getTicketComments(ticketId: number, start = 0, limit = 50) {
		return this.request(`/tickets/${ticketId}/comments?start=${start}&limit=${limit}`);
	}

	async createTicketComment(
		ticketId: number,
		body: string,
		isInternal: boolean,
		mentions?: number[],
		// Marcie R11 / A1 — pasted screenshots / attached files on the comment.
		attachments?: { filename: string; content_type: string; data_base64: string }[],
		// Round 7 (contract section 15 d): a staff public reply that also shares the
		// ticket with its library. Sent only when true, so no other caller's body changes.
		shareWithLibrary?: boolean,
	) {
		// Stage 81 — mentions[] is the optional resolved @-mention user_id
		// array from the cos-mention-textarea composer; the server uses it
		// directly (skipping the text parse) when present, else parses body.
		return this.request(`/tickets/${ticketId}/comments`, {
			method: 'POST',
			body: JSON.stringify({ body, is_internal: isInternal, mentions, attachments, ...(shareWithLibrary ? { share_with_library: true } : {}) }),
		});
	}

	// Round 7 (contract section 15 b, c): what became of a reply's email, and who
	// will get the next one. Communico staff only; nothing a library login calls.
	// `kind` asks about the nudge or the resolve note instead of a reply (the server's default is a reply).
	async getReplyPreview(ticketId: number, kind?: 'nudge' | 'resolve'): Promise<unknown> {
		return this.request(`/tickets/${ticketId}/reply-preview${kind ? `?kind=${kind}` : ''}`);
	}

	// `acknowledgeAge` is sent only after the person has said yes to "this reply is more than a day old".
	async retryReplyEmail(ticketId: number, commentId: number, acknowledgeAge = false): Promise<unknown> {
		return this.request(`/tickets/${ticketId}/comments/${commentId}/email/retry`, {
			method: 'POST',
			...(acknowledgeAge ? { body: JSON.stringify({ acknowledge_age: true }) } : {}),
		});
	}

	async releaseReplyEmail(ticketId: number, commentId: number): Promise<unknown> {
		return this.request(`/tickets/${ticketId}/comments/${commentId}/email/release`, { method: 'POST' });
	}

	// Round 8: the support mailbox, for Communico staff (the sender's state and its breaker
	// are for administrators; the held and unmatched lists are for any staff login). No call
	// carries or returns the body of a message.
	async getSupportMailSender(): Promise<unknown> {
		return this.request('/admin/support-mail/sender');
	}

	async closeSupportMailBreaker(): Promise<unknown> {
		return this.request('/admin/support-mail/breaker/close', { method: 'POST' });
	}

	// A page is asked for by a cursor (contract section 20): `after` or `before` is the id of a line the page showed.
	async getSupportMailHeld(limit = 50, cursor?: SupportMailCursor): Promise<unknown> {
		return this.request(`/admin/support-mail/held?limit=${encodeURIComponent(String(limit))}${supportMailCursorQuery(cursor)}`);
	}

	async getSupportMailUnmatched(limit = 50, cursor?: SupportMailCursor): Promise<unknown> {
		return this.request(`/admin/support-mail/unmatched?limit=${encodeURIComponent(String(limit))}${supportMailCursorQuery(cursor)}`);
	}

	async releaseSupportMailHeld(id: number): Promise<unknown> {
		return this.request(`/admin/support-mail/held/${id}/release`, { method: 'POST' });
	}

	// A message COS could not read, or could not put on its ticket, is put back for another go.
	async retrySupportMailHeld(id: number): Promise<unknown> {
		return this.request(`/admin/support-mail/held/${id}/retry`, { method: 'POST' });
	}

	async dismissSupportMailHeld(id: number): Promise<unknown> {
		return this.request(`/admin/support-mail/held/${id}/dismiss`, { method: 'POST' });
	}

	async dismissSupportMailUnmatched(id: number): Promise<unknown> {
		return this.request(`/admin/support-mail/unmatched/${id}/dismiss`, { method: 'POST' });
	}

	// The spam list (what Gmail filed as spam in the last 30 days), paged by the same cursor as the unmatched list.
	async getSupportMailSpam(limit = 50, cursor?: SupportMailCursor): Promise<unknown> {
		return this.request(`/admin/support-mail/spam?limit=${encodeURIComponent(String(limit))}${supportMailCursorQuery(cursor)}`);
	}

	// Tells Gmail the email is not spam; the normal reader then reads it. Answers {"moved": true}.
	async notSpamSupportMailSpam(id: number): Promise<unknown> {
		return this.request(`/admin/support-mail/spam/${id}/not-spam`, { method: 'POST' });
	}

	// A person has looked and it is spam. Gmail is not called. Answers {"dismissed": true}.
	async dismissSupportMailSpam(id: number): Promise<unknown> {
		return this.request(`/admin/support-mail/spam/${id}/dismiss`, { method: 'POST' });
	}

	// A received message as plain text, so a person can read it before deciding about it.
	async getSupportMailMessageText(id: number): Promise<unknown> {
		return this.request(`/admin/support-mail/messages/${id}/text`);
	}

	// Marcie R11 / A1 — bearer-authed blob URL for an inline comment-attachment
	// image (the endpoint requires auth, so a plain <img src> would 401). Caller
	// owns revoking the URL. Mirrors fetchChatAttachmentObjectURL.
	async fetchCommentAttachmentObjectURL(attachmentId: number): Promise<string> {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/ticket-comment-attachments/${attachmentId}`, { headers });
		if (!resp.ok) {
			throw new ApiError(resp.status, (await resp.text()) || `Load failed (${resp.status})`);
		}
		return URL.createObjectURL(await resp.blob());
	}

	// Projects
	async getProjects(filter: ProjectFilter = {}) {
		return this.request(`/projects${toQuery(filter as Record<string, unknown>)}`);
	}

	async getProject(id: number) {
		return this.request(`/projects/${id}`);
	}

	// Stage 78d.1b — project KPI overview aggregate (staff-only; the
	// endpoint 404s for partner-tier callers).
	async getProjectOverview(id: number) {
		return this.request(`/projects/${id}/overview`);
	}

	async createProject(data: Record<string, unknown>) {
		return this.request('/projects', { method: 'POST', body: JSON.stringify(data) });
	}

	async updateProject(id: number, patch: Record<string, unknown>) {
		return this.request(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteProject(id: number) {
		return this.request(`/projects/${id}`, { method: 'DELETE' });
	}

	// Stage 42b — Project Phases (admin-managed vocabulary).
	// Public read powers the project-detail dropdown; admin
	// endpoints back /admin/project-phases.
	async getProjectPhases() {
		return this.request(`/projects/phases`);
	}

	async getAdminProjectPhases() {
		return this.request(`/admin/project-phases`);
	}

	async getAdminProjectPhase(id: number) {
		return this.request(`/admin/project-phases/${id}`);
	}

	async createAdminProjectPhase(data: Record<string, unknown>) {
		return this.request(`/admin/project-phases`, {
			method: 'POST', body: JSON.stringify(data),
		});
	}

	async updateAdminProjectPhase(id: number, patch: Record<string, unknown>) {
		return this.request(`/admin/project-phases/${id}`, {
			method: 'PATCH', body: JSON.stringify(patch),
		});
	}

	async deactivateAdminProjectPhase(id: number) {
		return this.request(`/admin/project-phases/${id}/deactivate`, { method: 'POST' });
	}

	async reactivateAdminProjectPhase(id: number) {
		return this.request(`/admin/project-phases/${id}/reactivate`, { method: 'POST' });
	}

	// Stage 44c — admin escalation rules CRUD. Six endpoints
	// mirror the project-phases admin shape (list / get /
	// create / patch / deactivate / reactivate); plus a public
	// read of fire history on a ticket.
	async getAdminEscalationRules() {
		return this.request(`/admin/escalation-rules`);
	}
	async getAdminEscalationRule(id: number) {
		return this.request(`/admin/escalation-rules/${id}`);
	}
	async createAdminEscalationRule(data: Record<string, unknown>) {
		return this.request(`/admin/escalation-rules`, {
			method: 'POST', body: JSON.stringify(data),
		});
	}
	async updateAdminEscalationRule(id: number, patch: Record<string, unknown>) {
		return this.request(`/admin/escalation-rules/${id}`, {
			method: 'PATCH', body: JSON.stringify(patch),
		});
	}
	async deactivateAdminEscalationRule(id: number) {
		return this.request(`/admin/escalation-rules/${id}/deactivate`, { method: 'POST' });
	}
	async reactivateAdminEscalationRule(id: number) {
		return this.request(`/admin/escalation-rules/${id}/reactivate`, { method: 'POST' });
	}
	async getTicketEscalationFires(ticketId: number) {
		return this.request(`/tickets/${ticketId}/escalation-fires`);
	}
	// Stage 82d — conversation derivation rules admin.
	async getAdminDerivationRules() {
		return this.request(`/admin/derivation-rules`);
	}
	async getAdminDerivationCapabilities() {
		return this.request(`/admin/derivation-rules/capabilities`);
	}
	async createAdminDerivationRule(data: Record<string, unknown>) {
		return this.request(`/admin/derivation-rules`, {
			method: 'POST', body: JSON.stringify(data),
		});
	}
	async setAdminDerivationRuleEnabled(id: number, enabled: boolean) {
		return this.request(`/admin/derivation-rules/${id}/enabled`, {
			method: 'PATCH', body: JSON.stringify({ enabled }),
		});
	}
	async deleteAdminDerivationRule(id: number) {
		return this.request(`/admin/derivation-rules/${id}`, { method: 'DELETE' });
	}

	// Stage 44d — ticket templates: 6 admin endpoints + the
	// public active-only list for the new-ticket picker.
	async getAdminTicketTemplates() {
		return this.request(`/admin/ticket-templates`);
	}
	async getAdminTicketTemplate(id: number) {
		return this.request(`/admin/ticket-templates/${id}`);
	}
	async createAdminTicketTemplate(data: Record<string, unknown>) {
		return this.request(`/admin/ticket-templates`, {
			method: 'POST', body: JSON.stringify(data),
		});
	}
	async updateAdminTicketTemplate(id: number, patch: Record<string, unknown>) {
		return this.request(`/admin/ticket-templates/${id}`, {
			method: 'PATCH', body: JSON.stringify(patch),
		});
	}
	async deactivateAdminTicketTemplate(id: number) {
		return this.request(`/admin/ticket-templates/${id}/deactivate`, { method: 'POST' });
	}
	async reactivateAdminTicketTemplate(id: number) {
		return this.request(`/admin/ticket-templates/${id}/reactivate`, { method: 'POST' });
	}
	async getTicketTemplates() {
		return this.request(`/tickets/templates`);
	}

	// Stage 44d — custom field definitions: 5 admin endpoints +
	// 3 ticket-scoped value endpoints (list / set / clear).
	// Stage 49 — admin data console (super_admin only; the backend gate
	// is RequireSuperAdmin — a plain admin gets 403 from these).
	dataConsoleGenerate(prompt: string) {
		return this.request(`/admin/data-console/generate`, { method: 'POST', body: JSON.stringify({ prompt }) });
	}
	dataConsolePreview(plan: unknown, sampleLimit = 10) {
		return this.request(`/admin/data-console/preview`, {
			method: 'POST',
			body: JSON.stringify({ plan, sample_limit: sampleLimit }),
		});
	}
	dataConsoleExecute(body: { prompt: string; plan: unknown; confirm?: string }) {
		return this.request(`/admin/data-console/execute`, { method: 'POST', body: JSON.stringify(body) });
	}
	dataConsoleOperations(limit = 50) {
		return this.request(`/admin/data-console/operations?limit=${limit}`);
	}
	dataConsoleRevert(id: number) {
		return this.request(`/admin/data-console/operations/${id}/revert`, { method: 'POST' });
	}
	dataConsoleHealth() {
		return this.request(`/admin/data-console/health`);
	}

	// Stage 47e.1 — admin schema-explorer.
	async getSchemaCandidates(entity: string) {
		return this.request(`/admin/schema-explorer/candidates${toQuery({ entity })}`);
	}
	async getSchemaMappings(entity?: string) {
		return this.request(`/admin/schema-explorer/mappings${toQuery({ entity: entity ?? '' })}`);
	}
	async schemaPromote(body: Record<string, unknown>) {
		return this.request(`/admin/schema-explorer/promote`, { method: 'POST', body: JSON.stringify(body) });
	}
	async schemaRollback(mappingId: number) {
		return this.request(`/admin/schema-explorer/rollback`, { method: 'POST', body: JSON.stringify({ mapping_id: mappingId }) });
	}

	async getAdminCustomFields() {
		return this.request(`/admin/custom-fields`);
	}
	async createAdminCustomField(data: Record<string, unknown>) {
		return this.request(`/admin/custom-fields`, {
			method: 'POST', body: JSON.stringify(data),
		});
	}
	async updateAdminCustomField(id: number, patch: Record<string, unknown>) {
		return this.request(`/admin/custom-fields/${id}`, {
			method: 'PATCH', body: JSON.stringify(patch),
		});
	}
	async deactivateAdminCustomField(id: number) {
		return this.request(`/admin/custom-fields/${id}/deactivate`, { method: 'POST' });
	}
	async reactivateAdminCustomField(id: number) {
		return this.request(`/admin/custom-fields/${id}/reactivate`, { method: 'POST' });
	}
	async getTicketCustomFields(ticketId: number) {
		return this.request(`/tickets/${ticketId}/custom-fields`);
	}
	async setTicketCustomField(ticketId: number, fieldId: number, value: unknown) {
		return this.request(`/tickets/${ticketId}/custom-fields/${fieldId}`, {
			method: 'PUT', body: JSON.stringify({ value }),
		});
	}
	async clearTicketCustomField(ticketId: number, fieldId: number) {
		return this.request(`/tickets/${ticketId}/custom-fields/${fieldId}`, {
			method: 'DELETE',
		});
	}

	// Stage 44c — bulk-update tickets. Hard cap of 100 ticket
	// IDs per call (server-side enforced); partial-failure
	// shape is { succeeded_ids: [...], failed: [{ticket_id, error}] }.
	async bulkUpdateTickets(ticketIDs: number[], updates: Record<string, unknown>) {
		return this.request(`/tickets/bulk-update`, {
			method: 'POST',
			body: JSON.stringify({ ticket_ids: ticketIDs, updates }),
		});
	}

	// Audit feed (Stage 2, level-1 staff only on the backend)
	async getRecentActivity(limit = 10) {
		return this.request(`/audit/recent?limit=${limit}`);
	}

	// Stage 18: adaptive work surface. /work/next returns a system-
	// prioritised ranked list with explicit reasoning chips. Dismissal
	// recalculates rather than fighting the user — see spec §2.4.
	async getWorkNext(limit = 12) {
		return this.request(`/work/next?limit=${limit}`);
	}

	async dismissWorkItem(type: string, entityId: number) {
		return this.request(`/work/dismiss`, {
			method: 'POST',
			body: JSON.stringify({ type, entity_id: entityId }),
		});
	}

	// Stage 4: contacts under a partner.
	async getContactsForPartner(partnerId: number) {
		return this.request(`/partners/${partnerId}/contacts`);
	}

	// Marcie arc 2b-3 — paginated + server-side searched contacts for the
	// partner-detail tile (the 1,126-contact partner can't client-filter).
	// Returns { data, total, start, limit }.
	async getPartnerContacts(partnerId: number, opts: { q?: string; start?: number; limit?: number } = {}) {
		const query = toQuery({ q: opts.q ?? '', start: opts.start ?? 0, limit: opts.limit ?? 50 });
		return this.request(`/partners/${partnerId}/contacts${query}`);
	}

	// Stage 55 — global contact list + hydrated single-contact detail.
	async getContacts(filter: Record<string, unknown> = {}) {
		return this.request(`/contacts${toQuery(filter)}`);
	}
	async getContact(id: number) {
		return this.request(`/contacts/${id}`);
	}

	async createContact(partnerId: number, data: Record<string, unknown>) {
		return this.request(`/partners/${partnerId}/contacts`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateContact(id: number, patch: Record<string, unknown>) {
		return this.request(`/contacts/${id}`, {
			method: 'PATCH',
			body: JSON.stringify(patch),
		});
	}

	// Stage 77 — admin-only customer-portal access record (Phase B groundwork).
	// Records who has access; no login flow is wired yet. Backend enforces
	// RequireAdminRole; each returns the updated contact.
	async grantContactPortal(id: number) {
		return this.request(`/contacts/${id}/grant-portal`, { method: 'POST' });
	}
	async revokeContactPortal(id: number) {
		return this.request(`/contacts/${id}/revoke-portal`, { method: 'POST' });
	}

	async deleteContact(id: number) {
		return this.request(`/contacts/${id}`, { method: 'DELETE' });
	}

	// Stage 4: staff picker — populates ownership / assignee dropdowns.
	async getUsers() {
		return this.request(`/users`);
	}

	// Marcie arc 2a — the staff bootstrap of every partner-property lookup's
	// active values (for the partner-detail edit-modal selects), one round-trip.
	async getPartnerPropertyOptions() {
		return this.request(`/partner-property-options`);
	}

	// Marcie arc 2a — generic admin CRUD for the 7 partner-property lookups,
	// parameterized by the route path segment (partner-ils-types, …). The
	// shared cos-lookup-list-editor consumes these; each admin page passes its
	// path. Backend: the 1a AdminPartnerLookupHandler (RequireAdminRole).
	async listPartnerLookup(path: string) {
		return this.request(`/admin/${path}`);
	}
	async createPartnerLookup(path: string, body: Record<string, unknown>) {
		return this.request(`/admin/${path}`, { method: 'POST', body: JSON.stringify(body) });
	}
	async updatePartnerLookup(path: string, id: number, patch: Record<string, unknown>) {
		return this.request(`/admin/${path}/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	async deactivatePartnerLookup(path: string, id: number) {
		return this.request(`/admin/${path}/${id}/deactivate`, { method: 'POST' });
	}
	async reactivatePartnerLookup(path: string, id: number) {
		return this.request(`/admin/${path}/${id}/reactivate`, { method: 'POST' });
	}

	// Stage 5: per-partner module catalogue (spec §3.1 modules array).
	async getPartnerModules(partnerId: number) {
		return this.request(`/partners/${partnerId}/modules`);
	}

	async updatePartnerModule(partnerId: number, moduleName: string, patch: Record<string, unknown>) {
		return this.request(`/partners/${partnerId}/modules/${moduleName}`, {
			method: 'PATCH',
			body: JSON.stringify(patch),
		});
	}

	// Stage 11: AI Ticket Triage Agent (spec §7.7 Triage Agent).
	async getTicketTriage(ticketId: number) {
		return this.request(`/tickets/${ticketId}/triage`);
	}

	async generateTicketTriage(ticketId: number): Promise<{ suggestion: any; rateLimited: boolean }> {
		const headers: Record<string, string> = { 'Content-Type': 'application/json' };
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const r = await fetch(`${API_BASE}/tickets/${ticketId}/triage`, { method: 'POST', headers });
		if (r.status === 401) {
			this.clearToken();
			window.dispatchEvent(new CustomEvent('cos:auth:expired'));
			throw new ApiError(401, 'Authentication expired');
		}
		const body = await r.json();
		if (r.status === 200 || r.status === 429) {
			return { suggestion: body, rateLimited: r.status === 429 };
		}
		throw new ApiError(r.status, body.message || body.error || `Triage failed (HTTP ${r.status})`);
	}

	async applyTriageSuggestion(suggestionId: number, acceptedFields: string[]) {
		return this.request(`/ai-triage/${suggestionId}/apply`, {
			method: 'POST', body: JSON.stringify({ accepted_fields: acceptedFields }),
		});
	}

	async rejectTriageSuggestion(suggestionId: number) {
		return this.request(`/ai-triage/${suggestionId}/reject`, { method: 'POST' });
	}

	// Stage 10: AI L1 reply drafter (spec §7.7 Email Response Agent).
	// Returns the draft directly. The HTTP layer reports 429 when a
	// recent draft already exists — the API client treats that as a
	// success and returns the cached draft so callers don't have to
	// special-case it.
	async generateAIDraft(ticketId: number): Promise<{ draft: any; rateLimited: boolean }> {
		const headers: Record<string, string> = { 'Content-Type': 'application/json' };
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const r = await fetch(`${API_BASE}/tickets/${ticketId}/draft-reply`, {
			method: 'POST', headers,
		});
		if (r.status === 401) {
			this.clearToken();
			window.dispatchEvent(new CustomEvent('cos:auth:expired'));
			throw new ApiError(401, 'Authentication expired');
		}
		const body = await r.json();
		if (r.status === 200 || r.status === 429) {
			return { draft: body, rateLimited: r.status === 429 };
		}
		throw new ApiError(r.status, body.message || body.error || `AI draft failed (HTTP ${r.status})`);
	}

	async useAIDraft(draftId: number, commentId: number) {
		return this.request(`/ai-drafts/${draftId}/use`, {
			method: 'POST', body: JSON.stringify({ comment_id: commentId }),
		});
	}

	async discardAIDraft(draftId: number) {
		return this.request(`/ai-drafts/${draftId}/discard`, { method: 'POST' });
	}

	// Stage 9: Knowledge Base (spec §7.4).
	async getKBArticles(filter: Record<string, unknown> = {}) {
		return this.request(`/kb/articles${toQuery(filter)}`);
	}

	async getKBArticle(idOrSlug: string | number) {
		return this.request(`/kb/articles/${idOrSlug}`);
	}

	async createKBArticle(data: Record<string, unknown>) {
		return this.request(`/kb/articles`, { method: 'POST', body: JSON.stringify(data) });
	}

	async updateKBArticle(id: number, patch: Record<string, unknown>) {
		return this.request(`/kb/articles/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async transitionKBArticle(id: number, status: string) {
		return this.request(`/kb/articles/${id}/transition`, {
			method: 'POST', body: JSON.stringify({ status }),
		});
	}

	async getKBVersions(id: number) {
		return this.request(`/kb/articles/${id}/versions`);
	}

	async getKBVersion(id: number, version: number) {
		return this.request(`/kb/articles/${id}/versions/${version}`);
	}

	// Stage 41b-3 — KB inline-media upload (image / video).
	// Multipart; can't go through `request()` because that hardcodes
	// JSON content type. Returns { url, filename, mime_type, size_bytes, uploaded_at }.
	async uploadKBMedia(file: File, articleSlug?: string): Promise<{
		url: string;
		filename: string;
		mime_type: string;
		size_bytes: number;
		uploaded_at: string;
	}> {
		const form = new FormData();
		form.append('file', file);
		const qs = articleSlug ? `?article_slug=${encodeURIComponent(articleSlug)}` : '';
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/kb/uploads${qs}`, {
			method: 'POST',
			headers,
			body: form,
		});
		if (!resp.ok) {
			const text = await resp.text();
			throw new ApiError(resp.status, text || `Upload failed (${resp.status})`);
		}
		return resp.json();
	}

	// Stage 41a — "Was this helpful?" footer on the article view.
	// Upsert semantics on the backend (ON CONFLICT DO UPDATE), so
	// calling this twice with different `helpful` values changes
	// the user's vote rather than spawning duplicates.
	async submitKBFeedback(slugOrID: string | number, helpful: boolean) {
		return this.request(`/kb/articles/${slugOrID}/feedback`, {
			method: 'POST', body: JSON.stringify({ helpful }),
		});
	}

	// AI help assistant (docs/help/ai-help-agent-spec.md) — retrieval-
	// grounded natural-language Q&A over the published help corpus.
	// answered:false ("I don't have documentation for that yet") is a
	// normal 200 response, not an error — see cos-help-panel.ts's
	// rendering of it as a designed state.
	/** The published help index for a module, from the live store. */
	async getHelpIndex(module: string) {
		return this.request(`/help/${encodeURIComponent(module)}`);
	}

	/** One published help article, body included, from the live store. */
	async getHelpArticle(module: string, pageId: string) {
		return this.request(`/help/${encodeURIComponent(module)}/${encodeURIComponent(pageId)}`);
	}

	async askHelp(question: string, module?: string, screen?: string, articleId?: string): Promise<{
		answered: boolean;
		answer: string;
		citations: Array<{
			page_id: string;
			module: string;
			title: string;
			screen?: string;
			has_walkthrough: boolean;
		}>;
	}> {
		return this.request('/help/ask', {
			method: 'POST',
			body: JSON.stringify({
				question,
				module: module || undefined,
				screen: screen || undefined,
				article_id: articleId || undefined,
				// Only used for a caller with no library of their own (staff
				// browsing a library); the server words a library login itself.
				cardholder_terminology: activeCardholderTerm().plural,
			}),
		});
	}

	// Stage 41b-1 — KB article-types taxonomy. The public read
	// powers the editor's type picker + the reader-view label; the
	// admin surface adds list-with-inactive + write methods.
	async getKBArticleTypes() {
		return this.request(`/kb/article-types`);
	}

	async getAdminKBArticleTypes() {
		return this.request(`/admin/kb-article-types`);
	}

	async getAdminKBArticleType(id: number) {
		return this.request(`/admin/kb-article-types/${id}`);
	}

	async createAdminKBArticleType(data: Record<string, unknown>) {
		return this.request(`/admin/kb-article-types`, {
			method: 'POST', body: JSON.stringify(data),
		});
	}

	async updateAdminKBArticleType(id: number, patch: Record<string, unknown>) {
		return this.request(`/admin/kb-article-types/${id}`, {
			method: 'PATCH', body: JSON.stringify(patch),
		});
	}

	async deactivateAdminKBArticleType(id: number) {
		return this.request(`/admin/kb-article-types/${id}/deactivate`, { method: 'POST' });
	}

	async reactivateAdminKBArticleType(id: number) {
		return this.request(`/admin/kb-article-types/${id}/reactivate`, { method: 'POST' });
	}

	// Stage 8: Customer Implementation Portal (spec §8.6).
	async getImplementationOverview(partnerId: number) {
		return this.request(`/partners/${partnerId}/implementation`);
	}

	async getImplementationTasks(partnerId: number, moduleName: string) {
		return this.request(`/partners/${partnerId}/implementation/${moduleName}`);
	}

	async updateImplementationTask(taskId: number, patch: Record<string, unknown>) {
		return this.request(`/implementation-tasks/${taskId}`, {
			method: 'PATCH',
			body: JSON.stringify(patch),
		});
	}

	// Stage 42a — implementation team cross-partner dashboard.
	// Filter shape mirrors the backend ?owner / ?health / ?phase
	// query params; values are URL-encoded by toQuery.
	async getImplementationDashboard(
		filter: { owner?: string; health?: string; phase?: string } = {},
	) {
		return this.request(
			`/implementation/dashboard${toQuery(filter as Record<string, unknown>)}`,
		);
	}

	// Stage 42c — project milestones. CRUD + reorder.
	// Stage 68e-7b — asCustomer appends ?as=customer for the staff
	// "View as partner" preview (server returns the client_visible-only
	// projection).
	async getProjectMilestones(projectId: number, asCustomer = false) {
		return this.request(`/projects/${projectId}/milestones${asCustomerSuffix(asCustomer)}`);
	}

	async createProjectMilestone(projectId: number, data: Record<string, unknown>) {
		return this.request(`/projects/${projectId}/milestones`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateProjectMilestone(milestoneId: number, patch: Record<string, unknown>) {
		return this.request(`/project-milestones/${milestoneId}`, {
			method: 'PATCH',
			body: JSON.stringify(patch),
		});
	}

	async deleteProjectMilestone(milestoneId: number) {
		return this.request(`/project-milestones/${milestoneId}`, { method: 'DELETE' });
	}

	async reorderProjectMilestones(projectId: number, order: number[]) {
		return this.request(`/projects/${projectId}/milestones/reorder`, {
			method: 'POST',
			body: JSON.stringify({ order }),
		});
	}

	// Stage 68a — project todo lists, todos, and members.
	async getProjectTodoLists(projectId: number, asCustomer = false) {
		return this.request(`/projects/${projectId}/todo-lists${asCustomerSuffix(asCustomer)}`);
	}

	async createProjectTodoList(projectId: number, data: Record<string, unknown>) {
		return this.request(`/projects/${projectId}/todo-lists`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateProjectTodoList(listId: number, patch: Record<string, unknown>) {
		return this.request(`/project-todo-lists/${listId}`, {
			method: 'PATCH',
			body: JSON.stringify(patch),
		});
	}

	async deleteProjectTodoList(listId: number) {
		return this.request(`/project-todo-lists/${listId}`, { method: 'DELETE' });
	}

	async reorderProjectTodoLists(projectId: number, order: number[]) {
		return this.request(`/projects/${projectId}/todo-lists/reorder`, {
			method: 'POST',
			body: JSON.stringify({ order }),
		});
	}

	async getProjectTodos(projectId: number, asCustomer = false) {
		return this.request(`/projects/${projectId}/todos${asCustomerSuffix(asCustomer)}`);
	}

	async createProjectTodo(projectId: number, data: Record<string, unknown>) {
		return this.request(`/projects/${projectId}/todos`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateProjectTodo(todoId: number, patch: Record<string, unknown>) {
		return this.request(`/project-todos/${todoId}`, {
			method: 'PATCH',
			body: JSON.stringify(patch),
		});
	}

	async deleteProjectTodo(todoId: number) {
		return this.request(`/project-todos/${todoId}`, { method: 'DELETE' });
	}

	// Stage 68e-4/7c — the one to-do write an external (partner) member is
	// allowed: toggle the done state of their own partner-side, visible
	// todo. Staff may use it too. Body {done}; server enforces the gate.
	async completeProjectTodo(todoId: number, done: boolean) {
		return this.request(`/project-todos/${todoId}/complete`, {
			method: 'POST',
			body: JSON.stringify({ done }),
		});
	}

	async reorderProjectTodos(projectId: number, order: number[]) {
		return this.request(`/projects/${projectId}/todos/reorder`, {
			method: 'POST',
			body: JSON.stringify({ order }),
		});
	}

	async getProjectMembers(projectId: number) {
		return this.request(`/projects/${projectId}/members`);
	}

	async addProjectMember(projectId: number, data: Record<string, unknown>) {
		return this.request(`/projects/${projectId}/members`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async removeProjectMember(memberId: number) {
		return this.request(`/project-members/${memberId}`, { method: 'DELETE' });
	}

	// Stage 42c — project status reports.
	async getProjectStatusReports(projectId: number, asCustomer = false) {
		return this.request(`/projects/${projectId}/status-reports${asCustomerSuffix(asCustomer)}`);
	}

	async createProjectStatusReport(projectId: number, data: Record<string, unknown>) {
		return this.request(`/projects/${projectId}/status-reports`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateProjectStatusReport(reportId: number, patch: Record<string, unknown>) {
		return this.request(`/project-status-reports/${reportId}`, {
			method: 'PATCH',
			body: JSON.stringify(patch),
		});
	}

	async deleteProjectStatusReport(reportId: number) {
		return this.request(`/project-status-reports/${reportId}`, { method: 'DELETE' });
	}

	// Stage 68c-1 — project schedule events.
	async getProjectEvents(projectId: number, asCustomer = false) {
		return this.request(`/projects/${projectId}/events${asCustomerSuffix(asCustomer)}`);
	}

	async createProjectEvent(projectId: number, data: Record<string, unknown>) {
		return this.request(`/projects/${projectId}/events`, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateProjectEvent(eventId: number, patch: Record<string, unknown>) {
		return this.request(`/project-events/${eventId}`, {
			method: 'PATCH',
			body: JSON.stringify(patch),
		});
	}

	async deleteProjectEvent(eventId: number) {
		return this.request(`/project-events/${eventId}`, { method: 'DELETE' });
	}

	// Stage 42h — project documents via Google Drive. GET returns
	// the local cache (which the backend refreshes from Drive on
	// stale/empty); the response carries an integration_disabled
	// flag when Drive isn't configured.
	// Stage 68c-2 — the documents surface is a nestable node tree.
	// `parent` is the Drive id of the folder being viewed; omit (or
	// pass '') for the project root. The response carries the child
	// nodes (files + folders), the breadcrumb trail, and the
	// integration_disabled flag when Drive isn't configured.
	async getProjectDocuments(projectId: number, parent = '', asCustomer = false) {
		const q: Record<string, string> = {};
		if (parent) q.parent = parent;
		if (asCustomer) q.as = 'customer';
		return this.request(`/projects/${projectId}/documents${Object.keys(q).length ? toQuery(q) : ''}`);
	}

	async refreshProjectDocuments(projectId: number, parent = '') {
		return this.request(`/projects/${projectId}/documents/refresh${parent ? toQuery({ parent }) : ''}`, { method: 'POST' });
	}

	// Multipart upload — bypasses this.request because we don't
	// want a JSON Content-Type header on a multipart body. The
	// target folder rides along as a form field.
	async uploadProjectDocument(projectId: number, file: File, parent = '') {
		const form = new FormData();
		form.append('file', file);
		if (parent) form.append('parent', parent);
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/projects/${projectId}/documents`, {
			method: 'POST',
			headers,
			body: form,
		});
		if (!resp.ok) {
			const text = await resp.text();
			throw new ApiError(resp.status, text || `Upload failed (${resp.status})`);
		}
		return resp.json();
	}

	// Create a subfolder inside `parent` (root when '').
	async createProjectFolder(projectId: number, name: string, parent = '') {
		return this.request(`/projects/${projectId}/documents/folders`, {
			method: 'POST',
			body: JSON.stringify({ name, parent }),
		});
	}

	// Create a blank Google Doc/Sheet/Slides inside `parent`. kind ∈
	// { 'doc', 'sheet', 'slides' }. The new file opens live in the
	// Drive editor via its web_view_link.
	async createProjectFile(projectId: number, name: string, kind: 'doc' | 'sheet' | 'slides', parent = '') {
		return this.request(`/projects/${projectId}/documents/files`, {
			method: 'POST',
			body: JSON.stringify({ name, kind, parent }),
		});
	}

	// Patch a node: rename (name), move (parent), and/or flip the
	// customer-visibility overlay (client_visible). Any subset.
	async patchProjectDocument(
		projectId: number,
		driveFileID: string,
		patch: { name?: string; parent?: string; client_visible?: boolean },
	) {
		return this.request(`/projects/${projectId}/documents/${encodeURIComponent(driveFileID)}`, {
			method: 'PATCH',
			body: JSON.stringify(patch),
		});
	}

	async deleteProjectDocument(projectId: number, driveFileID: string) {
		return this.request(`/projects/${projectId}/documents/${encodeURIComponent(driveFileID)}`, { method: 'DELETE' });
	}

	// Stage 68d-1 — project message board (posts + comment threads).
	async getProjectMessages(projectId: number, asCustomer = false) {
		return this.request(`/projects/${projectId}/messages${asCustomerSuffix(asCustomer)}`);
	}
	async createProjectMessage(projectId: number, body: { title: string; body?: string; client_visible?: boolean; pinned?: boolean }) {
		return this.request(`/projects/${projectId}/messages`, { method: 'POST', body: JSON.stringify(body) });
	}
	async getProjectMessage(messageId: number) {
		return this.request(`/project-messages/${messageId}`);
	}
	async patchProjectMessage(messageId: number, patch: { title?: string; body?: string; client_visible?: boolean; pinned?: boolean }) {
		return this.request(`/project-messages/${messageId}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	async deleteProjectMessage(messageId: number) {
		return this.request(`/project-messages/${messageId}`, { method: 'DELETE' });
	}
	async addProjectMessageComment(messageId: number, body: string) {
		return this.request(`/project-messages/${messageId}/comments`, { method: 'POST', body: JSON.stringify({ body }) });
	}
	async patchProjectMessageComment(commentId: number, body: string) {
		return this.request(`/project-message-comments/${commentId}`, { method: 'PATCH', body: JSON.stringify({ body }) });
	}
	async deleteProjectMessageComment(commentId: number) {
		return this.request(`/project-message-comments/${commentId}`, { method: 'DELETE' });
	}

	// Stage 42d — project activity feed + manual notes.
	// `getProjectActivity` returns the merged timeline
	// (tickets + notes + gmail placeholder); the note CRUD
	// methods power the inline editor inside the feed.
	async getProjectActivity(projectId: number, opts: { limit?: number; offset?: number } = {}) {
		return this.request(
			`/projects/${projectId}/activity${toQuery(opts as Record<string, unknown>)}`,
		);
	}

	async getProjectActivityNotes(projectId: number) {
		return this.request(`/projects/${projectId}/activity-notes`);
	}

	async createProjectActivityNote(projectId: number, body: string) {
		return this.request(`/projects/${projectId}/activity-notes`, {
			method: 'POST',
			body: JSON.stringify({ body }),
		});
	}

	// Stage 42i fold-in — URLs moved under the project nest so
	// all four note endpoints share the same shape. The
	// signatures here grow a projectId arg; callers thread it
	// from the parent component's projectId state.
	async updateProjectActivityNote(projectId: number, noteId: number, body: string) {
		return this.request(`/projects/${projectId}/activity-notes/${noteId}`, {
			method: 'PATCH',
			body: JSON.stringify({ body }),
		});
	}

	async deleteProjectActivityNote(projectId: number, noteId: number) {
		return this.request(`/projects/${projectId}/activity-notes/${noteId}`, { method: 'DELETE' });
	}

	// Stage 42e — per-staff "My work" surface. No params; the
	// caller's identity is the only filter. Returns
	// { my_projects, my_tasks, summary }.
	async getMyImplementations() {
		return this.request(`/me/implementations`);
	}

	// Stage 44a — ticket attachments. Same multipart upload
	// pattern as uploadProjectDocument; download is a plain
	// browser link (no JS client needed — the frontend just
	// builds the URL).
	async getTicketAttachments(ticketId: number) {
		return this.request(`/tickets/${ticketId}/attachments`);
	}

	async uploadTicketAttachment(ticketId: number, file: File) {
		const form = new FormData();
		form.append('file', file);
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/tickets/${ticketId}/attachments`, {
			method: 'POST',
			headers,
			body: form,
		});
		if (!resp.ok) {
			const text = await resp.text();
			throw new ApiError(resp.status, text || `Upload failed (${resp.status})`);
		}
		return resp.json();
	}

	async deleteTicketAttachment(ticketId: number, attachmentId: number) {
		return this.request(`/tickets/${ticketId}/attachments/${attachmentId}`, { method: 'DELETE' });
	}

	// Stage 44a — download with Bearer auth.
	//
	// Backend auth middleware only reads the Authorization
	// header, so a plain <a href> would 401. We do the dance
	// here: fetch with the bearer, convert to Blob, build an
	// object URL, and trigger a synthetic <a download> click.
	// The Content-Disposition header from the server provides
	// the filename; we strip it from the response header and
	// pass it to the synthetic anchor so the browser saves
	// under the original name.
	async downloadTicketAttachment(ticketId: number, attachmentId: number, fallbackFilename: string) {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(
			`${API_BASE}/tickets/${ticketId}/attachments/${attachmentId}/download`,
			{ headers },
		);
		if (!resp.ok) {
			const text = await resp.text();
			throw new ApiError(resp.status, text || `Download failed (${resp.status})`);
		}
		// Extract filename from Content-Disposition if present.
		let filename = fallbackFilename;
		const disp = resp.headers.get('Content-Disposition') || '';
		const match = disp.match(/filename="?([^";]+)"?/i);
		if (match) filename = match[1];

		const blob = await resp.blob();
		const url = URL.createObjectURL(blob);
		try {
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
		} finally {
			// Revoke after a beat to let the browser kick off
			// the save; revoking too early aborts the download
			// in some browsers.
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		}
	}

	// Stage 44b — ticket watchers + mention autocomplete.
	//
	// Watchers: get list + is_watching flag for the header
	// button, plus POST/DELETE /self for toggle.
	//
	// Mentionable users: backs the @-autocomplete dropdown
	// inside the comment composer. Scope-filtered server-side
	// (a partner viewer only sees own-partner + staff).
	async getTicketWatchers(ticketId: number) {
		return this.request(`/tickets/${ticketId}/watchers`);
	}
	// Marcie Round 2 (Phase B) — per-ticket change history (audit log). Staff-only.
	async getTicketHistory(ticketId: number, opts: { start?: number; limit?: number; includeSystem?: boolean } = {}) {
		const q = toQuery({
			start: opts.start ?? 0,
			limit: opts.limit ?? 20,
			include_system: opts.includeSystem ? 'true' : '',
		});
		return this.request(`/tickets/${ticketId}/history${q}`);
	}
	async watchTicket(ticketId: number) {
		return this.request(`/tickets/${ticketId}/watchers/self`, { method: 'POST' });
	}
	async unwatchTicket(ticketId: number) {
		return this.request(`/tickets/${ticketId}/watchers/self`, { method: 'DELETE' });
	}
	// Marcie Round 2 (T2b) — anyone-adds-anyone: add/remove a specific staff user
	// as a watcher (staff-only server-side).
	async addTicketWatcher(ticketId: number, userId: number) {
		return this.request(`/tickets/${ticketId}/watchers/${userId}`, { method: 'POST' });
	}
	async removeTicketWatcher(ticketId: number, userId: number) {
		return this.request(`/tickets/${ticketId}/watchers/${userId}`, { method: 'DELETE' });
	}
	async getMentionableUsers(ticketId: number, q = '', limit = 8) {
		const params = new URLSearchParams();
		if (q) params.set('q', q);
		if (limit) params.set('limit', String(limit));
		const qs = params.toString();
		return this.request(`/tickets/${ticketId}/mentionable-users${qs ? `?${qs}` : ''}`);
	}

	// Stage 46g Part 3 — unified activity drawer feed. Returns
	// { data, has_more, next_cursor }. Cursor is opaque; pass it
	// back verbatim to fetch the next page.
	async getTicketActivity(ticketId: number, opts: { limit?: number; cursor?: string } = {}) {
		const params = new URLSearchParams();
		if (opts.limit) params.set('limit', String(opts.limit));
		if (opts.cursor) params.set('cursor', opts.cursor);
		const qs = params.toString();
		return this.request(`/tickets/${ticketId}/activity${qs ? `?${qs}` : ''}`);
	}

	// Stage 46g Part 4 — related tickets (similarity-scored).
	// Returns { items: [{ ticket, score, reasons }] }.
	async getRelatedTickets(ticketId: number, limit = 5) {
		return this.request(`/tickets/${ticketId}/related?limit=${limit}`);
	}

	// Stage 46g Part 5 — AI draft-assist for customer-thread composer.
	// Returns { intent, draft }. 503 when ANTHROPIC_API_KEY not
	// configured; 422 on bad intent; 404 on cross-partner.
	async draftAssistTicket(ticketId: number, intent: string) {
		return this.request(`/tickets/${ticketId}/draft-assist`, {
			method: 'POST',
			body: JSON.stringify({ intent }),
		});
	}

	// Stage 46b — ticket outstanding actions. Read open to any
	// authed user (service-layer scope filters); mutations are
	// staff-only and surface 404 when a partner attempts.
	async getTicketOutstandingActions(ticketId: number) {
		return this.request(`/tickets/${ticketId}/outstanding-actions`);
	}
	async createTicketOutstandingAction(ticketId: number, body: { description: string; owner_side: string; due_date?: string | null }) {
		return this.request(`/tickets/${ticketId}/outstanding-actions`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}
	async updateTicketOutstandingAction(actionId: number, patch: Record<string, unknown>) {
		return this.request(`/ticket-outstanding-actions/${actionId}`, {
			method: 'PATCH',
			body: JSON.stringify(patch),
		});
	}
	async deleteTicketOutstandingAction(actionId: number) {
		return this.request(`/ticket-outstanding-actions/${actionId}`, { method: 'DELETE' });
	}

	// Big Arc Sub-arc 1 — unified tasks (staff-only; the /tasks group is
	// Level-gated). Scope filters (ticket_id/partner_id/…) narrow to a record.
	async getTasks(filter: Record<string, string | number | boolean> = {}) {
		return this.request(`/tasks${toQuery(filter)}`);
	}
	async createTask(body: Record<string, unknown>) {
		return this.request(`/tasks`, { method: 'POST', body: JSON.stringify(body) });
	}
	async completeTask(id: number, done: boolean) {
		return this.request(`/tasks/${id}/complete`, {
			method: 'POST',
			body: JSON.stringify({ done }),
		});
	}
	// Marcie R4 — a created task can now be edited/deleted (the backend PATCH/GET/
	// DELETE always existed; the frontend just never called them).
	async getTask(id: number) {
		return this.request(`/tasks/${id}`);
	}
	async updateTask(id: number, patch: Record<string, unknown>) {
		return this.request(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	async deleteTask(id: number) {
		return this.request(`/tasks/${id}`, { method: 'DELETE' });
	}
	async reorderTicketOutstandingActions(ticketId: number, order: number[]) {
		return this.request(`/tickets/${ticketId}/outstanding-actions/reorder`, {
			method: 'POST',
			body: JSON.stringify({ order }),
		});
	}

	// Stage 46b — AI recap. GET returns {recap_text, generated_at,
	// is_stale}. First call on an un-recapped ticket returns 404
	// with a stable `recap_not_generated` shape; the UI shows a
	// placeholder while the background EnqueueRefresh catches up.
	async getTicketRecap(ticketId: number) {
		return this.request(`/tickets/${ticketId}/recap`);
	}
	async refreshTicketRecapAdmin(ticketId: number) {
		return this.request(`/admin/tickets/${ticketId}/recap/refresh`, { method: 'POST' });
	}

	// Ticket links (staff only: the server answers a library caller 404).
	async getTicketLinks(ticketId: number): Promise<TicketLink[]> {
		const res = await this.request<{ links?: TicketLink[] | null }>(`/tickets/${ticketId}/links`);
		// A nil list arrives as null; never hand a null to a render.
		return res?.links ?? [];
	}

	async createTicketLink(ticketId: number, kind: TicketLinkKind, toTicketId: number): Promise<TicketLink> {
		return this.request<TicketLink>(`/tickets/${ticketId}/links`, {
			method: 'POST',
			body: JSON.stringify({ kind, to_ticket_id: toTicketId }),
		});
	}

	async deleteTicketLink(ticketId: number, linkId: number): Promise<void> {
		await this.request<void>(`/tickets/${ticketId}/links/${linkId}`, { method: 'DELETE' });
	}

	// One call per page of rows. The server takes at most 200 ids a call, so a
	// longer list is split rather than dropped. A chunk that fails keeps the chips
	// the other chunks fetched; only when every chunk fails does the call throw.
	async getTicketLinkSummaries(ids: number[]): Promise<Record<string, TicketLinkSummary[]>> {
		const out: Record<string, TicketLinkSummary[]> = {};
		let ok = 0;
		let lastErr: unknown = null;
		for (let i = 0; i < ids.length; i += TICKET_LINK_SUMMARY_LIMIT) {
			const chunk = ids.slice(i, i + TICKET_LINK_SUMMARY_LIMIT);
			try {
				const res = await this.request<{ summaries?: Record<string, TicketLinkSummary[] | null> | null }>(
					`/tickets/link-summaries?ids=${chunk.join(',')}`,
				);
				for (const [id, list] of Object.entries(res?.summaries ?? {})) out[id] = list ?? [];
				ok++;
			} catch (err) {
				lastErr = err;
			}
		}
		if (ok === 0 && lastErr) throw lastErr;
		return out;
	}

	// ---- Agent log (20 October 2026): Communico staff, engineering tickets ----

	// The agent log of an engineering ticket, newest first. A nil list arrives as
	// null; never hand a null to a render.
	async getTicketAgentLog(ticketId: number): Promise<TicketAgentLogEntry[]> {
		const res = await this.request<{ data?: TicketAgentLogEntry[] | null }>(`/tickets/${ticketId}/agent-log`);
		return res?.data ?? [];
	}

	async addTicketAgentLog(ticketId: number, body: TicketAgentLogInput): Promise<TicketAgentLogEntry> {
		return this.request<TicketAgentLogEntry>(`/tickets/${ticketId}/agent-log`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	// Release a ticket in the release queue: the server moves it to awaiting
	// reporter action (the existing hand back) and writes the released entry.
	// Work sections (21 October 2026): Active work and the named sections below it.
	// Checklist items on an engineering ticket (27 October 2026): staff only.
	async listTicketChecklist(ticketId: number): Promise<TicketChecklistItem[]> {
		const res = await this.request<{ items?: TicketChecklistItem[] | null }>(`/tickets/${ticketId}/checklist`);
		return res?.items ?? [];
	}

	async addTicketChecklistItem(ticketId: number, body: { title: string; description?: string }): Promise<TicketChecklistItem> {
		return this.request<TicketChecklistItem>(`/tickets/${ticketId}/checklist`, { method: 'POST', body: JSON.stringify(body) });
	}

	async updateTicketChecklistItem(ticketId: number, itemId: number, patch: { title?: string; description?: string; done?: boolean }): Promise<TicketChecklistItem> {
		return this.request<TicketChecklistItem>(`/tickets/${ticketId}/checklist/${itemId}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async reorderTicketChecklist(ticketId: number, ids: number[]): Promise<TicketChecklistItem[]> {
		const res = await this.request<{ items?: TicketChecklistItem[] | null }>(`/tickets/${ticketId}/checklist/reorder`, { method: 'POST', body: JSON.stringify({ ids }) });
		return res?.items ?? [];
	}

	async deleteTicketChecklistItem(ticketId: number, itemId: number): Promise<void> {
		await this.request<void>(`/tickets/${ticketId}/checklist/${itemId}`, { method: 'DELETE' });
	}

	// Defects on an engineering ticket (23 October 2026). Files are uploaded first with
	// uploadFeedbackMedia, which makes no feedback report.
	async getTicketDefects(ticketId: number): Promise<TicketDefect[]> {
		const res = await this.request<{ defects?: TicketDefect[] | null }>(`/tickets/${ticketId}/defects`);
		return res?.defects ?? [];
	}

	async getTicketDefect(ticketId: number, defectId: number): Promise<TicketDefect> {
		return this.request<TicketDefect>(`/tickets/${ticketId}/defects/${defectId}`);
	}

	async commentOnTicketDefect(ticketId: number, defectId: number, body: string): Promise<TicketDefectComment> {
		return this.request<TicketDefectComment>(`/tickets/${ticketId}/defects/${defectId}/comments`, { method: 'POST', body: JSON.stringify({ body }) });
	}

	async raiseTicketDefect(ticketId: number, body: { title: string; description?: string; where_url?: string; assets?: DefectAssetInput[] }): Promise<TicketDefect> {
		return this.request<TicketDefect>(`/tickets/${ticketId}/defects`, { method: 'POST', body: JSON.stringify(body) });
	}

	async moveTicketDefect(ticketId: number, defectId: number, body: { status: string; reason?: string }): Promise<TicketDefect> {
		return this.request<TicketDefect>(`/tickets/${ticketId}/defects/${defectId}/status`, { method: 'POST', body: JSON.stringify(body) });
	}

	async sendTicketDefects(ticketId: number): Promise<{ sent: TicketDefect[]; ticket: unknown }> {
		return this.request<{ sent: TicketDefect[]; ticket: unknown }>(`/tickets/${ticketId}/defects/send`, { method: 'POST' });
	}

	// The engineering tickets with a recorded release, newest first, 100 at a time (the Product
	// tab's Releases view, 21 October 2026). beforeId continues after the last one shown.
	async getReleasedTickets(params: { since?: string; beforeId?: number; limit?: number } = {}): Promise<{ released: ReleasedTicket[]; more: boolean }> {
		const q = new URLSearchParams();
		if (params.since) q.set('since', params.since);
		if (params.beforeId) q.set('before_id', String(params.beforeId));
		if (params.limit) q.set('limit', String(params.limit));
		const qs = q.toString();
		const res = await this.request<{ released?: ReleasedTicket[] | null; more?: boolean }>(`/tickets/released${qs ? `?${qs}` : ''}`);
		return { released: res?.released ?? [], more: res?.more === true };
	}

	async getWorkSections(): Promise<WorkSection[]> {
		const res = await this.request<{ sections?: WorkSection[] | null }>('/work-sections');
		return res?.sections ?? [];
	}

	async createWorkSection(name: string): Promise<WorkSection> {
		return this.request<WorkSection>('/work-sections', { method: 'POST', body: JSON.stringify({ name }) });
	}

	async renameWorkSection(id: number, name: string): Promise<WorkSection> {
		return this.request<WorkSection>(`/work-sections/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) });
	}

	// Moves the section's unfinished tickets into Active work and retires it.
	async promoteWorkSection(id: number): Promise<{ moved: number }> {
		return this.request<{ moved: number }>(`/work-sections/${id}/promote`, { method: 'POST' });
	}

	// Takes every ticket out of the section (not planned) and retires it.
	async deleteWorkSection(id: number): Promise<{ moved: number }> {
		return this.request<{ moved: number }>(`/work-sections/${id}`, { method: 'DELETE' });
	}

	// Pull requests the GitHub link recorded for an engineering ticket (staff only).
	async getTicketPullRequests(ticketId: number): Promise<TicketPullRequest[]> {
		const res = await this.request<{ pull_requests?: TicketPullRequest[] | null }>(`/tickets/${ticketId}/pull-requests`);
		return res?.pull_requests ?? [];
	}

	// The board badges for many tickets at once: the route takes at most 200 ids, so a
	// board with more is asked in batches of 200 and the answers merged.
	async getPullRequestSummaries(ids: number[]): Promise<Record<string, PullRequestSummary>> {
		const out: Record<string, PullRequestSummary> = {};
		for (let i = 0; i < ids.length; i += 200) {
			const res = await this.request<{ summaries?: Record<string, PullRequestSummary> | null }>(`/tickets/pull-request-summaries?ids=${ids.slice(i, i + 200).join(',')}`);
			Object.assign(out, res?.summaries ?? {});
		}
		return out;
	}

	// ---- The flow actions (26 October 2026): the only writers of who holds an
	// engineering ticket, its mode and its flag. Each answers the ticket after it.

	async getTicketFlowVocabulary(): Promise<TicketFlowVocabulary> {
		return this.request<TicketFlowVocabulary>('/tickets/flow-vocabulary');
	}

	private flowAction(ticketId: number, action: string, body?: Record<string, unknown>): Promise<unknown> {
		return this.request<unknown>(`/tickets/${ticketId}/${action}`, { method: 'POST', ...(body ? { body: JSON.stringify(body) } : {}) });
	}

	// Ask product: the flag, with its origin and why (1 to 500 characters).
	raiseTicketFlag(ticketId: number, origin: string, reason: string): Promise<unknown> {
		return this.flowAction(ticketId, 'flag', { origin, reason });
	}

	// Product hands a flagged ticket to an engineer, placed in Active work above (before_id)
	// or below (after_id) one ticket; neither only while Active work is empty.
	assignTicketEngineer(ticketId: number, body: { engineer_id: number; before_id?: number; after_id?: number }): Promise<unknown> {
		return this.flowAction(ticketId, 'assign-engineer', body);
	}

	// Who answered this ticket's last flag, for the assign dialog's hint.
	getTicketLastAnswered(ticketId: number): Promise<{ name: string; origin: string }> {
		return this.request<{ name: string; origin: string }>(`/tickets/${ticketId}/last-answered`);
	}

	sendTicketBackToAgent(ticketId: number): Promise<unknown> { return this.flowAction(ticketId, 'send-back-to-agent'); }
	approveTicketWork(ticketId: number): Promise<unknown> { return this.flowAction(ticketId, 'approve-work'); }
	/** Starts the product review workflow on an engineering ticket at Ready for Shaping; a running review answers itself (created false). */
	startTicketProductReview(ticketId: number): Promise<{ run_id: string; number: number; status: string; url: string; created: boolean }> {
		return this.request(`/tickets/${ticketId}/product-review`, { method: 'POST' });
	}

	takeTicketBack(ticketId: number): Promise<unknown> { return this.flowAction(ticketId, 'take-back'); }

	handTicketToAgent(ticketId: number, stage: string): Promise<unknown> { return this.flowAction(ticketId, 'hand-to-agent', { stage }); }

	changeTicketMode(ticketId: number, driveMode: 'agent' | 'engineer'): Promise<unknown> {
		return this.flowAction(ticketId, 'mode', { drive_mode: driveMode });
	}

	requestTicketRelease(ticketId: number): Promise<unknown> { return this.flowAction(ticketId, 'request-release'); }

	markTicketReleased(ticketId: number): Promise<unknown> { return this.flowAction(ticketId, 'mark-released'); }

	// ---- Planning (contract section 11): Communico staff, engineering tickets ----

	// Place a ticket in the backlog: exactly one of before_id, after_id, position.
	// The answer is the ticket. One call can place several tickets and can re-spread
	// them all, so the caller reads the order again afterwards.
	async rankTicket(ticketId: number, body: RankBody): Promise<unknown> {
		return this.request(`/tickets/${ticketId}/rank`, { method: 'POST', body: JSON.stringify(body) });
	}

	// The releases in use, newest first (at most 100).
	async getTicketReleases(): Promise<ReleaseOption[]> {
		const res = await this.request<{ releases?: ReleaseOption[] | null }>('/tickets/releases');
		return Array.isArray(res?.releases) ? res.releases : [];
	}

	// An epic's tickets, in rank order then newest (at most 500), with its progress.
	async getTicketChildren(ticketId: number): Promise<TicketChildren> {
		const res = await this.request<Partial<TicketChildren> | null>(`/tickets/${ticketId}/children`);
		return {
			children: Array.isArray(res?.children) ? res!.children as TicketChildren['children'] : [],
			child_total: typeof res?.child_total === 'number' ? res.child_total : 0,
			child_done: typeof res?.child_done === 'number' ? res.child_done : 0,
			percent_done: typeof res?.percent_done === 'number' ? res.percent_done : 0,
			truncated: res?.truncated === true,
		};
	}

	// Search for a ticket to link to: `kind` picks the list, `q` matches a
	// reference or a title. Both kinds are searched when `kinds` has two entries.
	async searchTicketsToLink(kinds: TicketKind[], q: string): Promise<TicketLinkCandidate[]> {
		const text = q.trim();
		if (!text) return [];
		const lists = await Promise.all(
			kinds.map((kind) =>
				this.request<{ data?: Array<Record<string, unknown>> | null }>(
					`/tickets?kind=${kind}&q=${encodeURIComponent(text)}&limit=10`,
				),
			),
		);
		const out: TicketLinkCandidate[] = [];
		for (const res of lists) {
			for (const t of res?.data ?? []) {
				out.push({
					id: Number(t.id),
					kind: (t.kind as TicketKind) ?? 'support',
					ref: String(t.ref ?? ''),
					title: String(t.title ?? ''),
					status: String(t.status ?? ''),
					partner_name: String(t.partner_name ?? ''),
				});
			}
		}
		return out;
	}

	// Create an engineering ticket from a support ticket: the ticket, the
	// worked_on_in link and (when asked) the support ticket's move to With
	// Engineering all happen in one transaction on the server.
	async createEngineeringTicket(supportTicketId: number, body: CreateEngineeringTicketBody): Promise<CreateEngineeringTicketResult> {
		return this.request<CreateEngineeringTicketResult>(`/tickets/${supportTicketId}/engineering-ticket`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	// Stage 46e — tickets list summary banner.
	// `kind` left out asks for the support tiles (today's shape); 'engineering'
	// asks for the engineering tiles. Never pass it for a library user.
	// `fresh` (staff): count again now, after the person's own change, instead of the cached counts.
	async getTicketsSummary(kind?: string, fresh = false) {
		const q = [kind ? `kind=${encodeURIComponent(kind)}` : '', fresh ? 'fresh=1' : ''].filter(Boolean).join('&');
		return this.request(`/tickets/summary${q ? `?${q}` : ''}`);
	}

	// Stage 46g Part 2 — quick actions on the ticket detail page.
	// Stage B′ (Q4) — the gated two-way priority change; reason required
	// (validated at the service layer; 422 without it).
	async changeTicketPriority(ticketId: number, priority: string, reason: string) {
		return this.request(`/tickets/${ticketId}/priority`, {
			method: 'POST',
			body: JSON.stringify({ priority, reason }),
		});
	}
	async escalateTicket(ticketId: number, reason: string) {
		return this.request(`/tickets/${ticketId}/escalate`, {
			method: 'POST',
			body: JSON.stringify({ reason }),
		});
	}
	async nudgeTicket(ticketId: number, message: string) {
		return this.request(`/tickets/${ticketId}/nudge`, {
			method: 'POST',
			body: JSON.stringify({ message }),
		});
	}
	async resolveTicket(ticketId: number, resolutionNotes: string, internalNotes = '') {
		// Marcie R12 — the customer-facing summary (required) + an optional
		// staff-only internal note the backend strips from any partner view.
		return this.request(`/tickets/${ticketId}/resolve`, {
			method: 'POST',
			body: JSON.stringify({ resolution_notes: resolutionNotes, resolution_internal_notes: internalNotes }),
		});
	}

	// Marcie R12 — AI-drafted, customer-facing resolution summary. Read-only
	// generation; staff review/edit before resolving. Returns the draft text.
	async draftTicketResolution(ticketId: number): Promise<string> {
		const res = (await this.request(`/tickets/${ticketId}/draft-resolution`, { method: 'POST' })) as { draft?: string };
		return res?.draft ?? '';
	}

	// Stage 46b — per-user preferences. Self-scoped. SetMyPreference
	// PUTs a single key with {"value": <anything>}; the backend
	// runs per-key shape validation.
	async getMyPreferences() {
		return this.request(`/me/preferences`);
	}
	async setMyPreference(key: string, value: unknown) {
		return this.request(`/me/preferences/${encodeURIComponent(key)}`, {
			method: 'PUT',
			body: JSON.stringify({ value }),
		});
	}

	// Stage 46c — section-state + section-layout convenience routes.
	// The backend wraps the nested-key write so the frontend doesn't
	// fetch-merge-patch. All self-scoped (derive userID from claims).
	async setSectionState(page: string, section: string, expanded: boolean) {
		return this.request(`/me/preferences/section-state`, {
			method: 'PATCH',
			body: JSON.stringify({ page, section, expanded }),
		});
	}
	async clearSectionState(page: string) {
		return this.request(`/me/preferences/section-state${toQuery({ page })}`, {
			method: 'DELETE',
		});
	}
	async setSectionLayout(page: string, layout: unknown[]) {
		return this.request(`/me/preferences/section-layout`, {
			method: 'PATCH',
			body: JSON.stringify({ page, layout }),
		});
	}
	async clearSectionLayout(page: string) {
		return this.request(`/me/preferences/section-layout${toQuery({ page })}`, {
			method: 'DELETE',
		});
	}

	// Stage 94d-2 — per-mode record-page layout (the object schema; distinct from
	// the array section-layout above). GET applies the lazy array→object migration
	// + returns { doc, migrated }; PUT validates + persists the per-mode doc.
	async getRecordLayout(page: string) {
		return this.request(`/me/preferences/record-layout${toQuery({ page })}`);
	}
	async putRecordLayout(page: string, doc: unknown) {
		return this.request(`/me/preferences/record-layout`, {
			method: 'PUT',
			body: JSON.stringify({ page, doc }),
		});
	}

	// Stage 20 — Admin panel. /admin/* requires both authentication and
	// the admin-role gate (level=1 + role in [admin, executive]).
	async getAdminUsers(filter: Record<string, unknown> = {}) {
		return this.request(`/admin/users${toQuery(filter)}`);
	}

	async getAdminUser(id: number) {
		return this.request(`/admin/users/${id}`);
	}

	async createAdminUser(data: Record<string, unknown>) {
		return this.request(`/admin/users`, { method: 'POST', body: JSON.stringify(data) });
	}

	async updateAdminUser(id: number, patch: Record<string, unknown>) {
		return this.request(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deactivateAdminUser(id: number) {
		return this.request(`/admin/users/${id}/deactivate`, { method: 'POST' });
	}

	async reactivateAdminUser(id: number) {
		return this.request(`/admin/users/${id}/reactivate`, { method: 'POST' });
	}

	async getAdminAudit(filter: Record<string, unknown> = {}) {
		return this.request(`/admin/audit${toQuery(filter)}`);
	}

	// docs/impersonation-plan.md, D3 — the partner-visible impersonation
	// audit trail. Read-only; session lifecycle (start/end/allow-writes)
	// lives in services/impersonation.ts.
	async getImpersonationAuditSessions(filter: Record<string, unknown> = {}) {
		return this.request(`/impersonation/audit/sessions${toQuery(filter)}`);
	}
	async getImpersonationAuditActions(sessionId: string, filter: Record<string, unknown> = {}) {
		return this.request(`/impersonation/audit/sessions/${encodeURIComponent(sessionId)}/actions${toQuery(filter)}`);
	}

	// Stage 65a-2 — the chat surface.
	async getChatConversations() {
		return this.request('/chat/conversations');
	}
	async getChatMessages(conversationId: number, opts: { before?: number; limit?: number } = {}) {
		return this.request(`/chat/conversations/${conversationId}/messages${toQuery(opts as Record<string, unknown>)}`);
	}
	async sendChatMessage(
		conversationId: number,
		body: string,
		attachments: { filename: string; content_type: string; data_base64: string }[] = [],
		replyTo = 0,
		anchors: { type: string; id: number }[] = [],
	) {
		// Stage 82b — anchors[] carries the explicit @-mention chips the composer
		// extracted; the server upserts them as origin='explicit'.
		return this.request(`/chat/conversations/${conversationId}/messages`, {
			method: 'POST',
			body: JSON.stringify({
				body,
				reply_to: replyTo,
				attachments: attachments.map(({ filename, content_type, data_base64 }) => ({ filename, content_type, data_base64 })),
				anchors,
			}),
		});
	}
	// Stage 82b — the /chat Mentions section: conversations I'm explicitly
	// @-anchored on.
	async getChatMentions() {
		return this.request('/chat/mentions');
	}
	async markChatRead(conversationId: number) {
		return this.request(`/chat/conversations/${conversationId}/read`, { method: 'POST' });
	}
	async startChatDM(userId: number) {
		return this.request('/chat/dms', { method: 'POST', body: JSON.stringify({ user_id: userId }) });
	}
	// 65a-3 — reactions, members, attachments.
	async toggleChatReaction(messageId: number, emoji: string) {
		return this.request(`/chat/messages/${messageId}/reactions`, {
			method: 'POST',
			body: JSON.stringify({ emoji }),
		});
	}
	async getChatMembers(conversationId: number) {
		return this.request(`/chat/conversations/${conversationId}/members`);
	}
	// 65a-4 — notification prefs + platform-wide status.
	async setChatNotify(conversationId: number, level: 'all' | 'mentions' | 'mute') {
		return this.request(`/chat/conversations/${conversationId}/notify`, {
			method: 'PUT',
			body: JSON.stringify({ level }),
		});
	}
	// 65a-5 — threads-lite, pins, mark-unread, reminders, Meet.
	async toggleChatPin(messageId: number) {
		return this.request(`/chat/messages/${messageId}/pin`, { method: 'POST' });
	}
	async getChatPins(conversationId: number) {
		return this.request(`/chat/conversations/${conversationId}/pins`);
	}
	async markChatUnread(messageId: number) {
		return this.request(`/chat/messages/${messageId}/unread`, { method: 'POST' });
	}
	async remindChatMessage(messageId: number, minutes: number) {
		return this.request(`/chat/messages/${messageId}/remind`, { method: 'POST', body: JSON.stringify({ minutes }) });
	}
	async startChatMeet(conversationId: number) {
		return this.request(`/chat/conversations/${conversationId}/meet`, { method: 'POST' });
	}
	// Stage 83 — a ticket's canonical internal-notes conversation. Returns
	// {conversation_id}, materialising + backfilling it on first open (the
	// reserved-topic record conversation; staff-only). Send/read then use the
	// generic sendChatMessage/getChatMessages.
	async ensureTicketInternalNotes(ticketId: number) {
		return this.request(`/chat/tickets/${ticketId}/internal-notes`, { method: 'POST' });
	}
	// Stage 88a — calendar. The signed-in user's events for a range (live read
	// from Google; { configured:false } when the calendar scope isn't granted).
	async getCalendarMe(startISO: string, endISO: string) {
		return this.request(`/calendar/me${toQuery({ start: startISO, end: endISO })}`);
	}
	// Stage 88b — the signed-in user + the requested people, side by side. Each
	// column is gated per-target (self + manager-chain → detail; peers →
	// free/busy). withIds is the column set (self is prepended server-side).
	async getCalendarMulti(startISO: string, endISO: string, withIds: number[]) {
		return this.request(`/calendar/multi${toQuery({ start: startISO, end: endISO, with: withIds.join(',') })}`);
	}
	// Stage 88b — the signed-in manager's direct reports as free/busy columns.
	async getCalendarTeam(startISO: string, endISO: string) {
		return this.request(`/calendar/team${toQuery({ start: startISO, end: endISO })}`);
	}
	// Stage 88c — calendar write path (self-only on the backend gate). Body:
	// { title, start, end, all_day?, description?, location?, attendees? }.
	async createCalendarEvent(body: CalendarEventWrite) {
		return this.request(`/calendar/events`, { method: 'POST', body: JSON.stringify(body) });
	}
	async updateCalendarEvent(id: string, body: CalendarEventWrite) {
		return this.request(`/calendar/events/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) });
	}
	async deleteCalendarEvent(id: string) {
		return this.request(`/calendar/events/${encodeURIComponent(id)}`, { method: 'DELETE' });
	}
	// Stage 88d — smart scheduler. Ranks candidate meeting times for the caller +
	// invitee staff ids over a date range. Returns top-5 ranked slots.
	async findMeetingTimes(body: ScheduleQuery) {
		return this.request(`/calendar/schedule`, { method: 'POST', body: JSON.stringify(body) });
	}
	// Stage 88f — the meeting Prep event↔record link.
	async getMeetingLink(eventId: string) {
		return this.request(`/calendar/events/${encodeURIComponent(eventId)}/link`);
	}
	async linkMeeting(eventId: string, recordType: string, recordId: number) {
		return this.request(`/calendar/events/${encodeURIComponent(eventId)}/link`, {
			method: 'POST',
			body: JSON.stringify({ record_type: recordType, record_id: recordId }),
		});
	}
	async unlinkMeeting(eventId: string) {
		return this.request(`/calendar/events/${encodeURIComponent(eventId)}/link`, { method: 'DELETE' });
	}
	// 65b — record conversations.
	async startRecordConversation(entityType: string, entityId: number, topic = '') {
		return this.request('/chat/records', {
			method: 'POST',
			body: JSON.stringify({ entity_type: entityType, entity_id: entityId, topic }),
		});
	}
	async getRecordConversations(entityType: string, entityId: number) {
		return this.request(`/chat/records/${entityType}/${entityId}`);
	}
	async joinChatConversation(conversationId: number) {
		return this.request(`/chat/conversations/${conversationId}/join`, { method: 'POST' });
	}
	async raiseToEngineering(ticketId: number, topic: string, body: string) {
		return this.request('/chat/records/raise', {
			method: 'POST',
			body: JSON.stringify({ ticket_id: ticketId, topic, body }),
		});
	}
	// 65b-2 — chat-local search (participant-scoped server-side).
	async searchChatMessages(q: string) {
		return this.request(`/chat/search${toQuery({ q })}`);
	}
	// 65c — chat AI layer (catch-me-up, draft task/ticket, find-answer).
	async getChatSummary(conversationId: number, opts: { mode?: 'unread' | 'recent'; limit?: number } = {}) {
		return this.request(`/chat/conversations/${conversationId}/summary`, {
			method: 'POST',
			body: JSON.stringify(opts),
		});
	}
	async draftChatTask(messageId: number) {
		return this.request(`/chat/messages/${messageId}/draft-task`, { method: 'POST' });
	}
	async draftChatTicket(messageId: number) {
		return this.request(`/chat/messages/${messageId}/draft-ticket`, { method: 'POST' });
	}
	async findChatAnswer(messageId: number) {
		return this.request(`/chat/messages/${messageId}/find-answer`, { method: 'POST' });
	}
	// Stage 66 — activity analytics.
	async getAdminActivity(day?: string) {
		return this.request(`/admin/activity${toQuery({ day })}`);
	}
	async getAdminActivityUser(userId: number, day?: string) {
		return this.request(`/admin/activity/user/${userId}${toQuery({ day })}`);
	}
	// Stage 67c — anomaly band flags for the team grid.
	async getActivityAnomalies(day?: string) {
		return this.request(`/admin/activity/anomalies${toQuery({ day })}`);
	}
	async getMyActivity(days = 7) {
		return this.request(`/activity/me${toQuery({ days })}`);
	}
	async getChatStatus() {
		return this.request('/chat/status');
	}
	async setChatStatus(status: string) {
		return this.request('/chat/status', { method: 'PUT', body: JSON.stringify({ status }) });
	}
	async downloadChatAttachment(attachmentId: number, fallbackFilename: string) {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/chat/attachments/${attachmentId}`, { headers });
		if (!resp.ok) {
			throw new ApiError(resp.status, (await resp.text()) || `Download failed (${resp.status})`);
		}
		let filename = fallbackFilename;
		const disp = resp.headers.get('Content-Disposition') || '';
		const match = disp.match(/filename="?([^";]+)"?/i);
		if (match) filename = match[1];
		const blob = await resp.blob();
		const url = URL.createObjectURL(blob);
		try {
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
		} finally {
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		}
	}

	// Marcie R10 — inline image display. The attachment endpoint is bearer-authed
	// (a plain <img src> would 401) and serves Content-Disposition: attachment, so
	// to show a pasted screenshot IN the thread we fetch it with auth and hand
	// back an object URL for an <img>. The CALLER owns revoking it (on disconnect)
	// — this returns the raw URL, unlike downloadChatAttachment which triggers a
	// download and revokes its own.
	async fetchChatAttachmentObjectURL(attachmentId: number): Promise<string> {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/chat/attachments/${attachmentId}`, { headers });
		if (!resp.ok) {
			throw new ApiError(resp.status, (await resp.text()) || `Load failed (${resp.status})`);
		}
		return URL.createObjectURL(await resp.blob());
	}
	// Stage 65a-1 — chat channels admin.
	async getAdminChatChannels(includeArchived = false) {
		return this.request(`/admin/chat/channels${includeArchived ? '?include_archived=true' : ''}`);
	}
	async createAdminChatChannel(data: { name: string; description?: string }) {
		return this.request('/admin/chat/channels', { method: 'POST', body: JSON.stringify(data) });
	}
	async updateAdminChatChannel(id: number, patch: { name: string; description?: string }) {
		return this.request(`/admin/chat/channels/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	async archiveAdminChatChannel(id: number, archived: boolean) {
		return this.request(`/admin/chat/channels/${id}/archive`, { method: 'POST', body: JSON.stringify({ archived }) });
	}
	async deleteAdminChatChannel(id: number) {
		return this.request(`/admin/chat/channels/${id}`, { method: 'DELETE' });
	}
	async getAdminChatChannelMembers(id: number) {
		return this.request(`/admin/chat/channels/${id}/members`);
	}
	async addAdminChatChannelMember(id: number, userId: number) {
		return this.request(`/admin/chat/channels/${id}/members`, { method: 'POST', body: JSON.stringify({ user_id: userId }) });
	}
	async removeAdminChatChannelMember(id: number, userId: number) {
		return this.request(`/admin/chat/channels/${id}/members/${userId}`, { method: 'DELETE' });
	}
	async getAdminLabels(filter: Record<string, unknown> = {}) {
		return this.request(`/admin/labels${toQuery(filter)}`);
	}

	async createAdminLabel(data: Record<string, unknown>) {
		return this.request(`/admin/labels`, { method: 'POST', body: JSON.stringify(data) });
	}

	async updateAdminLabel(id: number, patch: Record<string, unknown>) {
		return this.request(`/admin/labels/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteAdminLabel(id: number) {
		return this.request(`/admin/labels/${id}`, { method: 'DELETE' });
	}

	async mergeAdminLabel(sourceId: number, targetId: number) {
		return this.request(`/admin/labels/${sourceId}/merge`, {
			method: 'POST', body: JSON.stringify({ target_id: targetId }),
		});
	}

	async getAdminTemplates() {
		return this.request(`/admin/module-templates`);
	}

	async createAdminTemplate(data: Record<string, unknown>) {
		return this.request(`/admin/module-templates`, { method: 'POST', body: JSON.stringify(data) });
	}

	async updateAdminTemplate(id: number, patch: Record<string, unknown>) {
		return this.request(`/admin/module-templates/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteAdminTemplate(id: number) {
		return this.request(`/admin/module-templates/${id}`, { method: 'DELETE' });
	}

	// Stage 28b — admin triage-review surface. Six routes off
	// /admin/triage-suggestions, all gated by RequireAdminRole at the
	// router level. The shapes mirror what the backend handler in
	// internal/handler/admin_triage_review.go returns; the page
	// components apply their own typing on top of these `unknown`
	// payloads via the AITriageReviewRow / BulkResult interfaces in
	// cos-triage-review-page.ts.
	async getTriageReviewList(filter: Record<string, unknown> = {}) {
		return this.request(`/admin/triage-suggestions${toQuery(filter)}`);
	}

	async getTriageReviewItem(id: number) {
		return this.request(`/admin/triage-suggestions/${id}`);
	}

	async getTriageConfidenceDistribution(source = 'backfill', status = 'pending') {
		return this.request(`/admin/triage-suggestions/confidence-distribution${toQuery({ source, status })}`);
	}

	async acceptTriageReview(id: number) {
		return this.request(`/admin/triage-suggestions/${id}/accept`, { method: 'POST' });
	}

	async rejectTriageReview(id: number, reason = '') {
		return this.request(`/admin/triage-suggestions/${id}/reject`, {
			method: 'POST',
			body: JSON.stringify({ reason }),
		});
	}

	async bulkAcceptTriageReview(constraint: Record<string, unknown>, maxRows = 1000) {
		return this.request(`/admin/triage-suggestions/bulk-accept`, {
			method: 'POST',
			body: JSON.stringify({ constraint, max_rows: maxRows }),
		});
	}

	async bulkRejectTriageReview(constraint: Record<string, unknown>, reason: string, maxRows = 1000) {
		return this.request(`/admin/triage-suggestions/bulk-reject`, {
			method: 'POST',
			body: JSON.stringify({ constraint, reason, max_rows: maxRows }),
		});
	}

	// Stage 35b — Deals (spec §3.3). Staff-only on the backend
	// (RequireLevel(1) on the /deals group). Same envelope shape as
	// /tickets and /partners: list returns {data, total, start,
	// limit}; get/create/update return the deal directly; delete
	// returns 204.
	async getDeals(filter: DealFilter = {}) {
		return this.request(`/deals${toQuery(filter as Record<string, unknown>)}`);
	}

	async getDeal(id: number) {
		return this.request(`/deals/${id}`);
	}

	async createDeal(data: Record<string, unknown>) {
		return this.request('/deals', { method: 'POST', body: JSON.stringify(data) });
	}

	async updateDeal(id: number, patch: Record<string, unknown>) {
		return this.request(`/deals/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteDeal(id: number) {
		return this.request(`/deals/${id}`, { method: 'DELETE' });
	}

	// Stage 36b — Annual Reviews (spec §3.1). One row per
	// (partner_id, year) — the backend enforces composite uniqueness
	// and surfaces a 409 on duplicate. The list endpoint returns
	// {data, total} ordered by year DESC.
	async listAnnualReviews(partnerID: number) {
		return this.request(`/partners/${partnerID}/annual-reviews`);
	}

	async createAnnualReview(partnerID: number, data: Record<string, unknown>) {
		return this.request(`/partners/${partnerID}/annual-reviews`, {
			method: 'POST', body: JSON.stringify(data),
		});
	}

	async getAnnualReview(id: number) {
		return this.request(`/annual-reviews/${id}`);
	}

	async updateAnnualReview(id: number, patch: Record<string, unknown>) {
		return this.request(`/annual-reviews/${id}`, {
			method: 'PATCH', body: JSON.stringify(patch),
		});
	}

	async deleteAnnualReview(id: number) {
		return this.request(`/annual-reviews/${id}`, { method: 'DELETE' });
	}

	// Stage 38a — Notifications REST hydration. The WebSocket push
	// path delivers live; these endpoints back the bell-icon
	// initial-load + the "mark read" mutations.
	async listUnreadNotifications(limit = 50) {
		return this.request(`/notifications/unread?limit=${limit}`);
	}

	// Stage 75 — `before` is an id cursor for the /notifications page's
	// infinite scroll; the bell omits it (first page only).
	async listRecentNotifications(limit = 50, before?: number) {
		return this.request(`/notifications/recent${toQuery({ limit, before })}`);
	}

	async markAllNotificationsRead() {
		return this.request('/notifications/mark-all-read', { method: 'POST' });
	}

	async markNotificationRead(id: number) {
		return this.request(`/notifications/${id}/mark-read`, { method: 'POST' });
	}

	// Stage 75 — dismiss (hide) notifications + per-user mute prefs.
	async dismissNotifications(ids: number[]) {
		return this.request('/notifications/dismiss', {
			method: 'POST',
			body: JSON.stringify({ ids }),
		});
	}

	async getNotificationPreferences() {
		return this.request('/me/preferences/notifications');
	}

	async updateNotificationPreferences(mutedKinds: string[]) {
		return this.request('/me/preferences/notifications', {
			method: 'PATCH',
			body: JSON.stringify({ muted_kinds: mutedKinds }),
		});
	}

	// Stage 46a — Design settings (runtime theme).
	async getDesignSettings() {
		return this.request(`/design-settings`);
	}
	async getAdminDesignSettings() {
		return this.request(`/admin/design-settings`);
	}
	async upsertAdminDesignSetting(tokenKey: string, value: string) {
		// token_key carries the leading `--`; encode it for the URL.
		const encoded = encodeURIComponent(tokenKey);
		return this.request(`/admin/design-settings/${encoded}`, {
			method: 'PUT',
			body: JSON.stringify({ value }),
		});
	}
	async deleteAdminDesignSetting(tokenKey: string) {
		const encoded = encodeURIComponent(tokenKey);
		return this.request(`/admin/design-settings/${encoded}`, { method: 'DELETE' });
	}
	async resetAllAdminDesignSettings() {
		return this.request(`/admin/design-settings/reset-all`, { method: 'POST' });
	}

	// Stage 67 — time off (Timetastic replacement). Staff surface.
	async getTimeOffTypes() {
		return this.request('/timeoff/types');
	}
	async getMyTimeOff(year?: number) {
		return this.request(`/timeoff/mine${toQuery({ year })}`);
	}
	async requestTimeOff(data: Record<string, unknown>) {
		return this.request('/timeoff', { method: 'POST', body: JSON.stringify(data) });
	}
	async cancelTimeOff(id: number) {
		return this.request(`/timeoff/${id}`, { method: 'DELETE' });
	}
	async getPendingTimeOff() {
		return this.request('/timeoff/pending');
	}
	async approveTimeOff(id: number, note = '') {
		return this.request(`/timeoff/${id}/approve`, { method: 'POST', body: JSON.stringify({ note }) });
	}
	async declineTimeOff(id: number, note = '') {
		return this.request(`/timeoff/${id}/decline`, { method: 'POST', body: JSON.stringify({ note }) });
	}
	async getTimeOffTeam(from: string, to: string) {
		return this.request(`/timeoff/team${toQuery({ from, to })}`);
	}
	async getTimeOffBalance(userId: number, year?: number) {
		return this.request(`/timeoff/balance/${userId}${toQuery({ year })}`);
	}
	// Stage 67 — time off admin curation.
	async getAdminTimeOffStaff(year?: number) {
		return this.request(`/admin/timeoff/staff${toQuery({ year })}`);
	}
	async updateAdminTimeOffStaff(id: number, data: Record<string, unknown>) {
		return this.request(`/admin/timeoff/staff/${id}`, { method: 'PUT', body: JSON.stringify(data) });
	}
	async getAdminLeaveTypes(includeArchived = false) {
		return this.request(`/admin/timeoff/leave-types${includeArchived ? '?include_archived=true' : ''}`);
	}
	async createAdminLeaveType(data: Record<string, unknown>) {
		return this.request('/admin/timeoff/leave-types', { method: 'POST', body: JSON.stringify(data) });
	}
	async updateAdminLeaveType(id: number, patch: Record<string, unknown>) {
		return this.request(`/admin/timeoff/leave-types/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	async archiveAdminLeaveType(id: number) {
		return this.request(`/admin/timeoff/leave-types/${id}/archive`, { method: 'POST' });
	}
	async restoreAdminLeaveType(id: number) {
		return this.request(`/admin/timeoff/leave-types/${id}/restore`, { method: 'POST' });
	}
	async getAdminCompanyHolidays(country?: string, year?: number) {
		return this.request(`/admin/timeoff/holidays${toQuery({ country, year })}`);
	}
	async createAdminCompanyHoliday(data: Record<string, unknown>) {
		return this.request('/admin/timeoff/holidays', { method: 'POST', body: JSON.stringify(data) });
	}
	async setAdminCompanyHolidayGranted(id: number, granted: boolean) {
		return this.request(`/admin/timeoff/holidays/${id}`, { method: 'PATCH', body: JSON.stringify({ granted }) });
	}
	async deleteAdminCompanyHoliday(id: number) {
		return this.request(`/admin/timeoff/holidays/${id}`, { method: 'DELETE' });
	}

	// fleet/training-ui: Training (docs/training-spec.md, docs/training-api.md).
	// Staff learner surface.
	async trainingCourses(): Promise<{ courses: TrainingCourseCard[] }> {
		return this.request('/training/courses');
	}
	async trainingCourse(key: string): Promise<TrainingCourseDetail> {
		return this.request(`/training/courses/${key}`);
	}
	async trainingSectionOpened(key: string, id: number, secondsOpen: number): Promise<TrainingSectionProgress> {
		return this.request(`/training/courses/${key}/sections/${id}/opened`, {
			method: 'POST',
			body: JSON.stringify({ secondsOpen }),
		});
	}
	async trainingSectionRead(key: string, id: number): Promise<TrainingSectionProgress> {
		return this.request(`/training/courses/${key}/sections/${id}/read`, { method: 'POST' });
	}
	async trainingSectionCheck(key: string, id: number): Promise<{ questions: TrainingServedQuestion[] }> {
		return this.request(`/training/courses/${key}/sections/${id}/check`);
	}
	async trainingSectionCheckSubmit(key: string, id: number, answers: TrainingAttemptAnswers): Promise<TrainingSectionCheckResponse> {
		return this.request(`/training/courses/${key}/sections/${id}/check`, {
			method: 'POST',
			body: JSON.stringify({ answers }),
		});
	}
	async trainingScenario(key: string): Promise<TrainingScenario> {
		return this.request(`/training/scenarios/${key}`);
	}
	async trainingScenarioStep(key: string, step: number, answer: string): Promise<TrainingScenarioStepResult> {
		return this.request(`/training/scenarios/${key}/steps/${step}`, {
			method: 'POST',
			body: JSON.stringify({ answer }),
		});
	}
	async trainingStartAttempt(key: string): Promise<TrainingAttemptStart> {
		return this.request(`/training/courses/${key}/attempts`, { method: 'POST' });
	}
	async trainingAttempt(id: number): Promise<TrainingAttemptStart> {
		return this.request(`/training/attempts/${id}`);
	}
	async trainingSaveAnswers(id: number, answers: TrainingAttemptAnswers): Promise<{ savedAt: string }> {
		return this.request(`/training/attempts/${id}/answers`, { method: 'PUT', body: JSON.stringify({ answers }) });
	}
	async trainingSubmitAttempt(id: number): Promise<TrainingResults> {
		return this.request(`/training/attempts/${id}/submit`, { method: 'POST' });
	}
	async trainingResults(id: number): Promise<TrainingResults> {
		return this.request(`/training/attempts/${id}/results`);
	}
	async trainingReviewTick(id: number, questionId: number): Promise<TrainingResults> {
		return this.request(`/training/attempts/${id}/review`, { method: 'POST', body: JSON.stringify({ questionId }) });
	}
	async trainingMyAttempts(course?: string): Promise<{ attempts: TrainingAttemptSummary[] }> {
		return this.request(`/training/attempts/mine${toQuery({ course })}`);
	}
	/** A bearer-authenticated Blob, not a plain URL. See getBillingDocumentPDF
	 *  for why a `<a href>`/`window.open` can't carry the Authorization header
	 *  this staff-only route requires. The contract (training-api.md section 4)
	 *  names this `trainingCertificateUrl`; the name is kept, the return type
	 *  isn't, see the stage report for why. */
	async trainingCertificateUrl(publicId: string): Promise<Blob> {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/training/certificates/${publicId}.pdf`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `Certificate PDF failed (${resp.status})`);
		return resp.blob();
	}
	// Admin board.
	async adminTrainingBoard(course: string, status?: string): Promise<TrainingBoard> {
		return this.request(`/admin/training/board${toQuery({ course, status })}`);
	}
	/** Same bearer-Blob reasoning as trainingCertificateUrl above. */
	async adminTrainingBoardCsvUrl(course: string): Promise<Blob> {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/admin/training/board.csv${toQuery({ course })}`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `Training board CSV failed (${resp.status})`);
		return resp.blob();
	}
	async adminTrainingUser(id: number, course: string): Promise<TrainingAdminUserDetail> {
		return this.request(`/admin/training/users/${id}${toQuery({ course })}`);
	}
	async adminTrainingUserAttempt(id: number, attemptId: number): Promise<TrainingResults> {
		return this.request(`/admin/training/users/${id}/attempts/${attemptId}`);
	}

	// Stage 68b — project templates (staff picker + admin CRUD).
	async getProjectTemplates() {
		return this.request(`/project-templates`);
	}

	async createOnboarding(data: Record<string, unknown>) {
		return this.request(`/onboardings`, { method: 'POST', body: JSON.stringify(data) });
	}

	async getAdminProjectTemplates() {
		return this.request(`/admin/project-templates`);
	}

	async getAdminProjectTemplate(id: number) {
		return this.request(`/admin/project-templates/${id}`);
	}

	async createAdminProjectTemplate(data: Record<string, unknown>) {
		return this.request(`/admin/project-templates`, {
			method: 'POST', body: JSON.stringify(data),
		});
	}

	async updateAdminProjectTemplate(id: number, patch: Record<string, unknown>) {
		return this.request(`/admin/project-templates/${id}`, {
			method: 'PATCH', body: JSON.stringify(patch),
		});
	}

	async archiveAdminProjectTemplate(id: number) {
		return this.request(`/admin/project-templates/${id}/archive`, { method: 'POST' });
	}

	async restoreAdminProjectTemplate(id: number) {
		return this.request(`/admin/project-templates/${id}/restore`, { method: 'POST' });
	}

	// Stage 68b (multi-list) — list CRUD
	async createAdminProjectTemplateList(templateId: number, data: Record<string, unknown>) {
		return this.request(`/admin/project-templates/${templateId}/lists`, {
			method: 'POST', body: JSON.stringify(data),
		});
	}

	async updateAdminProjectTemplateList(listId: number, patch: Record<string, unknown>) {
		return this.request(`/admin/project-template-lists/${listId}`, {
			method: 'PATCH', body: JSON.stringify(patch),
		});
	}

	async deleteAdminProjectTemplateList(listId: number) {
		return this.request(`/admin/project-template-lists/${listId}`, { method: 'DELETE' });
	}

	async reorderAdminProjectTemplateLists(templateId: number, order: number[]) {
		return this.request(`/admin/project-templates/${templateId}/lists/reorder`, {
			method: 'POST', body: JSON.stringify({ order }),
		});
	}

	// Stage 68b (multi-list) — item CRUD (now keyed under a list)
	async createAdminProjectTemplateItem(listId: number, data: Record<string, unknown>) {
		return this.request(`/admin/project-template-lists/${listId}/items`, {
			method: 'POST', body: JSON.stringify(data),
		});
	}

	async updateAdminProjectTemplateItem(itemId: number, patch: Record<string, unknown>) {
		return this.request(`/admin/project-template-items/${itemId}`, {
			method: 'PATCH', body: JSON.stringify(patch),
		});
	}

	async deleteAdminProjectTemplateItem(itemId: number) {
		return this.request(`/admin/project-template-items/${itemId}`, { method: 'DELETE' });
	}

	async reorderAdminProjectTemplateItems(listId: number, order: number[]) {
		return this.request(`/admin/project-template-lists/${listId}/items/reorder`, {
			method: 'POST', body: JSON.stringify({ order }),
		});
	}

	// Broadcast (Stage B1a) — digital-signage staff CRUD over the legacy
	// MySQL tables (/api/v1/broadcast/*). Level-1 staff pass client_id
	// explicitly on every call (the page has a client picker). shows.data
	// and templates.template are raw legacy JSON passthroughs — validated
	// server-side, never re-shaped here (port plan D1/D3).
	async getBroadcastClients() {
		return this.request(`/broadcast/clients`);
	}

	async getLegacyClients() {
		return this.request(`/legacy/clients`);
	}

	// Collections — the Explore-carousel collection manager port + the
	// new CUSTOM type (docs/collections-cos-migration-prd.md).
	async getCollectionsContext(clientId?: number) {
		return this.request(`/collections/context${toQuery({ client_id: clientId })}`);
	}

	// filter's folder/unfiled/favorite keys are the overlay narrowing
	// (legacy-tree folder membership + COS-Postgres per-user favorites)
	// — see service.CollectionsListFilter. Omitted keys are simply
	// absent from the query string (toQuery drops falsy values), so a
	// plain getCollections(clientId) is unaffected.
	async getCollections(
		clientId: number,
		type = '',
		filter: { folder?: string; unfiled?: boolean; favorite?: boolean } = {},
	) {
		return this.request(`/collections${toQuery({ client_id: clientId, type, ...filter })}`);
	}

	async getCollection(clientId: number, id: number) {
		return this.request(`/collections/${id}${toQuery({ client_id: clientId })}`);
	}

	async createCollection(clientId: number, payload: Record<string, unknown>) {
		return this.request(`/collections${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(payload),
		});
	}

	// The save payload carries the optimistic-concurrency choice (PRD §7):
	// `collection.revision` is the caller's last-known revision — omit it
	// and the backend skips the check entirely (control-panel's absent-key
	// semantics), so a caller that can't produce one is no worse off than
	// before. `force_save: true` overrides a rejection and must only ever
	// be set from an explicit user choice; on a 409 the thrown ApiError
	// carries `currentRevision`.
	async updateCollection(clientId: number, id: number, payload: Record<string, unknown>) {
		return this.request(`/collections/${id}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(payload),
		});
	}

	async deleteCollection(clientId: number, id: number) {
		return this.request(`/collections/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	async deleteCollectionItem(clientId: number, collectionId: number, itemId: string) {
		return this.request(
			`/collections/${collectionId}/items/${encodeURIComponent(itemId)}${toQuery({ client_id: clientId })}`,
			{ method: 'DELETE' },
		);
	}

	async getCollectionsDynamicFeeds(clientId: number) {
		return this.request(`/collections/dynamic-feeds${toQuery({ client_id: clientId })}`);
	}

	// Folders — the legacy generic co-table tree (control-panel's own
	// hierarchical-folder system; see collections_tree.go). Favorites
	// stay a COS-Postgres per-user overlay.
	async getCollectionFolders(clientId: number) {
		return this.request(`/collections/folders${toQuery({ client_id: clientId })}`);
	}

	// parentKey nests the new folder under an existing one; omit (or
	// null) to create it at the root.
	async createCollectionFolder(clientId: number, name: string, parentKey: string | null = null) {
		return this.request(`/collections/folders${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ name, parent_key: parentKey }),
		});
	}

	async renameCollectionFolder(clientId: number, folderKey: string, name: string) {
		return this.request(`/collections/folders/${folderKey}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ name }),
		});
	}

	async deleteCollectionFolder(clientId: number, folderKey: string) {
		return this.request(`/collections/folders/${folderKey}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	// folderKey null unfiles the collection.
	async assignCollectionFolder(clientId: number, collectionId: number, folderKey: string | null) {
		return this.request(`/collections/${collectionId}/folder${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ folder_key: folderKey }),
		});
	}

	async setCollectionFavorite(clientId: number, collectionId: number, on: boolean) {
		return this.request(`/collections/${collectionId}/favorite${toQuery({ client_id: clientId })}`, {
			method: on ? 'PUT' : 'DELETE',
		});
	}

	async getCollectionsReserveRooms(clientId: number, locationIds: number[] = []) {
		const params = new URLSearchParams({ client_id: String(clientId) });
		for (const id of locationIds) params.append('location_id', String(id));
		return this.request(`/collections/reserve-rooms?${params.toString()}`);
	}

	async getCollectionsRoomsByIds(clientId: number, ids: number[]) {
		const params = new URLSearchParams({ client_id: String(clientId) });
		for (const id of ids) params.append('id', String(id));
		return this.request(`/collections/reserve-rooms?${params.toString()}`);
	}

	async searchCollectionsSources(clientId: number, provider: string, q: string, limit = 25) {
		return this.request(`/collections/search${toQuery({ client_id: clientId, provider, q, limit })}`);
	}

	async getAttendContext(clientId: number) {
		return this.request(`/attend/context${toQuery({ client_id: clientId })}`);
	}

	async getScheduleContext(clientId: number) {
		return this.request(`/schedule/context${toQuery({ client_id: clientId })}`);
	}

	// Schedule: bookings list
	// params carries the list filters (start, end, locations, categories,
	// statuses, provider_id, term); client_id is added here. Multi-id
	// filters ride as comma lists, as the backend parses them.
	async getScheduleBookings(clientId: number, params: URLSearchParams) {
		const p = new URLSearchParams(params);
		p.set('client_id', String(clientId));
		return this.request(`/schedule/bookings?${p.toString()}`);
	}

	async getScheduleBookingFilters(clientId: number) {
		return this.request(`/schedule/bookings/filters${toQuery({ client_id: clientId })}`);
	}
	// End Schedule: bookings list

	// Schedule: settings groups
	// Stage 4, slice B: the Group editor, a RESOURCE group's assets and the
	// legacy exceptions inside a group. Every call carries client_id (library
	// staff are pinned to their own library by the server).
	async getScheduleGroupOptions(clientId: number, categoryId: number) {
		return this.request(`/schedule/settings/groups/options${toQuery({ client_id: clientId, category_id: categoryId })}`);
	}
	async getScheduleGroupAdmin(clientId: number, id: number) {
		return this.request(`/schedule/settings/groups/${id}${toQuery({ client_id: clientId })}`);
	}
	async createScheduleGroup(clientId: number, body: Record<string, unknown>) {
		return this.request(`/schedule/settings/groups${toQuery({ client_id: clientId })}`, { method: 'POST', body: JSON.stringify(body) });
	}
	async updateScheduleGroup(clientId: number, id: number, body: Record<string, unknown>) {
		return this.request(`/schedule/settings/groups/${id}${toQuery({ client_id: clientId })}`, { method: 'PUT', body: JSON.stringify(body) });
	}
	async deleteScheduleGroup(clientId: number, id: number) {
		return this.request(`/schedule/settings/groups/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}
	async getScheduleGroupAssets(clientId: number, id: number) {
		return this.request(`/schedule/settings/groups/${id}/assets${toQuery({ client_id: clientId })}`);
	}
	async saveScheduleGroupAssets(clientId: number, id: number, body: Record<string, unknown>) {
		return this.request(`/schedule/settings/groups/${id}/assets${toQuery({ client_id: clientId })}`, { method: 'PUT', body: JSON.stringify(body) });
	}
	async getScheduleGroupExceptions(clientId: number, id: number) {
		return this.request(`/schedule/settings/groups/${id}/exceptions${toQuery({ client_id: clientId })}`);
	}
	async saveScheduleGroupException(clientId: number, id: number, exceptionId: number, body: Record<string, unknown>) {
		const path = exceptionId > 0 ? `/schedule/settings/groups/${id}/exceptions/${exceptionId}` : `/schedule/settings/groups/${id}/exceptions`;
		return this.request(`${path}${toQuery({ client_id: clientId })}`, { method: exceptionId > 0 ? 'PUT' : 'POST', body: JSON.stringify(body) });
	}
	async deleteScheduleGroupException(clientId: number, id: number, exceptionId: number) {
		return this.request(`/schedule/settings/groups/${id}/exceptions/${exceptionId}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}
	// End Schedule: settings groups

	// Attend A1 — multi-value filters ride REPEATED params (type/age
	// names may contain commas), so the caller passes a prebuilt
	// URLSearchParams and client_id is appended here.
	async getAttendEvents(clientId: number, params: URLSearchParams) {
		const p = new URLSearchParams(params);
		p.set('client_id', String(clientId));
		return this.request(`/attend/events?${p.toString()}`);
	}

	async getAttendMarketing(clientId: number, timeId: number) {
		return this.request(`/attend/occurrences/${timeId}/marketing${toQuery({ client_id: clientId })}`);
	}

	async getAttendLocationRooms(clientId: number, locationId: number, date?: string) {
		return this.request(
			`/attend/locations/${locationId}/rooms${toQuery({ client_id: clientId, date: date || undefined })}`,
		);
	}

	async getAttendRoomAssets(clientId: number, roomId: number, start: string, end: string, exclude?: number[]) {
		return this.request(
			`/attend/rooms/${roomId}/assets${toQuery({
				client_id: clientId,
				start,
				end,
				exclude: exclude && exclude.length > 0 ? exclude.join(',') : undefined,
			})}`,
		);
	}

	async getAttendImages(clientId: number) {
		return this.request(`/attend/images${toQuery({ client_id: clientId })}`);
	}

	async uploadAttendImage(clientId: number, file: File) {
		const fd = new FormData();
		fd.append('file', file, file.name);
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers['Authorization'] = `Bearer ${tok}`;
		const resp = await fetch(`${API_BASE}/attend/images${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			headers,
			body: fd,
		});
		const body = (await resp.json().catch(() => ({}))) as { message?: string; error?: string; image?: unknown };
		if (!resp.ok) throw new ApiError(resp.status, body.message || body.error || `Upload failed (${resp.status})`);
		return body;
	}

	async getAttendTemplates(clientId: number, params: URLSearchParams) {
		const p = new URLSearchParams(params);
		p.set('client_id', String(clientId));
		return this.request(`/attend/templates?${p.toString()}`);
	}

	async getAttendFilterOptions(clientId: number) {
		return this.request(`/attend/filter-options${toQuery({ client_id: clientId })}`);
	}

	async getAttendEvent(clientId: number, id: number) {
		return this.request(`/attend/events/${id}${toQuery({ client_id: clientId })}`);
	}

	async createAttendEvent(clientId: number, data: Record<string, unknown>) {
		return this.request(`/attend/events${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ data }),
		});
	}

	async updateAttendEvent(clientId: number, id: number, data: Record<string, unknown>) {
		return this.request(`/attend/events/${id}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ data }),
		});
	}

	async setAttendEventStatus(clientId: number, id: number, status: number, group?: number | null, title = '', message = '') {
		return this.request(`/attend/events/${id}/status${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ status, group: group ?? undefined, title, message }),
		});
	}

	// --- event-details panel (Marcie round 3, batch B) -------------------

	async getAttendEventDetails(clientId: number, id: number) {
		return this.request(`/attend/events/${id}/details${toQuery({ client_id: clientId })}`);
	}

	async getAttendEventHistory(clientId: number, id: number) {
		return this.request(`/attend/events/${id}/history${toQuery({ client_id: clientId })}`);
	}

	async addAttendEventComment(clientId: number, id: number, message: string) {
		return this.request(`/attend/events/${id}/comments${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ message }),
		});
	}

	async saveAttendDateNotes(clientId: number, timeId: number, notes: string) {
		return this.request(`/attend/occurrences/${timeId}/notes${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ notes }),
		});
	}

	async saveAttendSeriesNotes(clientId: number, id: number, notes: string) {
		return this.request(`/attend/events/${id}/series-notes${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ notes }),
		});
	}

	// --- Attend A3: registrants / attendance / occurrence ops ------------

	async getAttendOccurrence(clientId: number, timeId: number) {
		return this.request(`/attend/occurrences/${timeId}${toQuery({ client_id: clientId })}`);
	}

	async getAttendRegistrants(clientId: number, timeId: number) {
		return this.request(`/attend/occurrences/${timeId}/registrants${toQuery({ client_id: clientId })}`);
	}

	async addAttendRegistrant(clientId: number, timeId: number, input: Record<string, unknown>) {
		return this.request(`/attend/occurrences/${timeId}/registrants${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(input),
		});
	}

	async updateAttendRegistrant(clientId: number, regId: number, input: Record<string, unknown>) {
		return this.request(`/attend/registrations/${regId}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(input),
		});
	}

	async cancelAttendRegistrant(clientId: number, regId: number, refund: boolean, series = false, selected?: number[]) {
		return this.request(`/attend/registrations/${regId}/cancel${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ refund, series, selected: selected ?? [] }),
		});
	}

	async deleteAttendRegistrant(clientId: number, regId: number) {
		return this.request(`/attend/registrations/${regId}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	async addAttendGuests(clientId: number, regId: number, guests: Record<string, unknown>[]) {
		return this.request(`/attend/registrations/${regId}/guests${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ guests }),
		});
	}

	async deleteAttendGuest(clientId: number, guestId: number) {
		return this.request(`/attend/guests/${guestId}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	async getAttendAttendance(clientId: number, timeId: number) {
		return this.request(`/attend/occurrences/${timeId}/attendance${toQuery({ client_id: clientId })}`);
	}

	async addAttendAttendee(clientId: number, timeId: number, input: Record<string, unknown>) {
		return this.request(`/attend/occurrences/${timeId}/attendance${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(input),
		});
	}

	async checkInAttendRegistrant(clientId: number, timeId: number, regId: number, checked: number) {
		return this.request(`/attend/occurrences/${timeId}/check-in/${regId}${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ checked }),
		});
	}

	async updateAttendAttendee(clientId: number, id: number, input: Record<string, unknown>) {
		return this.request(`/attend/attendance/${id}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(input),
		});
	}

	async deleteAttendAttendee(clientId: number, id: number) {
		return this.request(`/attend/attendance/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	async setAttendPostEventData(clientId: number, timeId: number, data: string) {
		return this.request(`/attend/occurrences/${timeId}/post-data${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ data }),
		});
	}

	async getAttendInterested(clientId: number, timeId: number) {
		return this.request(`/attend/occurrences/${timeId}/interested${toQuery({ client_id: clientId })}`);
	}

	async deleteAttendInterested(clientId: number, id: number) {
		return this.request(`/attend/interested/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	// --- Check-in wave 1 (docs/check-in-spec.md): events surface only ---

	async getCheckinSettings(clientId: number, surface: 'event' = 'event') {
		return this.request(`/checkin/settings${toQuery({ client_id: clientId, surface })}`);
	}

	async saveCheckinSettings(
		clientId: number,
		categories: Record<string, { mode: string; identify_methods?: string[] }>,
	) {
		return this.request(`/checkin/settings/event${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ categories }),
		});
	}

	async getCheckinOccurrenceSummary(clientId: number, timeId: number) {
		return this.request(`/checkin/event/occurrences/${timeId}/summary${toQuery({ client_id: clientId })}`);
	}

	// wave 2a (docs/check-in-spec.md): Desk's one-call read for finding a
	// booked person, resolved for many of today's occurrences at once.
	async resolveCheckinOccurrences(clientId: number, timeIds: number[]) {
		if (timeIds.length === 0) return { occurrences: {}, anonymous_patrons: false };
		return this.request(
			`/checkin/event/occurrences/resolve${toQuery({ client_id: clientId, time_ids: timeIds.join(',') })}`,
		);
	}

	async addCheckinAdjustment(clientId: number, timeId: number, delta: number, note: string) {
		return this.request(`/checkin/event/occurrences/${timeId}/adjustments${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ delta, note }),
		});
	}

	// Check-in questions (Paul's decisions 15 and 17 September 2026): the
	// versioned set, events surface only in this wave.

	async getCheckinQuestions(clientId: number, surface: 'event' = 'event') {
		return this.request(`/checkin/questions${toQuery({ client_id: clientId, surface })}`);
	}

	async saveCheckinQuestions(
		clientId: number,
		categories: Record<string, { questions: unknown[] }>,
	) {
		return this.request(`/checkin/questions/event${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ categories }),
		});
	}

	// Bearer-fetched CSV (the getProspectCSV precedent — sessionStorage
	// token, so a plain link navigation would 401).
	async getAttendExportCSV(
		clientId: number,
		timeId: number,
		type: 'registrants' | 'attendance',
		opts?: { customQuestions?: boolean; withNotes?: boolean },
	): Promise<Blob> {
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers['Authorization'] = `Bearer ${tok}`;
		const params: Record<string, unknown> = { client_id: clientId, type };
		if (opts?.customQuestions) params.customquestions = 1;
		if (opts?.withNotes) params.withnotes = 1;
		const resp = await fetch(`${API_BASE}/attend/occurrences/${timeId}/export.csv${toQuery(params)}`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `CSV export failed (${resp.status})`);
		return resp.blob();
	}

	async sendAttendRegistrantEmail(clientId: number, timeId: number, input: Record<string, unknown>) {
		return this.request<AttendEmailResult>(`/attend/occurrences/${timeId}/email${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(input),
		});
	}

	async postponeAttendOccurrence(clientId: number, timeId: number, input: Record<string, unknown>) {
		return this.request(`/attend/occurrences/${timeId}/postpone${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(input),
		});
	}

	async cancelAttendOccurrence(clientId: number, timeId: number, reason: string, notify: boolean) {
		return this.request(`/attend/occurrences/${timeId}/cancel${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ reason, notify }),
		});
	}

	async cancelAttendSeries(clientId: number, eventId: number, reason: string, notify: boolean) {
		return this.request(`/attend/events/${eventId}/cancel-series${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ reason, notify }),
		});
	}

	async deleteAttendEvent(clientId: number, eventId: number) {
		return this.request(`/attend/events/${eventId}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	async deleteAttendOccurrence(clientId: number, timeId: number) {
		return this.request(`/attend/occurrences/${timeId}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	// columbus-attend-spec.md item 7 — real counts for the delete
	// confirmation (registrations lost, room bookings released).
	async getAttendEventDeleteImpact(clientId: number, eventId: number) {
		return this.request(`/attend/events/${eventId}/delete-impact${toQuery({ client_id: clientId })}`);
	}

	async getAttendOccurrenceDeleteImpact(clientId: number, timeId: number) {
		return this.request(`/attend/occurrences/${timeId}/delete-impact${toQuery({ client_id: clientId })}`);
	}

	// B23 (Columbus, "un-cancel a program") — real counts for the
	// reinstate confirmation, and the reinstate write itself.
	async getAttendReinstateImpact(clientId: number, timeId: number) {
		return this.request(`/attend/occurrences/${timeId}/reinstate-impact${toQuery({ client_id: clientId })}`);
	}

	async reinstateAttendOccurrence(clientId: number, timeId: number, input: Record<string, unknown>) {
		return this.request(`/attend/occurrences/${timeId}/reinstate${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(input),
		});
	}

	// B20 — the three-way edit. "This occurrence only" keeps using
	// postponeAttendOccurrence above; these two are this_and_future/all only.
	async getAttendSeriesEditImpact(clientId: number, timeId: number, scope: SeriesEditScope) {
		return this.request(`/attend/occurrences/${timeId}/series-edit-impact${toQuery({ client_id: clientId, scope })}`);
	}

	async editAttendSeries(clientId: number, timeId: number, scope: SeriesEditScope, input: AttendSeriesEditInput) {
		return this.request(`/attend/occurrences/${timeId}/series-edit${toQuery({ client_id: clientId, scope })}`, {
			method: 'POST',
			body: JSON.stringify(input),
		});
	}

	async archiveAttendEvent(clientId: number, eventId: number, archive: number) {
		return this.request(`/attend/events/${eventId}/archive${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ archive }),
		});
	}

	// --- Attend A4: taxonomy admin + bulk publish ------------------------

	async getAttendTaxonomy(clientId: number, kind: string) {
		return this.request(`/attend/taxonomy/${kind}${toQuery({ client_id: clientId })}`);
	}

	async saveAttendTaxonomy(clientId: number, kind: string, body: Record<string, unknown>) {
		return this.request(`/attend/taxonomy/${kind}${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	async deleteAttendTaxonomy(clientId: number, kind: string, id: number) {
		return this.request(`/attend/taxonomy/${kind}/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	async attendTaxonomyOps(clientId: number, kind: 'types' | 'ages', id: number, body: Record<string, unknown>) {
		return this.request(`/attend/taxonomy/${kind}/${id}/ops${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	async archiveAttendAgeGroup(clientId: number, id: number, archive: number) {
		return this.request(`/attend/taxonomy/ages/${id}/archive${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ archive }),
		});
	}

	async reorderAttendTaxonomy(clientId: number, kind: 'ages' | 'mediation-groups', ids: number[]) {
		return this.request(`/attend/taxonomy/${kind}/reorder${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ ids }),
		});
	}

	async checkAttendMediationGroup(clientId: number, id: number) {
		return this.request(`/attend/taxonomy/mediation-groups/${id}/check${toQuery({ client_id: clientId })}`);
	}

	async getAttendBulkCandidates(clientId: number) {
		return this.request(`/attend/bulk-publish/candidates${toQuery({ client_id: clientId })}`);
	}

	async attendBulkPublish(clientId: number, ids: number[]) {
		return this.request(`/attend/bulk-publish${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ ids }),
		});
	}

	// Notice templates (Comms Hub contract §14) — the LEGACY store. There
	// is no reset endpoint: absence means the notice is NOT SENT, and
	// writing the Communico default (via a normal save) is what turns
	// it on. Deleting would silence it, so there is nothing to delete.
	async listAttendNoticeTemplates(clientId: number) {
		return this.request(`/attend/notice-templates${toQuery({ client_id: clientId })}`);
	}

	async saveAttendNoticeTemplate(clientId: number, type: string, tmpl: Record<string, string>) {
		return this.request(
			`/attend/notice-templates/${encodeURIComponent(type)}${toQuery({ client_id: clientId })}`,
			{ method: 'PUT', body: JSON.stringify(tmpl) },
		);
	}

	// Client-settings staff test/config panel — the raw legacy
	// client_settings rows the Attend context bundle already gates on.
	// GET returns every whitelisted row's raw stored value (absent is
	// "", never a fabricated default); PUT upserts a subset and is
	// Communico-staff-only server-side.
	async getAttendSettings(clientId: number) {
		return this.request(`/attend/settings${toQuery({ client_id: clientId })}`);
	}

	async saveAttendSettings(clientId: number, patch: Record<string, string>) {
		return this.request(`/attend/settings${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(patch),
		});
	}

	// Communico-staff auto-discovering client-settings viewer/editor
	// (/admin/client-settings) — every client_settings_defaults row for a
	// legacy client, merged with its client_settings overrides. Unlike
	// the Attend panel above (a whitelist), there is no fixed name set:
	// the catalogue itself is the source of truth. Save is per-setting,
	// never a whole-form save.
	async getClientSettingsCatalogue(clientId: number) {
		return this.request(`/admin/client-settings${toQuery({ client_id: clientId })}`);
	}

	async saveClientSetting(clientId: number, name: string, value: string) {
		return this.request(`/admin/client-settings${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ name, value }),
		});
	}

	async getBroadcastContext(clientId: number) {
		return this.request(`/broadcast/context${toQuery({ client_id: clientId })}`);
	}

	async getBroadcastShows(clientId: number, q = '', type = '') {
		return this.request(`/broadcast/shows${toQuery({ client_id: clientId, q, type })}`);
	}

	async getBroadcastShow(clientId: number, id: number) {
		return this.request(`/broadcast/shows/${id}${toQuery({ client_id: clientId })}`);
	}

	async createBroadcastShow(payload: Record<string, unknown>) {
		return this.request(`/broadcast/shows`, { method: 'POST', body: JSON.stringify(payload) });
	}

	async updateBroadcastShow(id: number, payload: Record<string, unknown>) {
		return this.request(`/broadcast/shows/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
	}

	async duplicateBroadcastShow(id: number, clientId: number, name: string) {
		return this.request(`/broadcast/shows/${id}/duplicate`, {
			method: 'POST', body: JSON.stringify({ client_id: clientId, name }),
		});
	}

	async deleteBroadcastShow(clientId: number, id: number) {
		return this.request(`/broadcast/shows/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	async getBroadcastTemplates(clientId: number) {
		return this.request(`/broadcast/templates${toQuery({ client_id: clientId })}`);
	}

	async getBroadcastTemplate(clientId: number, id: number) {
		return this.request(`/broadcast/templates/${id}${toQuery({ client_id: clientId })}`);
	}

	async createBroadcastTemplate(payload: Record<string, unknown>) {
		return this.request(`/broadcast/templates`, { method: 'POST', body: JSON.stringify(payload) });
	}

	async updateBroadcastTemplate(id: number, payload: Record<string, unknown>) {
		return this.request(`/broadcast/templates/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
	}

	async deleteBroadcastTemplate(clientId: number, id: number) {
		return this.request(`/broadcast/templates/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	async getBroadcastLocations(clientId: number) {
		return this.request(`/broadcast/locations${toQuery({ client_id: clientId })}`);
	}

	// Broadcast channels + styles (Stage B2). channels.data is the raw
	// am-channel-editor schedule blob — serialized byte-compatibly by
	// cos-broadcast-channels, passed through verbatim here.
	async getBroadcastChannels(clientId: number) {
		return this.request(`/broadcast/channels${toQuery({ client_id: clientId })}`);
	}

	async getBroadcastChannel(clientId: number, id: number) {
		return this.request(`/broadcast/channels/${id}${toQuery({ client_id: clientId })}`);
	}

	async createBroadcastChannel(payload: Record<string, unknown>) {
		return this.request(`/broadcast/channels`, { method: 'POST', body: JSON.stringify(payload) });
	}

	async updateBroadcastChannel(id: number, payload: Record<string, unknown>) {
		return this.request(`/broadcast/channels/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
	}

	async deleteBroadcastChannel(clientId: number, id: number) {
		return this.request(`/broadcast/channels/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	async getBroadcastStyles(clientId: number) {
		return this.request(`/broadcast/styles${toQuery({ client_id: clientId })}`);
	}

	// Book carousel (docs/broadcast-book-carousel-spec.md) — staff CRUD
	// over a carousel config. Same client_id-scoping convention as every
	// other Broadcast CRUD method above. NOTE: these paths are an
	// ASSUMPTION about the backend contract (built in parallel) — they
	// follow the established `/broadcast/<noun>` shape this file already
	// uses, not a confirmed API. See types/broadcastCarousel.ts's header
	// comment.
	async getBroadcastCarousels(clientId: number) {
		return this.request(`/broadcast/carousels${toQuery({ client_id: clientId })}`);
	}

	async getBroadcastCarousel(clientId: number, id: number) {
		return this.request(`/broadcast/carousels/${id}${toQuery({ client_id: clientId })}`);
	}

	async createBroadcastCarousel(payload: Record<string, unknown>) {
		return this.request(`/broadcast/carousels`, { method: 'POST', body: JSON.stringify(payload) });
	}

	async updateBroadcastCarousel(id: number, payload: Record<string, unknown>) {
		return this.request(`/broadcast/carousels/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
	}

	async deleteBroadcastCarousel(clientId: number, id: number) {
		return this.request(`/broadcast/carousels/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}

	// Broadcast media (Stage B1b, D6) — the designer's image store, video
	// presign, and widget picker.
	async getBroadcastImages(clientId: number) {
		return this.request(`/broadcast/media/images${toQuery({ client_id: clientId })}`);
	}

	async uploadBroadcastImage(clientId: number, file: File) {
		const form = new FormData();
		form.append('file', file);
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/broadcast/media/images${toQuery({ client_id: clientId })}`, { method: 'POST', headers, body: form });
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { message?: string; error?: string };
			throw new ApiError(resp.status, b.message || b.error || `Upload failed (${resp.status})`);
		}
		return resp.json();
	}

	async deleteBroadcastImage(clientId: number, filename: string) {
		return this.request(`/broadcast/media/images${toQuery({ client_id: clientId, file: filename })}`, { method: 'DELETE' });
	}

	async getBroadcastVideos(clientId: number) {
		return this.request(`/broadcast/media/videos${toQuery({ client_id: clientId })}`);
	}

	async presignBroadcastVideoUpload(clientId: number, filename: string) {
		return this.request(`/broadcast/media/videos/presign${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ filename }),
		});
	}

	async getBroadcastWidgets(clientId: number) {
		return this.request(`/broadcast/media/widgets${toQuery({ client_id: clientId })}`);
	}

	// Broadcast screens (Stage B3) — the /control socket-auth embed +
	// INTERACT reference data. The live screens surface itself is the
	// broadcast socket server, not REST.
	async getBroadcastSocketAuth(clientId: number) {
		return this.request(`/broadcast/socket-auth${toQuery({ client_id: clientId })}`);
	}

	async getBroadcastInteractProfiles(clientId: number) {
		return this.request(`/broadcast/interact-profiles${toQuery({ client_id: clientId })}`);
	}

	async getBroadcastSIP2Profiles(clientId: number) {
		return this.request(`/broadcast/sip2-profiles${toQuery({ client_id: clientId })}`);
	}

	async getBroadcastStripeTerminals(clientId: number) {
		return this.request(`/broadcast/stripe-terminals${toQuery({ client_id: clientId })}`);
	}

	// ── Staff feedback / issue-capture platform ──────────────────────
	// Wire contract is camelCase (pinned — a deliberate departure from the
	// rest of this client's snake_case bodies; see types/feedback.ts).

	async createFeedbackReport(input: FeedbackCreateInput): Promise<FeedbackReport> {
		return this.request(`/feedback`, { method: 'POST', body: JSON.stringify(input) });
	}

	// Multipart upload — hand-rolled fetch + bearer header, mirroring
	// uploadAttendImage: request() forces application/json, which doesn't
	// apply to a FormData body.
	async uploadFeedbackMedia(blob: Blob, filename: string): Promise<FeedbackMediaUploadResult> {
		const fd = new FormData();
		fd.append('file', blob, filename);
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers['Authorization'] = `Bearer ${tok}`;
		const resp = await fetch(`${API_BASE}/feedback/media`, { method: 'POST', headers, body: fd });
		const body = (await resp.json().catch(() => ({}))) as Partial<FeedbackMediaUploadResult> & { message?: string; error?: string };
		if (!resp.ok) throw new ApiError(resp.status, body.message || body.error || `Upload failed (${resp.status})`);
		return body as FeedbackMediaUploadResult;
	}

	async reviewFeedbackReport(input: FeedbackReviewInput): Promise<FeedbackReviewVerdict> {
		return this.request(`/feedback/review`, { method: 'POST', body: JSON.stringify(input) });
	}

	async getMyFeedbackReports(params: { status?: FeedbackStatus; page?: number; limit?: number } = {}): Promise<FeedbackListEnvelope<FeedbackReportRow>> {
		return this.request(`/feedback/mine${toQuery(params as Record<string, unknown>)}`);
	}

	async getSimilarFeedbackReports(params: { pagePath: string; module?: string; limit?: number }): Promise<FeedbackListEnvelope<FeedbackReportRow>> {
		return this.request(`/feedback/similar${toQuery(params as Record<string, unknown>)}`);
	}

	async getAllFeedbackReports(params: { status?: FeedbackStatus; pagePath?: string; page?: number; limit?: number } = {}): Promise<FeedbackListEnvelope<FeedbackReportRow>> {
		return this.request(`/feedback/all${toQuery(params as Record<string, unknown>)}`);
	}

	async getFeedbackReport(id: number): Promise<FeedbackReport> {
		return this.request(`/feedback/${id}`);
	}

	async editFeedbackReport(id: number, input: FeedbackEditInput): Promise<FeedbackReport> {
		return this.request(`/feedback/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
	}

	// A reporter withdrawing their OWN report, allowed only while nobody has
	// started on it. Distinct from deleteAdminFeedbackReport (unconditional,
	// admin-only) — the backend decides; this just asks.
	async deleteOwnFeedbackReport(id: number): Promise<void> {
		return this.request(`/feedback/${id}`, { method: 'DELETE' });
	}

	async postFeedbackMessage(id: number, input: FeedbackMessageInput): Promise<FeedbackMessage> {
		return this.request(`/feedback/${id}/messages`, { method: 'POST', body: JSON.stringify(input) });
	}

	async plusOneFeedbackReport(id: number): Promise<{ watcherCount: number; plusOneCount: number }> {
		return this.request(`/feedback/${id}/plus-one`, { method: 'POST' });
	}

	async retestFeedbackReport(id: number, input: FeedbackRetestInput): Promise<FeedbackReport> {
		return this.request(`/feedback/${id}/retest`, { method: 'POST', body: JSON.stringify(input) });
	}

	async markFeedbackReportSeen(id: number): Promise<void> {
		return this.request(`/feedback/${id}/seen`, { method: 'POST' });
	}

	// Bulk mark-as-read for the caller's own "My reports" tab (Feedback
	// ticket 424) — either a ticked selection or every currently-unread id.
	async markManyFeedbackReportsSeen(ids: number[]): Promise<void> {
		return this.request(`/feedback/mine/read`, { method: 'POST', body: JSON.stringify({ ids }) });
	}

	// Admin console (RequireAdminRole server-side).
	async getAdminFeedbackReports(
		params: FeedbackAdminListParams = {},
	): Promise<FeedbackListEnvelope<FeedbackReportRow> & { statusCounts: FeedbackStatusCounts }> {
		return this.request(`/feedback/admin${toQuery(params as Record<string, unknown>)}`);
	}

	/** Stats are scoped by kind — a bug and an idea share the table but not a
	 *  status vocabulary or a notion of severity, so an unscoped call puts
	 *  enhancement statuses in a bug breakdown and gives every idea a "none"
	 *  severity bucket. Omit `kind` only when you genuinely want every kind. */
	async getAdminFeedbackStats(kind?: FeedbackReportKind): Promise<FeedbackAdminStats> {
		return this.request(`/feedback/admin/stats${kind ? `?kind=${encodeURIComponent(kind)}` : ''}`);
	}

	async patchAdminFeedbackReport(id: number, patch: FeedbackAdminPatch): Promise<FeedbackReport> {
		return this.request(`/feedback/admin/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	// Move a report between bug and enhancement — the status remaps to the
	// nearest equivalent in the new kind's vocabulary server-side (see
	// FeedbackReclassifyInput). A dedicated endpoint, not a field on
	// patchAdminFeedbackReport's patch body.
	async reclassifyAdminFeedbackReportKind(id: number, kind: FeedbackReportKind): Promise<FeedbackReport> {
		const input: FeedbackReclassifyInput = { kind };
		return this.request(`/feedback/admin/${id}/kind`, { method: 'PATCH', body: JSON.stringify(input) });
	}

	// Dispatcher confirming a batch of reports just went live — moves every
	// requested id currently at awaiting_deploy to needs_retest in one call.
	// Ids not at awaiting_deploy are silently skipped; `promoted` names which
	// ones actually moved. See model.FeedbackPromoteInput/Result.
	async promoteAdminFeedbackReports(ids: number[]): Promise<{ promoted: number[] }> {
		return this.request(`/feedback/admin/promote`, { method: 'POST', body: JSON.stringify({ ids }) });
	}

	async rescanAdminFeedbackReport(id: number): Promise<FeedbackReport> {
		return this.request(`/feedback/admin/${id}/rescan`, { method: 'POST' });
	}

	async addFeedbackScopeNote(id: number, note: string): Promise<FeedbackMessage> {
		const input: FeedbackScopeNoteInput = { note };
		return this.request(`/feedback/admin/${id}/scope`, { method: 'POST', body: JSON.stringify(input) });
	}

	// A per-round note on what was fixed — a fix_note thread message, riding
	// FeedbackReport.fixNotes (never a report-wide field). See
	// FeedbackFixNoteInput.
	async addFeedbackFixNote(id: number, note: string): Promise<FeedbackMessage> {
		const input: FeedbackFixNoteInput = { note };
		return this.request(`/feedback/admin/${id}/fix-note`, { method: 'POST', body: JSON.stringify(input) });
	}

	async deleteAdminFeedbackReport(id: number): Promise<void> {
		return this.request(`/feedback/admin/${id}`, { method: 'DELETE' });
	}

	// "Send to agents" — hands every "waiting" report to the external
	// agent runner over a signed webhook. Does NOT change any report's
	// status. 501 = runner not configured, 502 = runner unreachable.
	async dispatchAdminFeedbackAgents(): Promise<FeedbackAgentDispatchResult> {
		return this.request(`/feedback/admin/dispatch-agents`, { method: 'POST' });
	}

	// ---------- billing: contracts, billing accounts, price book ----------
	// The Maxio/SaaSOptics replacement (docs/maxio-replacement-plan.md §4,
	// §6). Every route below is staff-only server-side (RequireLevel(1));
	// issuing and closing sit behind RequireSuperAdmin on their own
	// surfaces.
	//
	// The three cross-scope reads the contract detail page needs
	// (`getContractInvoices` / `getContractScheduleItems` /
	// `getContractRevenueItems`) are deliberately named for the CONTRACT
	// they hang off rather than the collection they read, so they can't
	// collide with the invoice/close surfaces' own list methods.

	async getBillingContracts(params: {
		bill_to_id?: number;
		partner_id?: number;
		status?: string;
		search?: string;
		/** 'soon' (last term ends within 90 days) | 'lapsed' (already ended) */
		renewal?: string;
		sort?: string;
		start?: number;
		limit?: number;
	} = {}): Promise<BillingListResponse<Contract>> {
		const q = new URLSearchParams();
		if (params.bill_to_id !== undefined) q.set('bill_to_id', String(params.bill_to_id));
		if (params.partner_id !== undefined) q.set('partner_id', String(params.partner_id));
		if (params.status) q.set('status', params.status);
		if (params.search) q.set('search', params.search);
		if (params.renewal) q.set('renewal', params.renewal);
		if (params.sort) q.set('sort', params.sort);
		q.set('start', String(params.start ?? 0));
		q.set('limit', String(params.limit ?? 25));
		return this.request(`/billing/contracts?${q.toString()}`);
	}

	/** The contracts list grouped by partner — paginated by PARTNER, not by
	 *  row (`start`/`limit` count partners). See
	 *  cos-billing-contracts-list.ts for why a contract-paginated list
	 *  would be wrong here. */
	async getBillingContractsByPartner(params: {
		q?: string;
		status?: string;
		/** 'soon' (last term ends within 90 days) | 'lapsed' (already ended) */
		renewal?: string;
		sort?: string;
		start?: number;
		limit?: number;
	} = {}): Promise<ContractsByPartnerResponse> {
		const q = new URLSearchParams();
		if (params.q) q.set('q', params.q);
		if (params.status) q.set('status', params.status);
		if (params.renewal) q.set('renewal', params.renewal);
		if (params.sort) q.set('sort', params.sort);
		q.set('start', String(params.start ?? 0));
		q.set('limit', String(params.limit ?? 25));
		return this.request(`/billing/contracts/by-partner?${q.toString()}`);
	}

	/** The renewal worklist — horizon totals over the whole book, plus
	 *  partner-grouped contracts for the requested horizon.
	 *
	 *  ONE READ, TWO SCREENS. /billing/renewals is the worklist itself;
	 *  /billing calls it with `horizon: 'lapsed'` for its needs-attention
	 *  panel and reads `buckets` for its runway. Keeping it one endpoint is
	 *  what stops the two screens disagreeing about what "lapsed" means or
	 *  what a renewal is worth.
	 *
	 *  Default horizon is 'soon' server-side (lapsed + the 90-day runway +
	 *  no-term) — the actionable set, not the whole book. */
	/** `sort` is the backend form from utils/listSort.ts's toBackendSort —
	 *  `worth` / `worth_desc` / `term_end` / `term_end_desc`, or "" for the
	 *  worklist's own per-horizon order. */
	/** `includeClosedOut` shows contracts somebody has already decided not to
	 *  renew. Off by default — the worklist is the working list — and the
	 *  response's `closed_out` summary states what was left out either way. */
	async getBillingRenewals(
		horizon?: RenewalHorizonFilter,
		sort?: string,
		includeClosedOut = false,
	): Promise<BillingRenewals> {
		const q = new URLSearchParams();
		if (horizon) q.set('horizon', horizon);
		if (sort) q.set('sort', sort);
		if (includeClosedOut) q.set('include_closed_out', 'true');
		const qs = q.toString();
		return this.request(`/billing/contracts/renewals${qs ? `?${qs}` : ''}`);
	}

	/** Record that a lapsed contract is not being renewed.
	 *
	 *  A WORKLIST DECISION, NOT A FINANCIAL ONE — it takes the contract off
	 *  the renewals worklist and moves no revenue figure. Only a lapsed
	 *  contract is accepted; a live one 422s. */
	async closeOutBillingContract(
		id: number,
		reason: ContractCloseOutReason,
		replacementContractId?: number,
	): Promise<Contract> {
		return this.request(`/billing/contracts/${id}/close-out`, {
			method: 'POST',
			body: JSON.stringify({ reason, replacement_contract_id: replacementContractId }),
		});
	}

	/** Undo a close-out, returning the contract to the renewals worklist. */
	async reopenBillingContractCloseOut(id: number): Promise<Contract> {
		return this.request(`/billing/contracts/${id}/close-out/reverse`, {
			method: 'POST',
		});
	}

	/** Correct the reason on an ALREADY closed-out contract, without
	 *  reopening it. closed_out_at/closed_out_by are untouched — they
	 *  record the decision, not this edit. The commonest use is replacing
	 *  `no_reason_given`, a deliberate placeholder, once the real reason
	 *  is known. */
	async updateBillingContractCloseOutReason(
		id: number,
		reason: ContractCloseOutReason,
		replacementContractId?: number,
	): Promise<Contract> {
		return this.request(`/billing/contracts/${id}/close-out`, {
			method: 'PATCH',
			body: JSON.stringify({ reason, replacement_contract_id: replacementContractId }),
		});
	}

	/** Record that a customer has said, mid-term, they won't be renewing —
	 *  Brief M, the complement of close-out. Only a contract whose term is
	 *  still running is accepted; a lapsed one 422s (use close-out
	 *  instead). Takes it off the renewals worklist's main groups (it
	 *  still appears in its own "not renewing" section) and moves no
	 *  revenue figure. Auto-converts to a close-out, reason carried
	 *  across, once the term lapses. */
	async markContractNonRenewal(
		id: number,
		reason: ContractCloseOutReason,
		replacementContractId?: number,
	): Promise<Contract> {
		return this.request(`/billing/contracts/${id}/non-renewal`, {
			method: 'POST',
			body: JSON.stringify({ reason, replacement_contract_id: replacementContractId }),
		});
	}

	/** Undo a non-renewal mark, returning the contract to the renewals
	 *  worklist's main groups. */
	async reverseContractNonRenewal(id: number): Promise<Contract> {
		return this.request(`/billing/contracts/${id}/non-renewal/reverse`, {
			method: 'POST',
		});
	}

	/** Correct the reason on an ALREADY marked contract, without reversing
	 *  it. non_renewal_noted_at/non_renewal_noted_by are untouched. */
	async updateContractNonRenewalReason(
		id: number,
		reason: ContractCloseOutReason,
		replacementContractId?: number,
	): Promise<Contract> {
		return this.request(`/billing/contracts/${id}/non-renewal`, {
			method: 'PATCH',
			body: JSON.stringify({ reason, replacement_contract_id: replacementContractId }),
		});
	}

	/** Names every contract "mark customer as lost" would touch, and what
	 *  it would do to each — read-only, writes nothing (Brief P). */
	async previewMarkCustomerLost(partnerId: number): Promise<MarkCustomerLostPreview> {
		return this.request(`/billing/customers/${partnerId}/mark-lost/preview`, {
			method: 'POST',
		});
	}

	/** Applies ONE reason across every eligible contract a customer holds
	 *  — a running one gets a non-renewal mark, a lapsed one gets a
	 *  close-out, both cascading onto their lines. Does not set a
	 *  "churned" state anywhere: whether this customer has actually
	 *  reached zero ARR stays derived from the ledger. Each contract is
	 *  individually reversible via the existing close-out/non-renewal
	 *  undo routes. */
	async markCustomerLost(partnerId: number, reason: ContractCloseOutReason): Promise<MarkCustomerLostResult> {
		return this.request(`/billing/customers/${partnerId}/mark-lost`, {
			method: 'POST',
			body: JSON.stringify({ reason }),
		});
	}

	/** Everything a delete would destroy — read-only, super_admin only.
	 *  See ContractDeleteInventory's own comment. */
	async getBillingContractDeletePreview(id: number): Promise<ContractDeleteInventory> {
		return this.request(`/billing/contracts/${id}/delete-preview`);
	}

	/** DESTRUCTIVE — permanently deletes a contract and everything on it.
	 *  super_admin only. `contractNumber` must match the contract's own
	 *  number exactly (read off the preview moments earlier); `reason` is
	 *  required. See handler.DeleteContractRequest. */
	async deleteBillingContract(
		id: number,
		contractNumber: string,
		reason: string,
	): Promise<ContractDeleteInventory> {
		return this.request(`/billing/contracts/${id}`, {
			method: 'DELETE',
			body: JSON.stringify({ contract_number: contractNumber, reason }),
		});
	}

	async getBillingContract(id: number): Promise<Contract> {
		return this.request(`/billing/contracts/${id}`);
	}

	async updateBillingContract(id: number, patch: Record<string, unknown>): Promise<Contract> {
		return this.request(`/billing/contracts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	/** What changing when this contract's invoices go out WOULD do. Writes
	 *  nothing. The fingerprint in the answer goes back with the apply. */
	async previewBillingInvoiceTiming(id: number, body: InvoiceTimingRequest): Promise<InvoiceTimingPreview> {
		return this.request(`/billing/contracts/${id}/invoice-timing/preview`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	/** Applies the approved change. super_admin only. A 409 means the contract
	 *  changed since the preview and nothing was changed. */
	async applyBillingInvoiceTiming(id: number, body: InvoiceTimingRequest): Promise<InvoiceTimingResult> {
		return this.request(`/billing/contracts/${id}/invoice-timing`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	async getBillingContractTerms(id: number): Promise<BillingDataResponse<ContractTerm>> {
		return this.request(`/billing/contracts/${id}/terms`);
	}

	/** The append-only line history, oldest effective date first. Includes
	 *  reversal lines — do NOT filter them out, the negation is the
	 *  arithmetic. */
	async getBillingContractLines(id: number): Promise<BillingDataResponse<ContractLine>> {
		return this.request(`/billing/contracts/${id}/lines`);
	}

	/** The immutable amendment log, newest first. This is the human-readable
	 *  history of a contract; the lines are what it did arithmetically. */
	async getBillingContractEvents(id: number): Promise<BillingDataResponse<ContractEvent>> {
		return this.request(`/billing/contracts/${id}/events`);
	}

	async getContractInvoices(contractId: number, limit = 50): Promise<BillingListResponse<ContractInvoiceRow>> {
		const q = new URLSearchParams({ contract_id: String(contractId), start: '0', limit: String(limit) });
		return this.request(`/billing/invoices?${q.toString()}`);
	}

	/** "Don't bill this": settle scheduled, uninvoiced rows of ONE contract so no
	 *  invoice run raises them. super_admin. All or nothing; a 422 names the rows
	 *  that were refused. */
	async dontBillScheduleItems(
		contractId: number,
		body: { item_ids: number[]; reason: string },
	): Promise<{ rows: number; total: number }> {
		return this.request(`/billing/contracts/${contractId}/schedule-items/settle`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	/** "Bill this again": put a row that "Don't bill this" settled back to
	 *  scheduled. super_admin; refused for a row an amendment or script settled. */
	async billScheduleItemAgain(contractId: number, itemId: number): Promise<{ item_id: number }> {
		return this.request(`/billing/contracts/${contractId}/schedule-items/${itemId}/unsettle`, { method: 'POST' });
	}

	/** Which of the contract's rows "Bill this again" can undo. super_admin. */
	async getScheduleItemsSettledByStaff(contractId: number): Promise<{ item_ids: number[] }> {
		return this.request(`/billing/contracts/${contractId}/schedule-items/settled-by-staff`);
	}

	async getContractScheduleItems(contractId: number, limit = 100): Promise<BillingListResponse<ContractScheduleRow>> {
		const q = new URLSearchParams({ contract_id: String(contractId), start: '0', limit: String(limit) });
		return this.request(`/billing/schedule-items?${q.toString()}`);
	}

	/** Recognised-revenue rows for the contract detail's rev-rec summary.
	 *  Read-only — the derive engine owns generation. */
	async getContractRevenueItems(contractId: number, limit = 200): Promise<BillingListResponse<ContractRevenueRow>> {
		const q = new URLSearchParams({ contract_id: String(contractId), start: '0', limit: String(limit) });
		return this.request(`/billing/revenue-items?${q.toString()}`);
	}

	// /billing/customers/:id — the customer contract page (partner id).
	// Returns the BACKEND'S OWN wire shape (snake_case, flattened) — see
	// types/billing-customer.ts's header for why, and
	// utils/billingCustomerAdapt.ts for the adapter that turns this into
	// the BillingCustomer the page actually renders. Do not widen this
	// method's return type back to BillingCustomer: that was the exact
	// trap cos-billing-customer-detail.ts's `_loadCustomer` comment named
	// — it answers 200, so a mismatched shape never throws and never
	// falls back, it just renders undefined money silently.
	async getBillingCustomer(partnerId: number): Promise<BillingCustomerDetailWire> {
		return this.request(`/billing/customers/${partnerId}`);
	}

	/** The customer index — /billing/customers. `sort`: name|live_arr|
	 *  contract_count|next_expiry (suffix `_desc` to reverse). Default
	 *  next_expiry (soonest to act first). */
	async getBillingCustomers(params: {
		search?: string;
		sort?: string;
		start?: number;
		limit?: number;
	} = {}): Promise<BillingListResponse<BillingCustomerSummary>> {
		const q = new URLSearchParams();
		if (params.search) q.set('search', params.search);
		if (params.sort) q.set('sort', params.sort);
		q.set('start', String(params.start ?? 0));
		q.set('limit', String(params.limit ?? 25));
		return this.request(`/billing/customers?${q.toString()}`);
	}

	async getBillingAccounts(params: {
		partner_id?: number;
		is_active?: boolean;
		search?: string;
		sort?: string;
		start?: number;
		limit?: number;
	} = {}): Promise<BillingListResponse<BillingAccount>> {
		const q = new URLSearchParams();
		if (params.partner_id !== undefined) q.set('partner_id', String(params.partner_id));
		if (params.is_active !== undefined) q.set('is_active', params.is_active ? 'true' : 'false');
		if (params.search) q.set('search', params.search);
		if (params.sort) q.set('sort', params.sort);
		q.set('start', String(params.start ?? 0));
		q.set('limit', String(params.limit ?? 25));
		return this.request(`/billing/accounts?${q.toString()}`);
	}

	async getBillingAccount(id: number): Promise<BillingAccount> {
		return this.request(`/billing/accounts/${id}`);
	}

	/** Who a billing account's email goes to by default: its library's
	 *  accounting contacts, else the addresses in its billing email. */
	async getBillingAccountRecipients(id: number): Promise<AccountRecipients> {
		return this.request(`/billing/accounts/${id}/accounting-contacts`);
	}

	/** Whether an address can become an accounting contact on the account's
	 *  library, or is already a contact on another organisation. */
	async checkBillingAccountAddress(id: number, email: string): Promise<AddressCheck> {
		return this.request(`/billing/accounts/${id}/address-check${toQuery({ email })}`);
	}

	/** Replace the account's shared billing desk addresses, first address
	 *  first. super_admin. It changes who later invoices and reminders go to and
	 *  emails nobody. An empty list clears them. */
	async setBillingDeskAddresses(id: number, addresses: string[]): Promise<BillingAccount> {
		return this.request(`/billing/accounts/${id}/billing-desk-addresses`, {
			method: 'PUT',
			body: JSON.stringify({ addresses }),
		});
	}

	async updateBillingAccount(id: number, patch: Record<string, unknown>): Promise<BillingAccount> {
		return this.request(`/billing/accounts/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async getBillingProducts(params: {
		kind?: string;
		is_active?: boolean;
		search?: string;
		sort?: string;
		start?: number;
		limit?: number;
	} = {}): Promise<BillingListResponse<BillingProduct>> {
		const q = new URLSearchParams();
		if (params.kind) q.set('kind', params.kind);
		if (params.is_active !== undefined) q.set('is_active', params.is_active ? 'true' : 'false');
		if (params.search) q.set('search', params.search);
		if (params.sort) q.set('sort', params.sort);
		q.set('start', String(params.start ?? 0));
		q.set('limit', String(params.limit ?? 25));
		return this.request(`/billing/products?${q.toString()}`);
	}

	async getBillingProduct(id: number): Promise<BillingProduct> {
		return this.request(`/billing/products/${id}`);
	}

	async updateBillingProduct(id: number, patch: Record<string, unknown>): Promise<BillingProduct> {
		return this.request(`/billing/products/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	/** Every price band for a product, including expired ones — the
	 *  effective-dated history IS the point. */
	async getBillingProductPrices(id: number): Promise<BillingDataResponse<BillingProductPrice>> {
		return this.request(`/billing/products/${id}/prices`);
	}

	/** Opens a new band. Superseding an existing price is this call plus a
	 *  close of the old row (`effective_to`) — never an in-place edit of
	 *  `list_price`. */
	async createBillingProductPrice(productId: number, body: Record<string, unknown>): Promise<BillingProductPrice> {
		return this.request(`/billing/products/${productId}/prices`, { method: 'POST', body: JSON.stringify(body) });
	}

	async updateBillingProductPrice(priceId: number, patch: Record<string, unknown>): Promise<BillingProductPrice> {
		return this.request(`/billing/prices/${priceId}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	/** Replaces a band's price, keeping the old one: the outgoing band is
	 *  closed the day before the replacement opens, both in ONE
	 *  transaction. The only way to change a price that has been in force
	 *  — PATCH refuses that, because rewriting `list_price` destroys what
	 *  list said when a signed quote was written. */
	async supersedeBillingProductPrice(
		priceId: number,
		body: { list_price: number; effective_from?: string },
	): Promise<BillingProductPrice> {
		return this.request(`/billing/prices/${priceId}/supersede`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	/** The whole price book as a matrix — products down, population bands
	 *  across, every term in one payload so the term toggle costs no round
	 *  trip. */
	async getPriceBook(): Promise<PriceBook> {
		return this.request('/billing/price-book');
	}

	/** One-time revenue across the whole book (Brief AR): implementation,
	 *  custom development, training and every other product the catalogue
	 *  marks non-recurring, over three windows and split by fee type.
	 *
	 *  The same query /billing/reconciliation's one-time finding measures,
	 *  so the overview card cannot disagree with the finding that asked
	 *  for it. */
	async getBillingOneTime(): Promise<OneTimeRevenue> {
		return this.request('/billing/one-time');
	}

	/** One customer's one-time revenue: the same totals narrowed to one
	 *  partner, plus every one-time line behind them, newest first. */
	async getBillingCustomerOneTime(partnerId: number): Promise<OneTimeRevenue> {
		return this.request(`/billing/one-time/customers/${partnerId}`);
	}

	async deleteBillingProductPrice(priceId: number): Promise<void> {
		return this.request(`/billing/prices/${priceId}`, { method: 'DELETE' });
	}

	// ---------- billing: order forms, quotes and renewals ----------
	// ONE DOCUMENT with three clause sets — see types/billing-documents.ts.

	/** The clause library, plus the closed merge vocabulary in the same
	 *  payload so the builder can name a clause's dependencies without a
	 *  second round trip. `kind` narrows to the clauses that apply to it. */
	async getBillingDocumentClauses(params: {
		kind?: BillingDocumentKind;
		active_only?: boolean;
	} = {}): Promise<BillingDocumentClauseList> {
		const q = new URLSearchParams();
		if (params.kind) q.set('kind', params.kind);
		if (params.active_only) q.set('active_only', 'true');
		const qs = q.toString();
		return this.request(`/billing/document-clauses${qs ? `?${qs}` : ''}`);
	}

	async createBillingDocumentClause(body: Record<string, unknown>): Promise<BillingDocumentClause> {
		return this.request('/billing/document-clauses', { method: 'POST', body: JSON.stringify(body) });
	}

	/** A clause edit reaches NEW documents only: every document carries its
	 *  own resolved copy of the body, so a wording change cannot restate
	 *  paper a customer already holds. */
	async updateBillingDocumentClause(
		id: number,
		patch: Record<string, unknown>,
	): Promise<BillingDocumentClause> {
		return this.request(`/billing/document-clauses/${id}`, {
			method: 'PATCH',
			body: JSON.stringify(patch),
		});
	}

	async getBillingDocuments(params: {
		kind?: BillingDocumentKind;
		status?: BillingDocumentStatus;
		contract_id?: number;
		partner_id?: number;
		search?: string;
		sort?: string;
		start?: number;
		limit?: number;
	} = {}): Promise<BillingListResponse<BillingDocument>> {
		const q = new URLSearchParams();
		if (params.kind) q.set('kind', params.kind);
		if (params.status) q.set('status', params.status);
		if (params.contract_id) q.set('contract_id', String(params.contract_id));
		if (params.partner_id) q.set('partner_id', String(params.partner_id));
		if (params.search) q.set('search', params.search);
		if (params.sort) q.set('sort', params.sort);
		q.set('start', String(params.start ?? 0));
		q.set('limit', String(params.limit ?? 25));
		return this.request(`/billing/documents?${q.toString()}`);
	}

	/** One document with its snapshotted lines and clauses — everything a
	 *  render needs, and nothing read from a live record. */
	async getBillingDocument(id: number): Promise<BillingDocument> {
		return this.request(`/billing/documents/${id}`);
	}

	/** Builds a document. A 422 here is the loud failure the merge
	 *  vocabulary promises: its message names every fact COS does not hold
	 *  and which record each comes from. Nothing is written and no reference
	 *  is consumed, so the caller can fix the record and retry. */
	async createBillingDocument(body: BillingDocumentCreate): Promise<BillingDocument> {
		return this.request('/billing/documents', { method: 'POST', body: JSON.stringify(body) });
	}

	async deleteBillingDocument(id: number): Promise<void> {
		return this.request(`/billing/documents/${id}`, { method: 'DELETE' });
	}

	/** The contract's terms, so the builder offers a real choice of which
	 *  term a document quotes rather than assuming one. */
	async getBillingDocumentTerms(contractId: number): Promise<BillingDataResponse<BillingDocumentTerm>> {
		return this.request(`/billing/documents/contract/${contractId}/terms`);
	}

	/** The PDF, as a bearer-authenticated Blob — a plain <a href> would 401,
	 *  since the token lives in sessionStorage rather than a cookie (the
	 *  prospect-CSV pattern). */
	async getBillingDocumentPDF(id: number): Promise<Blob> {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/billing/documents/${id}/pdf`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `Document PDF failed (${resp.status})`);
		return resp.blob();
	}

	// ---------- billing: reconciliation against Maxio ----------
	// Maxio is the source of truth for the migration. These serve the
	// screen that names every measured difference, and the worklist for
	// the one a human can clear.

	/** Every difference between COS and Maxio, measured on load. */
	async getBillingReconciliation(): Promise<Reconciliation> {
		return this.request('/billing/reconciliation');
	}

	/** Maxio's own reported figures, newest period first. The first row is
	 *  the one every Maxio-side finding was measured against; the rest are
	 *  the history of what has been pasted. */
	async getMaxioReportedFigures(): Promise<MaxioFiguresResponse> {
		return this.request('/billing/reconciliation/maxio-figures');
	}

	/** Record one month of Maxio's reports.
	 *
	 *  A PATCH on a period that exists, only the fields sent change, so
	 *  correcting one transcribed digit cannot reset the other fifteen
	 *  numbers. A period that does not exist yet is created, and then
	 *  everything is required. super_admin; 422 names the fields. */
	async updateMaxioReportedFigures(
		period: string, body: MaxioFiguresPatch,
	): Promise<MaxioReportedFigures> {
		return this.request(`/billing/reconciliation/maxio-figures/${encodeURIComponent(period)}`, {
			method: 'PATCH', body: JSON.stringify(body),
		});
	}

	/** The Maxio contracts absent from COS because their customer could
	 *  not be matched to a partner, worth their ARR each. */
	async getUnmatchedMaxioContracts(sort?: string): Promise<UnmatchedResponse> {
		const q = sort ? `?sort=${encodeURIComponent(sort)}` : '';
		return this.request(`/billing/reconciliation/unmatched${q}`);
	}

	/** Record which COS partner a Maxio customer is.
	 *
	 *  THIS IMPORTS NOTHING. It records a decision the importer consults
	 *  on its next run; the contract stays absent from COS until an
	 *  operator deliberately re-runs it. */
	async resolveMaxioCustomer(
		customerId: number, body: { partner_id: number; note?: string },
	): Promise<CustomerResolution> {
		return this.request(`/billing/reconciliation/unmatched/${customerId}/resolve`, {
			method: 'POST', body: JSON.stringify(body),
		});
	}

	/** Take a mapping back, before the next import acts on it. */
	async unresolveMaxioCustomer(customerId: number): Promise<void> {
		return this.request(`/billing/reconciliation/unmatched/${customerId}/resolve`, { method: 'DELETE' });
	}

	/** Existing partners that resemble a Maxio customer — "did you mean
	 *  this one?", read BEFORE offering to create a new one so it is
	 *  something the reader sees rather than an error they hit. */
	async maxioCustomerNearMatches(customerId: number): Promise<NearMatchPartner[]> {
		return this.request(`/billing/reconciliation/unmatched/${customerId}/near-matches`);
	}

	/** Create the COS partner a Maxio customer should always have had, and
	 *  map it — in one transaction, server-side.
	 *
	 *  THIS IS THE ANSWER FOR MOST OF THIS LIST, not an exception: none of
	 *  the unmatched customers carrying a billing domain appears in
	 *  HubSpot, and HubSpot holds fewer companies than COS holds partners,
	 *  so it cannot be the source for what COS lacks.
	 *
	 *  IT STILL IMPORTS NOTHING. The contracts land when an operator
	 *  deliberately re-runs the importer.
	 *
	 *  ONE CUSTOMER PER CALL. There is no bulk form of this, deliberately. */
	async createPartnerFromMaxioCustomer(
		customerId: number, body: CreatePartnerRequest,
	): Promise<CreatedPartner> {
		return this.request(`/billing/reconciliation/unmatched/${customerId}/create-partner`, {
			method: 'POST', body: JSON.stringify(body),
		});
	}

	/** Partner search for the worklist, INCLUDING review-flagged rows.
	 *
	 *  The plain /partners list filters to the customer+other lifecycles
	 *  (Stage 60f), which is right for Marcie's 363 libraries and wrong
	 *  here: a Maxio customer that failed to match is quite likely to sit
	 *  in COS as a prospect-lifecycle row, and a search that cannot see it
	 *  cannot resolve it.
	 *
	 *  `include_review` is admin-gated server-side and SILENTLY IGNORED
	 *  for a non-admin, so a plain staff user searches the narrower set.
	 *  That is a real limitation, not a bug to route around client-side —
	 *  the filter exists for a reason and the gate is the server's. */
	async searchPartnersIncludingReview(q: string, limit: number, signal?: AbortSignal) {
		return this.request(
			`/partners${toQuery({ search: q, limit, include_review: true })}`,
			signal ? { signal } : {},
		);
	}

	/** The price-book band for one product at a given population and term.
	 *  A SUGGESTED price only — it is never used to reject a quoted one.
	 *
	 *  404 means the product has no band covering that population, which
	 *  is a real and common answer: three of the four best-selling
	 *  products have no price rows at all. The contract editor renders
	 *  that as "no list price" and lets the figure be typed, so callers
	 *  should catch rather than treat it as a failure. */
	async getBillingProductSuggestedPrice(
		productId: number,
		params: { population: number; term_months?: number; as_of?: string },
	): Promise<BillingProductPrice> {
		const q = new URLSearchParams({ population: String(params.population) });
		if (params.term_months !== undefined) q.set('term_months', String(params.term_months));
		if (params.as_of) q.set('as_of', params.as_of);
		return this.request(`/billing/products/${productId}/price?${q.toString()}`);
	}

	/** Brief D — every active currency (id/slug/label/current USD rate),
	 *  for a contract's currency picker and for converting the USD price
	 *  book into a non-USD suggested price. */
	async getBillingCurrencies(): Promise<{ data: BillingCurrency[] }> {
		return this.request('/billing/currencies');
	}

	/** Sets the CURRENT USD reference rate a new non-USD line freezes
	 *  onto itself at write time. Never touches a line already written. */
	async setBillingCurrencyRate(id: number, usdExchangeRate: number): Promise<BillingCurrency> {
		return this.request(`/billing/currencies/${id}/rate`, {
			method: 'PATCH',
			body: JSON.stringify({ usd_exchange_rate: usdExchangeRate }),
		});
	}

	// ---------- billing: recording and changing an agreement ----------
	// The WRITE half of the contract surface. Everything above reads;
	// these three are what put a contract in the book and change it.
	//
	// contract_lines is APPEND-ONLY, so none of them edits a row: a new
	// contract appends its opening lines, a renewal appends a term and
	// its lines, and an amendment appends a negating line plus its
	// replacement. `previewContractAmendment` is the dry run that shows
	// exactly which rows the last of those would write.

	/** Records a whole new agreement — contract, first term, every line,
	 *  the opening `create` event, and the payer if the partner has none
	 *  yet — in one transaction. */
	async createFullContract(body: NewContractRequest): Promise<NewContractResult> {
		return this.request('/billing/contracts/full', { method: 'POST', body: JSON.stringify(body) });
	}

	/** The review in front of the save: what recording this agreement would
	 *  do to revenue and which invoices it would create. Same body and the
	 *  same refusals as createFullContract; writes nothing. */
	async previewFullContract(body: NewContractRequest): Promise<NewContractReview> {
		return this.request('/billing/contracts/full/preview', { method: 'POST', body: JSON.stringify(body) });
	}

	/** What an amendment WOULD write. Writes nothing, and shares every
	 *  step of the real call up to the commit — so the negating line and
	 *  its replacement shown here are the rows that will be appended,
	 *  not an approximation of them.
	 *
	 *  Staff-level, unlike the amendment itself: working out what a
	 *  change would do must not need the privilege that makes it. */
	async previewContractAmendment(contractId: number, body: AmendRequest): Promise<AmendPreview> {
		return this.request(`/billing/contracts/${contractId}/amend/preview`, {
			method: 'POST', body: JSON.stringify(body),
		});
	}

	/** Reviews a mid-contract change (a drop, an add, or both on one date)
	 *  as ONE change before it is saved. Takes the same bodies the save
	 *  sends, in the order it sends them. Staff-level, writes nothing. */
	async previewMidTermChange(contractId: number, body: MidTermRequest): Promise<MidTermPreview> {
		return this.request(`/billing/contracts/${contractId}/amend/preview-change`, {
			method: 'POST', body: JSON.stringify(body),
		});
	}

	/** Saves a mid-contract change (a drop, an add, or both on one date) in
	 *  ONE database transaction: all of it or none of it, including the credit
	 *  note when one is asked for. Takes the body previewMidTermChange
	 *  reviewed. super_admin server-side. */
	async amendMidTermChange(contractId: number, body: MidTermRequest): Promise<MidTermResult> {
		return this.request(`/billing/contracts/${contractId}/amend/change`, {
			method: 'POST', body: JSON.stringify(body),
		});
	}

	/** Applies one commercial change — add, drop, reprice, extend, renew,
	 *  cancel or correct. super_admin server-side. */
	async amendContract(contractId: number, body: AmendRequest): Promise<AmendResult> {
		return this.request(`/billing/contracts/${contractId}/amend`, {
			method: 'POST', body: JSON.stringify(body),
		});
	}

	// ---------------------------------------------------------------------
	// Billing — invoices, invoice runs, the month-end close, per-partner
	// financials. The Maxio/SaaSOptics replacement (docs/maxio-replacement-
	// plan.md). Every route is staff-only (RequireLevel(1)); the four WRITES
	// that put a document in front of a customer or freeze the books —
	// invoice issue, invoice run, credit-note generation, period close — are
	// RequireSuperAdmin on top of that.
	// ---------------------------------------------------------------------

	async getInvoices(
		filter: {
			status?: string;
			search?: string;
			billing_account_id?: number;
			contract_id?: number;
			sort?: string;
			start?: number;
			limit?: number;
		} = {},
	): Promise<InvoiceListEnvelope> {
		return this.request(`/billing/invoices${toQuery(filter as Record<string, unknown>)}`);
	}

	/** One invoice WITH its lines (the grouped-by-member-library detail). */
	async getInvoice(id: number): Promise<Invoice> {
		return this.request(`/billing/invoices/${id}`);
	}

	/** Change a DRAFT's header (dates, service period, PO number). A 422 past
	 *  draft carries the server's sentence. */
	async patchInvoice(id: number, body: InvoiceDraftPatch): Promise<Invoice> {
		return this.request(`/billing/invoices/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
	}

	/** Replace a DRAFT's lines, whole: existing lines by id, new ones without.
	 *  Totals are recomputed by the server. A line left out is removed, and its
	 *  billing schedule item is released so the work is billed again. 422 past
	 *  draft. */
	async replaceInvoiceLines(id: number, lines: InvoiceLineEdit[], expectedUpdatedAt: string): Promise<Invoice> {
		return this.request(`/billing/invoices/${id}/lines`, {
			method: 'PUT',
			body: JSON.stringify({ lines, expected_updated_at: expectedUpdatedAt }),
		});
	}

	/** Add a one-off charge to a DRAFT by adding it to its contract. With
	 *  `preview: true` the server computes the full effect and rolls it back. */
	async addInvoiceCharge(id: number, body: DraftAddChargeBody): Promise<DraftChangeFigures> {
		return this.request(`/billing/invoices/${id}/charges`, { method: 'POST', body: JSON.stringify(body) });
	}

	/** Change the price of a DRAFT line by changing its contract (a new price
	 *  from that period on, or a one-off discount with a reason). `preview`
	 *  rolls it back. */
	async changeInvoiceLinePrice(id: number, lineId: number, body: DraftChangePriceBody): Promise<DraftChangeFigures> {
		return this.request(`/billing/invoices/${id}/lines/${lineId}/price`, { method: 'POST', body: JSON.stringify(body) });
	}

	/** What removing lines from a DRAFT would do, writing nothing. */
	async previewInvoiceLines(id: number, lines: InvoiceLineEdit[], expectedUpdatedAt: string): Promise<DraftChangeFigures> {
		return this.request(`/billing/invoices/${id}/lines/preview`, {
			method: 'POST',
			body: JSON.stringify({ lines, expected_updated_at: expectedUpdatedAt }),
		});
	}

	/** One invoice's lines against its contract. Empty `lines` means they agree. */
	async getInvoiceContractCheck(id: number): Promise<InvoiceContractCheck> {
		return this.request(`/billing/invoices/${id}/contract-check`);
	}

	/** Draft and issued invoices dated in the range whose lines do not match their contract. */
	async getInvoiceContractCheckForPeriod(from: string, to: string): Promise<PeriodContractCheck> {
		return this.request(`/billing/invoices/contract-check${toQuery({ from, to })}`);
	}

	/**
	 * Transition a draft invoice to issued, FREEZING it. super_admin.
	 * There is no un-issue: the correction path from here is a credit note.
	 */
	async issueInvoice(id: number): Promise<Invoice> {
		return this.request(`/billing/invoices/${id}/issue`, { method: 'POST' });
	}

	/**
	 * Cancel a DRAFT invoice: status void, balance zero, and every
	 * billing schedule item it claimed released so the next run bills the
	 * work again. super_admin, and a reason is required, it lands in the
	 * audit row the corrections register reads.
	 *
	 * Refused (422) on anything past draft: an issued invoice is a
	 * document the customer holds, and the correction path there is a
	 * credit note.
	 */
	async voidInvoice(id: number, reason: string): Promise<Invoice> {
		return this.request(`/billing/invoices/${id}/void`, {
			method: 'POST',
			body: JSON.stringify({ reason }),
		});
	}

	/**
	 * Change an invoice's invoice date and/or due date by hand. super_admin.
	 * Allowed on a draft, issued or paid invoice, not a void one. A reason is
	 * required past draft. A 422 carries the server's sentence (a closed
	 * accounting month, a due date before the invoice date). QuickBooks is
	 * updated too when the invoice is in it; `quickbooks` says how that went.
	 */
	async changeInvoiceDates(
		id: number,
		body: { invoice_date?: string; due_date?: string; reason?: string },
	): Promise<InvoiceDatesResult> {
		return this.request(`/billing/invoices/${id}/dates`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	/** Send the invoice's current dates to QuickBooks again, after a date
	 *  change that did not get there. Changes nothing in COS. */
	async resendInvoiceDatesToQBO(id: number): Promise<InvoiceDatesResult> {
		return this.request(`/billing/invoices/${id}/dates/resend-to-quickbooks`, { method: 'POST' });
	}

	/**
	 * Send an invoice out of COS (Brief AK): issues it first if it's still
	 * a draft, renders the PDF, renders the email template, dispatches
	 * through the Communications Hub with the online link (and the PDF only
	 * when `attachPdf` is set, or when no link could be made), the first
	 * recipient as To and the rest on Cc, and records the send. super_admin. Refuses (422)
	 * when outbound sending is disabled in this environment, the invoice
	 * is never marked sent for a message that never left.
	 */
	async sendInvoice(id: number, request?: InvoiceSendRequest): Promise<InvoiceSendResult> {
		// Nothing to say sends no body at all: the endpoint's body is optional
		// and the server uses the account's default recipients.
		const body: {
			recipients?: string[];
			add_accounting_contacts?: string[];
			remove_accounting_contacts?: number[];
			add_billing_emails?: string[];
			remove_billing_emails?: string[];
			attach_pdf?: boolean;
		} = {};
		if (request?.attachPdf) body.attach_pdf = true;
		if (request?.recipients && request.recipients.length > 0) body.recipients = request.recipients;
		if (request?.addAccountingContacts && request.addAccountingContacts.length > 0) {
			body.add_accounting_contacts = request.addAccountingContacts;
		}
		if (request?.removeAccountingContacts && request.removeAccountingContacts.length > 0) {
			body.remove_accounting_contacts = request.removeAccountingContacts;
		}
		if (request?.addBillingEmails && request.addBillingEmails.length > 0) body.add_billing_emails = request.addBillingEmails;
		if (request?.removeBillingEmails && request.removeBillingEmails.length > 0) {
			body.remove_billing_emails = request.removeBillingEmails;
		}
		const init: RequestInit = { method: 'POST' };
		if (Object.keys(body).length > 0) init.body = JSON.stringify(body);
		return this.request(`/billing/invoices/${id}/send`, init);
	}

	/**
	 * Send yourself a test of the invoice email: the same subject (marked
	 * TEST), body, PDF and online link, to the signed-in user's own address
	 * only (the server picks it). Changes nothing on the invoice, so it works
	 * on a draft. super_admin.
	 */
	async sendInvoiceTest(id: number, attachPdf = false): Promise<InvoiceTestSendResult> {
		// attachPdf only sets the test's closing line (the test always carries the
		// PDF), so it matches what the real send will do.
		const init: RequestInit = { method: 'POST' };
		if (attachPdf) init.body = JSON.stringify({ attach_pdf: true });
		return this.request(`/billing/invoices/${id}/send-test`, init);
	}

	/** Record a send made outside COS (a PDF posted or emailed by hand). It
	 *  sends nothing. super_admin; refused for an invoice COS emailed itself. */
	async recordInvoiceSend(id: number, body: { sent_on: string; recipients: string[]; note: string }): Promise<Invoice> {
		return this.request(`/billing/invoices/${id}/record-send`, { method: 'POST', body: JSON.stringify(body) });
	}

	/** What really happened to each send of an invoice: queued, sent to the
	 *  mail provider, delivered, or failed with the reason, per recipient. */
	async getInvoiceDeliveries(id: number): Promise<BillingDelivery[]> {
		return this.request(`/billing/invoices/${id}/deliveries`);
	}

	/** Every payment reminder sent for an invoice, oldest first, with its real
	 *  delivery result and who opened it. */
	async getInvoiceReminderDeliveries(id: number): Promise<BillingDelivery[]> {
		return this.request(`/billing/invoices/${id}/reminder-deliveries`);
	}

	/** Every statement sent to a billing account, oldest first, with its real
	 *  delivery result and who opened it. */
	async getStatementDeliveries(billingAccountId: number): Promise<BillingDelivery[]> {
		return this.request(`/billing/accounts/${billingAccountId}/statement-deliveries`);
	}

	/** The online invoice link's status and the views recorded against it. */
	async getInvoiceViews(id: number): Promise<InvoiceViews> {
		return this.request(`/billing/invoices/${id}/views`);
	}

	/** Makes the invoice's online link when it has none. */
	async createInvoiceViewLink(id: number): Promise<InvoiceViews> {
		return this.request(`/billing/invoices/${id}/view-link`, { method: 'POST' });
	}

	/** super_admin. Kills the old URL at once. */
	async revokeInvoiceViewLink(id: number): Promise<InvoiceViews> {
		return this.request(`/billing/invoices/${id}/view-link/revoke`, { method: 'POST' });
	}

	/** super_admin. Revokes the old link and makes a new one with zero views. */
	async recreateInvoiceViewLink(id: number): Promise<InvoiceViews> {
		return this.request(`/billing/invoices/${id}/view-link/recreate`, { method: 'POST' });
	}

	/** The invoice PDF, as a bearer-authenticated Blob — see
	 *  getBillingDocumentPDF for why this can't be a plain <a href>. The
	 *  exact bytes a send attaches. */
	async getInvoicePDF(id: number): Promise<Blob> {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(`${API_BASE}/billing/invoices/${id}/pdf`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `Invoice PDF failed (${resp.status})`);
		return resp.blob();
	}

	/** The invoice-send email template + PDF payment-terms/remit-to text +
	 *  the QuickBooks push-on-send switch. One row for the whole
	 *  deployment. */
	async getInvoiceSettings(): Promise<BillingInvoiceSettings> {
		return this.request(`/billing/invoice-settings`);
	}

	/** Reads, from QuickBooks, whether each invoice emailed by hand has gone
	 *  out, and records what it finds. Reads only: nothing is written to
	 *  QuickBooks. */
	async syncQBOSentStatus(): Promise<QBOSentStatusResult> {
		return this.request(`/billing/qbo/sent-status/sync`, { method: 'POST' });
	}

	/** Patch-only — every field not sent is left exactly as it was. */
	async patchInvoiceSettings(patch: BillingInvoiceSettingsPatch): Promise<BillingInvoiceSettings> {
		return this.request(`/billing/invoice-settings`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	/** Renders the CURRENTLY STORED template against a real invoice — no
	 *  send, no mutation. The settings editor's live preview. */
	async previewInvoiceEmail(invoiceId: number, to?: string): Promise<{ subject: string; body: string }> {
		// `to` is who the email goes to first: the greeting names that contact.
		return this.request(`/billing/invoice-settings/preview${toQuery({ invoice_id: invoiceId, to })}`);
	}

	/** What a run WOULD produce. Writes nothing; the returned invoices carry
	 *  no ids and no numbers, so it is a shape-and-money check rather than a
	 *  reservation. */
	async previewInvoiceRun(
		params: { as_of?: string; billing_account_id?: number } = {},
	): Promise<InvoiceRunResult> {
		return this.request(`/billing/invoice-runs/preview${toQuery(params as Record<string, unknown>)}`);
	}

	/** Generate the DRAFT invoices. super_admin. Drafts — issuing each one is
	 *  a separate, deliberate step. */
	async createInvoiceRun(req: InvoiceRunRequest = {}): Promise<InvoiceRunResult> {
		return this.request(`/billing/invoice-runs`, { method: 'POST', body: JSON.stringify(req) });
	}

	// ---------------------------------------------------------------------
	// Accounts Receivable (Brief AL) — aging, record-a-payment (with a
	// deposit account and an allocation across open invoices), reminders and
	// statements. Every route is staff-only, super_admin like the rest of
	// billing (billingGate()). The QuickBooks payment PUSH is server-side
	// only, gated behind COS_BILLING_PUSH_PAYMENTS (default off) — nothing
	// here controls it.
	// ---------------------------------------------------------------------

	/** GET /billing/payments — every field, including the receivables ones
	 *  (method, deposit account, status, reversal) now that GetByID/List
	 *  select the full column set. Used by the receivables view's "Recent
	 *  payments" panel to offer Reverse on a COS-recorded (source_system
	 *  'cos') row. */
	async getPayments(
		filter: { billing_account_id?: number; invoice_id?: number; search?: string; sort?: string; start?: number; limit?: number } = {},
	): Promise<{ data: ReceivablesPayment[] | null; total: number }> {
		return this.request(`/billing/payments${toQuery(filter as Record<string, unknown>)}`);
	}

	async getReceivablesAging(
		filter: {
			billing_account_id?: number;
			partner_id?: number;
		} = {},
	): Promise<AgingReport> {
		return this.request(`/billing/receivables/aging${toQuery(filter as Record<string, unknown>)}`);
	}

	async getDepositAccounts(): Promise<QBOBankAccount[] | null> {
		return this.request(`/billing/receivables/deposit-accounts`);
	}

	/** Re-reads QuickBooks' chart of accounts. Throws (503) if QuickBooks is
	 *  not configured — surface via userMessage(). */
	async refreshDepositAccounts(): Promise<QBOBankAccount[] | null> {
		return this.request(`/billing/receivables/deposit-accounts/refresh`, { method: 'POST' });
	}

	async recordReceivablesPayment(body: RecordPaymentRequest): Promise<ReceivablesPayment> {
		return this.request(`/billing/receivables/payments`, { method: 'POST', body: JSON.stringify(body) });
	}

	// ---------------------------------------------------------------------
	// Cheque intake (migration 000462), under /billing/deposits.
	//
	// A deposit batch is one trip to the bank: upload the scan, read it,
	// confirm each cheque against a billing account and its open invoices,
	// record the payments, print the cover sheet. Every route is
	// super_admin like the rest of billing (billingGate()).
	//
	// Its own prefix rather than a /billing/receivables child: "deposits"
	// would collide with the deposit-ACCOUNTS picker that already lives
	// there, and a bank account and a trip to the bank are different
	// things.
	// ---------------------------------------------------------------------

	async getDepositBatches(
		filter: { status?: DepositBatchStatus; search?: string; start?: number; limit?: number } = {},
	): Promise<{ data: DepositBatch[] | null; total: number }> {
		return this.request(`/billing/deposits${toQuery(filter as Record<string, unknown>)}`);
	}

	/** The batch, its scans, and its items with the matcher's LIVE
	 *  proposals. The proposals are derived per request from current
	 *  invoice balances, so this is the read to repeat after any change
	 *  rather than a payload to cache. */
	async getDepositBatch(id: number): Promise<DepositBatchDetail> {
		return this.request(`/billing/deposits/${id}`);
	}

	async createDepositBatch(
		body: { deposit_account_id?: string; deposit_account_name?: string; note?: string } = {},
	): Promise<DepositBatch> {
		return this.request(`/billing/deposits`, { method: 'POST', body: JSON.stringify(body) });
	}

	async updateDepositBatch(
		id: number,
		patch: { deposit_account_id?: string; deposit_account_name?: string; note?: string },
	): Promise<DepositBatch> {
		return this.request(`/billing/deposits/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}

	async deleteDepositBatch(id: number): Promise<void> {
		await this.request(`/billing/deposits/${id}`, { method: 'DELETE' });
	}

	/** Multipart upload: a hand-rolled fetch plus the bearer header,
	 *  mirroring uploadFeedbackMedia: request() forces
	 *  application/json, which does not apply to a FormData body. */
	async uploadDepositScan(id: number, file: File): Promise<DepositBatchScan> {
		const form = new FormData();
		form.append('file', file);
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers['Authorization'] = `Bearer ${tok}`;
		const resp = await fetch(`${API_BASE}/billing/deposits/${id}/scan`, { method: 'POST', headers, body: form });
		const body = (await resp.json().catch(() => ({}))) as Partial<DepositBatchScan> & { message?: string; error?: string };
		if (!resp.ok) throw new ApiError(resp.status, body.message || body.error || `Upload failed (${resp.status})`);
		return body as DepositBatchScan;
	}

	/** Reads the scan into proposed items, or stores hand-keyed ones.
	 *  `read: false` with a reason is not an error: the runtime may not
	 *  be configured, and the cheques can still be typed in. */
	async readDepositBatch(id: number, items?: DepositItemInput[]): Promise<DepositReadResult> {
		return this.request(`/billing/deposits/${id}/read`, {
			method: 'POST',
			body: JSON.stringify(items && items.length > 0 ? { items } : {}),
		});
	}

	/** Records the confirmed cheques as payments. A per-item result: a
	 *  cheque that cannot record is a sentence in `refusals`, not a
	 *  failed request, and the others still recorded. */
	async confirmDepositBatch(id: number, items: DepositItemInput[]): Promise<DepositConfirmResult> {
		return this.request(`/billing/deposits/${id}/confirm`, {
			method: 'POST',
			body: JSON.stringify({ items }),
		});
	}

	async markDepositBatchDeposited(id: number, depositDate?: string): Promise<DepositBatch> {
		return this.request(`/billing/deposits/${id}/deposited`, {
			method: 'POST',
			body: JSON.stringify(depositDate ? { deposit_date: depositDate } : {}),
		});
	}

	async markDepositBatchMatched(id: number, bankReference: string): Promise<DepositBatch> {
		return this.request(`/billing/deposits/${id}/matched`, {
			method: 'POST',
			body: JSON.stringify({ bank_reference: bankReference }),
		});
	}

	// ---------------------------------------------------------------------
	// Bank activity (migration 000472) - /billing/bank-activity.
	//
	// What QuickBooks says moved on Communico's bank accounts, matched
	// against COS payments and cheque deposit batches. READ ONLY towards
	// QuickBooks: nothing here can create, amend, void or send anything
	// in the accounting file.
	//
	// QuickBooks does not expose bank feed items that are still in For
	// Review through its API, so these lines lag the bank by days. The
	// page says so in its own copy.
	// ---------------------------------------------------------------------

	async getBankActivity(filter: BankActivityFilter = {}): Promise<BankActivityPage> {
		return this.request(`/billing/bank-activity${toQuery(filter as Record<string, unknown>)}`);
	}

	/** One line with its LIVE match candidates. The candidates are derived
	 *  per request from current payments and batches, so this is the read
	 *  to repeat after a change rather than a payload to cache. */
	async getBankTransaction(id: number): Promise<BankTransactionDetail> {
		return this.request(`/billing/bank-activity/${id}`);
	}

	/** List two: COS payments in the window with no bank line. */
	async getUnrecordedPayments(
		filter: Pick<BankActivityFilter, 'since' | 'until' | 'limit'> = {},
	): Promise<UnrecordedPaymentsPage> {
		return this.request(
			`/billing/bank-activity/unrecorded-payments${toQuery(filter as Record<string, unknown>)}`,
		);
	}

	/** Reads the window from QuickBooks. Answers 503 when
	 *  COS_QBO_BANK_SYNC is off, which is the default everywhere; the
	 *  page checks sync_enabled first so it can say so without asking. */
	async syncBankActivity(since?: string): Promise<BankSyncResult> {
		return this.request(`/billing/bank-activity/sync`, {
			method: 'POST',
			body: JSON.stringify(since ? { since } : {}),
		});
	}

	/** A person confirming what a line is. Exactly one of payment_id,
	 *  batch_id or ignore; ignore needs a reason. */
	async matchBankTransaction(id: number, input: BankMatchInput): Promise<BankTransaction> {
		return this.request(`/billing/bank-activity/${id}/match`, {
			method: 'POST',
			body: JSON.stringify(input),
		});
	}

	async unmatchBankTransaction(id: number): Promise<BankTransaction> {
		return this.request(`/billing/bank-activity/${id}/unmatch`, { method: 'POST' });
	}

	// -----------------------------------------------------------------
	// Rules for non-receivable credits (migration 000478).
	//
	// Some credits will never be a customer payment: bank interest, a tax
	// refund, an owner contribution, a transfer from another Communico
	// account. A rule says that once instead of every month.
	//
	// NOTHING HERE REACHES QUICKBOOKS either, and a rule can only ever
	// move a bank line to 'not_receivable': the row stays, names the rule
	// and is counted on the summary strip.
	// -----------------------------------------------------------------

	async getBankLineRules(): Promise<BankLineRule[]> {
		return this.request(`/billing/bank-activity/rules`);
	}

	async getBankLineRule(ruleId: number): Promise<BankLineRule> {
		return this.request(`/billing/bank-activity/rules/${ruleId}`);
	}

	/** The dry run. Writes NOTHING, and runs the same engine over the
	 *  same window the save will, so the count in the form and the count
	 *  after saving are the same number by construction. */
	async previewBankLineRule(input: BankLineRuleInput): Promise<BankRulePreview> {
		return this.request(`/billing/bank-activity/rules/preview`, {
			method: 'POST',
			body: JSON.stringify(input),
		});
	}

	/** Saves a rule and, with `apply`, runs it. A rule over the threshold
	 *  is refused with a 422 naming the count and the total until
	 *  `confirm_large` comes back true. */
	async createBankLineRule(input: BankLineRuleInput): Promise<BankRuleSaveResponse> {
		return this.request(`/billing/bank-activity/rules`, {
			method: 'POST',
			body: JSON.stringify(input),
		});
	}

	/** A full replacement, not a patch: a rule is small and one form
	 *  writes it. Everything the OLD rule set aside is released first. */
	async updateBankLineRule(
		ruleId: number,
		input: BankLineRuleInput,
	): Promise<BankRuleSaveResponse> {
		return this.request(`/billing/bank-activity/rules/${ruleId}`, {
			method: 'PATCH',
			body: JSON.stringify(input),
		});
	}

	/** Switching a rule off RELEASES every line it set aside, and only
	 *  those. That is why it is a verb rather than a field. */
	async deactivateBankLineRule(ruleId: number): Promise<BankRuleActiveResponse> {
		return this.request(`/billing/bank-activity/rules/${ruleId}/deactivate`, { method: 'POST' });
	}

	async activateBankLineRule(ruleId: number): Promise<BankRuleActiveResponse> {
		return this.request(`/billing/bank-activity/rules/${ruleId}/activate`, { method: 'POST' });
	}

	/** Deleting releases what the rule took before it goes. */
	async deleteBankLineRule(ruleId: number): Promise<BankRuleReleaseResult> {
		return this.request(`/billing/bank-activity/rules/${ruleId}`, { method: 'DELETE' });
	}

	/** Runs the active rules over a window on demand. This and the sync
	 *  are the only two things that run rules: there is no scheduler. */
	async applyBankLineRules(window: { since?: string; until?: string } = {}): Promise<BankRuleApplyResult> {
		return this.request(`/billing/bank-activity/rules/apply`, {
			method: 'POST',
			body: JSON.stringify(window),
		});
	}

	/** A rule that would have covered this line, prefilled and UNSAVED,
	 *  plus how often the same payer or category has been ignored by hand
	 *  already. An offer, never a rule. */
	async getBankRuleSuggestion(id: number): Promise<BankRuleSuggestion> {
		return this.request(`/billing/bank-activity/${id}/rule-suggestion`);
	}

	/** A bearer fetch plus a Blob, not an <a href>, for the same reason as
	 *  getStatementPDF: the token lives in sessionStorage, not a cookie,
	 *  so a plain link navigation would 401. */
	async getDepositCoverSheetPDF(id: number): Promise<Blob> {
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers['Authorization'] = `Bearer ${tok}`;
		const resp = await fetch(`${API_BASE}/billing/deposits/${id}/cover-sheet.pdf`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `Cover sheet failed (${resp.status})`);
		return resp.blob();
	}

	/** The three cheque-intake setup questions, in the shared pathway
	 *  shape so <cos-setup-pathway> renders them like every other
	 *  module's journey. */
	async getBillingDepositSetupState(): Promise<PathwayState> {
		return this.request(`/billing/deposits/setup-state`);
	}

	// ── QuickBooks Online connection (super-admin) ──────────────────────
	//
	// Three calls, all under /admin/integrations/qbo. The OAuth CALLBACK is
	// deliberately not here: Intuit redirects the browser to a backend route
	// outside /api/v1 which renders its own terminal page, so the SPA never
	// sees it and has nothing to fetch.

	async getQBOStatus(): Promise<QBOConnectionStatus> {
		return this.request(`/admin/integrations/qbo/status`);
	}

	/** Mints a single-use state and returns Intuit's consent URL. The caller
	 *  NAVIGATES to it — following it with fetch would land the consent page
	 *  inside a JSON response, which is why the endpoint returns a URL rather
	 *  than a 302. */
	async connectQBO(): Promise<QBOAuthorizeURL> {
		return this.request(`/admin/integrations/qbo/connect`, { method: 'POST' });
	}

	/** Pushes one issued invoice to QuickBooks (super-admin). Also the retry
	 *  button. in_flight means another push for it started within the
	 *  last few minutes and nothing was sent this time. */
	async pushInvoiceToQBO(id: number): Promise<{
		invoice_id: number;
		qbo_invoice_id: string;
		already_synced: boolean;
		adopted: boolean;
		in_flight: boolean;
	}> {
		return this.request(`/admin/integrations/qbo/invoices/${id}/push`, { method: 'POST' });
	}

	/** Revokes the grant at Intuit and clears the stored credentials.
	 *  `force` deactivates without revoking when the stored tokens cannot be
	 *  decrypted (the plain call answers 409 for those); the response then
	 *  carries a `warning` that the Intuit authorization was not revoked. */
	async disconnectQBO(force = false): Promise<{ status?: string; warning?: string }> {
		const q = force ? '?force=true' : '';
		return this.request(`/admin/integrations/qbo/disconnect${q}`, { method: 'POST' });
	}

	/** Whether a payment receipt may go to this billing account, to whom, and
	 *  if not, why in plain words. The Record payment dialog's checkbox. */
	async getPaymentReceiptOptions(billingAccountId: number): Promise<PaymentReceiptOptions> {
		return this.request(`/billing/receivables/receipt-options${toQuery({ billing_account_id: billingAccountId })}`);
	}

	/** Sends, or resends, the receipt email for a payment recorded in COS. */
	async sendPaymentReceipt(paymentId: number, cc: string[] = []): Promise<PaymentReceiptSendResult> {
		return this.request(`/billing/receivables/payments/${paymentId}/receipt`, {
			method: 'POST',
			body: JSON.stringify({ cc }),
		});
	}

	/** Voids a COS-recorded payment — reopens every allocated invoice's
	 *  balance. A reason is required. */
	async reverseReceivablesPayment(id: number, reason: string): Promise<ReceivablesPayment> {
		return this.request(`/billing/receivables/payments/${id}/reverse`, {
			method: 'POST',
			body: JSON.stringify({ reason }),
		});
	}

	async getReminderSettings(): Promise<BillingReminderSettings> {
		return this.request(`/billing/receivables/reminder-settings`);
	}

	async updateReminderSettings(patch: BillingReminderSettingsPatch): Promise<BillingReminderSettings> {
		return this.request(`/billing/receivables/reminder-settings`, {
			method: 'PATCH',
			body: JSON.stringify(patch),
		});
	}

	/** The editor's SERVED merge vocabularies, the schedule day bounds and
	 *  how many billing accounts have opted out. Served rather than
	 *  mirrored: the same list drives the backend's save-time validator. */
	async getReminderSettingsMeta(): Promise<BillingReminderSettingsMeta> {
		return this.request(`/billing/receivables/reminder-settings/meta`);
	}

	/** Renders the CURRENTLY STORED reminder or statement template against a
	 *  real invoice or billing account, no send, no send-log row, and no
	 *  Comms Hub involvement. Save first, then preview what was saved. */
	async previewReminderTemplate(
		kind: 'reminder' | 'statement',
		id: number,
	): Promise<BillingTemplatePreview> {
		const params = kind === 'reminder' ? { kind, invoice_id: id } : { kind, billing_account_id: id };
		return this.request(`/billing/receivables/reminder-settings/preview${toQuery(params)}`);
	}

	/** "Send me a test" of the reminder email. Goes to the signed-in user
	 *  only, writes nothing, and records nothing on the invoice. */
	async sendTestReminder(body: BillingSendTestRequest): Promise<BillingSendTestResponse> {
		return this.request('/billing/receivables/reminder-settings/send-test', {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	/** "Send me a test" of the statement email, PDF attached. Same rules. */
	async sendTestStatement(body: BillingSendTestRequest): Promise<BillingSendTestResponse> {
		return this.request('/billing/receivables/statements/send-test', {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	/** The manual "Send reminder" row action — same eligibility and template
	 *  as the scheduled pass. `sent: false` with a `refusal` string means the
	 *  Hub is off or the account can't be reached — not an error. */
	async sendInvoiceReminder(invoiceId: number): Promise<SendActionResponse> {
		return this.request(`/billing/receivables/invoices/${invoiceId}/send-reminder`, { method: 'POST' });
	}

	/** The newest posted payment applied to an invoice through either link
	 *  (its own invoice_id or an allocation), or null when there is none. */
	async getInvoiceLatestPayment(invoiceId: number): Promise<ReceivablesPayment | null> {
		const res = await this.request<{ payment: ReceivablesPayment | null }>(
			`/billing/receivables/invoices/${invoiceId}/latest-payment`,
		);
		return res.payment ?? null;
	}

	async getInvoiceSendTimeline(invoiceId: number): Promise<BillingSendLog[] | null> {
		return this.request(`/billing/receivables/invoices/${invoiceId}/timeline`);
	}

	async getStatement(billingAccountId: number): Promise<Statement> {
		return this.request(`/billing/receivables/statements/${billingAccountId}`);
	}

	/** A bearer fetch + Blob, not an <a href> — same reasoning as
	 *  getBillingClosePackCSV: the token lives in sessionStorage, not a
	 *  cookie, so a plain link navigation would 401. */
	async getStatementPDF(billingAccountId: number, from?: string): Promise<Blob> {
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers['Authorization'] = `Bearer ${tok}`;
		const resp = await fetch(`${API_BASE}/billing/receivables/statements/${billingAccountId}/pdf${from ? `?from=${encodeURIComponent(from)}` : ''}`, { headers });
		if (!resp.ok) {
			// A 422 carries a sentence that says what to fix (a bad start date).
			const b = (await resp.json().catch(() => ({}))) as { message?: string };
			throw new ApiError(resp.status, b.message || `Statement PDF failed (${resp.status})`);
		}
		return resp.blob();
	}

	/** `from` (YYYY-MM-DD) is the first day of the payments listed; left out,
	 *  the server uses 1 January of this year. */
	async sendStatement(billingAccountId: number, from?: string): Promise<SendActionResponse> {
		return this.request(`/billing/receivables/statements/${billingAccountId}/send`, {
			method: 'POST',
			...(from ? { body: JSON.stringify({ from }) } : {}),
		});
	}

	/** A DRAFT credit note for what a contract has invoiced but no longer
	 *  owes. super_admin. Amounts are negative. */
	async generateCreditNote(
		contractId: number,
		body: { credit_date?: string; reason?: string; credits_invoice_id?: number } = {},
	): Promise<{ id: number; credit_note_number: string; total: number; status: string }> {
		return this.request(`/billing/contracts/${contractId}/credit-note`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	/** Credit notes, filtered. `credits_invoice_id` is the invoice-page
	 *  filter: the notes raised against one invoice. */
	async getCreditNotes(
		filter: {
			billing_account_id?: number;
			credits_invoice_id?: number;
			contract_id?: number;
			status?: string;
			sort?: string;
			start?: number;
			limit?: number;
		} = {},
	): Promise<CreditNoteListEnvelope> {
		return this.request(`/billing/credit-notes${toQuery(filter as Record<string, unknown>)}`);
	}

	/** One credit note WITH `remaining`, what it still has left to
	 *  apply, which nothing on the note itself records. */
	async getCreditNote(id: number): Promise<CreditNoteDetail> {
		return this.request(`/billing/credit-notes/${id}`);
	}

	/**
	 * Apply an issued credit note to an issued invoice on the same
	 * billing account. Records a credit_application payment and moves the
	 * invoice balance in the same transaction, NOT a cash payment, so no
	 * deposit account and no method. super_admin. Amount omitted means as
	 * much as the invoice can take from what the note has left. Nothing
	 * is pushed to QuickBooks.
	 */
	async applyCreditNote(
		id: number,
		body: { invoice_id: number; amount?: number; payment_date?: string },
	): Promise<ApplyCreditNoteResult> {
		return this.request(`/billing/credit-notes/${id}/apply`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	/**
	 * Refund an issued credit note's remaining balance (or part of it)
	 * BACK to the customer as cash. Records a credit_refund payment, NOT
	 * an application and NOT a cash receipt, so this never looks like
	 * money that arrived. super_admin. Nothing is pushed to QuickBooks.
	 */
	async refundCreditNote(
		id: number,
		body: {
			amount: number;
			refund_date?: string;
			method: string;
			reference?: string;
			deposit_account_id: string;
			deposit_account_name?: string;
			reason: string;
		},
	): Promise<RefundCreditNoteResult> {
		return this.request(`/billing/credit-notes/${id}/refund`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	async getAccountingPeriods(): Promise<{ data: AccountingPeriod[] | null }> {
		return this.request(`/billing/periods`);
	}

	/**
	 * Close an accounting period. super_admin, and EFFECTIVELY IRREVERSIBLE:
	 * it freezes every revenue_schedule_items row in the period (posted=true)
	 * so a later correction lands as a catch-up adjustment in the next open
	 * period instead. There is no reopen endpoint.
	 */
	async closeAccountingPeriod(id: number): Promise<AccountingPeriod> {
		return this.request(`/billing/periods/${id}/close`, { method: 'POST' });
	}

	/**
	 * Create (or get) an accounting period row for (year, month) — Brief AH:
	 * a period earlier than the derive engine's own forward walk has ever
	 * reached (nothing before 2026-08 has a row) still needs to exist so it
	 * can carry a signed-off opening-balance override. Idempotent: re-issuing
	 * the same (year, month) returns the existing row rather than a second one.
	 */
	async createAccountingPeriod(year: number, month: number): Promise<AccountingPeriod> {
		return this.request(`/billing/periods`, {
			method: 'POST',
			body: JSON.stringify({ year, month }),
		});
	}

	/**
	 * Record a signed-off opening balance directly on a period (Brief AH) —
	 * e.g. Paul setting 1 Jan 2025's opening to QuickBooks' own reported
	 * $3,088,693.84. super_admin. Both halves are required together.
	 */
	async setOpeningBalanceOverride(
		id: number,
		body: { opening_short_cents: number; opening_long_cents: number; note: string },
	): Promise<AccountingPeriod> {
		return this.request(`/billing/periods/${id}/opening-override`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	/**
	 * Revenue schedule items — the close pack's read path. `posted=false`
	 * plus a period is EXACTLY the row set the revenue-recognition journal
	 * entry is built from (see src/billing/closepack.ts).
	 */
	/**
	 * THE MONTH-END CLOSE PACK — the revenue recognition journal entry and
	 * the deferred revenue rollforward, both computed server-side by
	 * internal/billing/closepack.
	 *
	 * This is a READ. It creates no accounting period, writes no audit row,
	 * and does not advance a period toward closing — run it as often as you
	 * like while working a month.
	 *
	 * EVERY MONEY FIELD IS WHOLE CENTS (`*_cents`). Render with
	 * moneyFromCents(); never sum them as dollars.
	 */
	async getBillingClosePack(year: number, month: number): Promise<ClosePack> {
		return this.request(`/billing/close/pack?year=${year}&month=${month}`);
	}

	/**
	 * The same pack as CSV. A bearer fetch + Blob, not an <a href> — the
	 * token lives in sessionStorage rather than a cookie, so a plain link
	 * navigation would 401 (the prospect-CSV precedent).
	 */
	async getBillingClosePackCSV(year: number, month: number): Promise<Blob> {
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers['Authorization'] = `Bearer ${tok}`;
		const resp = await fetch(`${API_BASE}/billing/close/pack.csv?year=${year}&month=${month}`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `Close pack CSV export failed (${resp.status})`);
		return resp.blob();
	}

	/**
	 * THE CORRECTIONS REGISTER (Brief AX): every correction recorded in a
	 * window as one document: contract amendments that end or restate a
	 * line, close-outs, non-renewals, credit notes issued, credit
	 * applications, payment reversals and invoice voids.
	 *
	 * A READ, like the close pack. `from` and `to` are YYYY-MM-DD and both
	 * are required: a financial report that quietly defaults its period
	 * produces a document for a month nobody asked for.
	 */
	async getBillingCorrections(from: string, to: string): Promise<CorrectionsRegister> {
		return this.request(
			`/billing/corrections?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
	}

	/**
	 * The same register as CSV. A bearer fetch + Blob for the same reason
	 * the close pack's is: the token lives in sessionStorage, so a plain
	 * link navigation would 401.
	 */
	async getBillingCorrectionsCSV(from: string, to: string): Promise<Blob> {
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers['Authorization'] = `Bearer ${tok}`;
		const resp = await fetch(
			`${API_BASE}/billing/corrections.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
			{ headers });
		if (!resp.ok) throw new ApiError(resp.status, `Corrections register CSV export failed (${resp.status})`);
		return resp.blob();
	}

	// THE CONTRACT BALANCE CHECK (Brief BD). All three are reads: checking
	// the books must not be able to change them.

	/** One contract's balance, with the periods that do not line up and the
	 *  verdict on every credit note. */
	async getContractBalance(contractId: number): Promise<ContractBalanceDetail> {
		return this.request(`/billing/contracts/${contractId}/balance`);
	}

	/** The book-wide check. `outOfBalanceOnly` drops the contracts that
	 *  foot; the counts and totals still cover everything checked. */
	async getBillingBalanceCheck(
		opts: { status?: string; outOfBalanceOnly?: boolean } = {},
	): Promise<BalanceCheck> {
		const q = new URLSearchParams();
		if (opts.status) q.set('status', opts.status);
		if (opts.outOfBalanceOnly) q.set('out_of_balance', 'true');
		const qs = q.toString();
		return this.request(`/billing/balance-check${qs ? `?${qs}` : ''}`);
	}

	/**
	 * The same check as CSV. A bearer fetch + Blob for the same reason the
	 * corrections register's is: the token lives in sessionStorage, so a
	 * plain link navigation would 401.
	 */
	async getBillingBalanceCheckCSV(
		opts: { status?: string; outOfBalanceOnly?: boolean } = {},
	): Promise<Blob> {
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers['Authorization'] = `Bearer ${tok}`;
		const q = new URLSearchParams();
		if (opts.status) q.set('status', opts.status);
		if (opts.outOfBalanceOnly) q.set('out_of_balance', 'true');
		const qs = q.toString();
		const resp = await fetch(`${API_BASE}/billing/balance-check.csv${qs ? `?${qs}` : ''}`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `Balance check CSV export failed (${resp.status})`);
		return resp.blob();
	}

	async getRevenueItems(
		filter: {
			contract_id?: number;
			partner_id?: number;
			period_year?: number;
			period_month?: number;
			posted?: boolean;
		} = {},
	): Promise<{ data: RevenueItem[] | null }> {
		return this.request(`/billing/revenue-items${toQuery(filter as Record<string, unknown>)}`);
	}

	/** The per-partner financial section (plan §4.7) — contracts, invoices,
	 *  payments, recognised revenue and modules in one round trip. */
	/** invoiceSort is a whitelisted key (`due_date`, `due_date_desc`, …) for the
	 *  partner record's invoice table. An unknown value degrades to the
	 *  server's newest-first default rather than erroring. */
	async getPartnerFinancials(partnerId: number, invoiceSort = ''): Promise<PartnerFinancials> {
		const qs = invoiceSort ? `?invoice_sort=${encodeURIComponent(invoiceSort)}` : '';
		return this.request(`/partners/${partnerId}/financials${qs}`);
	}

	// ---------- billing metrics: ARR, MRR, the waterfall, cohorts ----------
	// The finance reporting surface (/billing/reports, plan §4.7). Wire shape
	// is fixed by docs/billing-metrics-contract.md — read that before changing
	// a signature here; both sides of the contract move together or not at
	// all. Money is DOLLARS on the wire, dates are 'YYYY-MM-DD'. Staff-only
	// server-side (RequireLevel(1)), like every other /billing route.

	/** Point-in-time ARR/MRR. `as_of` defaults to today server-side. */
	async getBillingArr(asOf?: string): Promise<ArrSummary> {
		const q = new URLSearchParams();
		if (asOf) q.set('as_of', asOf);
		const qs = q.toString();
		return this.request(`/billing/metrics/arr${qs ? `?${qs}` : ''}`);
	}

	/** ARR/MRR at each period end across a range. The backend caps the range
	 *  at 60 points and 422s a wider request naming the cap — callers pick a
	 *  window that fits rather than relying on truncation. */
	async getBillingArrSeries(params: {
		from: string;
		to: string;
		interval?: MetricsInterval;
	}): Promise<ArrSeries> {
		const q = new URLSearchParams({ from: params.from, to: params.to });
		if (params.interval) q.set('interval', params.interval);
		return this.request(`/billing/metrics/series?${q.toString()}`);
	}

	/** The ARR bridge between two dates — this is the churn report. Carries
	 *  `caveats.expired_unrenewed_*`, which the UI must surface next to the
	 *  churn figure rather than fold into it. */
	async getBillingWaterfall(params: { from: string; to: string }): Promise<WaterfallReport> {
		const q = new URLSearchParams({ from: params.from, to: params.to });
		return this.request(`/billing/metrics/waterfall?${q.toString()}`);
	}

	/** Retention by the year a partner first held ARR. `retention_pct` may
	 *  exceed 100 (expansion) and is never capped.
	 *
	 *  Takes a single measurement date, NOT a range: a cohort report asks
	 *  "for every cohort, what are they worth now", so `as_of` is when
	 *  `current_arr` is measured while each cohort's `initial_arr` is measured
	 *  at its own formation. Every cohort year is returned. */
	async getBillingCohorts(params: { as_of: string }): Promise<CohortReport> {
		const q = new URLSearchParams({ as_of: params.as_of });
		return this.request(`/billing/metrics/cohorts?${q.toString()}`);
	}

	/** The ARR bridge between two dates, per-partner-per-product: expansion
	 *  vs. price uplift vs. contraction vs. lost (Brief N). A sibling to
	 *  `getBillingWaterfall`, not a replacement — that report's
	 *  new/expansion/contraction/churn stays at the partner grain; this one
	 *  adds the product grain and separates uplift from expansion. */
	async getBillingMovement(params: { from: string; to: string }): Promise<MovementReport> {
		const q = new URLSearchParams({ from: params.from, to: params.to });
		return this.request(`/billing/metrics/movement?${q.toString()}`);
	}

	/** The same product-movement bridge as `getBillingMovement`, one row per
	 *  calendar period — month or year (Brief Q). There is no separate
	 *  detail endpoint: a caller wanting the partner-product rows behind one
	 *  period calls `getBillingMovement` with that row's own
	 *  `period_start`/`period_end`. */
	async getBillingMovementSeries(params: {
		from: string;
		to: string;
		interval?: MovementSeriesInterval;
	}): Promise<MovementSeriesReport> {
		const q = new URLSearchParams({ from: params.from, to: params.to });
		if (params.interval) q.set('interval', params.interval);
		return this.request(`/billing/metrics/movement/series?${q.toString()}`);
	}

	// Roam — responsive Workstream B (roam-responsive-pwa-spec.md §7).
	// "Send this link to me" over the Comms Hub staff channel. `sent`
	// is best-effort delivery-RAIL intake, not delivery confirmation
	// (COS_COMMS_HUB_SEND defaults off everywhere — the message queues
	// either way); `sent:false` is a normal degrade (no partner tenant,
	// or the Hub isn't configured here), never an error the caller
	// needs to react to beyond falling back to copying the link.
	async sendGateLink(url: string, surfaceLabel: string): Promise<{ sent: boolean; reason?: string }> {
		return this.request('/roam/send-link', {
			method: 'POST',
			body: JSON.stringify({ url, label: surfaceLabel }),
		});
	}

	// One beacon per capability-gate render — the prioritization data
	// for what to make mobile-capable next (§17). Fire-and-forget:
	// callers must not let a failure here affect the gated surface.
	async reportGateHit(route: string, tier: string, level: string): Promise<void> {
		await this.request('/roam/gate-hit', {
			method: 'POST',
			body: JSON.stringify({ route, tier, level }),
		});
	}

	// ═══════════════════════════════════════════════════════════════════
	// DESK CIRCULATION — the ILS provider seam (added by the desk-ils-seam
	// seat; appended as one block to keep the merge with parallel work on
	// this file trivial).
	//
	// These go through Desk's provider seam, so the answer comes from
	// whichever ILS the library actually runs. Every response carries
	// `connected`: false means that library's ILS has no adapter yet (it
	// names which ILS) — an honest answer, never an error. A refusal
	// ("blocked", "already checked out") also arrives on a 200, because
	// the ILS answered; only an unreachable ILS is a 502.
	//
	// `clientId` follows the getDeskNow convention exactly: >0 for a
	// Communico staffer (the shared nav client picker's pick), 0/omitted
	// for library staff (their own tenant is inferred server-side).
	// `branchId` is the desk's own branch — required for a checkout or
	// check-in, because an ILS records a loan at a place.
	// ═══════════════════════════════════════════════════════════════════

	async findDeskItem(barcode: string, branchId = 0, clientId = 0) {
		const params = new URLSearchParams({ barcode });
		if (branchId > 0) params.set("branch_id", String(branchId));
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/desk/item?${params.toString()}`);
	}

	// "Do you have X" — a title/author/barcode term against the whole
	// catalogue, grouped by title. The SAME call resolves a scanned
	// barcode too (circulate's own search ORs it into the query) — there
	// is no separate barcode-lookup path from this screen.
	async searchDeskCatalogue(term: string, branchId = 0, clientId = 0, hereOnly = false) {
		const params = new URLSearchParams({ q: term });
		if (branchId > 0) params.set("branch_id", String(branchId));
		// Only titles with a copy at branch_id. Meaningless without one.
		if (hereOnly && branchId > 0) params.set("here", "true");
		if (clientId > 0) params.set("client_id", String(clientId));
		return this.request(`/desk/catalogue-search?${params.toString()}`);
	}

	async deskPlaceHold(
		body: DeskPlaceHoldRequest,
		clientId = 0,
	) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/holds${qs}`, { method: "POST", body: JSON.stringify(body) });
	}

	/** One wish for any of several titles, or several copies of one. */
	async deskPlaceHoldGroup(body: DeskPlaceHoldGroupRequest, clientId = 0) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/circulation/hold-groups${qs}`, { method: "POST", body: JSON.stringify(body) });
	}

	async deskCheckout(
		body: DeskCheckoutRequest,
		clientId = 0,
	) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/checkout${qs}`, { method: "POST", body: JSON.stringify(body) });
	}

	async deskCheckin(body: { item_barcode: string; branch_id: number }, clientId = 0) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/checkin${qs}`, { method: "POST", body: JSON.stringify(body) });
	}

	// ── Held transactions ("the handover", docs/modules/desk.md board
	// section): a hold records what has ALREADY happened, never a
	// pending basket. GET returns { held: DeskHeldTransaction | null };
	// a null `held` (or a 404) is the ordinary "nothing held here" answer
	// and hides the board banner outright rather than showing an error.
	async getDeskHeld(branchId: number, clientId = 0, servicePointId = 0) {
		const params = new URLSearchParams({ branch_id: String(branchId) });
		if (clientId > 0) params.set("client_id", String(clientId));
		if (servicePointId > 0) params.set("service_point_id", String(servicePointId));
		return this.request(`/desk/held?${params.toString()}`);
	}

	// `opts.keepalive` lets a real tab-close (the `pagehide` case) survive
	// page teardown for this one small write: the standard use of
	// fetch's `keepalive` flag, since `navigator.sendBeacon` can't carry
	// the Authorization header this request needs.
	async putDeskHeld(
		body: {
			branch_id: number;
			client_id?: number;
			service_point_id?: number;
			action: string;
			person_id?: number;
			// The Circulate cardholder id (never the card number); a hold names this or person_id.
			patron_id?: string;
			person_display: string;
			summary: { item_count: number; items?: Array<{ title: string; barcode?: string; due?: string }> };
		},
		opts: { keepalive?: boolean } = {},
	) {
		return this.request(`/desk/held`, {
			method: "PUT",
			body: JSON.stringify(body),
			keepalive: opts.keepalive,
		});
	}

	async deleteDeskHeld(id: number, clientId = 0) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/held/${id}${qs}`, { method: "DELETE" });
	}

	async deskIssueCard(
		body: {
			barcode: string;
			name: string;
			patron_type: string;
			branch_id: number;
			email?: string;
			phone?: string;
			address_line1?: string;
			address_line2?: string;
			city?: string;
			state?: string;
			postal_code?: string;
			date_of_birth?: string;
			home_library_branch_id?: number;
		},
		clientId = 0,
	) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/issue-card${qs}`, { method: "POST", body: JSON.stringify(body) });
	}

	// This library's ILS-fed patron-type category list, for the "Issue a
	// (getDeskPatronTypes lives with the other desk patron methods above —
	// two parallel fleet branches each added one; the earlier definition
	// is the canonical and this comment marks where the duplicate sat.)

	// Replace a lost, stolen or damaged card: reissues an already-
	// identified patron's card through the ILS provider seam. patronId is
	// circulate's OWN patron id (DeskCandidate.patron_id off an already-
	// resolved /desk/lookup) — never a barcode.
	async deskReissueCard(
		patronId: string,
		body: { branch_id: number; new_barcode: string; reason: string },
		clientId = 0,
	) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/patron/${encodeURIComponent(patronId)}/reissue-card${qs}`, {
			method: "POST",
			body: JSON.stringify(body),
		});
	}

	// ── Desk log, the service-desk interaction log ("Desk log") ───────
	// /api/v1/desk/log/*. Tenant resolution mirrors every other Desk
	// route: client_id names the caller's tenant, resolved to a COS
	// partner server-side, same contract as getDeskLookup/getDeskNow.

	async getDeskLogVocab(clientId = 0) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/log/vocab${qs}`);
	}

	async putDeskLogVocab(
		body: { categories: DeskLogCategory[]; tags: DeskLogTag[]; patron_types?: DeskLogPatronType[] },
		clientId = 0,
	) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/log/vocab${qs}`, { method: "PUT", body: JSON.stringify(body) });
	}

	async getDeskLogTapboard(branchId: number, clientId = 0) {
		const params = new URLSearchParams();
		if (branchId > 0) params.set("branch_id", String(branchId));
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/desk/log/tapboard${qs ? `?${qs}` : ""}`);
	}

	async putDeskLogTapboard(
		body: { branch_id: number; name: string; buttons: DeskLogTapboardButton[] },
		clientId = 0,
	) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/log/tapboard${qs}`, { method: "PUT", body: JSON.stringify(body) });
	}

	async createDeskLogInteraction(body: Record<string, unknown>, clientId = 0) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/log/interactions${qs}`, { method: "POST", body: JSON.stringify(body) });
	}

	async getDeskLogInteractions(
		params: Record<string, string | number | undefined>,
		clientId = 0,
	) {
		const qp = new URLSearchParams();
		for (const [k, v] of Object.entries(params)) {
			if (v !== undefined && v !== "") qp.set(k, String(v));
		}
		if (clientId > 0) qp.set("client_id", String(clientId));
		const qs = qp.toString();
		return this.request(`/desk/log/interactions${qs ? `?${qs}` : ""}`);
	}

	async getDeskLogInteraction(id: number, clientId = 0) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/log/interactions/${id}${qs}`);
	}

	async updateDeskLogInteraction(id: number, body: Record<string, unknown>, clientId = 0) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/log/interactions/${id}${qs}`, { method: "PATCH", body: JSON.stringify(body) });
	}

	async deleteDeskLogInteraction(id: number, clientId = 0) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/log/interactions/${id}${qs}`, { method: "DELETE" });
	}

	async getDeskLogReportSummary(
		params: Record<string, string | number | undefined>,
		clientId = 0,
	) {
		const qp = new URLSearchParams();
		for (const [k, v] of Object.entries(params)) {
			if (v !== undefined && v !== "") qp.set(k, String(v));
		}
		if (clientId > 0) qp.set("client_id", String(clientId));
		const qs = qp.toString();
		return this.request(`/desk/log/reports/summary${qs ? `?${qs}` : ""}`);
	}

	// Desk log reports, CSV export. Bearer-fetched Blob (the getTicketsCSV /
	// getRosterHoursReportCSV precedent: the token lives in sessionStorage, a
	// plain <a href> navigation would 401).
	async getDeskLogReportExportCSV(
		params: Record<string, string | number | undefined>,
		clientId = 0,
	): Promise<Blob> {
		const headers: Record<string, string> = {};
		const tok = this.getToken();
		if (tok) headers["Authorization"] = `Bearer ${tok}`;
		// A raw fetch, bypassing request(), so the Desk log headers it
		// would otherwise attach have to be repeated here.
		if (this.surface) headers[SURFACE_HEADER] = this.surface;
		if (this.servicePointId) headers[SERVICE_POINT_HEADER] = String(this.servicePointId);
		if (this.branchId) headers[BRANCH_HEADER] = String(this.branchId);
		const qp = new URLSearchParams();
		for (const [k, v] of Object.entries(params)) {
			if (v !== undefined && v !== "") qp.set(k, String(v));
		}
		if (clientId > 0) qp.set("client_id", String(clientId));
		const qs = qp.toString();
		const resp = await fetch(`${API_BASE}/desk/log/reports/export.csv${qs ? `?${qs}` : ""}`, { headers });
		if (!resp.ok) throw new ApiError(resp.status, `CSV export failed (${resp.status})`);
		return resp.blob();
	}

	// Desk log wave 2, GET/PATCH /desk/log/settings. See DeskLogSettings's
	// own doc comment: PATCH is a partial merge, never a full replace.
	async getDeskLogSettings(clientId = 0) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/log/settings${qs}`);
	}

	async patchDeskLogSettings(body: Record<string, unknown>, clientId = 0) {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/log/settings${qs}`, { method: "PATCH", body: JSON.stringify(body) });
	}

	/** Desk's setup pathway (docs/modules/setup-pathway.md): currently one
	 *  step, the board's two attention windows, in the shared pathway
	 *  shape so <cos-setup-pathway> renders it like every other module's. */
	async getDeskSetupState(clientId = 0): Promise<PathwayState> {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/desk/log/setup-state${qs}`);
	}

	// Circulate "Staff access by network" (docs/modules/circulate.md): the
	// library's optional network limit on staff access through the
	// /circulation proxy. Communico staff name the library by legacy client id.
	async getCirculateApiAccess(clientId?: number) {
		return this.request(`/circulate/api-access${toQuery({ client_id: clientId })}`);
	}

	async putCirculateApiAccess(
		body: { enabled: boolean; allowed_networks: string[]; confirm_lockout?: boolean },
		clientId?: number,
	) {
		return this.request(`/circulate/api-access${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(body),
		});
	}

	async getCirculateApiAccessSetupState(clientId?: number): Promise<PathwayState> {
		return this.request(`/circulate/api-access/setup-state${toQuery({ client_id: clientId })}`);
	}

	// Desk board (fleet/desk-board shared contract): the per-branch/
	// partner default layout a manager can set, and the resolved default a
	// staffer with no own preference falls back to. `branchId` 0 means
	// "the partner-wide default" for a PUT; a GET always resolves branch
	// first, else partner, else the built-in board (the response's own
	// `source` names which). Both routes may not exist yet on an older
	// backend, so the desk board degrades to no-pills/builtin-order on any
	// error, never a red error state (see cos-desk-board.ts).
	async getDeskBoardDefault(branchId = 0, clientId = 0) {
		const params = new URLSearchParams();
		if (branchId > 0) params.set("branch_id", String(branchId));
		if (clientId > 0) params.set("client_id", String(clientId));
		const qs = params.toString();
		return this.request(`/desk/board/default${qs ? `?${qs}` : ""}`);
	}

	async setDeskBoardDefault(branchId: number, clientId: number, layout: unknown) {
		return this.request(`/desk/board/default`, {
			method: "PUT",
			body: JSON.stringify({ branch_id: branchId, client_id: clientId, layout }),
		});
	}

	// -----------------------------------------------------------------
	// Explore admin: the read side.
	//
	// All four are READS even though three are POSTs, because their
	// arguments do not survive a query string: filterBy is a repeated
	// list, advanced is a nested object, boosts is a free-form map.
	//
	// clientId is the LIBRARY's legacy client id and is only honoured for
	// Communico staff; a library caller is pinned to their own tenant
	// server-side and the param is ignored for them. It decides which
	// catalogue is read, so passing the wrong one succeeds silently
	// against the wrong library.
	//
	// There is nothing here for search tuning, curation or import status:
	// those tables are inert, so those three features read and write the
	// mock layer in explore/mock-store.ts. When the write API lands, the
	// swap is adding methods here and pointing the store at them.
	// -----------------------------------------------------------------

	async exploreSearch(body: ExploreSearchRequest, clientId = 0): Promise<ExploreSearchResult> {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/explore/search${qs}`, { method: "POST", body: JSON.stringify(body) });
	}

	// workKey is opaque and contains "||" and ":", so it goes in a query
	// param and must be encoded. Never split it, never rebuild it.
	async exploreWork(workKey: string, clientId = 0): Promise<ExploreWork> {
		const qp = new URLSearchParams({ work_key: workKey });
		if (clientId > 0) qp.set("client_id", String(clientId));
		return this.request(`/explore/work?${qp.toString()}`);
	}

	async exploreWorkByEdition(editionId: string, clientId = 0): Promise<ExploreWork> {
		const qp = new URLSearchParams({ edition_id: editionId });
		if (clientId > 0) qp.set("client_id", String(clientId));
		return this.request(`/explore/work?${qp.toString()}`);
	}

	// An empty array is the NORMAL outcome: only about 38% of works carry
	// a similarity vector. Do not render it as an error.
	async exploreSimilar(body: ExploreSimilarRequest, clientId = 0): Promise<ExploreWork[]> {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/explore/similar${qs}`, { method: "POST", body: JSON.stringify(body) });
	}

	async exploreWorksList(body: ExploreWorksListRequest, clientId = 0): Promise<ExploreSearchResult> {
		const qs = clientId > 0 ? `?client_id=${clientId}` : "";
		return this.request(`/explore/works${qs}`, { method: "POST", body: JSON.stringify(body) });
	}

	// Schedule: booking detail
	async getScheduleBooking(clientId: number, id: number) {
		return this.request(`/schedule/bookings/${id}${toQuery({ client_id: clientId })}`);
	}
	// end Schedule: booking detail

	// Schedule: booking form
	// The three GETs behind the book and edit screens, the patron lookup, and
	// the create and edit writes. Every one carries client_id (Communico staff
	// pick a library; the server pins library staff to their own).
	async getScheduleFormGroups(clientId: number) {
		return this.request(`/schedule/groups${toQuery({ client_id: clientId })}`);
	}
	async getScheduleGroupSettings(clientId: number, groupId: number) {
		return this.request(`/schedule/groups/${groupId}/settings${toQuery({ client_id: clientId })}`);
	}
	async getScheduleGroupAvailability(clientId: number, groupId: number, date: string, days = 7, excludeBookingId = 0, multiplier = 1) {
		return this.request(
			`/schedule/groups/${groupId}/availability${toQuery({
				client_id: clientId,
				date,
				days,
				exclude_booking_id: excludeBookingId > 0 ? excludeBookingId : undefined,
				multiplier: multiplier > 1 ? multiplier : undefined,
			})}`,
		);
	}
	async lookupSchedulePatron(clientId: number, barcode: string) {
		return this.request(`/schedule/patrons/lookup${toQuery({ client_id: clientId, barcode })}`);
	}
	async createScheduleBooking(clientId: number, body: Record<string, unknown>) {
		return this.request(`/schedule/bookings${toQuery({ client_id: clientId })}`, { method: 'POST', body: JSON.stringify(body) });
	}
	async updateScheduleBooking(clientId: number, id: number, body: Record<string, unknown>) {
		return this.request(`/schedule/bookings/${id}${toQuery({ client_id: clientId })}`, { method: 'PUT', body: JSON.stringify(body) });
	}
	// end Schedule: booking form

	// Schedule: booking actions
	// One POST per verb (PUT for notes). Every answer is a ScheduleActionResult.
	private postScheduleVerb(clientId: number, id: number, path: string, body: Record<string, unknown> = {}, method: 'POST' | 'PUT' = 'POST') {
		return this.request(`/schedule/bookings/${id}/${path}${toQuery({ client_id: clientId })}`, {
			method,
			body: JSON.stringify(body),
		});
	}
	async approveScheduleBooking(clientId: number, id: number) {
		return this.postScheduleVerb(clientId, id, 'approve');
	}
	async declineScheduleBooking(clientId: number, id: number, reason: string) {
		return this.postScheduleVerb(clientId, id, 'decline', { reason });
	}
	async cancelScheduleBooking(clientId: number, id: number) {
		return this.postScheduleVerb(clientId, id, 'cancel');
	}
	async markScheduleBookingPreparationComplete(clientId: number, id: number) {
		return this.postScheduleVerb(clientId, id, 'preparation-complete');
	}
	async undoScheduleBookingPreparationComplete(clientId: number, id: number) {
		return this.postScheduleVerb(clientId, id, 'preparation-complete/undo');
	}
	async markScheduleBookingComplete(clientId: number, id: number) {
		return this.postScheduleVerb(clientId, id, 'complete');
	}
	async undoScheduleBookingComplete(clientId: number, id: number) {
		return this.postScheduleVerb(clientId, id, 'complete/undo');
	}
	async setScheduleBookingNoShow(clientId: number, id: number, on: boolean) {
		return this.postScheduleVerb(clientId, id, 'no-show', { on });
	}
	async updateScheduleBookingNotes(clientId: number, id: number, staffNotes: string) {
		return this.postScheduleVerb(clientId, id, 'notes', { staff_notes: staffNotes }, 'PUT');
	}
	async addScheduleBookingMessage(clientId: number, id: number, message: string) {
		return this.postScheduleVerb(clientId, id, 'messages', { message });
	}
	async resendScheduleBookingReminder(clientId: number, id: number) {
		return this.postScheduleVerb(clientId, id, 'resend-reminder');
	}
	// end Schedule: booking actions

	// Schedule: settings categories
	// Stage 4, slice A. Every call carries client_id (Communico staff pick a
	// library; the server pins library staff to their own).
	async getScheduleSettingsCategories(clientId: number) {
		return this.request(`/schedule/settings/categories${toQuery({ client_id: clientId })}`);
	}
	async createScheduleSettingsCategory(clientId: number, body: Record<string, unknown>) {
		return this.request(`/schedule/settings/categories${toQuery({ client_id: clientId })}`, { method: 'POST', body: JSON.stringify(body) });
	}
	async getScheduleSettingsCategory(clientId: number, id: number) {
		return this.request(`/schedule/settings/categories/${id}${toQuery({ client_id: clientId })}`);
	}
	// A patch: only the fields in body are written.
	async updateScheduleSettingsCategory(clientId: number, id: number, body: Record<string, unknown>) {
		return this.request(`/schedule/settings/categories/${id}${toQuery({ client_id: clientId })}`, { method: 'PUT', body: JSON.stringify(body) });
	}
	async deleteScheduleSettingsCategory(clientId: number, id: number) {
		return this.request(`/schedule/settings/categories/${id}${toQuery({ client_id: clientId })}`, { method: 'DELETE' });
	}
	async setScheduleSettingsCategoryModel(clientId: number, id: number, availabilityModel: string) {
		return this.request(`/schedule/settings/categories/${id}/model${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ availability_model: availabilityModel }),
		});
	}
	async getScheduleSettingsCategoryGroups(clientId: number, id: number) {
		return this.request(`/schedule/settings/categories/${id}/groups${toQuery({ client_id: clientId })}`);
	}
	async getScheduleSettingsCategoryFilterLists(clientId: number, id: number) {
		return this.request(`/schedule/settings/categories/${id}/filter-lists${toQuery({ client_id: clientId })}`);
	}
	async saveScheduleSettingsCategoryFilterLists(clientId: number, id: number, lists: unknown[]) {
		return this.request(`/schedule/settings/categories/${id}/filter-lists${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ lists }),
		});
	}
	async getScheduleSettingsCategoryQR(clientId: number, id: number) {
		return this.request(`/schedule/settings/categories/${id}/qr${toQuery({ client_id: clientId })}`);
	}
	// end Schedule: settings categories
	// Schedule: settings providers
	// Stage 4, slice C: the PROVIDER model's people, assignments, weekly hours,
	// dated changes (typed exceptions) and the provider week. Every call carries
	// client_id (Communico staff pick a library; the server pins library staff to
	// their own). Bodies are the server's snake_case shapes
	// (types/schedule-providers.ts).
	async getScheduleProviderRoster(clientId: number, categoryId: number) {
		return this.request(`/schedule/settings/categories/${categoryId}/providers${toQuery({ client_id: clientId })}`);
	}
	async createScheduleProvider(clientId: number, categoryId: number, body: Record<string, unknown>) {
		return this.request(`/schedule/settings/categories/${categoryId}/providers${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}
	async updateScheduleProvider(clientId: number, providerId: number, body: Record<string, unknown>) {
		return this.request(`/schedule/settings/providers/${providerId}${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify(body),
		});
	}
	async setScheduleProviderArchived(clientId: number, providerId: number, archived: boolean) {
		return this.request(
			`/schedule/settings/providers/${providerId}/${archived ? 'archive' : 'restore'}${toQuery({ client_id: clientId })}`,
			{ method: 'POST', body: JSON.stringify({}) },
		);
	}
	async assignScheduleProvider(clientId: number, providerId: number, groupId: number) {
		return this.request(`/schedule/settings/providers/${providerId}/assign${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ group_id: groupId }),
		});
	}
	async unassignScheduleProvider(clientId: number, providerId: number, groupId: number) {
		return this.request(`/schedule/settings/providers/${providerId}/unassign${toQuery({ client_id: clientId })}`, {
			method: 'POST',
			body: JSON.stringify({ group_id: groupId }),
		});
	}
	async getScheduleStaff(clientId: number, categoryId: number) {
		return this.request(`/schedule/settings/staff${toQuery({ client_id: clientId, category_id: categoryId })}`);
	}
	async getScheduleZoomRooms(clientId: number, categoryId: number) {
		return this.request(`/schedule/settings/categories/${categoryId}/zoom-rooms${toQuery({ client_id: clientId })}`);
	}
	async getScheduleAssetBlocks(clientId: number, assetId: number) {
		return this.request(`/schedule/settings/assets/${assetId}/blocks${toQuery({ client_id: clientId })}`);
	}
	async saveScheduleAssetBlocks(clientId: number, assetId: number, blocks: unknown[]) {
		return this.request(`/schedule/settings/assets/${assetId}/blocks${toQuery({ client_id: clientId })}`, {
			method: 'PUT',
			body: JSON.stringify({ blocks }),
		});
	}
	async getScheduleTypedExceptions(clientId: number, categoryId: number) {
		return this.request(`/schedule/settings/categories/${categoryId}/typed-exceptions${toQuery({ client_id: clientId })}`);
	}
	async saveScheduleTypedException(clientId: number, categoryId: number, exceptionId: number, body: Record<string, unknown>) {
		const path =
			exceptionId > 0
				? `/schedule/settings/categories/${categoryId}/typed-exceptions/${exceptionId}`
				: `/schedule/settings/categories/${categoryId}/typed-exceptions`;
		return this.request(`${path}${toQuery({ client_id: clientId })}`, {
			method: exceptionId > 0 ? 'PUT' : 'POST',
			body: JSON.stringify(body),
		});
	}
	async deleteScheduleTypedException(clientId: number, categoryId: number, exceptionId: number) {
		return this.request(
			`/schedule/settings/categories/${categoryId}/typed-exceptions/${exceptionId}${toQuery({ client_id: clientId })}`,
			{ method: 'DELETE' },
		);
	}
	async getScheduleProviderWeek(clientId: number, providerId: number) {
		return this.request(`/schedule/settings/providers/${providerId}/week${toQuery({ client_id: clientId })}`);
	}
	// end Schedule: settings providers

	// ── Agent platform admin (/admin/agents, PRD 11) ─────────────────────
	//
	// Admin-only (RequireAdminRole). Bodies are decoded strictly server side,
	// so callers send exactly the documented keys. Refusals come back as
	// {error, code}; ApiError carries both.

	async getAgentRegistry(): Promise<AgentRegistry> {
		return this.request(`/admin/agents/registry`);
	}
	async getAgents(): Promise<AgentListing> {
		return this.request(`/admin/agents`);
	}
	async createAgent(slug: string, config: AgentConfig): Promise<Agent> {
		return this.request(`/admin/agents`, { method: 'POST', body: JSON.stringify({ slug, config }) });
	}
	async getAgent(id: number): Promise<AgentDetail> {
		return this.request(`/admin/agents/${id}`);
	}
	async saveAgent(id: number, baseVersion: number, summary: string, config: AgentConfig): Promise<AgentSaveResult> {
		return this.request(`/admin/agents/${id}`, {
			method: 'PUT',
			body: JSON.stringify({ base_version: baseVersion, summary, config }),
		});
	}
	async getAgentVersions(id: number): Promise<AgentVersion[] | null> {
		return this.request(`/admin/agents/${id}/versions`);
	}
	async activateAgent(id: number): Promise<AgentActivateResult> {
		return this.request(`/admin/agents/${id}/activate`, { method: 'POST' });
	}
	async pauseAgent(id: number): Promise<Agent> {
		return this.request(`/admin/agents/${id}/pause`, { method: 'POST' });
	}
	async retireAgent(id: number): Promise<Agent> {
		return this.request(`/admin/agents/${id}/retire`, { method: 'POST' });
	}
	// Claude accounts: the pool of seats agents run on. Sign-in is per
	// account; COS never sees the credential.
	async getClaudeAccounts(): Promise<ClaudeAccountListing> {
		return this.request(`/admin/claude-accounts`);
	}
	async createClaudeAccount(input: ClaudeAccountInput): Promise<ClaudeAccount> {
		return this.request(`/admin/claude-accounts`, { method: 'POST', body: JSON.stringify(input) });
	}
	async updateClaudeAccount(id: number, input: Omit<ClaudeAccountInput, 'host_id'>): Promise<ClaudeAccount> {
		const { label, email, notes } = input;
		return this.request(`/admin/claude-accounts/${id}`, { method: 'PUT', body: JSON.stringify({ label, email, notes }) });
	}
	async retireClaudeAccount(id: number): Promise<ClaudeAccount> {
		return this.request(`/admin/claude-accounts/${id}/retire`, { method: 'POST' });
	}
	async requestClaudeAccountSignin(id: number): Promise<{ request_id: string; delivered: boolean }> {
		return this.request(`/admin/claude-accounts/${id}/signin-request`, { method: 'POST' });
	}
	// MCP library tool pool: which library-capable tools library staff may reach.
	async getMcpLibraryTools(): Promise<McpLibraryToolList> {
		return this.request(`/admin/mcp-library-tools`);
	}
	async addMcpLibraryTool(toolName: string): Promise<void> {
		await this.request(`/admin/mcp-library-tools`, { method: 'POST', body: JSON.stringify({ tool_name: toolName }) });
	}
	async removeMcpLibraryTool(toolName: string): Promise<void> {
		await this.request(`/admin/mcp-library-tools/${encodeURIComponent(toolName)}`, { method: 'DELETE' });
	}
	async createAgentSlackApp(id: number): Promise<SlackAppResult> {
		return this.request(`/admin/agents/${id}/slack/app`, { method: 'POST' });
	}
	async getAgentSlackInstallLink(id: number): Promise<SlackAppResult> {
		return this.request(`/admin/agents/${id}/slack/install-link`, { method: 'POST' });
	}
	async setAgentSlackAppToken(id: number, token: string): Promise<void> {
		return this.request(`/admin/agents/${id}/slack/app-token`, { method: 'PUT', body: JSON.stringify({ token }) });
	}
	async getAgentHosts(): Promise<AgentHostHealth[] | null> {
		return this.request(`/admin/agent-hosts`);
	}
	async registerAgentHost(name: string, kind: string): Promise<HostCredentialResult> {
		return this.request(`/admin/agent-hosts`, { method: 'POST', body: JSON.stringify({ name, kind }) });
	}
	async rotateAgentHost(id: number): Promise<HostCredentialResult> {
		return this.request(`/admin/agent-hosts/${id}/rotate`, { method: 'POST' });
	}
	async revokeAgentHost(id: number): Promise<void> {
		return this.request(`/admin/agent-hosts/${id}/revoke`, { method: 'POST' });
	}
	async getAgentSlackConfig(): Promise<SlackConfigStatus> {
		return this.request(`/admin/agent-platform/slack-config`);
	}
	async setAgentSlackConfig(token: string): Promise<SlackConfigStatus> {
		return this.request(`/admin/agent-platform/slack-config`, { method: 'PUT', body: JSON.stringify({ token }) });
	}
	async getAgentCompanyPolicy(): Promise<CompanyPolicy> {
		return this.request(`/admin/agent-platform/company-policy`);
	}
	async saveAgentCompanyPolicy(baseVersion: number, bodyMd: string, summary: string): Promise<AgentSaveResult> {
		return this.request(`/admin/agent-platform/company-policy`, {
			method: 'PUT',
			body: JSON.stringify({ base_version: baseVersion, body_md: bodyMd, summary }),
		});
	}
	async getAgentApprovalsApp(): Promise<ApprovalsAppStatus> {
		return this.request(`/admin/agent-platform/approvals-app`);
	}
	async setAgentApprovalsApp(botToken: string, signingSecret: string): Promise<ApprovalsAppStatus> {
		return this.request(`/admin/agent-platform/approvals-app`, {
			method: 'PUT',
			body: JSON.stringify({ bot_token: botToken, signing_secret: signingSecret }),
		});
	}

	// The approval page (/agent-approvals/:id, PRD 7.5): staff auth, not
	// admin. A proposal the viewer may not see is a 404. A refused (403) or
	// already decided (409) decision throws ApiError; its `body` is the
	// ApprovalDecision, carrying the refreshed approval view.
	async getAgentApproval(id: string): Promise<ApprovalView> {
		return this.request(`/agent-approvals/${encodeURIComponent(id)}`);
	}
	async approveAgentProposal(id: string): Promise<ApprovalDecision> {
		return this.request(`/agent-approvals/${encodeURIComponent(id)}/approve`, { method: 'POST' });
	}
	async declineAgentProposal(id: string, reason: string): Promise<ApprovalDecision> {
		return this.request(`/agent-approvals/${encodeURIComponent(id)}/decline`, {
			method: 'POST',
			body: JSON.stringify({ reason }),
		});
	}
	// Repeating tasks a person asked agents for in chat (agent missions M1):
	// staff auth. A person sees and changes their own; an admin may ask for
	// everyone's. Someone else's is a 404.
	async getAgentSchedules(everyone = false, includeCancelled = false): Promise<RecurringPage> {
		const q = new URLSearchParams();
		if (everyone) q.set('everyone', 'true');
		if (includeCancelled) q.set('include_cancelled', 'true');
		const qs = q.toString();
		return this.request(`/agent-schedules${qs ? `?${qs}` : ''}`);
	}
	async setAgentScheduleStatus(id: number, action: RecurringAction): Promise<RecurringTaskView> {
		return this.request(`/agent-schedules/${id}/${action}`, { method: 'POST' });
	}
	/** The agent editor's Repeating tab (admins). */
	async getAgentRecurring(agentId: number): Promise<RecurringPage> {
		return this.request(`/admin/agents/${agentId}/recurring`);
	}
	// Missions (agent missions M2, PRD 14.2): staff auth. A person sees and
	// acts on the missions they own; an admin may ask for everyone's and act
	// on any. Someone else's is a 404. Every action answers with the detail
	// as it now stands; a refusal (403, 409, 422) carries actionable text.
	/** Workflow runs (agent workflows W1): the viewer's own, or every run for an admin with everyone. */
	async getWorkflowRuns(everyone = false, status = ''): Promise<WorkflowRunListPage> {
		const q = new URLSearchParams();
		if (everyone) q.set('everyone', 'true');
		if (status) q.set('status', status);
		const qs = q.toString();
		return this.request(`/workflow-runs${qs ? `?${qs}` : ''}`);
	}
	/** One run's timeline: its owner's or an admin's; anyone else gets 404. */
	async getWorkflowRun(id: string): Promise<WorkflowRunDetail> {
		return this.request(`/workflow-runs/${encodeURIComponent(id)}`);
	}
	/** The Bash commands a step's code agent ran, in order (agent workflows W6). Admins only: anyone else gets 403. */
	async getWorkflowStepBash(runId: string, stepId: number): Promise<WorkflowStepBash> {
		return this.request(`/workflow-runs/${encodeURIComponent(runId)}/steps/${stepId}/bash`);
	}
	/** The node-type registry (palette, icons, routes): what the canvas draws node types from. */
	async getWorkflowNodeTypes(): Promise<FlowNodeTypeView[]> {
		return this.request('/workflow-node-types');
	}
	// Workflow definitions and the editor (agent workflows W5), admins only.
	/** Every definition, with its current version and any draft waiting. */
	async getWorkflowDefinitions(): Promise<WorkflowDefinition[]> {
		return this.request('/workflows');
	}
	/** One definition with its versions, newest first. */
	async getWorkflowDefinition(id: number): Promise<WorkflowDefinitionDetail> {
		return this.request(`/workflows/${id}`);
	}
	/** Every version, newest first, with its settings and definition fields (no graph). */
	async getWorkflowVersions(id: number): Promise<WorkflowVersionRow[]> {
		return this.request(`/workflows/${id}/versions`);
	}

	/** What changed from version a to version b (either may be the draft). */
	async getWorkflowVersionDiff(id: number, a: number, b: number): Promise<WorkflowVersionDiff> {
		return this.request(`/workflows/${id}/versions/${a}/diff/${b}`);
	}

	/** Starts a run of the live version by hand (needs an active manual trigger); the input is checked against the inputs schema. A repeated idempotency_key answers the same run. */
	async startWorkflowRun(id: number, input: Record<string, unknown>, idempotencyKey: string): Promise<WorkflowRunStarted> {
		return this.request(`/workflows/${id}/runs`, { method: 'POST', body: JSON.stringify({ input, idempotency_key: idempotencyKey }) });
	}
	/** A blank definition with an empty draft version 1. */
	async createWorkflow(body: { slug: string; title: string; description: string }): Promise<WorkflowCreated> {
		return this.request('/workflows', { method: 'POST', body: JSON.stringify(body) });
	}
	/** A version (the current one unless given) as the document an import takes; its checksum is a draft save's base. */
	async exportWorkflow(id: number, version?: number): Promise<FlowDocument> {
		return this.request(`/workflows/${id}/export${version ? `?version=${version}` : ''}`);
	}
	/** Writes the document as a new draft (publish false), replacing any draft; a definition with its slug is updated. */
	async importWorkflow(document: unknown): Promise<WorkflowImportResult> {
		return this.request('/workflows/import', { method: 'POST', body: JSON.stringify({ document, publish: false }) });
	}
	/** The closed lists a workflow's settings and triggers are chosen from. */
	async getWorkflowVocabulary(): Promise<FlowVocabulary> {
		return this.request('/workflow-vocabulary');
	}

	/** Active staff by name or email (q), or those ids named (ids; a person who can no longer start has staff false). Admins only. */
	async getWorkflowPeople(opts: { q?: string; ids?: number[] }): Promise<WorkflowPerson[]> {
		return this.request(`/workflow-people${toQuery({ q: opts.q, ids: opts.ids?.length ? opts.ids.join(',') : undefined })}`);
	}

	/** Saves the whole draft; a stale base_checksum is 409 draft_conflict and writes nothing. */
	async saveWorkflowDraft(id: number, body: DraftPayload): Promise<WorkflowDraftSaved> {
		return this.request(`/workflows/${id}/draft`, { method: 'PUT', body: JSON.stringify(body) });
	}
	/** Publishes the draft with a one-line summary; the answer names any widening. base_checksum is the draft the editor saw: one saved since is 409 draft_conflict. */
	async publishWorkflow(id: number, summary: string, baseChecksum: string): Promise<WorkflowImportResult> {
		return this.request(`/workflows/${id}/publish`, { method: 'POST', body: JSON.stringify({ summary, base_checksum: baseChecksum }) });
	}
	/** The viewer's inbox (agent workflows W3): open workflow requests they can answer and proposals they can decide. */
	async getWorkflowInbox(): Promise<WorkflowInboxPage> {
		return this.request('/workflow-inbox');
	}
	/** Answer a workflow request; pinned_ref is the version the page showed (a stale one is 409 request_moved). */
	async respondWorkflowRequest(id: number, answer: WorkflowInboxAnswer): Promise<WorkflowHumanAnswered> {
		return this.request(`/workflow-inbox/${id}/respond`, { method: 'POST', body: JSON.stringify(answer) });
	}
	/** Connect GitHub (agent workflows W4): the signed-in person's link. */
	async getMyGitHub(): Promise<GitHubLinkView> {
		return this.request('/me/github');
	}
	/** GitHub's authorize link for the signed-in person. */
	async startGitHubConnect(): Promise<{ url: string }> {
		return this.request('/me/github/connect');
	}
	/** Finish Connect GitHub with what GitHub handed back: never a login. */
	async finishGitHubConnect(code: string, state: string): Promise<GitHubLinkView> {
		return this.request('/me/github/connect', { method: 'POST', body: JSON.stringify({ code, state }) });
	}
	async unlinkGitHub(): Promise<GitHubLinkView> {
		return this.request('/me/github', { method: 'DELETE' });
	}
	/** The GitHub App and its repositories, admins only. */
	async getGitHubAdmin(): Promise<GitHubAdminView> {
		return this.request('/github/repos');
	}
	async addGitHubRepo(fullName: string): Promise<GitHubRepo> {
		return this.request('/github/repos', { method: 'POST', body: JSON.stringify({ full_name: fullName }) });
	}
	async updateGitHubRepo(id: number, patch: GitHubRepoPatch): Promise<GitHubRepo> {
		return this.request(`/github/repos/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
	}
	/** The workflow definitions, admins only (the repository page's review workflow choice). */
	async getWorkflowChoices(): Promise<WorkflowChoice[]> {
		return this.request('/workflows');
	}
	/** Decision engines (agent workflows W2), admins only: never a key, only its variable's name. */
	async getDecisionEngines(): Promise<DecisionEnginesPage> {
		return this.request('/decision-engines');
	}
	async createDecisionEngine(input: DecisionEngineInput): Promise<DecisionEngine> {
		return this.request('/decision-engines', { method: 'POST', body: JSON.stringify(input) });
	}
	async updateDecisionEngine(id: number, input: DecisionEngineInput): Promise<DecisionEngine> {
		return this.request(`/decision-engines/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
	}
	/** A workflow's metrics over its runs started in the last days (W5 metrics v1). */
	async getWorkflowMetrics(id: number, days: number): Promise<WorkflowMetrics> {
		return this.request(`/workflows/${id}/metrics?days=${days}`);
	}
	/** A repository's review metrics over the last days (W5 metrics v1). */
	async getGitHubRepoMetrics(id: number, days: number): Promise<GitHubRepoMetrics> {
		return this.request(`/github/repos/${id}/metrics?days=${days}`);
	}
	async getDecisionEngineComparison(days = 30): Promise<DecisionCompareReport> {
		return this.request(`/decision-engines/compare?days=${days}`);
	}
	async getDecisionEngineCalibration(id: number): Promise<DecisionCalibration[]> {
		return this.request(`/decision-engines/${id}/calibration`);
	}
	async getRecentDecisions(engine = '', errorsOnly = false): Promise<WorkflowDecision[]> {
		const q = new URLSearchParams();
		if (engine) q.set('engine', engine);
		if (errorsOnly) q.set('errors', 'true');
		const qs = q.toString();
		return this.request(`/decision-engines/decisions${qs ? `?${qs}` : ''}`);
	}
	async getDecision(id: number): Promise<WorkflowDecisionDetail> {
		return this.request(`/decision-engines/decisions/${id}`);
	}
	async getAgentMissions(everyone = false, status = ''): Promise<MissionListPage> {
		const q = new URLSearchParams();
		if (everyone) q.set('everyone', 'true');
		if (status) q.set('status', status);
		const qs = q.toString();
		return this.request(`/agent-missions${qs ? `?${qs}` : ''}`);
	}
	async getAgentMission(id: string): Promise<MissionDetail> {
		return this.request(`/agent-missions/${encodeURIComponent(id)}`);
	}
	/** The estimated cost of one task's subtree, or of the whole mission without a task. */
	async getAgentMissionCost(id: string, taskId?: string): Promise<MissionCostRollup> {
		const qs = taskId ? `?task_id=${encodeURIComponent(taskId)}` : '';
		return this.request(`/agent-missions/${encodeURIComponent(id)}/cost${qs}`);
	}
	async pauseAgentMission(id: string): Promise<MissionDetail> {
		return this.request(`/agent-missions/${encodeURIComponent(id)}/pause`, { method: 'POST' });
	}
	async resumeAgentMission(id: string): Promise<MissionDetail> {
		return this.request(`/agent-missions/${encodeURIComponent(id)}/resume`, { method: 'POST' });
	}
	async cancelAgentMission(id: string, reason: string): Promise<MissionDetail> {
		return this.request(`/agent-missions/${encodeURIComponent(id)}/cancel`, {
			method: 'POST',
			body: JSON.stringify(reason ? { reason } : {}),
		});
	}
	async setAgentMissionBudget(id: string, budget: MissionBudgetPatch): Promise<MissionDetail> {
		return this.request(`/agent-missions/${encodeURIComponent(id)}/budget`, {
			method: 'POST',
			body: JSON.stringify(budget),
		});
	}
	/**
	 * Downloads a mission file (agent missions M3). The server answers either a
	 * five-minute link whose response is Content-Disposition: attachment (so an
	 * agent's HTML never renders here), or the file itself as an attachment.
	 * Every download is an audit row on the server.
	 */
	async downloadAgentMissionFile(missionId: string, fileId: string, name: string): Promise<void> {
		const headers: Record<string, string> = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const resp = await fetch(
			`${API_BASE}/agent-missions/${encodeURIComponent(missionId)}/files/${encodeURIComponent(fileId)}/download`,
			{ headers },
		);
		if (!resp.ok) {
			const b = (await resp.json().catch(() => ({}))) as { error?: string; code?: string };
			throw new ApiError(resp.status, b.error || `Download failed (${resp.status})`, false, undefined, undefined, b.code);
		}
		const a = document.createElement('a');
		let objectURL = '';
		if ((resp.headers.get('Content-Type') || '').includes('application/json')) {
			const link = (await resp.json()) as { url: string };
			a.href = link.url;
		} else {
			objectURL = URL.createObjectURL(await resp.blob());
			a.href = objectURL;
			a.download = name;
		}
		a.rel = 'noopener';
		try {
			document.body.appendChild(a);
			a.click();
			a.remove();
		} finally {
			if (objectURL) setTimeout(() => URL.revokeObjectURL(objectURL), 1000);
		}
	}
	/** Admins only. */
	async setAgentMissionOwner(id: string, userId: number): Promise<MissionDetail> {
		return this.request(`/agent-missions/${encodeURIComponent(id)}/owner`, {
			method: 'POST',
			body: JSON.stringify({ user_id: userId }),
		});
	}
	// Playbooks (agent missions M5, PRD 14.4): admin only. A save is the
	// next version, applied at once; base_version must be the version the
	// form was loaded at (409 otherwise). A refused spec is 422 with the
	// field and the reason.
	async getAgentPlaybooks(): Promise<PlaybookListing> {
		return this.request(`/admin/agent-playbooks`);
	}
	async getAgentPlaybook(id: number): Promise<PlaybookDetail> {
		return this.request(`/admin/agent-playbooks/${id}`);
	}
	async createAgentPlaybook(slug: string, w: PlaybookWrite): Promise<PlaybookDetail> {
		return this.request(`/admin/agent-playbooks`, { method: 'POST', body: JSON.stringify({ slug, ...w }) });
	}
	async saveAgentPlaybook(id: number, baseVersion: number, w: PlaybookWrite): Promise<PlaybookSaved> {
		return this.request(`/admin/agent-playbooks/${id}`, {
			method: 'PUT',
			body: JSON.stringify({ base_version: baseVersion, ...w }),
		});
	}
	async retireAgentPlaybook(id: number): Promise<Playbook> {
		return this.request(`/admin/agent-playbooks/${id}/retire`, { method: 'POST' });
	}
	// end Agent platform admin
}

export const api = new ApiClient();
